import * as React from 'react';
import React__default, { createContext, useMemo, useContext, useState, useLayoutEffect, useEffect, useRef, useCallback } from 'react';
import { AnnotationLabel, AnnotationCalloutCircle, AnnotationCalloutRect, AnnotationCalloutCustom, AnnotationXYThreshold, AnnotationCallout, AnnotationBracket } from 'react-annotation';
import labella from 'labella';
import { line, curveLinear, arc, curveLinearClosed, curveCardinal, curveStep, curveStepBefore, curveStepAfter, curveBasis, curveCatmullRom, curveMonotoneY, curveMonotoneX, curveNatural, area, linkHorizontal, linkVertical } from 'd3-shape';
import { sum, extent as extent$1, quantile, histogram, max, min } from 'd3-array';
import { scaleLinear, scaleOrdinal, scaleBand } from 'd3-scale';
import { brushX, brushY, brush } from 'd3-brush';
import { select } from 'd3-selection';
import { Delaunay } from 'd3-delaunay';
import { interpolateNumber } from 'd3-interpolate';
import { contourDensity } from 'd3-contour';
import { hexbin } from 'd3-hexbin';
import regression from 'regression';
import { packEnclose, hierarchy, tree, pack, cluster, treemap, partition } from 'd3-hierarchy';
import { polygonHull } from 'd3-polygon';
import Offset from 'polygon-offset';
import { forceSimulation, forceY, forceX, forceCollide, forceLink, forceManyBody } from 'd3-force';
import pathBounds from 'svg-path-bounding-box';
import { d } from 'd3-glyphedge';
import { pathArrows } from 'd3-path-arrows';
import { sankeyCircular, sankeyLeft, sankeyRight, sankeyCenter, sankeyJustify } from 'd3-sankey-circular';
import { ribbon, chord } from 'd3-chord';
import memoize from 'memoize-one';

// from Evan Wang's https://github.com/tinker10/D3-Labeler
function labeler () {
    let lab = [], anc = [], w = 1, // box width
    h = 1, // box width
    labeler = {
        start: x => { },
        width: x => { },
        height: x => { },
        label: x => { },
        anchor: x => { },
        alt_energy: x => { },
        alt_schedule: x => { }
    };
    let max_move = 5.0, max_angle = 0.5;
    // weights
    let w_len = 0.2, // leader line length
    w_inter = 1.0, // leader line intersection
    w_lab2 = 30.0, // label-label overlap
    w_lab_anc = 30.0, // label-anchor overlap
    w_orient = 3.0; // orientation bias
    // booleans for user defined functions
    let user_energy = false;
    let user_defined_energy;
    const energy = function (index) {
        // energy function, tailored for label placement
        let m = lab.length, ener = 0, dx = lab[index].x - anc[index].x, dy = anc[index].y - lab[index].y, dist = Math.sqrt(dx * dx + dy * dy);
        // penalty for length of leader line
        if (dist > 0)
            ener += dist * w_len;
        // label orientation bias
        dx /= dist;
        dy /= dist;
        if (dx > 0 && dy > 0) {
            ener += 0 * w_orient;
        }
        else if (dx < 0 && dy > 0) {
            ener += 1 * w_orient;
        }
        else if (dx < 0 && dy < 0) {
            ener += 2 * w_orient;
        }
        else {
            ener += 3 * w_orient;
        }
        let x21 = lab[index].x, y21 = lab[index].y - lab[index].height + 2.0, x22 = lab[index].x + lab[index].width, y22 = lab[index].y + 2.0;
        let x11, x12, y11, y12, x_overlap, y_overlap, overlap_area;
        for (let i = 0; i < m; i++) {
            if (i !== index) {
                // penalty for intersection of leader lines
                const overlap = intersect(anc[index].x, lab[index].x, anc[i].x, lab[i].x, anc[index].y, lab[index].y, anc[i].y, lab[i].y);
                if (overlap)
                    ener += w_inter;
                // penalty for label-label overlap
                x11 = lab[i].x;
                y11 = lab[i].y - lab[i].height + 2.0;
                x12 = lab[i].x + lab[i].width;
                y12 = lab[i].y + 2.0;
                x_overlap = Math.max(0, Math.min(x12, x22) - Math.max(x11, x21));
                y_overlap = Math.max(0, Math.min(y12, y22) - Math.max(y11, y21));
                overlap_area = x_overlap * y_overlap;
                ener += overlap_area * w_lab2;
            }
            // penalty for label-anchor overlap
            x11 = anc[i].x - anc[i].r;
            y11 = anc[i].y - anc[i].r;
            x12 = anc[i].x + anc[i].r;
            y12 = anc[i].y + anc[i].r;
            x_overlap = Math.max(0, Math.min(x12, x22) - Math.max(x11, x21));
            y_overlap = Math.max(0, Math.min(y12, y22) - Math.max(y11, y21));
            overlap_area = x_overlap * y_overlap;
            ener += overlap_area * w_lab_anc;
        }
        return ener;
    };
    const mcmove = function (currT) {
        // Monte Carlo translation move
        // select a random label
        let i = Math.floor(Math.random() * lab.length);
        // save old coordinates
        let x_old = lab[i].x;
        let y_old = lab[i].y;
        // old energy
        let old_energy;
        if (user_energy) {
            old_energy = user_defined_energy(i, lab, anc);
        }
        else {
            old_energy = energy(i);
        }
        // random translation
        lab[i].x += (Math.random() - 0.5) * max_move;
        lab[i].y += (Math.random() - 0.5) * max_move;
        // hard wall boundaries
        if (lab[i].x > w)
            lab[i].x = x_old;
        if (lab[i].x < 0)
            lab[i].x = x_old;
        if (lab[i].y > h)
            lab[i].y = y_old;
        if (lab[i].y < 0)
            lab[i].y = y_old;
        // new energy
        let new_energy;
        if (user_energy) {
            new_energy = user_defined_energy(i, lab, anc);
        }
        else {
            new_energy = energy(i);
        }
        // delta E
        let delta_energy = new_energy - old_energy;
        if (!(Math.random() < Math.exp(-delta_energy / currT))) {
            // move back to old coordinates
            lab[i].x = x_old;
            lab[i].y = y_old;
        }
    };
    const mcrotate = function (currT) {
        // Monte Carlo rotation move
        // select a random label
        let i = Math.floor(Math.random() * lab.length);
        // save old coordinates
        let x_old = lab[i].x;
        let y_old = lab[i].y;
        // old energy
        let old_energy;
        if (user_energy) {
            old_energy = user_defined_energy(i, lab, anc);
        }
        else {
            old_energy = energy(i);
        }
        // random angle
        let angle = (Math.random() - 0.5) * max_angle;
        let s = Math.sin(angle);
        let c = Math.cos(angle);
        // translate label (relative to anchor at origin):
        lab[i].x -= anc[i].x;
        lab[i].y -= anc[i].y;
        // rotate label
        let x_new = lab[i].x * c - lab[i].y * s, y_new = lab[i].x * s + lab[i].y * c;
        // translate label back
        lab[i].x = x_new + anc[i].x;
        lab[i].y = y_new + anc[i].y;
        // hard wall boundaries
        if (lab[i].x > w)
            lab[i].x = x_old;
        if (lab[i].x < 0)
            lab[i].x = x_old;
        if (lab[i].y > h)
            lab[i].y = y_old;
        if (lab[i].y < 0)
            lab[i].y = y_old;
        // new energy
        let new_energy;
        if (user_energy) {
            new_energy = user_defined_energy(i, lab, anc);
        }
        else {
            new_energy = energy(i);
        }
        // delta E
        let delta_energy = new_energy - old_energy;
        if (!(Math.random() < Math.exp(-delta_energy / currT))) {
            // move back to old coordinates
            lab[i].x = x_old;
            lab[i].y = y_old;
        }
    };
    const intersect = function (x1, x2, x3, x4, y1, y2, y3, y4) {
        // returns true if two lines intersect, else false
        // from http://paulbourke.net/geometry/lineline2d/
        let mua, mub;
        let denom, numera, numerb;
        denom = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);
        numera = (x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3);
        numerb = (x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3);
        /* Is the intersection along the the segments */
        mua = numera / denom;
        mub = numerb / denom;
        if (!(mua < 0 || mua > 1 || mub < 0 || mub > 1)) {
            return true;
        }
        return false;
    };
    const cooling_schedule = function (currT, initialT, nsweeps) {
        // linear cooling
        return currT - initialT / nsweeps;
    };
    labeler.start = function (nsweeps) {
        // main simulated annealing function
        let m = lab.length, currT = 1.0, initialT = 1.0;
        for (let i = 0; i < nsweeps; i++) {
            for (let j = 0; j < m; j++) {
                if (Math.random() < 0.5) {
                    mcmove(currT);
                }
                else {
                    mcrotate(currT);
                }
            }
            currT = cooling_schedule(currT, initialT, nsweeps);
        }
    };
    labeler.width = function (x) {
        // users insert graph width
        if (!arguments.length)
            return w;
        w = x;
        return labeler;
    };
    labeler.height = function (x) {
        // users insert graph height
        if (!arguments.length)
            return h;
        h = x;
        return labeler;
    };
    labeler.label = function (x) {
        // users insert label positions
        if (!arguments.length)
            return lab;
        lab = x;
        return labeler;
    };
    labeler.anchor = function (x) {
        // users insert anchor positions
        if (!arguments.length)
            return anc;
        anc = x;
        return labeler;
    };
    labeler.alt_energy = function (x) {
        // user defined energy
        if (!arguments.length)
            return energy;
        user_defined_energy = x;
        user_energy = true;
        return labeler;
    };
    labeler.alt_schedule = function (x) {
        // user defined cooling_schedule
        if (!arguments.length)
            return cooling_schedule;
        return labeler;
    };
    return labeler;
}
/*eslint-enable */

const basicLabelSizeFunction = (noteData, characterWidth, lineHeight, padding) => {
    const text = noteData.note.label || noteData.note.title;
    const textLength = text.length;
    const wrap = noteData.note.wrap || 120;
    const width = Math.min(wrap, textLength * characterWidth) + padding * 2;
    const height = Math.ceil((textLength * characterWidth) / 120) * lineHeight + padding * 2;
    return [width, height];
};
function bumpAnnotations(adjustableNotes, processor, size, propsPointSizeFunction, propsLabelSizeFunction) {
    const { padding = 1, characterWidth = 8, lineHeight = 20, iterations = 500, pointSizeFunction = propsPointSizeFunction, labelSizeFunction = propsLabelSizeFunction || basicLabelSizeFunction } = processor;
    const labels = adjustableNotes.map((d, i) => {
        const anchorX = (d.props.noteData.x[0] || d.props.noteData.x) +
            (d.props.noteData.dx !== undefined
                ? d.props.noteData.dx
                : ((i % 3) - 1) * -10);
        const anchorY = (d.props.noteData.y[0] || d.props.noteData.y) +
            (d.props.noteData.dy !== undefined
                ? d.props.noteData.dy
                : ((i % 3) - 1) * 10);
        const [labelWidth, labelHeight] = labelSizeFunction(d.props.noteData, characterWidth, lineHeight, padding);
        return {
            x: anchorX,
            y: anchorY,
            above: anchorY < d.props.noteData.y,
            left: anchorX < d.props.noteData.x,
            width: labelWidth,
            height: labelHeight,
            type: "label",
            name: "",
            originalNote: d
        };
    });
    const points = adjustableNotes.map(d => ({
        x: d.props.noteData.x,
        y: d.props.noteData.y,
        fx: d.props.noteData.x,
        fy: d.props.noteData.y,
        r: (pointSizeFunction && pointSizeFunction(d.props.noteData)) || 5,
        type: "point",
        originalNote: d
    }));
    const instantiatedLabeler = labeler();
    instantiatedLabeler.label(labels);
    instantiatedLabeler.anchor(points);
    instantiatedLabeler.width(size[0]);
    instantiatedLabeler.height(size[1]);
    instantiatedLabeler.start(iterations);
    labels.forEach(d => {
        if (d.type === "label") {
            const adjusted = adjustedXY(d.originalNote.props.noteData, d, padding);
            d.originalNote.props.noteData.nx = adjusted[0];
            d.originalNote.props.noteData.ny = adjusted[1];
        }
    });
    return adjustableNotes;
}
function adjustedXY(note, calculated, padding) {
    if (note.y > calculated.y) {
        //below
        return [
            calculated.x + calculated.width / 2 + padding / 2,
            calculated.y - calculated.height + padding / 2
        ];
    }
    return [calculated.x + calculated.width / 2, calculated.y];
}

const typeHash = {
    fill: (style) => React.createElement("rect", { style: style, width: 20, height: 20 }),
    line: (style) => React.createElement("line", { style: style, x1: 0, y1: 0, x2: 20, y2: 20 })
};
function renderType(item, i, type, styleFn) {
    let renderedType;
    if (typeof type === "function") {
        renderedType = type(item);
    }
    else {
        const Type = typeHash[type];
        const style = styleFn(item, i);
        renderedType = Type(style);
    }
    return renderedType;
}
const renderLegendGroupVertical = (legendGroup, customClickBehavior) => {
    const { type = "fill", styleFn, items } = legendGroup;
    const renderedItems = [];
    let itemOffset = 0;
    items.forEach((item, i) => {
        const renderedType = renderType(item, i, type, styleFn);
        renderedItems.push(React.createElement("g", { key: `legend-item-${i}`, transform: `translate(0,${itemOffset})`, onClick: customClickBehavior ? () => customClickBehavior(item) : undefined, style: {
                cursor: customClickBehavior ? "pointer" : "default"
            } },
            renderedType,
            React.createElement("text", { y: 15, x: 30 }, item.label)));
        itemOffset += 25;
    });
    return renderedItems;
};
const renderLegendGroupHorizontal = (legendGroup, customClickBehavior) => {
    const { type = "fill", styleFn, items } = legendGroup;
    const renderedItems = [];
    let itemOffset = 0;
    items.forEach((item, i) => {
        const renderedType = renderType(item, i, type, styleFn);
        renderedItems.push(React.createElement("g", { key: `legend-item-${i}`, transform: `translate(${itemOffset},0)`, onClick: customClickBehavior ? () => customClickBehavior(item) : undefined, style: {
                cursor: customClickBehavior ? "pointer" : "default"
            } },
            renderedType,
            React.createElement("text", { y: 15, x: 25 }, item.label)));
        itemOffset += 35;
        itemOffset += item.label.length * 8;
    });
    return { items: renderedItems, offset: itemOffset };
};
const renderVerticalGroup = ({ legendGroups, width, customClickBehavior }) => {
    let offset = 30;
    const renderedGroups = [];
    legendGroups.forEach((l, i) => {
        offset += 5;
        renderedGroups.push(React.createElement("line", { key: `legend-top-line legend-symbol-${i}`, stroke: "gray", x1: 0, y1: offset, x2: width, y2: offset }));
        offset += 10;
        if (l.label) {
            offset += 20;
            renderedGroups.push(React.createElement("text", { key: `legend-text-${i}`, y: offset, className: "legend-group-label" }, l.label));
            offset += 10;
        }
        renderedGroups.push(React.createElement("g", { key: `legend-group-${i}`, className: "legend-item", transform: `translate(0,${offset})` }, renderLegendGroupVertical(l, customClickBehavior)));
        offset += l.items.length * 25 + 10;
    });
    return renderedGroups;
};
const renderHorizontalGroup = ({ legendGroups, title, height, customClickBehavior }) => {
    let offset = 0;
    const renderedGroups = [];
    const verticalOffset = title === false ? 10 : 40;
    legendGroups.forEach((l, i) => {
        if (l.label) {
            renderedGroups.push(React.createElement("text", { key: `legend-text-${i}`, transform: `translate(${offset},${verticalOffset}) rotate(90)`, textAnchor: "start", className: "legend-group-label" }, l.label));
            offset += 20;
        }
        const renderedItems = renderLegendGroupHorizontal(l, customClickBehavior);
        renderedGroups.push(React.createElement("g", { key: `legend-group-${i}`, className: "legend-item", transform: `translate(${offset},${verticalOffset})` }, renderedItems.items));
        offset += renderedItems.offset + 5;
        if (legendGroups[i + 1]) {
            renderedGroups.push(React.createElement("line", { key: `legend-top-line legend-symbol-${i}`, stroke: "gray", x1: offset, y1: verticalOffset - 10, x2: offset, y2: height + verticalOffset + 10 }));
        }
        offset += 15;
    });
    return (React.createElement("g", null,
        title !== false && (React.createElement("line", { x1: 0, x2: offset + 10, y1: verticalOffset - 10, y2: verticalOffset - 10, stroke: "gray", className: "title-neatline" })),
        renderedGroups));
};
function Legend(props) {
    const { legendGroups, customClickBehavior, title = "Legend", width = 100, height = 20, orientation = "vertical" } = props;
    const renderedGroups = orientation === "vertical"
        ? renderVerticalGroup({
            legendGroups,
            width,
            customClickBehavior
        })
        : renderHorizontalGroup({
            legendGroups,
            title,
            height,
            customClickBehavior
        });
    return (React.createElement("g", null,
        title !== undefined && (React.createElement("text", { className: "legend-title", y: 20, x: orientation === "horizontal" ? 0 : width / 2, textAnchor: orientation === "horizontal" ? "start" : "middle" }, title)),
        renderedGroups));
}

const interactivityFns = ["onDragEnd", "onDragStart", "onDrag"];
function SemioticAnnotation(props) {
    const { noteData: baseNoteData } = props;
    const { screenCoordinates } = baseNoteData;
    const noteData = Object.assign({}, baseNoteData);
    for (const fn of interactivityFns) {
        if (baseNoteData[fn]) {
            delete noteData[fn];
            const originalFn = baseNoteData[fn];
            noteData[fn] = (updatedSettingsFromRA) => {
                originalFn({
                    originalSettings: baseNoteData,
                    updatedSettings: updatedSettingsFromRA,
                    noteIndex: baseNoteData.i
                });
            };
        }
    }
    const AnnotationType = typeof noteData.type === "function" ? noteData.type : AnnotationLabel;
    const eventListeners = noteData.eventListeners || noteData.events || {};
    const finalStyle = {};
    if (noteData.events || noteData.eventListeners || noteData.editMode) {
        finalStyle.pointerEvents = "all";
    }
    if (noteData.coordinates && screenCoordinates) {
        //Multisubject annotation
        const setNX = noteData.nx || screenCoordinates[0][0] + noteData.dx;
        const setNY = noteData.ny || screenCoordinates[0][1] + noteData.dy;
        const notes = screenCoordinates.map((d, i) => {
            const subjectNote = Object.assign({}, noteData, {
                note: i === 0 ? noteData.note : { label: "" },
                x: d[0],
                y: d[1],
                nx: setNX,
                ny: setNY
            });
            return (React.createElement(AnnotationType, Object.assign({ "data-testid": "semiotic-annotation", key: `multi-annotation-${i}` }, subjectNote)));
        });
        return (React.createElement("g", Object.assign({}, eventListeners, { style: finalStyle }), notes));
    }
    const keyData = noteData.note || { title: "none", label: noteData.label };
    const annotationKey = `${keyData.label}-${keyData.title}-${noteData.i}`;
    const finalAnnotation = (React.createElement(AnnotationType, Object.assign({ "data-testid": "semiotic-annotation", key: annotationKey, events: eventListeners }, noteData)));
    if (finalStyle.pointerEvents) {
        return (React.createElement("g", { "data-testid": "annotation-pointer-events-wrapper", style: finalStyle }, finalAnnotation));
    }
    return finalAnnotation;
}

function SpanOrDiv(props) {
    const { style, className, span, children } = props;
    if (span)
        return (React.createElement("span", { className: className, style: Object.assign({ display: "block" }, style) }, children));
    return (React.createElement("div", { className: className, style: style }, children));
}
const HOCSpanOrDiv = (span) => {
    if (span) {
        return (props) => {
            const { className, style, children } = props;
            return (React.createElement("span", { className: className, style: Object.assign({ display: "block" }, style) }, children));
        };
    }
    return (props) => {
        const { className, style, children } = props;
        return (React.createElement("div", { className: className, style: style }, children));
    };
};

function useSyncExternalStoreShim(subscribe, getSnapshot) {
    const [value, setValue] = useState(getSnapshot);
    useLayoutEffect(() => {
        return subscribe(() => setValue(getSnapshot));
    }, [subscribe]);
    return value;
}
function createStore(fn) {
    let Ctx = createContext(null);
    function Provider({ children }) {
        let source = useMemo(() => createSource(fn), []);
        return React.createElement(Ctx.Provider, { value: source, children: children });
    }
    let useSelector = (selector) => {
        let source = useContext(Ctx);
        let getSnapshot = () => selector(source.getState());
        return useSyncExternalStoreShim(source === null || source === void 0 ? void 0 : source.subscribe, getSnapshot);
    };
    return [Provider, useSelector];
}
function createSource(fn) {
    let events = new EventTarget();
    let state = fn(set);
    function set(fn) {
        state = Object.assign(state, fn(set));
        events.dispatchEvent(new CustomEvent("update"));
    }
    function subscribe(cb) {
        events.addEventListener("update", cb);
        return () => events.removeEventListener("update", cb);
    }
    return {
        getState() {
            return state;
        },
        subscribe
    };
}

let [TooltipProvider, useTooltip] = createStore((set) => ({
    tooltip: null,
    changeTooltip(tooltip) {
        set(() => ({ tooltip }));
    }
}));

function marginOffsetFn(orient, axisSettings, marginOffset) {
  if (typeof marginOffset === "number") {
    return marginOffset
  }
  if (axisSettings && axisSettings.find((d) => d.props.orient === orient)) {
    return 50
  }
  return 10
}

const keyFromSVGAnnotations = (
  adjustableAnnotations,
  annotationProcessor,
  size
) => {
  return `${adjustableAnnotations
    .map(adjustedAnnotationKeyMapper)
    .join(",")}${JSON.stringify(annotationProcessor)}${size.join(",")}`
};

function adjustedAnnotationKeyMapper(d) {
  if (!d || !d.props || !d.props.noteData) {
    return ""
  }
  const { note = {} } = d.props.noteData;
  const { label, title } = note;
  const id =
    d.props.noteData.id || `${d.props.noteData.x}-${d.props.noteData.y}`;
  return `${id}-${label}=${title}`
}

function safeStringify(value) {
  const seen = new Set();
  return JSON.stringify(value, (k, v) => {
    if (seen.has(v)) {
      return "..."
    }
    if (typeof v === "object") {
      seen.add(v);
      if (k === "note") {
        return `${v.label}-${v.title}`
      }
      if (k === "connector") {
        return `${v.end}-${v.type}`
      }
      if (k === "subject") {
        return `${v.radius}`
      }
      if (typeof v.column === "object") {
        return `${v.column.x}-${v.column.y}-${v.column.name}`
      }

      if (
        v.voronoiX ||
        v.voronoiY ||
        v.x ||
        v.y ||
        v.dx ||
        v.dy ||
        v.label ||
        v.type ||
        v.key ||
        v.hierarchicalID ||
        v.id ||
        v.name
      ) {
        return `${v.voronoiX}-${v.voronoiY}-${v.dx}-${v.dy}-${v.x}-${v.y}-${v.label}-${v.type}-${v.key}-${v.hierarchicalID}-${v.id}-${v.name}`
      }

      return "..."
    }
    return v
  })
}

function noteDataWidth(noteData, charWidth = 8, layoutNoteWidth) {
  const { noteWidth = layoutNoteWidth } = noteData;

  let noteWidthFn = noteWidth;

  if (typeof noteWidth === "number") {
    noteWidthFn = () => noteWidth;
  }

  const wrap = (noteData.note && noteData.note.wrap) || 120;
  const noteText = noteData.note.label || noteData.note.label || "";
  const width =
    (noteWidth && noteWidthFn(noteData)) ||
    (React__default.isValidElement(noteData.note)
      ? 100
      : Math.min(wrap, noteText.length * charWidth));
  return width
}

function noteDataHeight(
  noteData,
  charWidth = 8,
  lineHeight = 20,
  layoutNoteHeight
) {
  const { noteHeight = layoutNoteHeight } = noteData;

  let noteHeightFn = noteHeight;

  if (typeof noteHeight === "number") {
    noteHeightFn = () => noteHeight;
  }
  const wrap = (noteData.note && noteData.note.wrap) || 120;
  const text = noteData.note.label || noteData.note.title || "";
  
  const height =
    (noteHeight && noteHeightFn(noteData)) ||
    (React__default.isValidElement(noteData.note)
      ? 30
      : Math.ceil((text.length * charWidth) / wrap) * lineHeight +
        (noteData.note.label && noteData.note.title ? lineHeight : 0));

  return height
}

// modules
const processAnnotations = (adjustableAnnotations, annotationProcessor, props) => {
    const { layout = { type: false, noteHeight: undefined, noteWidth: undefined } } = annotationProcessor;
    if (layout.type === false) {
        return adjustableAnnotations;
    }
    const { noteWidth: layoutNoteHeight, noteHeight: layoutNoteWidth } = layout;
    let { margin = { top: 0, bottom: 0, left: 0, right: 0 } } = props;
    const { size, axes = [] } = props;
    margin =
        typeof margin === "number"
            ? { top: margin, left: margin, right: margin, bottom: margin }
            : margin;
    if (layout.type === "bump") {
        const adjustedAnnotations = bumpAnnotations(adjustableAnnotations, layout, size, props.pointSizeFunction, props.labelSizeFunction);
        return adjustedAnnotations;
    }
    else if (layout.type === "marginalia") {
        const { marginOffset, orient = "nearest", characterWidth = 8, lineHeight = 20, padding = 2, axisMarginOverride = {} } = layout;
        const finalOrientation = orient === "nearest"
            ? ["left", "right", "top", "bottom"]
            : Array.isArray(orient)
                ? orient
                : [orient];
        const leftOn = finalOrientation.find((d) => d === "left");
        const rightOn = finalOrientation.find((d) => d === "right");
        const topOn = finalOrientation.find((d) => d === "top");
        const bottomOn = finalOrientation.find((d) => d === "bottom");
        const leftNodes = [];
        const rightNodes = [];
        const topNodes = [];
        const bottomNodes = [];
        for (const aNote of adjustableAnnotations) {
            const noteData = aNote.props.noteData;
            const noteX = noteData.x[0] || noteData.x;
            const noteY = noteData.y[0] || noteData.y;
            const leftDist = leftOn ? noteX : Infinity;
            const rightDist = rightOn ? size[0] - noteX : Infinity;
            const topDist = topOn ? noteY : Infinity;
            const bottomDist = bottomOn ? size[1] - noteY : Infinity;
            const minDist = Math.min(leftDist, rightDist, topDist, bottomDist);
            if (leftDist === minDist) {
                leftNodes.push(aNote);
            }
            else if (rightDist === minDist) {
                rightNodes.push(aNote);
            }
            else if (topDist === minDist) {
                topNodes.push(aNote);
            }
            else {
                bottomNodes.push(aNote);
            }
        }
        //Adjust the margins based on which regions are active
        const leftForce = new labella.Force({
            minPos: axisMarginOverride.top !== undefined
                ? 0 + axisMarginOverride.top
                : 0 - margin.top,
            maxPos: axisMarginOverride.bottom !== undefined
                ? size[1] - axisMarginOverride.bottom
                : bottomOn
                    ? size[1]
                    : size[1] + margin.bottom
        })
            .nodes(leftNodes.map((d) => {
            const noteY = d.props.noteData.y[0] || d.props.noteData.y;
            return new labella.Node(noteY, noteDataHeight(d.props.noteData, characterWidth, lineHeight, layoutNoteHeight) + padding);
        }))
            .compute();
        const rightForce = new labella.Force({
            minPos: axisMarginOverride.top !== undefined
                ? 0 + axisMarginOverride.top
                : topOn
                    ? 0
                    : 0 - margin.top,
            maxPos: axisMarginOverride.bottom !== undefined
                ? size[1] - axisMarginOverride.bottom
                : size[1] + margin.bottom
        })
            .nodes(rightNodes.map((d) => {
            const noteY = d.props.noteData.y[0] || d.props.noteData.y;
            return new labella.Node(noteY, noteDataHeight(d.props.noteData, characterWidth, lineHeight, layoutNoteHeight) + padding);
        }))
            .compute();
        const topForce = new labella.Force({
            minPos: axisMarginOverride.left !== undefined
                ? 0 + axisMarginOverride.left
                : leftOn
                    ? 0
                    : 0 - margin.left,
            maxPos: axisMarginOverride.right !== undefined
                ? size[0] - axisMarginOverride.right
                : size[0] + margin.right
        })
            .nodes(topNodes.map((d) => {
            const noteX = d.props.noteData.x[0] || d.props.noteData.x;
            return new labella.Node(noteX, noteDataWidth(d.props.noteData, characterWidth, layoutNoteWidth) +
                padding);
        }))
            .compute();
        const bottomForce = new labella.Force({
            minPos: axisMarginOverride.left !== undefined
                ? 0 + axisMarginOverride.left
                : 0 - margin.left,
            maxPos: axisMarginOverride.right !== undefined
                ? size[0] - axisMarginOverride.right
                : rightOn
                    ? size[0]
                    : size[0] + margin.right
        })
            .nodes(bottomNodes.map((d) => {
            const noteX = d.props.noteData.x[0] || d.props.noteData.x;
            return new labella.Node(noteX, noteDataWidth(d.props.noteData, characterWidth, layoutNoteWidth) +
                padding);
        }))
            .compute();
        const bottomOffset = Math.max(...bottomNodes.map((d) => noteDataHeight(d.props.noteData, characterWidth, lineHeight, layoutNoteHeight) + padding));
        const topOffset = Math.max(...topNodes.map((d) => noteDataHeight(d.props.noteData, characterWidth, lineHeight, layoutNoteHeight) + padding));
        const leftOffset = Math.max(...leftNodes.map((d) => noteDataWidth(d.props.noteData, characterWidth, layoutNoteWidth) +
            padding));
        const rightOffset = Math.max(...rightNodes.map((d) => noteDataWidth(d.props.noteData, characterWidth, layoutNoteWidth) +
            padding));
        //      const nodeOffsetHeight = Math.max()
        const leftSortedNodes = leftForce.nodes();
        const rightSortedNodes = rightForce.nodes();
        const topSortedNodes = topForce.nodes();
        const bottomSortedNodes = bottomForce.nodes();
        leftNodes.forEach((note, i) => {
            const x = 0 -
                leftSortedNodes[i].layerIndex * leftOffset -
                marginOffsetFn("left", axes, marginOffset);
            const y = leftSortedNodes[i].currentPos;
            note.props.noteData.nx = x;
            note.props.noteData.ny = y;
            if (note.props.noteData.note && !React.isValidElement(note)) {
                note.props.noteData.note.orientation =
                    note.props.noteData.note.orientation || "leftRight";
                note.props.noteData.note.align =
                    note.props.noteData.note.align || "right";
            }
        });
        rightNodes.forEach((note, i) => {
            const x = size[0] +
                rightSortedNodes[i].layerIndex * rightOffset +
                marginOffsetFn("right", axes, marginOffset);
            const y = rightSortedNodes[i].currentPos;
            note.props.noteData.nx = x;
            note.props.noteData.ny = y;
            if (note.props.noteData.note && !React.isValidElement(note)) {
                note.props.noteData.note.orientation =
                    note.props.noteData.note.orientation || "leftRight";
                note.props.noteData.note.align =
                    note.props.noteData.note.align || "left";
            }
        });
        topNodes.forEach((note, i) => {
            const x = topSortedNodes[i].currentPos;
            const y = 0 -
                topSortedNodes[i].layerIndex * topOffset -
                marginOffsetFn("top", axes, marginOffset);
            note.props.noteData.nx = x;
            note.props.noteData.ny = y;
        });
        bottomNodes.forEach((note, i) => {
            const x = bottomSortedNodes[i].currentPos;
            const y = size[1] +
                bottomSortedNodes[i].layerIndex * bottomOffset +
                marginOffsetFn("bottom", axes, marginOffset);
            note.props.noteData.nx = x;
            note.props.noteData.ny = y;
        });
        return adjustableAnnotations;
    }
    return adjustableAnnotations;
};
const generateSVGAnnotations = (props, annotations) => {
    const renderedAnnotations = annotations
        .map((d, i) => props.svgAnnotationRule(d, i, props))
        .filter((d) => d !== null && d !== undefined);
    return renderedAnnotations;
};
const generateHTMLAnnotations = (props, annotations) => {
    const renderedAnnotations = annotations
        .map((d, i) => props.htmlAnnotationRule(d, i, props))
        .filter((d) => d !== null && d !== undefined);
    return renderedAnnotations;
};
const createAnnotations = (props, state) => {
    let adjustedAnnotations = state.adjustedAnnotations, adjustableAnnotationsKey = state.adjustedAnnotationsKey, adjustableAnnotations = state.adjustableAnnotations, fixedAnnotations = state.fixedAnnotations;
    let renderedSVGAnnotations = [];
    let renderedHTMLAnnotations = [];
    const adjustedAnnotationsKey = state.adjustedAnnotationsKey, adjustedAnnotationsDataVersion = state.adjustedAnnotationsDataVersion;
    const { annotations, annotationHandling = false, size, svgAnnotationRule, htmlAnnotationRule } = props;
    const annotationProcessor = typeof annotationHandling === "object"
        ? annotationHandling
        : { layout: { type: annotationHandling }, dataVersion: "" };
    const { dataVersion = "" } = annotationProcessor;
    if (svgAnnotationRule) {
        const initialSVGAnnotations = generateSVGAnnotations(props, annotations);
        adjustableAnnotationsKey = keyFromSVGAnnotations(initialSVGAnnotations, annotationProcessor, size);
        if (annotationHandling === false) {
            adjustedAnnotations = adjustableAnnotations;
        }
        if (adjustedAnnotations.length !== adjustableAnnotations.length ||
            adjustedAnnotationsKey !== adjustableAnnotationsKey ||
            adjustedAnnotationsDataVersion !== dataVersion) {
            adjustedAnnotations = processAnnotations(adjustableAnnotations, annotationProcessor, props);
        }
        else {
            //Handle when style or other attributes change
            adjustedAnnotations = adjustableAnnotations.map((d, i) => {
                const oldAnnotation = adjustedAnnotations[i];
                const newNoteData = Object.assign(Object.assign({}, oldAnnotation.props.noteData), d.props.noteData);
                return React.createElement(SemioticAnnotation, { key: d.key, noteData: newNoteData });
            });
        }
        renderedSVGAnnotations = [...adjustedAnnotations, ...fixedAnnotations];
    }
    if (htmlAnnotationRule) {
        renderedHTMLAnnotations = generateHTMLAnnotations(props, annotations);
    }
    return {
        svgAnnotations: renderedSVGAnnotations,
        htmlAnnotations: renderedHTMLAnnotations,
        adjustedAnnotations: adjustedAnnotations,
        adjustedAnnotationsKey: adjustableAnnotationsKey,
        adjustedAnnotationsDataVersion: dataVersion
    };
};
function AnnotationLayer(props) {
    const { legendSettings, margin, size, annotations: baseAnnotations, annotationHandling, useSpans } = props;
    const tooltip = useTooltip((store) => {
        return store.tooltip;
    });
    let annotations = tooltip != null ? baseAnnotations.concat(tooltip) : baseAnnotations;
    let changeTooltip = useTooltip((state) => state.changeTooltip);
    const voronoiHover = (d) => {
        changeTooltip(d);
    };
    const updatedAnnotationProps = Object.assign(Object.assign({}, props), { annotations,
        voronoiHover });
    const [SpanOrDiv] = useState(() => HOCSpanOrDiv(useSpans));
    const annotationProcessor = typeof annotationHandling === "object"
        ? annotationHandling
        : { layout: { type: annotationHandling }, dataVersion: "" };
    const { dataVersion = "" } = annotationProcessor;
    const [adjustedAnnotations, changeAdjustedAnnotations] = useState([]);
    const [svgAnnotations, changeSVGAnnotations] = useState([]);
    const [htmlAnnotations, changeHTMLAnnotations] = useState([]);
    const [adjustedAnnotationsKey, changeAdjustedAnnotationsKey] = useState("");
    const [adjustedAnnotationsDataVersion, changeAdjustedAnnotationsDataVersion] = useState(dataVersion);
    const initialSVGAnnotations = generateSVGAnnotations(updatedAnnotationProps, annotations);
    const adjustableAnnotations = initialSVGAnnotations.filter((d) => d.props && d.props.noteData && !d.props.noteData.fixedPosition);
    const updatedAnnotationsKey = keyFromSVGAnnotations(adjustableAnnotations, annotationProcessor, size);
    useEffect(() => {
        const fixedAnnotations = initialSVGAnnotations.filter((d) => !d.props || !d.props.noteData || d.props.noteData.fixedPosition);
        const updatedState = createAnnotations(updatedAnnotationProps, {
            adjustedAnnotations,
            adjustedAnnotationsKey,
            adjustedAnnotationsDataVersion,
            adjustableAnnotations,
            fixedAnnotations
        });
        changeAdjustedAnnotations(updatedState.adjustedAnnotations);
        changeAdjustedAnnotationsKey(updatedState.adjustedAnnotationsKey);
        changeAdjustedAnnotationsDataVersion(updatedState.adjustedAnnotationsDataVersion);
        changeSVGAnnotations(updatedState.svgAnnotations);
        changeHTMLAnnotations(updatedState.htmlAnnotations);
    }, [
        updatedAnnotationsKey,
        dataVersion,
        annotations.length,
        annotations.map((a) => safeStringify(a)).join("-")
    ]);
    let renderedLegend;
    if (legendSettings) {
        const positionHash = {
            left: [15, 15],
            right: [size[0] + 15, 15]
        };
        const { position = "right", title = "Legend" } = legendSettings;
        const legendPosition = positionHash[position];
        renderedLegend = (React.createElement("g", { transform: `translate(${legendPosition.join(",")})` },
            React.createElement(Legend, Object.assign({}, legendSettings, { title: title, position: position }))));
    }
    if (annotations.length === 0) {
        return null;
    }
    return (React.createElement(SpanOrDiv, { className: "annotation-layer", style: {
            position: "absolute",
            pointerEvents: "none",
            background: "none"
        } },
        React.createElement("svg", { className: "annotation-layer-svg", height: size[1], width: size[0], style: {
                background: "none",
                pointerEvents: "none",
                position: "absolute",
                left: `${margin.left}px`,
                top: `${margin.top}px`,
                overflow: "visible"
            } },
            React.createElement("g", null,
                renderedLegend,
                svgAnnotations)),
        React.createElement(SpanOrDiv, { className: "annotation-layer-html", style: {
                background: "none",
                pointerEvents: "none",
                position: "absolute",
                height: `${size[1]}px`,
                width: `${size[0]}px`,
                left: `${margin.left}px`,
                top: `${margin.top}px`
            } }, htmlAnnotations)));
}

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

const findFirstAccessorValue = (accessorArray, data) => {
    for (let i = 0; i < accessorArray.length; i++) {
        const valueCheck = accessorArray[i](data);
        if (valueCheck !== undefined &&
            !Number.isNaN(valueCheck) &&
            valueCheck !== null)
            return valueCheck;
    }
    return undefined;
};

const datesForUnique = (d) => (d instanceof Date ? d.getTime() : d);
const projectSummaryData = ({ data, summaryDataAccessor, xAccessor, yAccessor }) => {
    const projectedData = [];
    summaryDataAccessor.forEach((actualSummaryAccessor) => {
        xAccessor.forEach((actualXAccessor) => {
            yAccessor.forEach((actualYAccessor) => {
                const projection = (d) => actualSummaryAccessor(d).map((p, q) => [
                    actualXAccessor(p, q),
                    actualYAccessor(p, q)
                ]);
                data.forEach((d) => {
                    projectedData.push(Object.assign(Object.assign({}, d), { _baseData: actualSummaryAccessor(d), _xyfCoordinates: projection(d) }));
                });
            });
        });
    });
    return projectedData;
};
const projectLineData = ({ data, lineDataAccessor, xProp, xPropTop, xPropBottom, yProp, yPropTop, yPropBottom, xAccessor, yAccessor }) => {
    if (!Array.isArray(data)) {
        data = [data];
    }
    const projectedLine = [];
    lineDataAccessor.forEach((actualLineAccessor, lineIndex) => {
        xAccessor.forEach((actualXAccessor, xIndex) => {
            yAccessor.forEach((actualYAccessor, yIndex) => {
                data.forEach((d) => {
                    const originalLineData = Object.assign(Object.assign({}, d), { xIndex, yIndex, lineIndex });
                    originalLineData.data = actualLineAccessor(d).map((p, q) => {
                        const originalCoords = { data: p };
                        originalCoords[xProp] = actualXAccessor(p, q);
                        originalCoords[xPropTop] = originalCoords[xProp];
                        originalCoords[xPropBottom] = originalCoords[xProp];
                        originalCoords[yProp] = actualYAccessor(p, q);
                        originalCoords[yPropTop] = originalCoords[yProp];
                        originalCoords[yPropBottom] = originalCoords[yProp];
                        return originalCoords;
                    });
                    originalLineData.key = originalLineData.key || projectedLine.length;
                    projectedLine.push(originalLineData);
                });
            });
        });
    });
    return projectedLine;
};
const differenceLine = ({ data, yProp, yPropTop, yPropBottom }) => {
    data.forEach((l, i) => {
        l.data.forEach((point, q) => {
            const otherLine = i === 0 ? 1 : 0;
            if (point[yProp] > data[otherLine].data[q][yProp]) {
                point[yPropBottom] = data[otherLine].data[q][yProp];
                point[yPropTop] = point[yProp];
            }
            else {
                point[yPropTop] = point[yProp];
                point[yPropBottom] = point[yProp];
            }
        });
    });
    return data;
};
const stackedArea = ({ type = "stackedarea", data, xProp, yProp, yPropMiddle, sort, yPropTop, yPropBottom }) => {
    const valueMap = new Map();
    const lineSums = data.map(() => 0);
    let lineIndex = 0;
    for (const line of data) {
        /*
          After we calculate this, we then sort the lines (and the value used for the sorting is in this for loop)
          So, we need to know their original index order in order to use the naively populated coordinate array in valueMap
          Otherwise the coordinates in the map will be in different order than the sorted order of the lines
        */
        line.__lineIndex = lineIndex;
        for (const coord of line.data) {
            const coordX = datesForUnique(coord[xProp]);
            lineSums[lineIndex] += coord[yProp];
            if (!valueMap.has(coordX)) {
                valueMap.set(coordX, []);
            }
            valueMap.get(coordX)[lineIndex] = coord;
        }
        lineIndex++;
    }
    let stackSort = (a, b) => lineSums[b.key] - lineSums[a.key];
    if (type === "stackedpercent-invert" || type === "stackedarea-invert") {
        stackSort = (a, b) => lineSums[a.key] - lineSums[b.key];
    }
    sort = sort === undefined ? stackSort : sort;
    if (sort !== null) {
        data = data.sort(sort);
    }
    const lineIndexSortLookup = data.map((line) => line.__lineIndex);
    for (const [, coordsAtX] of valueMap) {
        let negativeOffset = 0;
        let positiveOffset = 0;
        const positiveStepTotal = sum(coordsAtX, (d) => (d === null || d === void 0 ? void 0 : d[yProp]) > 0 ? d[yProp] : 0);
        const negativeStepTotal = sum(coordsAtX, (d) => (d === null || d === void 0 ? void 0 : d[yProp]) < 0 ? d[yProp] : 0);
        for (const newIndex of lineIndexSortLookup) {
            const l = coordsAtX[newIndex];
            if (l) {
                if (l[yProp] < 0) {
                    if (type === "linepercent" ||
                        type === "stackedpercent" ||
                        type === "stackedpercent-invert") {
                        const percent = l[yProp] / negativeStepTotal;
                        l.percent = percent;
                        if (type === "linepercent") {
                            l[yPropBottom] =
                                l[yPropBottom] =
                                    l[yPropTop] =
                                        l[yPropMiddle] =
                                            percent;
                        }
                        else {
                            const adjustment = negativeStepTotal >= 0 ? 0 : percent;
                            l[yPropBottom] =
                                negativeStepTotal === 0
                                    ? 0
                                    : -(negativeOffset / negativeStepTotal);
                            l[yPropTop] = l[yPropBottom] - adjustment;
                            l[yPropMiddle] = l[yPropBottom] - adjustment / 2;
                        }
                    }
                    else {
                        l[yPropBottom] = negativeOffset;
                        l[yPropTop] = negativeOffset + l[yProp];
                        l[yPropMiddle] = negativeOffset + l[yProp] / 2;
                    }
                    negativeOffset += l[yProp];
                }
                else {
                    if (type === "linepercent" ||
                        type === "stackedpercent" ||
                        type === "stackedpercent-invert") {
                        const percent = l[yProp] / positiveStepTotal;
                        l.percent = percent;
                        if (type === "linepercent") {
                            l[yPropBottom] = l[yPropTop] = l[yPropMiddle] = percent;
                        }
                        else {
                            const adjustment = positiveStepTotal <= 0 ? 0 : percent;
                            l[yPropBottom] =
                                positiveStepTotal === 0 ? 0 : positiveOffset / positiveStepTotal;
                            l[yPropTop] = l[yPropBottom] + adjustment;
                            l[yPropMiddle] = l[yPropBottom] + adjustment / 2;
                        }
                    }
                    else {
                        l[yPropBottom] = positiveOffset;
                        l[yPropTop] = positiveOffset + l[yProp];
                        l[yPropMiddle] = positiveOffset + l[yProp] / 2;
                    }
                    positiveOffset += l[yProp];
                }
            }
        }
    }
    return data;
};
const lineChart = ({ data, y1, x1, yPropTop, yPropMiddle, yPropBottom, xPropTop, xPropMiddle, xPropBottom }) => {
    if (y1) {
        for (const line of data) {
            for (const point of line.data) {
                point[yPropBottom] = y1(point);
                point[yPropMiddle] = (point[yPropBottom] + point[yPropTop]) / 2;
            }
        }
    }
    if (x1) {
        for (const line of data) {
            for (const point of line.data) {
                point[xPropBottom] = x1(point);
                point[xPropMiddle] = (point[xPropBottom] + point[xPropTop]) / 2;
            }
        }
    }
    return data;
};
const cumulativeLine = ({ data, y1, yPropTop, yPropMiddle, yPropBottom, type = "cumulative" }) => {
    for (const line of data) {
        let cumulativeValue = 0;
        const dataArray = type === "cumulative-reverse" ? line.data.reverse() : line.data;
        for (const point of dataArray) {
            cumulativeValue += point[yPropTop];
            point[yPropBottom] =
                point[yPropTop] =
                    point[yPropMiddle] =
                        cumulativeValue;
            if (y1) {
                point[yPropBottom] = y1(point);
                point[yPropMiddle] = point[yPropBottom] + point[yPropTop] / 2;
            }
        }
    }
    return data;
};
const bumpChart = ({ type = "bumpline", data, xProp, yProp, yPropMiddle, yPropTop, yPropBottom }) => {
    const uniqXValues = new Map();
    for (const line of data) {
        for (const point of line.data) {
            const xValue = datesForUnique(point[xProp]);
            if (!uniqXValues.has(xValue)) {
                uniqXValues.set(xValue, []);
            }
            uniqXValues.get(xValue).push(point);
        }
    }
    let bumpSort = (a, b) => {
        if (a[yProp] > b[yProp]) {
            return 1;
        }
        if (a[yProp] < b[yProp]) {
            return -1;
        }
        return -1;
    };
    if (type === "bumparea-invert" || type === "bumpline-invert") {
        bumpSort = (a, b) => {
            if (a[yProp] < b[yProp]) {
                return 1;
            }
            if (a[yProp] > b[yProp]) {
                return -1;
            }
            return -1;
        };
    }
    for (const [, pointsAtXArray] of uniqXValues) {
        let negativeOffset = 0;
        let positiveOffset = 0;
        pointsAtXArray.sort(bumpSort);
        let rank = 1;
        for (const point of pointsAtXArray) {
            point._XYFrameRank;
            if (type === "bumparea" || type === "bumparea-invert") {
                if (point[yProp] < 0) {
                    point[yPropTop] = negativeOffset + point[yProp];
                    point[yPropMiddle] = negativeOffset + point[yProp] / 2;
                    point[yPropBottom] = negativeOffset;
                    negativeOffset += point[yProp];
                }
                else {
                    point[yPropTop] = positiveOffset + point[yProp];
                    point[yPropMiddle] = positiveOffset + point[yProp] / 2;
                    point[yPropBottom] = positiveOffset;
                    positiveOffset += point[yProp];
                }
            }
            else {
                point[yProp] = rank;
                point[yPropTop] = rank;
                point[yPropBottom] = rank;
            }
            rank++;
        }
    }
    return data;
};
const dividedLine = (parameters, points, searchIterations = 10) => {
    let currentParameters = parameters(points[0], 0);
    let currentPointsArray = [];
    const dividedLinesData = [
        { key: currentParameters, points: currentPointsArray }
    ];
    points.forEach((point, pointI) => {
        const newParameters = parameters(point, pointI);
        let matchingParams = newParameters === currentParameters;
        const stringNewParams = JSON.stringify(newParameters);
        const stringCurrentParams = JSON.stringify(currentParameters);
        if (typeof currentParameters === "object") {
            matchingParams = stringNewParams === stringCurrentParams;
        }
        if (matchingParams) {
            currentPointsArray.push(point);
        }
        else {
            const lastPoint = currentPointsArray[currentPointsArray.length - 1];
            let pointA = lastPoint;
            let pointB = point;
            let stringBParams = stringNewParams;
            let x = 0;
            while (x < searchIterations && stringNewParams === stringBParams) {
                const keys = Object.keys(pointA);
                const findPoints = simpleSearchFunction({
                    pointA,
                    pointB,
                    currentParameters,
                    parameters,
                    keys
                });
                pointA = findPoints[0];
                pointB = findPoints[1];
                stringBParams = JSON.stringify(parameters(pointB));
                x++;
            }
            currentPointsArray.push(pointB);
            currentPointsArray = [pointB, point];
            dividedLinesData.push({ key: newParameters, points: currentPointsArray });
            currentParameters = newParameters;
        }
    });
    return dividedLinesData;
};
function simpleSearchFunction({ pointA, pointB, currentParameters, parameters, keys }) {
    const betweenPoint = {};
    keys.forEach((key) => {
        betweenPoint[key] =
            typeof pointA[key] === "number"
                ? (pointA[key] + pointB[key]) / 2
                : undefined;
    });
    const stringBetween = JSON.stringify(parameters(betweenPoint));
    const stringCurrent = JSON.stringify(currentParameters);
    if (stringBetween === stringCurrent) {
        return [betweenPoint, pointB];
    }
    return [pointA, betweenPoint];
}
function funnelize({ data, steps, key }) {
    const funnelData = [];
    if (!Array.isArray(data)) {
        data = [data];
    }
    if (!steps) {
        steps = data.map((d) => Object.keys(d)).reduce((a, b) => a.concat(b), []);
    }
    data.forEach((datum, i) => {
        const datumKey = key ? datum[key] : i;
        steps.forEach((step) => {
            const funnelDatum = { funnelKey: datumKey, stepName: "", stepValue: 0 };
            funnelDatum.stepName = step;
            funnelDatum.stepValue = datum[step] ? datum[step] : 0;
            funnelData.push(funnelDatum);
        });
    });
    return funnelData;
}
const whichPoint = {
    bottom: "yBottom",
    top: "yTop"
};
function relativeY({ point, projectedY, yAccessor, yScale, showLinePoints }) {
    const baseData = point &&
        (showLinePoints &&
            showLinePoints !== true &&
            point[whichPoint[showLinePoints]] !== undefined
            ? point[whichPoint[showLinePoints]]
            : point.yMiddle !== undefined
                ? point.yMiddle
                : point[projectedY] !== undefined
                    ? point[projectedY]
                    : findFirstAccessorValue(yAccessor, point));
    if (Array.isArray(baseData)) {
        return baseData.map((d) => yScale(d));
    }
    return baseData !== undefined ? yScale(baseData) : 0;
}
function relativeX({ point, projectedXMiddle, projectedX, xAccessor, xScale }) {
    const baseData = point &&
        (point[projectedXMiddle] !== undefined
            ? point[projectedXMiddle]
            : point[projectedX] !== undefined
                ? point[projectedX]
                : findFirstAccessorValue(xAccessor, point));
    if (Array.isArray(baseData)) {
        return baseData.map((d) => xScale(d));
    }
    return baseData !== undefined ? xScale(baseData) : 0;
}
function findPointByID({ point, idAccessor, lines, xScale, projectedX, xAccessor }) {
    const pointID = idAccessor(point.parentLine || point);
    if (pointID) {
        const thisLine = lines.data.find((l) => idAccessor(l) === pointID);
        if (!thisLine) {
            return null;
        }
        const pointX = xScale(findFirstAccessorValue(xAccessor, point));
        const thisPoint = thisLine.data.find((p) => xScale(p[projectedX]) === pointX);
        if (!thisPoint) {
            return null;
        }
        const newPoint = Object.assign(Object.assign(Object.assign(Object.assign({}, point), thisPoint), thisPoint.data), { parentLine: thisLine });
        const reactAnnotationProps = [
            "type",
            "label",
            "note",
            "connector",
            "disabled",
            "color",
            "subject"
        ];
        reactAnnotationProps.forEach((prop) => {
            if (point[prop])
                newPoint[prop] = point[prop];
        });
        return newPoint;
    }
    return point;
}

//All generic line constructors expect a projected coordinates array with x & y coordinates, if there are no y1 & x1 coordinates then it defaults to 0-width
function roundToTenth$1(number) {
    return Math.round(number * 10) / 10;
}
function pathStr({ x, y, width, height, cx, cy, r }) {
    if (cx !== undefined) {
        return ([
            "M",
            roundToTenth$1(cx - r),
            roundToTenth$1(cy),
            "a",
            r,
            r,
            0,
            1,
            0,
            r * 2,
            0,
            "a",
            r,
            r,
            0,
            1,
            0,
            -(r * 2),
            0
        ].join(" ") + "Z");
    }
    return ([
        "M",
        roundToTenth$1(x),
        roundToTenth$1(y),
        "h",
        width,
        "v",
        height,
        "h",
        -width,
        "v",
        -height
    ].join(" ") + "Z");
}
function circlePath$1(cx, cy, r) {
    return pathStr({ cx, cy, r });
}
function rectPath(x, y, width, height) {
    return pathStr({ x, y, width, height });
}
function generateSVG(props, className, markType, renderMode) {
    const cloneProps = props;
    cloneProps.className = className;
    let actualSVG = null;
    if (renderMode === "forcePath" && markType === "circle") {
        cloneProps.d = circlePath$1(cloneProps.cx || 0, cloneProps.cy || 0, cloneProps.r);
        markType = "path";
        actualSVG = React__default.createElement(markType, cloneProps);
    }
    else if (renderMode === "forcePath" && markType === "rect") {
        cloneProps.d = rectPath(cloneProps.x || 0, cloneProps.y || 0, cloneProps.width, cloneProps.height);
        markType = "path";
        actualSVG = React__default.createElement(markType, cloneProps);
    }
    else {
        if (props.markType === "text" && typeof cloneProps.children !== "object") {
            cloneProps.children = React__default.createElement("tspan", null, cloneProps.children);
        }
        actualSVG = React__default.createElement(markType, cloneProps);
    }
    return actualSVG;
}

const redrawSketchyList = [
  "fill",
  "stroke",
  "cx",
  "cy",
  "x",
  "y",
  "d",
  "height",
  "width",
  "x1",
  "x2",
  "y1",
  "y2",
  "rx",
  "ry",
  "r",
  "transform"
];

function generateSketchyKey(props) {
    let { style = {} } = props;
    let sketchyKey = "";
    redrawSketchyList.forEach((d) => {
        sketchyKey += `-${style[d] || props[d]}`;
    });
    return sketchyKey;
}
const updateSketchy = (nextProps, previousSketchyKey, RoughGenerator, markType, renderMode) => {
    const renderOptions = renderMode !== null && typeof renderMode === "object"
        ? renderMode
        : { renderMode };
    const sketchyKey = renderOptions.renderMode === "sketchy" && generateSketchyKey(nextProps);
    if (RoughGenerator && sketchyKey && sketchyKey !== previousSketchyKey) {
        const { style = {} } = nextProps;
        const { simplification = 0, curveStepCount = 9, fillStyle = "hachure", roughness = 1, bowing = 1, fillWeight = 1, hachureAngle = -41 } = renderOptions;
        const roughGenerator = RoughGenerator({}, { width: 1000, height: 1000 });
        let drawingInstructions;
        const roughOptions = {
            fill: style.fill || nextProps.fill,
            stroke: style.stroke || nextProps.stroke,
            strokeWidth: style.strokeWidth || nextProps.strokeWidth,
            fillStyle: fillStyle,
            roughness: roughness,
            bowing: bowing,
            fillWeight: fillWeight,
            hachureAngle: hachureAngle,
            hachureGap: renderOptions.hachureGap ||
                (style.fillOpacity && (5 - style.fillOpacity * 5) * fillWeight) ||
                fillWeight * 2,
            curveStepCount: curveStepCount,
            simplification: simplification
        };
        switch (markType) {
            case "line":
                drawingInstructions = roughGenerator.line(nextProps.x1 || 0, nextProps.y1 || 0, nextProps.x2 || 0, nextProps.y2 || 0, roughOptions);
                break;
            case "rect":
                if (nextProps.rx || nextProps.ry) {
                    drawingInstructions = roughGenerator.circle((nextProps.x || 0) + nextProps.width / 2, (nextProps.y || 0) + nextProps.width / 2, nextProps.width, roughOptions);
                }
                else {
                    drawingInstructions = roughGenerator.rectangle(nextProps.x || 0, nextProps.y || 0, nextProps.width, nextProps.height, roughOptions);
                }
                break;
            case "circle":
                drawingInstructions = roughGenerator.circle(nextProps.cx || 0, nextProps.cy || 0, nextProps.r * 2, roughOptions);
                break;
            case "ellipse":
                drawingInstructions = roughGenerator.ellipse(nextProps.x || 0, nextProps.y || 0, nextProps.width, nextProps.height, roughOptions);
                break;
            case "polygon":
                drawingInstructions = roughGenerator.polygon(nextProps.points, roughOptions);
                break;
            case "path":
                if (!nextProps.d.includes("NaN") && !nextProps.d.includes("Infinity")) {
                    drawingInstructions = roughGenerator.path(nextProps.d, roughOptions);
                }
                break;
        }
        if (!drawingInstructions) {
            return null;
        }
        const fillOpacityStyles = {};
        const strokeOpacityStyles = {};
        // Assume if hachure gap is explicitly set then opacity is real
        if (renderOptions.hachureGap || renderOptions.fillStyle === "solid") {
            fillOpacityStyles.opacity = style.opacity || nextProps.opacity;
            fillOpacityStyles.fillOpacity = style.fillOpacity || nextProps.fillOpacity;
            fillOpacityStyles.strokeOpacity =
                style.fillOpacity || nextProps.fillOpacity;
            strokeOpacityStyles.opacity = style.opacity || nextProps.opacity;
            strokeOpacityStyles.strokeOpacity =
                style.strokeOpacity || nextProps.strokeOpacity;
        }
        const roughPieces = [];
        roughGenerator
            .toPaths(drawingInstructions)
            .forEach(({ d, fill, stroke, strokeWidth, pattern }, i) => {
            const opacityStyles = i === 0 ? fillOpacityStyles : strokeOpacityStyles;
            if (pattern) {
                const roughRandomID = `rough-${Math.random()}`;
                roughPieces.push(React__default.createElement("pattern", { key: `pattern-${i}`, id: roughRandomID, x: pattern.x, y: pattern.y, height: pattern.height, width: pattern.width, viewBox: pattern.viewBox },
                    React__default.createElement("path", { key: `pattern-path-${i}`, d: pattern.path.d, style: {
                            fill: pattern.path.fill,
                            stroke: pattern.path.stroke,
                            strokeWidth: pattern.path.strokeWidth
                        } })));
                fill = `url(#${roughRandomID})`;
            }
            roughPieces.push(React__default.createElement("path", { key: `path-${i}`, d: d, style: Object.assign({ fill: fill, stroke: stroke, strokeWidth: strokeWidth }, opacityStyles), transform: nextProps.transform }));
        });
        return roughPieces;
    }
    return null;
};
function SemioticMark(_a) {
    var _b, _c;
    var { sketchyGenerator, markType, renderMode, ["aria-label"]: ariaLabel } = _a, props = __rest(_a, ["sketchyGenerator", "markType", "renderMode", "aria-label"]);
    const { className = "" } = props;
    const sketchyKey = generateSketchyKey(props);
    const [sketchyFill, changeSketchyFill] = useState(updateSketchy(props, sketchyKey, sketchyGenerator, markType, renderMode));
    const [previousSketchyKey, updateSketchyKey] = useState("");
    useEffect(() => {
        changeSketchyFill(updateSketchy(props, previousSketchyKey, sketchyGenerator, markType, renderMode));
        updateSketchyKey(sketchyKey);
    }, [!!sketchyGenerator, sketchyKey, (_b = renderMode === null || renderMode === void 0 ? void 0 : renderMode.renderMode) !== null && _b !== void 0 ? _b : renderMode]);
    const actualSVG = sketchyGenerator && ((_c = renderMode === null || renderMode === void 0 ? void 0 : renderMode.renderMode) !== null && _c !== void 0 ? _c : renderMode === "sketchy")
        ? sketchyFill
        : generateSVG(props, className, markType, renderMode);
    return (React__default.createElement("g", { className: className, "aria-label": ariaLabel }, actualSVG));
}

const createLineSegments = (props) => {
    const { parameters, className, interpolate = curveLinear, customAccessors, lineDataAccessor, data, searchIterations } = props, rest = __rest(props, ["parameters", "className", "interpolate", "customAccessors", "lineDataAccessor", "data", "searchIterations"]);
    const { x, y } = customAccessors;
    const lineData = projectLineData({
        data: data,
        lineDataAccessor: [lineDataAccessor],
        xProp: "x",
        yProp: "y",
        xAccessor: [x],
        yAccessor: [y]
    });
    //Compatibility before Semiotic 2
    for (const projectedD of lineData) {
        projectedD.data = projectedD.data.map((d) => (Object.assign(Object.assign({}, d.data), d)));
    }
    const lines = dividedLine(parameters, lineData[0].data, searchIterations);
    const lineRender = line()
        .curve(interpolate)
        .x((d) => d.x)
        .y((d) => d.y);
    return lines.map((d, i) => (React.createElement(SemioticMark, Object.assign({}, rest, { className: className, markType: "path", key: `DividedLine-${i}`, style: d.key, d: lineRender(d.points) }))));
};
function DividedLine(props) {
    const lines = createLineSegments(props);
    return React.createElement("g", null, lines);
}

const flatten = (list) => list.reduce((a, b) => a.concat(Array.isArray(b) ? flatten(b.slice().sort((a, b) => a - b)) : b), []);
function flatShortArray(baseArray = []) {
    if (!Array.isArray(baseArray))
        return "not-array";
    let array = baseArray.slice();
    if (!Array.isArray(array[0])) {
        array = array.slice().sort((a, b) => a - b);
    }
    const flat = flatten(array);
    const stringifiedFlattened = flat
        .map((d) => (d instanceof Date && d.toString()) ||
        (d !== undefined && d.toFixed && d.toFixed(2)) ||
        "empty")
        .toString();
    return stringifiedFlattened;
}
const createBrush$1 = (node, props) => {
    const { svgBrush: brush, selectedExtent: baseSelectedExtent } = props;
    select(node).call(brush);
    if (baseSelectedExtent) {
        let selectedExtent = baseSelectedExtent;
        if (Array.isArray(baseSelectedExtent[0])) {
            const sortedY = [selectedExtent[0][1], selectedExtent[1][1]].sort((a, b) => a - b);
            selectedExtent = [
                [selectedExtent[0][0], sortedY[0]],
                [selectedExtent[1][0], sortedY[1]]
            ];
        }
        select(node).call(brush.move, selectedExtent);
    }
};
function Brush(props) {
    const { extent, selectedExtent } = props;
    const node = useRef(null);
    const flatExtent = flatShortArray(extent);
    const flatSelectedExtent = flatShortArray(selectedExtent);
    useEffect(() => {
        if (node === null || node === void 0 ? void 0 : node.current) {
            createBrush$1(node.current, props);
        }
    }, [flatExtent, flatSelectedExtent, node]);
    const { position = [0, 0] } = props;
    return (React.createElement("g", { transform: `translate(${position})`, ref: node, className: "xybrush" }));
}

const projectedX = "x";
const projectedY = "y";
const projectedYMiddle = "yMiddle";
const projectedYTop = "yTop";
const projectedYBottom = "yBottom";
const projectedXMiddle = "xMiddle";
const projectedXTop = "xTop";
const projectedXBottom = "xBottom";

const constructDataObject = (d, points) => {
    if (d === undefined)
        return d;
    return d && d.data ? Object.assign(Object.assign({ points }, d.data), d) : Object.assign({ points }, d);
};
const changeVoronoi = (voronoiHover, d, customHoverTypes, customHoverBehavior, points, e) => {
    //Until semiotic 2
    const dataObject = constructDataObject(d, points);
    if (customHoverBehavior)
        customHoverBehavior(dataObject);
    if (!d)
        voronoiHover(null);
    else if (customHoverTypes === true) {
        const vorD = Object.assign({}, dataObject);
        vorD.type = vorD.type === "column-hover" ? "column-hover" : "frame-hover";
        voronoiHover(vorD, e);
    }
    else if (customHoverTypes) {
        const arrayWrappedHoverTypes = Array.isArray(customHoverTypes)
            ? customHoverTypes
            : [customHoverTypes];
        const mappedHoverTypes = arrayWrappedHoverTypes
            .map((c) => {
            const finalC = typeof c === "function" ? c(dataObject) : c;
            if (!finalC)
                return undefined;
            return Object.assign({}, dataObject, finalC);
        })
            .filter((d) => d);
        voronoiHover(mappedHoverTypes, e);
    }
};
const clickVoronoi = (d, customClickBehavior, points, e) => {
    //Until semiotic 2
    const dataObject = constructDataObject(d, points);
    if (customClickBehavior)
        customClickBehavior(dataObject, e);
};
const doubleclickVoronoi = (d, customDoubleClickBehavior, points, e) => {
    //Until semiotic 2
    const dataObject = constructDataObject(d, points);
    if (customDoubleClickBehavior)
        customDoubleClickBehavior(dataObject, e);
};
const brushStart = (e, columnName, data, columnData, interaction) => {
    if (interaction && interaction.start)
        interaction.start(e, columnName, data, columnData);
};
const brushing = (e, columnName, data, columnData, interaction) => {
    if (interaction && interaction.during)
        interaction.during(e, columnName, data, columnData);
};
const brushEnd = (e, columnName, data, columnData, interaction) => {
    if (interaction && interaction.end)
        interaction.end(e, columnName, data, columnData);
};
const calculateOverlay = (props, voronoiHover) => {
    let voronoiPaths = [];
    const { xScale, yScale, points, projectedX, showLinePoints, size, overlay, interactionOverflow = { top: 0, bottom: 0, left: 0, right: 0 }, customClickBehavior, customDoubleClickBehavior, customHoverBehavior, hoverAnnotation, margin, advancedSettings = {} } = props;
    const whichPoints = {
        top: projectedYTop,
        bottom: projectedYBottom
    };
    const pointerStyle = customClickBehavior || customDoubleClickBehavior
        ? { cursor: "pointer" }
        : {};
    if (points && hoverAnnotation && !overlay) {
        const { voronoiFilter = () => true } = advancedSettings;
        const voronoiDataset = [];
        const voronoiUniqueMap = new Map();
        for (const d of points) {
            if (voronoiFilter(Object.assign(Object.assign({}, d), d.data))) {
                const xValue = Math.floor(xScale(d[projectedX]));
                const yValue = Math.floor(yScale(showLinePoints && d[whichPoints[showLinePoints]] !== undefined
                    ? d[whichPoints[showLinePoints]]
                    : d[projectedYMiddle] !== undefined
                        ? d[projectedYMiddle]
                        : d[projectedY]));
                if (xValue >= 0 - margin.left &&
                    xValue <= size[0] + margin.right &&
                    yValue >= 0 - margin.top &&
                    yValue <= size[1] + margin.bottom &&
                    xValue !== undefined &&
                    yValue !== undefined &&
                    isNaN(xValue) === false &&
                    isNaN(yValue) === false) {
                    const pointKey = `${xValue},${yValue}`;
                    if (!voronoiUniqueMap.has(pointKey)) {
                        const voronoiPoint = Object.assign(Object.assign({}, d), { coincidentPoints: [d], voronoiX: xValue, voronoiY: yValue });
                        voronoiDataset.push(voronoiPoint);
                        voronoiUniqueMap.set(pointKey, voronoiPoint);
                    }
                    else
                        voronoiUniqueMap.get(pointKey).coincidentPoints.push(d);
                }
            }
        }
        let voronoiXExtent = extent$1(voronoiDataset.map((d) => d.voronoiX));
        let voronoiYExtent = extent$1(voronoiDataset.map((d) => d.voronoiY));
        if (voronoiXExtent[0] == null || voronoiYExtent == null) {
            voronoiXExtent = [0, 0];
            voronoiYExtent = [0, 0];
        }
        const voronoiExtent = [
            Math.min(voronoiXExtent[0] - 5, -interactionOverflow.left),
            Math.min(voronoiYExtent[0] - 5, -interactionOverflow.top),
            Math.max(voronoiXExtent[1] + 5, size[0] + interactionOverflow.right),
            Math.max(voronoiYExtent[1] + 5, size[1] + interactionOverflow.bottom)
        ];
        const delaunay = Delaunay.from(voronoiDataset, (d) => d.voronoiX, (d) => d.voronoiY);
        const voronoi = delaunay.voronoi(voronoiExtent);
        voronoiPaths = voronoiDataset.map((d, i) => {
            let clipPath = null;
            if (advancedSettings.voronoiClipping) {
                const circleSize = advancedSettings.voronoiClipping === true
                    ? 50
                    : advancedSettings.voronoiClipping;
                clipPath = (React.createElement("clipPath", { id: `voronoi-${i}` },
                    React.createElement("circle", { r: circleSize, cx: d.voronoiX, cy: d.voronoiY })));
            }
            return (React.createElement("g", { key: `voronoi-${i}` },
                React.createElement("path", { onClick: (e) => {
                        clickVoronoi(voronoiDataset[i], customClickBehavior, points, e);
                    }, onDoubleClick: (e) => {
                        doubleclickVoronoi(voronoiDataset[i], customDoubleClickBehavior, points, e);
                    }, onMouseEnter: (e) => {
                        changeVoronoi(voronoiHover, voronoiDataset[i], hoverAnnotation, customHoverBehavior, points, e);
                    }, onMouseLeave: (e) => {
                        changeVoronoi(voronoiHover, undefined, undefined, customHoverBehavior, undefined, e);
                    }, key: `interactionVoronoi${i}`, d: voronoi.renderCell(i), style: Object.assign({ fillOpacity: 0 }, pointerStyle), clipPath: `url(#voronoi-${i})` }),
                clipPath));
        }, window);
        return voronoiPaths;
    }
    else if (overlay) {
        const renderedOverlay = overlay.map((overlayRegion, i) => {
            const { overlayData } = overlayRegion, rest = __rest(overlayRegion, ["overlayData"]);
            const overlayProps = {
                key: `overlay-${i}`,
                onMouseEnter: (e) => {
                    changeVoronoi(voronoiHover, overlayData, props.hoverAnnotation, customHoverBehavior, points, e);
                },
                onMouseLeave: (e) => {
                    changeVoronoi(voronoiHover, undefined, undefined, customHoverBehavior, undefined, e);
                },
                onClick: (e) => {
                    clickVoronoi(overlayData, customClickBehavior, points, e);
                },
                onDoubleClick: (e) => {
                    doubleclickVoronoi(overlayData, customDoubleClickBehavior, points, e);
                },
                style: Object.assign({ opacity: 0 }, pointerStyle)
            };
            if (React.isValidElement(overlayRegion.renderElement)) {
                return React.cloneElement(overlayRegion.renderElement, overlayProps);
            }
            else {
                return React.createElement(SemioticMark, Object.assign({}, rest, { key: `overlay-${i}` }, overlayProps));
            }
        });
        return renderedOverlay;
    }
};

const emptyObjectReturnFn$1 = () => ({});
const twoPI$2 = Math.PI * 2;
const dedupeRibbonPoints = (weight = 1) => (p, c) => {
    const l = p[p.length - 1];
    if (!l ||
        Math.round(l.x / weight) !== Math.round(c.x / weight) ||
        Math.round(l.y / weight) !== Math.round(c.y / weight)) {
        p.push(c);
    }
    return p;
};
const drawAreaConnector = ({ x1, x2, y1, y2, sizeX1, sizeY1, sizeX2, sizeY2 }) => {
    return `M${x1},${y1}L${x2},${y2}L${x2 + sizeX2},${y2 + sizeY2}L${x1 + sizeX1},${y1 + sizeY1}Z`;
};
const groupBarMark = ({ bins, binMax, relativeBuckets, columnWidth, projection, adjustedSize, summaryI, summary, renderValue, summaryStyle, type, baseMarkProps }) => {
    const mappedBins = [];
    const mappedPoints = [];
    const actualMax = (relativeBuckets && relativeBuckets[summary.name]) || binMax;
    const summaryElementStylingFn = type.elementStyleFn || emptyObjectReturnFn$1;
    const barPadding = type.padding || 0;
    bins.forEach((d, i) => {
        const opacity = d.value / actualMax;
        const additionalStyle = summaryElementStylingFn(d.value, opacity, d.pieces);
        const finalStyle = type.type === "heatmap"
            ? Object.assign({ opacity: opacity, fill: summaryStyle.fill }, additionalStyle) : Object.assign(Object.assign({}, summaryStyle), additionalStyle);
        const thickness = Math.max(1, d.y1 - barPadding * 2);
        const finalColumnWidth = type.type === "heatmap" ? columnWidth : columnWidth * opacity;
        let yProp = d.y + barPadding;
        let xProp = type.type === "heatmap" || type.flip
            ? -columnWidth / 2
            : columnWidth / 2 - finalColumnWidth;
        let height = thickness;
        let width = finalColumnWidth;
        let xOffset = type.type === "heatmap" ? finalColumnWidth / 2 : finalColumnWidth;
        let yOffset = d.y1 / 2;
        if (projection === "horizontal") {
            yProp =
                type.type === "heatmap"
                    ? -columnWidth / 2
                    : type.flip
                        ? -columnWidth / 2
                        : columnWidth / 2 - finalColumnWidth;
            xProp = d.y - d.y1 + barPadding;
            height = finalColumnWidth;
            width = thickness;
            yOffset =
                type.type === "heatmap" ? finalColumnWidth / 2 : finalColumnWidth;
            xOffset = d.y1 / 2;
        }
        else if (projection === "radial") {
            const arcGenerator = arc()
                .innerRadius(d.y / 2)
                .outerRadius((d.y + d.y1) / 2);
            const angle = summary.pct - summary.pct_padding;
            let startAngle = summary.pct_middle - summary.pct_padding;
            let endAngle = type.type === "heatmap"
                ? startAngle + angle
                : startAngle + angle * opacity;
            startAngle *= twoPI$2;
            endAngle *= twoPI$2;
            const arcAdjustX = adjustedSize[0] / 2;
            const arcAdjustY = adjustedSize[1] / 2;
            const arcTranslate = `translate(${arcAdjustX},${arcAdjustY})`;
            const arcCenter = arcGenerator.centroid({ startAngle, endAngle });
            mappedPoints.push({
                key: summary.name,
                value: d.value,
                pieces: d.pieces.map((p) => p.piece),
                label: "Heatmap",
                x: arcCenter[0] + arcAdjustX,
                y: arcCenter[1] + arcAdjustY
            });
            mappedBins.push(Object.assign(Object.assign({}, baseMarkProps), { markType: "path", transform: arcTranslate, renderMode: renderValue, key: `groupIcon-${summaryI}-${i}`, d: arcGenerator({ startAngle, endAngle }), style: finalStyle }));
        }
        if (projection !== "radial") {
            mappedPoints.push({
                key: summary.name,
                value: d.value,
                pieces: d.pieces.map((p) => p.piece),
                label: "Heatmap",
                x: xProp + xOffset,
                y: yProp + yOffset
            });
            mappedBins.push(Object.assign(Object.assign({}, baseMarkProps), { markType: "rect", renderMode: renderValue, key: `groupIcon-${summaryI}-${i}`, x: xProp, y: yProp, height: height, width: width, style: finalStyle }));
        }
    });
    return { marks: mappedBins, points: mappedPoints };
};
// FROM d3-svg-ribbon
function linearRibbon() {
    const _lineConstructor = line();
    let _xAccessor = function (d) {
        return d.x;
    };
    let _yAccessor = function (d) {
        return d.y;
    };
    let _rAccessor = function (d) {
        return d.r;
    };
    let _interpolator = curveLinearClosed;
    function _ribbon(pathData) {
        if (pathData.multiple) {
            const original_r = _rAccessor;
            const parallelTotal = pathData.multiple.reduce((p, c) => p + c.weight, 0);
            _rAccessor = () => parallelTotal;
            const totalPoints = buildRibbon(pathData.points);
            let currentPoints = totalPoints
                .filter((d) => d.direction === "forward")
                .reduce(dedupeRibbonPoints(), []);
            const allRibbons = [];
            pathData.multiple.forEach((siblingPath, siblingI) => {
                _rAccessor = () => siblingPath.weight;
                const currentRibbon = buildRibbon(currentPoints);
                allRibbons.push(currentRibbon);
                const nextSibling = pathData.multiple[siblingI + 1];
                if (nextSibling) {
                    const currentLeftSide = currentRibbon
                        .reverse()
                        .filter((d) => d.direction === "back")
                        .reduce(dedupeRibbonPoints(), []);
                    _rAccessor = () => nextSibling.weight;
                    const leftHandInflatedRibbon = buildRibbon(currentLeftSide);
                    currentPoints = leftHandInflatedRibbon
                        .reverse()
                        .filter((d) => d.direction === "back")
                        .reduce(dedupeRibbonPoints(), []);
                }
            });
            _rAccessor = original_r;
            return allRibbons.map((d) => _lineConstructor.x(_xAccessor).y(_yAccessor).curve(_interpolator)(d));
        }
        const bothPoints = buildRibbon(pathData).reduce(dedupeRibbonPoints(), []);
        return _lineConstructor.x(_xAccessor).y(_yAccessor).curve(_interpolator)(bothPoints);
    }
    _ribbon.x = function (_value) {
        if (!arguments.length)
            return _xAccessor;
        _xAccessor = _value;
        return _ribbon;
    };
    _ribbon.y = function (_value) {
        if (!arguments.length)
            return _yAccessor;
        _yAccessor = _value;
        return _ribbon;
    };
    _ribbon.r = function (_value) {
        if (!arguments.length)
            return _rAccessor;
        _rAccessor = _value;
        return _ribbon;
    };
    _ribbon.interpolate = function (_value) {
        if (!arguments.length)
            return _interpolator;
        _interpolator = _value;
        return _ribbon;
    };
    return _ribbon;
    function offsetEdge(d) {
        const diffX = _yAccessor(d.target) - _yAccessor(d.source);
        const diffY = _xAccessor(d.target) - _xAccessor(d.source);
        const angle0 = Math.atan2(diffY, diffX) + Math.PI / 2;
        const angle1 = angle0 + Math.PI * 0.5;
        const angle2 = angle0 + Math.PI * 0.5;
        const x1 = _xAccessor(d.source) + _rAccessor(d.source) * Math.cos(angle1);
        const y1 = _yAccessor(d.source) - _rAccessor(d.source) * Math.sin(angle1);
        const x2 = _xAccessor(d.target) + _rAccessor(d.target) * Math.cos(angle2);
        const y2 = _yAccessor(d.target) - _rAccessor(d.target) * Math.sin(angle2);
        return { x1: x1, y1: y1, x2: x2, y2: y2 };
    }
    function buildRibbon(points) {
        const bothCode = [];
        let x = 0;
        let transformedPoints = { x1: 0, y1: 0, x2: 0, y2: 0 };
        while (x < points.length) {
            if (x !== points.length - 1) {
                transformedPoints = offsetEdge({
                    source: points[x],
                    target: points[x + 1]
                });
                const p1 = {
                    x: transformedPoints.x1,
                    y: transformedPoints.y1,
                    direction: "forward"
                };
                const p2 = {
                    x: transformedPoints.x2,
                    y: transformedPoints.y2,
                    direction: "forward"
                };
                bothCode.push(p1, p2);
                if (bothCode.length > 3) {
                    const l = bothCode.length - 1;
                    const lineA = { a: bothCode[l - 3], b: bothCode[l - 2] };
                    const lineB = { a: bothCode[l - 1], b: bothCode[l] };
                    const intersect = findIntersect(lineA.a.x, lineA.a.y, lineA.b.x, lineA.b.y, lineB.a.x, lineB.a.y, lineB.b.x, lineB.b.y);
                    if (intersect.found === true) {
                        lineA.b.x = intersect.x;
                        lineA.b.y = intersect.y;
                        lineB.a.x = intersect.x;
                        lineB.a.y = intersect.y;
                    }
                }
            }
            x++;
        }
        x--;
        //Back
        while (x >= 0) {
            if (x !== 0) {
                transformedPoints = offsetEdge({
                    source: points[x],
                    target: points[x - 1]
                });
                const p1 = {
                    x: transformedPoints.x1,
                    y: transformedPoints.y1,
                    direction: "back"
                };
                const p2 = {
                    x: transformedPoints.x2,
                    y: transformedPoints.y2,
                    direction: "back"
                };
                bothCode.push(p1, p2);
                if (bothCode.length > 3) {
                    const l = bothCode.length - 1;
                    const lineA = { a: bothCode[l - 3], b: bothCode[l - 2] };
                    const lineB = { a: bothCode[l - 1], b: bothCode[l] };
                    const intersect = findIntersect(lineA.a.x, lineA.a.y, lineA.b.x, lineA.b.y, lineB.a.x, lineB.a.y, lineB.b.x, lineB.b.y);
                    if (intersect.found === true) {
                        lineA.b.x = intersect.x;
                        lineA.b.y = intersect.y;
                        lineB.a.x = intersect.x;
                        lineB.a.y = intersect.y;
                    }
                }
            }
            x--;
        }
        return bothCode;
    }
    function findIntersect(l1x1, l1y1, l1x2, l1y2, l2x1, l2y1, l2x2, l2y2) {
        let a, b;
        const result = {
            x: null,
            y: null,
            found: false
        };
        const d = (l2y2 - l2y1) * (l1x2 - l1x1) - (l2x2 - l2x1) * (l1y2 - l1y1);
        if (d === 0) {
            return result;
        }
        a = l1y1 - l2y1;
        b = l1x1 - l2x1;
        const n1 = (l2x2 - l2x1) * a - (l2y2 - l2y1) * b;
        const n2 = (l1x2 - l1x1) * a - (l1y2 - l1y1) * b;
        a = n1 / d;
        b = n2 / d;
        result.x = l1x1 + a * (l1x2 - l1x1);
        result.y = l1y1 + a * (l1y2 - l1y1);
        if (a > 0 && a < 1 && b > 0 && b < 1) {
            result.found = true;
        }
        return result;
    }
}

const horizontalTornTickGenerator = (width, ticks, y, orient) => {
    const step = width / ticks;
    let currentStep = 0;
    let tickPath = `M0,${y}`;
    const mod = orient === "right" ? -1 : 1;
    while (currentStep <= width) {
        tickPath += `L${currentStep},${y}`;
        if (currentStep < width) {
            tickPath += `L${currentStep + step / 2},${y + 10 * mod}`;
        }
        currentStep += step;
    }
    return tickPath;
};
const verticalTornTickGenerator = (height, ticks, x, orient) => {
    const step = height / ticks;
    let currentStep = 0;
    let tickPath = `M${x},0`;
    const mod = orient === "bottom" ? -1 : 1;
    while (currentStep <= height) {
        tickPath += `L${x},${currentStep}`;
        if (currentStep < height) {
            tickPath += `L${x + 10 * mod},${currentStep + step / 2}`;
        }
        currentStep += step;
    }
    return tickPath;
};
const generateTornBaseline = (orient, baselineSettings) => {
    let tornD = "";
    const { x1, x2, y1, y2 } = baselineSettings;
    if (orient === "left" || orient === "right") {
        const calcWidth = Math.abs(x2 - x1);
        const ticks = Math.ceil(calcWidth / 40);
        tornD = horizontalTornTickGenerator(calcWidth, ticks, orient === "right" ? 0 : y1, orient);
    }
    else {
        const calcHeight = Math.abs(y2 - y1);
        const ticks = Math.ceil(calcHeight / 40);
        tornD = verticalTornTickGenerator(calcHeight, ticks, x1, orient);
    }
    return tornD;
};
const defaultTickLineGenerator = ({ xy, orient, i, baseMarkProps, className = "", jaggedBase }) => {
    let genD = `M${xy.x1},${xy.y1}L${xy.x2},${xy.y2}`;
    if (jaggedBase && i === 0) {
        genD = generateTornBaseline(orient, xy);
    }
    return (React.createElement(SemioticMark, Object.assign({ key: i, markType: "path", renderMode: xy.renderMode, fill: "none", stroke: "black", strokeWidth: "1px", d: genD, className: `tick-line tick ${orient} ${className}` }, baseMarkProps)));
};
const outboundTickLineGenerator = ({ xy, orient, i, className = "" }) => {
    const tickLength = 8;
    let genD = `M-4,${xy.y1}L${xy.x1},${xy.y2}`;
    if (orient === "left") {
        genD = `M${xy.x1 - tickLength},${xy.y1}L${xy.x1},${xy.y2}`;
    }
    else if (orient === "right") {
        genD = `M${xy.x2},${xy.y1}L${xy.x2 + tickLength},${xy.y2}`;
    }
    else if (orient === "top") {
        genD = `M${xy.x1},${xy.y1 - tickLength}L${xy.x1},${xy.y1}`;
    }
    else if (orient === "bottom") {
        genD = `M${xy.x1},${xy.y2}L${xy.x1},${xy.y2 + tickLength}`;
    }
    return (React.createElement(SemioticMark, { key: i, markType: "path", renderMode: xy.renderMode, fill: "none", stroke: "black", strokeWidth: "1px", d: genD, className: `outbound-tick-line tick ${orient} ${className}` }));
};
function generateTickValues(tickValues, ticks, scale) {
    const axisSize = Math.abs(scale.range()[1] - scale.range()[0]);
    if (!tickValues) {
        if (!ticks) {
            ticks = Math.max(1, Math.floor(axisSize / 40));
        }
        tickValues = (scale.ticks && scale.ticks(ticks)) || scale.domain();
    }
    return tickValues;
}
function axisPieces({ renderMode = () => undefined, padding = 5, scale, ticks, tickValues = generateTickValues(undefined, ticks, scale), orient = "left", size, footer = false, tickSize = footer
    ? -10
    : ["top", "bottom"].find((d) => d === orient)
        ? size[1]
        : size[0], jaggedBase }) {
    //returns x1 (start of line), x2 (end of line) associated with the value of the tick
    let axisDomain = [], position1, position2, domain1, domain2, tposition1, tposition2, textPositionMod = 0, textPositionMod2 = 0, defaultAnchor = "middle";
    switch (orient) {
        case "top":
            position1 = "x1";
            position2 = "x2";
            domain1 = "y1";
            domain2 = "y2";
            axisDomain = [0, tickSize];
            tposition1 = "tx";
            tposition2 = "ty";
            textPositionMod -= 20 - padding;
            break;
        case "bottom":
            position1 = "x1";
            position2 = "x2";
            domain1 = "y2";
            domain2 = "y1";
            axisDomain = [size[1], size[1] - tickSize];
            tposition1 = "tx";
            tposition2 = "ty";
            textPositionMod += 20 + padding;
            break;
        case "right":
            position1 = "y2";
            position2 = "y1";
            domain1 = "x2";
            domain2 = "x1";
            axisDomain = [size[0], size[0] - tickSize];
            tposition1 = "ty";
            tposition2 = "tx";
            textPositionMod += 5 + padding;
            textPositionMod2 += 5;
            defaultAnchor = "start";
            break;
        //left
        default:
            position1 = "y1";
            position2 = "y2";
            domain1 = "x1";
            domain2 = "x2";
            axisDomain = [0, tickSize];
            tposition1 = "ty";
            tposition2 = "tx";
            textPositionMod -= 5 + padding;
            textPositionMod2 += 5;
            defaultAnchor = "end";
            break;
    }
    let generatedTicks = tickValues instanceof Function ? tickValues({ orient }) : tickValues;
    if (jaggedBase &&
        generatedTicks.find((t) => t === scale.domain()[0]) === undefined) {
        generatedTicks = [scale.domain()[0], ...generatedTicks];
    }
    return generatedTicks.map((tick, i) => {
        const tickPosition = scale(tick);
        return {
            [position1]: tickPosition,
            [position2]: tickPosition,
            [domain1]: axisDomain[0],
            [domain2]: axisDomain[1],
            [tposition1]: tickPosition + textPositionMod2,
            [tposition2]: axisDomain[0] + textPositionMod,
            defaultAnchor,
            renderMode: renderMode(tick, i),
            value: tick
        };
    });
}
const axisLabels = ({ axisParts, tickFormat, rotate = 0, center = false, orient }) => {
    return axisParts.map((axisPart, i) => {
        let renderedValue = tickFormat(axisPart.value, i);
        if (typeof renderedValue !== "object" || renderedValue instanceof Date) {
            renderedValue = (React.createElement("text", { textAnchor: axisPart.defaultAnchor, className: "axis-label" }, renderedValue.toString ? renderedValue.toString() : renderedValue));
        }
        let textX = axisPart.tx;
        let textY = axisPart.ty;
        if (center) {
            switch (orient) {
                case "right":
                    textX -= (axisPart.x2 - axisPart.x1) / 2;
                    break;
                case "left":
                    textX += (axisPart.x2 - axisPart.x1) / 2;
                    break;
                case "top":
                    textY += (axisPart.y2 - axisPart.y1) / 2;
                    break;
                case "bottom":
                    textY -= (axisPart.y2 - axisPart.y1) / 2;
                    break;
            }
        }
        return (React.createElement("g", { key: i, pointerEvents: "none", transform: `translate(${textX},${textY}) rotate(${rotate})`, className: "axis-label" }, renderedValue));
    });
};
const baselineGenerator = (orient, size, className) => {
    const offsets = {
        left: { x: 0, y: 0, width: 0, height: size[1] },
        right: { x: size[0], y: 0, width: 0, height: size[1] },
        top: { x: 0, y: 0, width: size[0], height: 0 },
        bottom: { x: 0, y: size[1], width: size[0], height: 0 }
    };
    const orientOffset = offsets[orient];
    return (React.createElement("line", { key: "baseline", className: `axis-baseline ${className}`, stroke: "black", strokeLinecap: "square", x1: orientOffset.x, x2: orientOffset.x + orientOffset.width, y1: orientOffset.y, y2: orientOffset.y + orientOffset.height }));
};
const axisLines = ({ axisParts, orient, tickLineGenerator = defaultTickLineGenerator, baseMarkProps, className, jaggedBase, scale, showOutboundTickLines = false }) => {
    const axisLines = axisParts.map((axisPart, i) => tickLineGenerator({
        xy: axisPart,
        orient,
        i,
        baseMarkProps,
        className,
        jaggedBase,
        scale
    }));
    const outboundAxisLines = showOutboundTickLines
        ? axisParts.map((axisPart, i) => outboundTickLineGenerator({
            xy: axisPart,
            orient,
            i,
            className
        }))
        : [];
    return [...axisLines, outboundAxisLines];
};

function translateX({ t, o, w }) {
    if ((t === "contour" || t === "boxplot") && (o === "left" || o === "right")) {
        return w / 2;
    }
    else {
        return 0;
    }
}
function translateY({ t, o, w }) {
    if ((t === "contour" || t === "boxplot") && (o === "top" || o === "bottom")) {
        return w / 2;
    }
    else {
        return 0;
    }
}
function SummaryGraphic(props) {
    const { translation, orient, decoratedSummaryType, summaryWidth, renderedSummary, points } = props;
    return (React__default.createElement("g", { className: "marginal-graphics", transform: `translate(${translation[orient]})` },
        React__default.createElement("g", { transform: `translate(${translateX({
                t: decoratedSummaryType.type,
                o: orient,
                w: summaryWidth
            })},${translateY({
                t: decoratedSummaryType.type,
                o: orient,
                w: summaryWidth
            })})` }, renderedSummary),
        points));
}

function AxisTitle(props) {
    const { className, translation, position, rotation, labelName, anchorMod } = props;
    return (React__default.createElement("g", { className: `axis-title ${className}`, transform: `translate(${[
            translation[0] + position[0],
            translation[1] + position[1]
        ]}) rotate(${rotation})` }, React__default.isValidElement(labelName) ? (labelName) : (React__default.createElement("text", { textAnchor: anchorMod }, labelName))));
}

const generateLineBounds = (xydata, basedata, topBoundingAccessor, bottomBoundingAccessor) => {
    const tops = xydata.map((d, i) => [
        d[0],
        d[1] + topBoundingAccessor(basedata[i])
    ]);
    const bottoms = xydata.map((d, i) => [
        d[0],
        d[1] - bottomBoundingAccessor(basedata[i])
    ]);
    return [...tops, ...bottoms.reverse()];
};
function lineBounding({ summaryType, data, defined }) {
    let projectedSummaries = [];
    if (!summaryType.type) {
        summaryType = { type: summaryType };
    }
    const { boundingAccessor, topBoundingAccessor = boundingAccessor, bottomBoundingAccessor = boundingAccessor } = summaryType;
    data.forEach((lineData) => {
        const definedData = lineData._baseData.map(defined);
        let currentBaseData = [];
        let currentXYFC = [];
        const boundingPieces = [
            {
                xyf: currentXYFC,
                base: currentBaseData
            }
        ];
        definedData.forEach((d, i) => {
            if (d === true) {
                currentBaseData.push(lineData._baseData[i]);
                currentXYFC.push(lineData._xyfCoordinates[i]);
            }
            else if (definedData[i + 1]) {
                currentBaseData = [];
                currentXYFC = [];
                boundingPieces.push({
                    xyf: currentXYFC,
                    base: currentBaseData
                });
            }
        });
        boundingPieces.forEach(({ xyf, base }) => {
            const boundingProjectedSummary = {
                data: lineData,
                parentSummary: lineData,
                _xyfCoordinates: generateLineBounds(xyf, base, topBoundingAccessor, bottomBoundingAccessor)
            };
            projectedSummaries = [...projectedSummaries, boundingProjectedSummary];
        });
    });
    return projectedSummaries;
}
function contouring({ summaryType, data, finalXExtent, finalYExtent }) {
    let projectedSummaries = [];
    if (!summaryType.type) {
        summaryType = { type: summaryType };
    }
    const { resolution = 500, thresholds = 10, bandwidth = 20, neighborhood } = summaryType;
    const xScale = scaleLinear()
        .domain(finalXExtent)
        .rangeRound([0, resolution])
        .nice();
    const yScale = scaleLinear()
        .domain(finalYExtent)
        .rangeRound([resolution, 0])
        .nice();
    data.forEach((contourData) => {
        let contourProjectedSummaries = contourDensity()
            .size([resolution, resolution])
            .x((d) => xScale(d[0]))
            .y((d) => yScale(d[1]))
            .thresholds(thresholds)
            .bandwidth(bandwidth)(contourData._xyfCoordinates);
        if (neighborhood) {
            contourProjectedSummaries = [contourProjectedSummaries[0]];
        }
        const max = Math.max(...contourProjectedSummaries.map((d) => d.value));
        contourProjectedSummaries.forEach((summary) => {
            summary.parentSummary = contourData;
            summary.bounds = [];
            summary.percent = summary.value / max;
            summary.coordinates.forEach((poly) => {
                poly.forEach((subpoly, i) => {
                    poly[i] = subpoly.map((coordpair) => {
                        coordpair = [
                            xScale.invert(coordpair[0]),
                            yScale.invert(coordpair[1])
                        ];
                        return coordpair;
                    });
                    //Only push bounds for the main poly, not its interior rings, otherwise you end up labeling interior cutouts
                    if (i === 0) {
                        summary.bounds.push(shapeBounds(poly[i]));
                    }
                });
            });
        });
        projectedSummaries = [...projectedSummaries, ...contourProjectedSummaries];
    });
    return projectedSummaries;
}
function hexbinning({ preprocess = true, processedData = false, summaryType: baseSummaryType, data: baseData, finalXExtent: baseXExtent, finalYExtent: baseYExtent, size, xScaleType = scaleLinear(), yScaleType = scaleLinear(), margin, baseMarkProps, styleFn, classFn, renderFn, chartSize }) {
    let finalXExtent = baseXExtent;
    let finalYExtent = baseYExtent;
    if (!finalXExtent) {
        const xData = baseData.coordinates.map((p) => p.x);
        finalXExtent = [Math.min(...xData), Math.max(...xData)];
    }
    if (!finalYExtent) {
        const yData = baseData.coordinates.map((p) => p.y);
        finalYExtent = [Math.min(...yData), Math.max(...yData)];
    }
    if (processedData && baseData) {
        return baseData.coordinates;
    }
    let projectedSummaries = [];
    let summaryType;
    if (!baseSummaryType.type) {
        summaryType = { type: summaryType };
    }
    else {
        summaryType = baseSummaryType;
    }
    const { 
    //    binGraphic = "hex",
    bins = 0.05, cellPx, binValue = (d) => d.length, binMax, customMark } = summaryType;
    if (baseData.coordinates && !baseData._xyfCoordinates) {
        baseData._xyfCoordinates = baseData.coordinates.map((d) => [d.x, d.y]);
    }
    const data = Array.isArray(baseData) ? baseData : [baseData];
    const hexBinXScale = xScaleType.domain(finalXExtent).range([0, size[0]]);
    const hexBinYScale = yScaleType.domain(finalYExtent).range([0, size[1]]);
    const actualResolution = (cellPx && cellPx / 2) || ((bins > 1 ? 1 / bins : bins) * size[0]) / 2;
    const hexbinner = hexbin()
        .x((d) => hexBinXScale(d._xyfPoint[0]))
        .y((d) => hexBinYScale(d._xyfPoint[1]))
        .radius(actualResolution)
        .size(size);
    let hexMax;
    const allHexes = hexbinner.centers();
    data.forEach((hexbinData) => {
        hexMax = 0;
        const hexes = hexbinner(hexbinData._xyfCoordinates.map((d, i) => (Object.assign({ _xyfPoint: d }, hexbinData.coordinates[i]))));
        const centerHash = {};
        hexes.forEach((d) => {
            centerHash[`${parseInt(d.x)}-${parseInt(d.y)}`] = true;
        });
        allHexes.forEach((hexCenter) => {
            if (!centerHash[`${parseInt(hexCenter[0])}-${parseInt(hexCenter[1])}`]) {
                const newHex = [];
                newHex.x = hexCenter[0];
                newHex.y = hexCenter[1];
                hexes.push(newHex);
            }
        });
        hexMax = Math.max(...hexes.map((d) => binValue(d)));
        if (binMax) {
            binMax(hexMax);
        }
        //Option for blank hexe
        const hexBase = [
            [0, -1],
            [0.866, -0.5],
            [0.866, 0.5],
            [0, 1],
            [-0.866, 0.5],
            [-0.866, -0.5]
        ];
        const hexWidth = hexBinXScale.invert(actualResolution) - finalXExtent[0];
        const hexHeight = hexBinYScale.invert(actualResolution) - finalYExtent[0];
        const hexacoordinates = hexBase.map((d) => [
            d[0] * hexWidth,
            d[1] * hexHeight
        ]);
        const hexbinProjectedSummaries = hexes.map((d) => {
            const hexValue = binValue(d);
            const gx = d.x;
            const gy = d.y;
            d.x = hexBinXScale.invert(d.x);
            d.y = hexBinYScale.invert(d.y);
            const percent = hexValue / hexMax;
            return {
                customMark: customMark && (React.createElement("g", { transform: `translate(${gx},${size[1] - gy})` }, customMark({
                    d: Object.assign(Object.assign({}, d), { binItems: d, percent, value: hexValue, radius: actualResolution, hexCoordinates: hexBase.map((d) => [
                            d[0] * actualResolution,
                            d[1] * actualResolution
                        ]) }),
                    baseMarkProps,
                    margin,
                    styleFn,
                    classFn,
                    renderFn,
                    chartSize,
                    adjustedSize: size
                }))),
                _xyfCoordinates: hexacoordinates.map((p) => [p[0] + d.x, p[1] + d.y]),
                value: hexValue,
                percent,
                data: d,
                parentSummary: hexbinData,
                centroid: true
            };
        });
        projectedSummaries = [...projectedSummaries, ...hexbinProjectedSummaries];
    });
    if (preprocess) {
        projectedSummaries.forEach((d) => {
            d.x = d.data.x;
            d.y = d.data.y;
        });
        return {
            type: "hexbin",
            processedData: true,
            coordinates: projectedSummaries,
            binMax: hexMax
        };
    }
    return projectedSummaries;
}
// ADD PRECALC AND EXPOSE PRECALC FUNCTION
function heatmapping({ preprocess = true, processedData = false, summaryType: baseSummaryType, data: baseData, finalXExtent = [
    Math.min(...baseData.coordinates.map((d) => d.x)),
    Math.max(...baseData.coordinates.map((d) => d.x))
], finalYExtent = [
    Math.min(...baseData.coordinates.map((d) => d.y)),
    Math.max(...baseData.coordinates.map((d) => d.y))
], size, xScaleType = scaleLinear(), yScaleType = scaleLinear(), margin, baseMarkProps, styleFn, classFn, renderFn, chartSize }) {
    if (processedData && baseData) {
        return baseData.coordinates;
    }
    if (baseData.coordinates && !baseData._xyfCoordinates) {
        baseData._xyfCoordinates = baseData.coordinates.map((d) => [d.x, d.y]);
    }
    const data = Array.isArray(baseData) ? baseData : [baseData];
    let projectedSummaries = [];
    let summaryType;
    if (!baseSummaryType.type) {
        summaryType = { type: summaryType };
    }
    else {
        summaryType = baseSummaryType;
    }
    const { 
    //    binGraphic = "square",
    binValue = (d) => d.length, xBins = summaryType.yBins || 0.05, yBins = xBins, xCellPx = !summaryType.xBins && summaryType.xCellPx, yCellPx = !summaryType.yBins && summaryType.yCellPx, customMark, binMax } = summaryType;
    const xBinPercent = xBins < 1 ? xBins : 1 / xBins;
    const yBinPercent = yBins < 1 ? yBins : 1 / yBins;
    const heatmapBinXScale = xScaleType.domain(finalXExtent).range([0, size[0]]);
    const heatmapBinYScale = yScaleType.domain(finalYExtent).range([size[1], 0]);
    const actualResolution = [
        Math.ceil(((xCellPx && xCellPx / size[0]) || xBinPercent) * size[0] * 10) /
            10,
        Math.ceil(((yCellPx && yCellPx / size[1]) || yBinPercent) * size[1] * 10) /
            10
    ];
    let maxValue = -Infinity;
    data.forEach((heatmapData) => {
        const grid = [];
        const flatGrid = [];
        let cell;
        let gridColumn;
        for (let i = 0; Math.ceil(i) < size[0]; i += actualResolution[0]) {
            const x = heatmapBinXScale.invert(i);
            const x1 = heatmapBinXScale.invert(i + actualResolution[0]);
            gridColumn = [];
            grid.push(gridColumn);
            for (let j = 0; Math.ceil(j) < size[1]; j += actualResolution[1]) {
                const y = heatmapBinYScale.invert(j);
                const y1 = heatmapBinYScale.invert(j + actualResolution[1]);
                cell = {
                    gx: i,
                    gy: j,
                    gw: actualResolution[0],
                    gh: actualResolution[1],
                    x: (x + x1) / 2,
                    y: (y + y1) / 2,
                    binItems: [],
                    value: 0,
                    _xyfCoordinates: [
                        [x, y],
                        [x1, y],
                        [x1, y1],
                        [x, y1]
                    ],
                    parentSummary: heatmapData
                };
                gridColumn.push(cell);
                flatGrid.push(cell);
            }
            gridColumn.push(cell);
        }
        grid.push(gridColumn);
        heatmapData._xyfCoordinates.forEach((d, di) => {
            const baseX = heatmapBinXScale(d[0]);
            const baseY = heatmapBinYScale(d[1]);
            const xCoordinate = Math.floor(baseX / actualResolution[0]);
            const yCoordinate = Math.floor(baseY / actualResolution[1]);
            if (grid[xCoordinate][yCoordinate]) {
                grid[xCoordinate][yCoordinate].binItems.push(heatmapData.coordinates[di]);
            }
        });
        flatGrid.forEach((d) => {
            d.value = binValue(d.binItems);
            maxValue = Math.max(maxValue, d.value);
        });
        flatGrid.forEach((d) => {
            d.percent = d.value / maxValue;
            d.customMark = customMark && (React.createElement("g", { transform: `translate(${d.gx},${d.gy})` }, customMark({
                d,
                baseMarkProps,
                margin,
                styleFn,
                classFn,
                renderFn,
                chartSize,
                adjustedSize: size
            })));
        });
        projectedSummaries = [...projectedSummaries, ...flatGrid];
    });
    if (binMax) {
        binMax(maxValue);
    }
    if (preprocess) {
        const preprocessedSummary = {
            type: "heatmap",
            processedData: true,
            _baseData: [],
            _xyfCoordinates: [],
            data: [],
            bounds: [],
            x: 0,
            y: 0,
            coordinates: projectedSummaries,
            binMax: maxValue
        };
        return preprocessedSummary;
    }
    return projectedSummaries;
}
function trendlining({ preprocess = false, summaryType: baseSummaryType, data: baseData, finalXExtent = [
    Math.min(...baseData.coordinates.map((d) => d.x)),
    Math.max(...baseData.coordinates.map((d) => d.x))
], xScaleType = scaleLinear() }) {
    if (preprocess) {
        return baseData[0].coordinates;
    }
    let projectedSummaries = [];
    let summaryType;
    if (!baseSummaryType.type) {
        summaryType = { type: summaryType };
    }
    else {
        summaryType = baseSummaryType;
    }
    const { regressionType: baseRegressionType = "linear", order = 2, precision = 4, controlPoints = 20, curve = curveCardinal } = summaryType;
    let regressionType = baseRegressionType;
    if (finalXExtent[0] < 0 &&
        (baseRegressionType === "logarithmic" ||
            baseRegressionType === "power" ||
            baseRegressionType === "exponential")) {
        console.error(`Cannot use this ${baseRegressionType} regressionType type with value range that goes below 0, defaulting to linear`);
        regressionType = "linear";
    }
    if (baseData.coordinates && !baseData._xyfCoordinates) {
        baseData._xyfCoordinates = baseData.coordinates.map((d) => [d.x, d.y]);
    }
    const data = Array.isArray(baseData) ? baseData : [baseData];
    const xScale = xScaleType.domain([0, 1]).range(finalXExtent);
    projectedSummaries = [];
    data.forEach((bdata) => {
        const regressionLine = regression[regressionType](bdata._xyfCoordinates.map((d) => {
            let x = d[0];
            let y = d[1];
            if (typeof x !== "number") {
                x = x.getTime();
            }
            if (typeof y !== "number") {
                y = y.getTime();
            }
            return [x, y];
        }), {
            order,
            precision
        });
        const controlStep = 1 / controlPoints;
        let steps = [0, 1];
        if (regressionType !== "linear") {
            steps = [];
            for (let step = 0; step < 1 + controlStep; step += controlStep) {
                steps.push(step);
            }
        }
        const controlPointArray = [];
        steps.forEach((controlPoint) => {
            controlPointArray.push(regressionLine.predict(xScale(controlPoint)));
        });
        projectedSummaries.push({
            centroid: false,
            customMark: undefined,
            data: bdata,
            parentSummary: bdata,
            value: regressionLine.string,
            r2: regressionLine.r2,
            curve,
            _xyfCoordinates: controlPointArray
        });
    });
    return projectedSummaries;
}
function shapeBounds(coordinates) {
    let left = [Infinity, 0];
    let right = [-Infinity, 0];
    let top = [0, Infinity];
    let bottom = [0, -Infinity];
    coordinates.forEach((d) => {
        left = d[0] < left[0] ? d : left;
        right = d[0] > right[0] ? d : right;
        bottom = d[1] > bottom[1] ? d : bottom;
        top = d[1] < top[1] ? d : top;
    });
    return {
        center: [(left[0] + right[0]) / 2, (top[1] + bottom[1]) / 2],
        top,
        left,
        right,
        bottom
    };
}

function pointOnArcAtAngle$1(center, angle, distance) {
    const radians = Math.PI * (angle + 0.75) * 2;
    const xPosition = center[0] + distance * Math.cos(radians);
    const yPosition = center[1] + distance * Math.sin(radians);
    return [xPosition, yPosition];
}
const renderLaidOutPieces = ({ data, shouldRender, canvasRender, canvasDrawing, styleFn, classFn, baseMarkProps, renderKeyFn, ariaLabel, axis }) => {
    const valueFormat = axis && axis[0] && axis[0].tickFormat;
    if (!shouldRender)
        return null;
    const renderedPieces = [];
    data.forEach((d, i) => {
        if (canvasRender && canvasRender(d) === true) {
            const canvasPiece = {
                baseClass: "orframe-piece",
                tx: d.renderElement.tx || 0,
                ty: d.renderElement.ty || 0,
                d: d.piece,
                i,
                markProps: d.renderElement || d,
                styleFn: styleFn,
                classFn
            };
            canvasDrawing.push(canvasPiece);
        }
        else {
            if (React.isValidElement(d.renderElement || d)) {
                renderedPieces.push(d.renderElement || d);
            }
            else {
                /*ariaLabel.items*/
                const pieceAriaLabel = `${d.o} ${ariaLabel.items} value ${(valueFormat && valueFormat(d.piece.value)) || d.piece.value}`;
                renderedPieces.push(React.createElement(SemioticMark, Object.assign({}, baseMarkProps, { key: renderKeyFn
                        ? renderKeyFn(d.piece)
                        : d.renderKey || `piece-render-${i}` }, (d.renderElement || d), { "aria-label": pieceAriaLabel })));
            }
        }
    });
    return renderedPieces;
};

const curveHash = {
    step: curveStep,
    stepbefore: curveStepBefore,
    stepafter: curveStepAfter,
    cardinal: curveCardinal,
    basis: curveBasis,
    linear: curveLinear,
    catmullrom: curveCatmullRom,
    monotone: curveMonotoneY,
    monotonex: curveMonotoneX,
    monotoney: curveMonotoneY,
    natural: curveNatural
};
function lineGeneratorDecorator({ generator, projectedCoordinateNames, defined, xScale, yScale, interpolator, simpleLine }) {
    const { x, y, yTop, yBottom, xBottom, xTop } = projectedCoordinateNames;
    generator.x((d) => xScale(d[x])).curve(interpolator);
    if (simpleLine) {
        generator.y((d) => yScale(d[y]));
    }
    else {
        generator.y0((d) => yScale(d[yBottom])).y1((d) => yScale(d[yTop]));
        generator.x0((d) => xScale(d[xBottom])).x1((d) => xScale(d[xTop]));
    }
    if (defined) {
        generator.defined((p, q) => defined(p, q));
    }
    else {
        generator.defined((p) => !p._xyFrameUndefined);
    }
}
function createPoints({ xScale, yScale, canvasDrawing, data, projectedCoordinateNames, customMark, canvasRender, styleFn, classFn, renderKeyFn, renderMode, baseMarkProps, showLinePoints: baseShowLinePoints }) {
    const { y, x, xMiddle, yMiddle, yTop, yBottom } = projectedCoordinateNames;
    const showLinePoints = baseShowLinePoints === true ? undefined : baseShowLinePoints;
    const whichPoints = {
        top: yTop,
        bottom: yBottom
    };
    const whichWay = whichPoints[showLinePoints];
    const mappedPoints = [];
    data.forEach((d, i) => {
        const dX = xScale(d[xMiddle] !== undefined ? d[xMiddle] : d[x]);
        const dY = yScale(d[whichWay] !== undefined
            ? d[whichWay]
            : d[yMiddle] !== undefined
                ? d[yMiddle]
                : d[y]);
        const pointAriaLabel = `Point at x ${d.x} and y ${d.y}`;
        // CUSTOM MARK IMPLEMENTATION
        const renderedCustomMark = !customMark
            ? undefined
            : React.isValidElement(customMark)
                ? customMark
                : customMark({ d: d.data, xy: d, i, xScale, yScale });
        const markProps = customMark
            ? Object.assign(baseMarkProps, renderedCustomMark.props, {
                "aria-label": pointAriaLabel
            })
            : Object.assign(Object.assign({}, baseMarkProps), { key: `piece-${i}`, markType: "circle", r: 2, "aria-label": pointAriaLabel });
        if (renderedCustomMark &&
            renderedCustomMark.props &&
            !renderedCustomMark.props.markType &&
            (!canvasRender || canvasRender(d.data, i) !== true)) {
            mappedPoints.push(React.createElement("g", { transform: `translate(${dX},${dY})`, key: renderKeyFn ? renderKeyFn(d.data, i) : `custom-point-mark-${i}`, style: styleFn ? styleFn(d.data, i) : {}, className: classFn ? classFn(d.data, i) : "" }, renderedCustomMark));
        }
        else {
            if (canvasRender && canvasRender(d.data, i) === true) {
                const canvasPoint = {
                    type: "point",
                    baseClass: "frame-piece",
                    tx: dX,
                    ty: dY,
                    d,
                    i,
                    markProps,
                    styleFn,
                    renderFn: renderMode,
                    classFn
                };
                canvasDrawing.push(canvasPoint);
            }
            else {
                const yCoordinates = Array.isArray(d[y])
                    ? d[y].map((p) => yScale(p))
                    : [dY];
                yCoordinates.forEach((yc, yi) => {
                    const xCoordinates = Array.isArray(d[x])
                        ? d[x].map((p) => xScale(p))
                        : [dX];
                    xCoordinates.forEach((xc, xi) => {
                        mappedPoints.push(clonedAppliedElement({
                            baseClass: "frame-piece",
                            tx: xc,
                            ty: yc,
                            d: (d.data && Object.assign(Object.assign({}, d), d.data)) || d,
                            i: yi === 0 && xi === 0 ? i : `${i}-${yi}-${xi}`,
                            markProps,
                            styleFn,
                            renderFn: renderMode,
                            renderKeyFn,
                            classFn,
                            yi
                        }));
                    });
                });
            }
        }
    });
    return mappedPoints;
}
function createLines({ xScale, yScale, canvasDrawing, data, projectedCoordinateNames, customMark, canvasRender, styleFn, classFn, renderMode, renderKeyFn, type, defined, baseMarkProps, ariaLabel, axesData = [] }) {
    const xAxis = axesData.find((d) => d.orient === "bottom" || d.orient === "top");
    const yAxis = axesData.find((d) => d.orient === "left" || d.orient === "right");
    const xAxisFormatter = (xAxis && xAxis.tickFormat) || ((d) => d);
    const yAxisFormatter = (yAxis && yAxis.tickFormat) || ((d) => d);
    const customLine = typeof type === "object" ? type : { type };
    const interpolator = typeof customLine.interpolator === "string"
        ? curveHash[customLine.interpolator]
        : customLine.interpolator || curveLinear;
    const lineGenerator = customLine.simpleLine ? line() : area();
    lineGeneratorDecorator({
        projectedCoordinateNames,
        defined,
        interpolator,
        generator: lineGenerator,
        xScale,
        yScale,
        simpleLine: customLine.simpleLine
    });
    const dynamicLineGenerator = (interpolator.dynamicInterpolator &&
        ((d, i) => {
            const dynLineGenerator = area();
            lineGeneratorDecorator({
                projectedCoordinateNames,
                defined,
                interpolator: interpolator.dynamicInterpolator(d, i),
                generator: dynLineGenerator,
                xScale,
                yScale,
                simpleLine: customLine.simpleLine
            });
            return dynLineGenerator;
        })) ||
        (() => lineGenerator);
    const mappedLines = [];
    data.forEach((d, i) => {
        const mappedDataForLines = d.data.map((p) => (Object.assign(Object.assign({}, p.data), p)));
        if (customMark && typeof customMark === "function") {
            //shim to make customLineMark work until Semiotic 2
            const compatibleData = Object.assign(Object.assign({}, d), { data: mappedDataForLines });
            mappedLines.push(customMark({ d: compatibleData, i, xScale, yScale, canvasDrawing }));
        }
        else {
            const builtInDisplayProps = {};
            if (customLine.simpleLine) {
                builtInDisplayProps.fill = "none";
                builtInDisplayProps.stroke = "black";
            }
            const pathString = dynamicLineGenerator(d, i)(mappedDataForLines);
            const markProps = Object.assign(Object.assign(Object.assign({}, builtInDisplayProps), baseMarkProps), { markType: "path", d: pathString, "aria-label": d.data &&
                    d.data.length > 0 &&
                    `${d.data.length} point ${ariaLabel.items} starting value ${yAxisFormatter(d.data[0].y)} at ${xAxisFormatter(d.data[0].x)} ending value ${yAxisFormatter(d.data[d.data.length - 1].y)} at ${xAxisFormatter(d.data[d.data.length - 1].x)}` });
            if (canvasRender && canvasRender(d, i) === true) {
                const canvasLine = {
                    type: "line",
                    baseClass: "xyframe-line",
                    tx: 0,
                    ty: 0,
                    d,
                    i,
                    markProps,
                    styleFn,
                    renderFn: renderMode,
                    classFn
                };
                canvasDrawing.push(canvasLine);
            }
            else {
                let moddedStyleFn = styleFn;
                if (customLine.type === "area" && customLine.enclosed !== true) {
                    moddedStyleFn = (d, i) => (Object.assign(Object.assign({}, styleFn(d, i)), { stroke: "none" }));
                }
                mappedLines.push(clonedAppliedElement({
                    baseClass: "xyframe-line",
                    d,
                    i,
                    markProps,
                    styleFn: moddedStyleFn,
                    renderFn: renderMode,
                    renderKeyFn,
                    classFn
                }));
                if (customLine.type === "area" && customLine.enclosed !== true) {
                    const topperStyleFn = (d, i) => (Object.assign(Object.assign({}, styleFn(d, i)), { fill: "none" }));
                    const topperLineGenerator = line();
                    lineGeneratorDecorator({
                        projectedCoordinateNames,
                        defined,
                        interpolator: interpolator,
                        generator: topperLineGenerator,
                        xScale,
                        yScale,
                        simpleLine: true
                    });
                    const topperMarkProps = Object.assign(Object.assign({}, markProps), { d: topperLineGenerator(mappedDataForLines) });
                    mappedLines.push(clonedAppliedElement({
                        baseClass: "xyframe-line",
                        d,
                        i,
                        markProps: topperMarkProps,
                        styleFn: topperStyleFn,
                        renderFn: renderMode,
                        renderKeyFn,
                        classFn
                    }));
                }
            }
        }
    });
    if (customLine.type === "difference" && data.length === 2) {
        //Create the overlay line for the difference chart
        const diffdataA = data[0].data.map((basedata, baseI) => {
            const linePoint = basedata.yTop > data[1].data[baseI].yTop
                ? basedata.yTop
                : basedata.yBottom;
            return {
                x: basedata.x,
                y: linePoint,
                yBottom: linePoint,
                yTop: linePoint
            };
        });
        const diffdataB = data[0].data.map((basedata, baseI) => {
            const linePoint = data[1].data[baseI].yTop > basedata.yTop
                ? data[1].data[baseI].yTop
                : data[1].data[baseI].yBottom;
            return {
                x: basedata.x,
                y: linePoint,
                yBottom: linePoint,
                yTop: linePoint
            };
        });
        const doClassname = classFn
            ? `xyframe-line ${classFn(diffdataA)}`
            : "xyframe-line";
        const overLine = line();
        lineGeneratorDecorator({
            projectedCoordinateNames,
            defined,
            interpolator,
            generator: overLine,
            xScale,
            yScale,
            simpleLine: true
        });
        //      let baseStyle = props.lineStyle ? props.lineStyle(diffdata, 0) : {}
        const diffOverlayA = (React.createElement(SemioticMark, { key: "xyline-diff-a", className: `${doClassname} difference-overlay-a`, markType: "path", d: overLine(diffdataA), style: { fill: "none", pointerEvents: "none" } }));
        mappedLines.push(diffOverlayA);
        const diffOverlayB = (React.createElement(SemioticMark, { key: "xyline-diff-b", className: `${doClassname} difference-overlay-b`, markType: "path", d: overLine(diffdataB), style: { fill: "none", pointerEvents: "none" } }));
        mappedLines.push(diffOverlayB);
    }
    return mappedLines;
}
function createSummaries({ xScale, yScale, canvasDrawing, data, canvasRender, styleFn, classFn, renderKeyFn, renderMode, baseMarkProps, customMark }) {
    const summaryClass = classFn || (() => "");
    const summaryStyle = styleFn || (() => ({}));
    const renderFn = renderMode;
    if (!Array.isArray(data)) {
        data = [data];
    }
    const renderedSummaries = [];
    data.forEach((d, i) => {
        let className = "xyframe-summary";
        if (summaryClass) {
            className = `xyframe-summary ${summaryClass(d)}`;
        }
        let drawD = "";
        let shouldBeValid = false;
        if (typeof d.customMark === "string" ||
            React.isValidElement(d.customMark)) {
            drawD = d.customMark;
            shouldBeValid = true;
        }
        else if (d.type === "MultiPolygon") {
            const polycoords = d.coordinates;
            polycoords.forEach((coord) => {
                coord.forEach((c) => {
                    drawD += `M${c
                        .map((p) => `${xScale(p[0])},${yScale(p[1])}`)
                        .join("L")}Z `;
                });
            });
        }
        else if (customMark) {
            const xyfCoords = d._xyfCoordinates;
            const projectedCoordinates = xyfCoords.map((p) => [
                xScale(p[0]),
                yScale(p[1])
            ]);
            // CUSTOM MARK IMPLEMENTATION
            drawD = customMark({
                d,
                i,
                classFn: summaryClass,
                styleFn: summaryStyle,
                renderFn,
                projectedCoordinates,
                xScale,
                yScale,
                bounds: shapeBounds(projectedCoordinates)
            });
            shouldBeValid = true;
        }
        else {
            const xyfCoords = d._xyfCoordinates;
            if (d.curve) {
                const lineDrawing = line()
                    .x((d) => xScale(d[0]))
                    .y((d) => yScale(d[1]))
                    .curve(d.curve);
                drawD = lineDrawing(xyfCoords);
            }
            else {
                drawD = `M${xyfCoords
                    .map((p) => `${xScale(p[0])},${yScale(p[1])}`)
                    .join("L")}Z`;
            }
        }
        const renderKey = renderKeyFn ? renderKeyFn(d, i) : `summary-${i}`;
        if (shouldBeValid && React.isValidElement(drawD)) {
            renderedSummaries.push(drawD);
        }
        else if (canvasRender && canvasRender(d, i) === true) {
            const canvasSummary = {
                type: "summary",
                baseClass: "xyframe-summary",
                tx: 0,
                ty: 0,
                d,
                i,
                markProps: { markType: "path", d: drawD },
                styleFn: summaryStyle,
                renderFn,
                classFn: () => className
            };
            canvasDrawing.push(canvasSummary);
        }
        else {
            renderedSummaries.push(React.createElement(SemioticMark, Object.assign({}, baseMarkProps, { "data-testid": "xyframe-summary", key: renderKey, renderMode: renderFn ? renderFn(d, i) : undefined, className: className, markType: "path", d: drawD, style: summaryStyle(d, i) })));
        }
    });
    return renderedSummaries;
}
function clonedAppliedElement({ tx, ty, d, i, markProps, styleFn, renderFn, classFn, renderKeyFn, baseClass, yi }) {
    markProps.style = styleFn ? styleFn(d, i, yi) : {};
    markProps.className = baseClass;
    markProps.key = renderKeyFn
        ? renderKeyFn(d, i, yi)
        : `${baseClass}-${d.key === undefined ? i : d.key}`;
    if (tx || ty) {
        markProps.transform = `translate(${tx || 0},${ty || 0})`;
    }
    if (classFn) {
        markProps.className = `${baseClass} ${classFn(d, i, yi)}`;
    }
    if (markProps.style.r) {
        markProps.r = markProps.style.r;
    }
    markProps["data-testid"] = baseClass;
    if (!markProps.markType) {
        const RenderableMark = markProps;
        return React.createElement(RenderableMark);
    }
    markProps.renderMode = renderFn ? renderFn(d, i, yi) : undefined;
    return React.createElement(SemioticMark, Object.assign({}, markProps));
}

/**
 * Ckmeans algorithm from d3-scale-cluster
 *
 * Much of the code that lies within was taken from the simple-statistics library,
 * which offers a javascript implementation of the ckmeans algorithm originally
 * designed by Haizhou Wang and Mingzhou Song
 *
 * https://cran.r-project.org/web/packages/Ckmeans.1d.dp/
 * https://github.com/simple-statistics/simple-statistics
 *
 * The simple-statistics software license is included below
 *
 * --
 *
 * ISC License
 *
 * Copyright (c) 2014, Tom MacWright
 *
 * Permission to use, copy, modify, and/or distribute this software for any
 * purpose with or without fee is hereby granted, provided that the above
 * copyright notice and this permission notice appear in all copies.
 *
 * THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
 * REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY AND
 * FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
 * INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
 * LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
 * OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
 * PERFORMANCE OF THIS SOFTWARE.
 */
function numericSort(array) {
    return (array
        // ensure the array is not changed in-place
        .slice()
        // comparator function that treats input as numeric
        .sort(function (a, b) {
        return a - b;
    }));
}
function uniqueCountSorted(input) {
    var uniqueValueCount = 0;
    var lastSeenValue;
    for (var i = 0; i < input.length; i++) {
        if (i === 0 || input[i] !== lastSeenValue) {
            lastSeenValue = input[i];
            uniqueValueCount++;
        }
    }
    return uniqueValueCount;
}
function makeMatrix(columns, rows) {
    var matrix = [];
    for (var i = 0; i < columns; i++) {
        var column = [];
        for (var j = 0; j < rows; j++) {
            column.push(0);
        }
        matrix.push(column);
    }
    return matrix;
}
function ssq(j, i, sumX, sumXsq) {
    var sji; // s(j, i)
    if (j > 0) {
        var muji = (sumX[i] - sumX[j - 1]) / (i - j + 1); // mu(j, i)
        sji = sumXsq[i] - sumXsq[j - 1] - (i - j + 1) * muji * muji;
    }
    else {
        sji = sumXsq[i] - (sumX[i] * sumX[i]) / (i + 1);
    }
    return sji < 0 ? 0 : sji;
}
function fillMatrixColumn(imin, imax, column, matrix, backtrackMatrix, sumX, sumXsq) {
    if (imin > imax) {
        return;
    }
    // Start at midpoint between imin and imax
    var i = Math.floor((imin + imax) / 2);
    // Initialization of S[k][i]:
    matrix[column][i] = matrix[column - 1][i - 1];
    backtrackMatrix[column][i] = i;
    var jlow = column; // the lower end for j
    if (imin > column) {
        jlow = Math.max(jlow, backtrackMatrix[column][imin - 1] || 0);
    }
    jlow = Math.max(jlow, backtrackMatrix[column - 1][i] || 0);
    var jhigh = i - 1; // the upper end for j
    if (imax < matrix[0].length - 1) {
        jhigh = Math.min(jhigh, backtrackMatrix[column][imax + 1] || 0);
    }
    var sji;
    var sjlowi;
    var ssqjlow;
    var ssqj;
    for (var j = jhigh; j >= jlow; --j) {
        // compute s(j,i)
        sji = ssq(j, i, sumX, sumXsq);
        // MS May 11, 2016 Added:
        if (sji + matrix[column - 1][jlow - 1] >= matrix[column][i]) {
            break;
        }
        // Examine the lower bound of the cluster border
        // compute s(jlow, i)
        sjlowi = ssq(jlow, i, sumX, sumXsq);
        ssqjlow = sjlowi + matrix[column - 1][jlow - 1];
        if (ssqjlow < matrix[column][i]) {
            // shrink the lower bound
            matrix[column][i] = ssqjlow;
            backtrackMatrix[column][i] = jlow;
        }
        jlow++;
        ssqj = sji + matrix[column - 1][j - 1];
        if (ssqj < matrix[column][i]) {
            matrix[column][i] = ssqj;
            backtrackMatrix[column][i] = j;
        }
    }
    fillMatrixColumn(imin, i - 1, column, matrix, backtrackMatrix, sumX, sumXsq);
    fillMatrixColumn(i + 1, imax, column, matrix, backtrackMatrix, sumX, sumXsq);
}
function fillMatrices(data, matrix, backtrackMatrix) {
    var nValues = matrix[0].length;
    var sumX = new Array(nValues);
    var sumXsq = new Array(nValues);
    // Use the median to shift values of x to improve numerical stability
    var shift = data[Math.floor(nValues / 2)];
    // Initialize first row in matrix & backtrackMatrix
    for (var i = 0; i < nValues; ++i) {
        if (i === 0) {
            sumX[0] = data[0] - shift;
            sumXsq[0] = (data[0] - shift) * (data[0] - shift);
        }
        else {
            sumX[i] = sumX[i - 1] + data[i] - shift;
            sumXsq[i] = sumXsq[i - 1] + (data[i] - shift) * (data[i] - shift);
        }
        // Initialize for k = 0
        matrix[0][i] = ssq(0, i, sumX, sumXsq);
        backtrackMatrix[0][i] = 0;
    }
    // Initialize the rest of the columns
    var imin;
    for (var k = 1; k < matrix.length; ++k) {
        if (k < matrix.length - 1) {
            imin = k;
        }
        else {
            // No need to compute matrix[K-1][0] ... matrix[K-1][N-2]
            imin = nValues - 1;
        }
        fillMatrixColumn(imin, nValues - 1, k, matrix, backtrackMatrix, sumX, sumXsq);
    }
}
/**
 * Ckmeans clustering is an improvement on heuristic-based clustering
 * approaches like Jenks. The algorithm was developed in
 * [Haizhou Wang and Mingzhou Song](http://journal.r-project.org/archive/2011-2/RJournal_2011-2_Wang+Song.pdf)
 * as a [dynamic programming](https://en.wikipedia.org/wiki/Dynamic_programming) approach
 * to the problem of clustering numeric data into groups with the least
 * within-group sum-of-squared-deviations.
 *
 * Minimizing the difference within groups - what Wang & Song refer to as
 * `withinss`, or within sum-of-squares, means that groups are optimally
 * homogenous within and the data is split into representative groups.
 * This is very useful for visualization, where you may want to represent
 * a continuous variable in discrete color or style groups. This function
 * can provide groups that emphasize differences between data.
 *
 * Being a dynamic approach, this algorithm is based on two matrices that
 * store incrementally-computed values for squared deviations and backtracking
 * indexes.
 *
 * Unlike the [original implementation](https://cran.r-project.org/web/packages/Ckmeans.1d.dp/index.html),
 * this implementation does not include any code to automatically determine
 * the optimal number of clusters: this information needs to be explicitly
 * provided.
 *
 * ### References
 * _Ckmeans.1d.dp: Optimal k-means Clustering in One Dimension by Dynamic
 * Programming_ Haizhou Wang and Mingzhou Song ISSN 2073-4859
 *
 * from The R Journal Vol. 3/2, December 2011
 * @param {Array<number>} data input data, as an array of number values
 * @param {number} nClusters number of desired classes. This cannot be
 * greater than the number of values in the data array.
 * @returns {Array<Array<number>>} clustered input
 * @example
 * ckmeans([-1, 2, -1, 2, 4, 5, 6, -1, 2, -1], 3);
 * // The input, clustered into groups of similar numbers.
 * //= [[-1, -1, -1, -1], [2, 2, 2], [4, 5, 6]]);
 */
function ckmeans(data, nClusters) {
    if (nClusters > data.length) {
        throw new Error("Cannot generate more classes than there are data values");
    }
    var nValues = data.length;
    var sorted = numericSort(data);
    // we'll use this as the maximum number of clusters
    var uniqueCount = uniqueCountSorted(sorted);
    // if all of the input values are identical, there's one cluster
    // with all of the input in it.
    if (uniqueCount === 1) {
        return [sorted[0]];
    }
    nClusters = Math.min(uniqueCount, nClusters);
    // named 'S' originally
    var matrix = makeMatrix(nClusters, nValues);
    // named 'J' originally
    var backtrackMatrix = makeMatrix(nClusters, nValues);
    // This is a dynamic programming way to solve the problem of minimizing
    // within-cluster sum of squares. It's similar to linear regression
    // in this way, and this calculation incrementally computes the
    // sum of squares that are later read.
    fillMatrices(sorted, matrix, backtrackMatrix);
    // The real work of Ckmeans clustering happens in the matrix generation:
    // the generated matrices encode all possible clustering combinations, and
    // once they're generated we can solve for the best clustering groups
    // very quickly.
    var clusters = [];
    var clusterRight = backtrackMatrix[0].length - 1;
    // Backtrack the clusters from the dynamic programming matrix. This
    // starts at the bottom-right corner of the matrix (if the top-left is 0, 0),
    // and moves the cluster target with the loop.
    for (var cluster = backtrackMatrix.length - 1; cluster >= 0; cluster--) {
        var clusterLeft = backtrackMatrix[cluster][clusterRight];
        // fill the cluster from the sorted input by taking a slice of the
        // array. the backtrack matrix makes this easy - it stores the
        // indexes where the cluster should start and end.
        clusters[cluster] = sorted[clusterLeft];
        if (cluster > 0) {
            clusterRight = clusterLeft - 1;
        }
    }
    return clusters;
}

const contourMap = (d) => [d.xy.x, d.xy.y];
const verticalXYSorting = (a, b) => a.xy.y - b.xy.y;
const horizontalXYSorting = (a, b) => b.xy.x - a.xy.x;
const emptyObjectReturnFn = () => ({});
function createSummaryAxis({ summary, summaryI, axisSettings, axisCreator, projection, actualMax, adjustedSize, columnWidth }) {
    let axisTranslate = `translate(${summary.x},0)`;
    let axisDomain = [0, actualMax];
    if (projection === "horizontal") {
        axisTranslate = `translate(${0},${summary.x})`;
        axisDomain = [actualMax, 0];
    }
    else if (projection === "radial") {
        axisTranslate = "translate(0, 0)";
    }
    const axisWidth = projection === "horizontal" ? adjustedSize[0] : columnWidth;
    const axisHeight = projection === "vertical" ? adjustedSize[1] : columnWidth;
    axisSettings.size = [axisWidth, axisHeight];
    const axisScale = scaleLinear().domain(axisDomain).range([0, columnWidth]);
    const renderedSummaryAxis = axisCreator(axisSettings, summaryI, axisScale);
    return (React.createElement("g", { className: "summary-axis", key: `summaryPiece-axis-${summaryI}`, transform: axisTranslate }, renderedSummaryAxis));
}
function ckBinsRenderFn(props) {
    const { data, type, renderMode, eventListenersGenerator, styleFn, classFn, projection, adjustedSize, baseMarkProps } = props;
    const renderedSummaryMarks = [];
    const summaryXYCoords = [];
    const keys = Object.keys(data);
    let maxClusterSize = 0;
    const allBins = [];
    keys.forEach((key, summaryI) => {
        const summary = data[key];
        eventListenersGenerator(summary, summaryI);
        summary.width;
        const thisSummaryData = summary.pieceData.sort((a, b) => a.value - b.value);
        const firstElement = thisSummaryData[0];
        const lastElement = thisSummaryData[thisSummaryData.length - 1];
        const binScale = scaleLinear()
            .domain([firstElement.value, lastElement.value])
            .range([firstElement.scaledValue, lastElement.scaledValue]);
        const calculatedSummaryStyle = styleFn(thisSummaryData[0].data, summaryI);
        const calculatedSummaryClass = classFn(thisSummaryData[0].data, summaryI);
        const binData = thisSummaryData.map((d) => d.value);
        const clusters = 5;
        const ckstops = ckmeans(binData, clusters);
        let ckBins = [];
        ckstops.forEach((ckstop, i) => {
            var _a;
            const start = ckstop;
            const end = (_a = ckstops[i + 1]) !== null && _a !== void 0 ? _a : lastElement.value;
            ckBins.push({
                start,
                end,
                key,
                summary,
                calculatedSummaryStyle,
                calculatedSummaryClass,
                scaledStart: binScale(start),
                scaledEnd: binScale(end),
                values: []
            });
        });
        allBins.push(ckBins);
        let binI = 0;
        for (const datapoint of thisSummaryData) {
            if (ckBins[binI].end <= datapoint.value) {
                if (ckBins[binI + 1]) {
                    binI++;
                }
            }
            ckBins[binI].values.push(datapoint);
        }
        ckBins.forEach((ckbin) => {
            const clusterSize = ckbin.values.length;
            maxClusterSize = Math.max(clusterSize, maxClusterSize);
        });
    });
    allBins.forEach((ckBins, index) => {
        const CKBinElements = ckBins.map((ckBin) => {
            const { key, summary, calculatedSummaryStyle, calculatedSummaryClass } = ckBin;
            const { fill = "black", stroke = "black" } = calculatedSummaryStyle;
            const lightScale = scaleLinear()
                .domain([1, maxClusterSize])
                .range(["white", fill]);
            return Object.assign(Object.assign({}, baseMarkProps), { 
                //        renderMode: renderValue,
                markType: "rect", x: ckBin.scaledStart, width: ckBin.scaledEnd - ckBin.scaledStart, y: ckBin.summary.x, height: ckBin.summary.width, style: Object.assign({ strokeWidth: 2 }, calculatedSummaryStyle, {
                    fill: lightScale(ckBin.values.length),
                    strokeWidth: 1,
                    stroke
                }) });
        });
        renderedSummaryMarks.push({
            containerProps: {
                //        className: calculatedSummaryClass,
                transform: "",
                key: `summaryPiece-${index}`,
                role: "img",
                tabIndex: -1
            },
            //These are drawn items
            elements: CKBinElements
        });
    });
    return { marks: renderedSummaryMarks, xyPoints: summaryXYCoords };
}
function boxplotRenderFn({ data, type, renderMode, eventListenersGenerator, styleFn, classFn, projection, adjustedSize, baseMarkProps }) {
    const summaryElementStylingFn = type.elementStyleFn || emptyObjectReturnFn;
    const { outliers, fixedInput } = type;
    const keys = Object.keys(data);
    const renderedSummaryMarks = [];
    const summaryXYCoords = [];
    keys.forEach((key, summaryI) => {
        const summary = data[key];
        const eventListeners = eventListenersGenerator(summary, summaryI);
        const columnWidth = summary.width;
        const thisSummaryData = summary.pieceData;
        const calculatedSummaryStyle = styleFn(thisSummaryData[0].data, summaryI);
        const calculatedSummaryClass = classFn(thisSummaryData[0].data, summaryI);
        let summaryPositionNest, summaryValueNest, translate, extentlineX1, extentlineX2, extentlineY1, extentlineY2, topLineX1, topLineX2, midLineX1, midLineX2, bottomLineX1, bottomLineX2, rectTopWidth, rectTopHeight, rectTopY, rectTopX, rectBottomWidth, rectBottomHeight, rectBottomY, rectBottomX, rectWholeWidth, rectWholeHeight, rectWholeY, rectWholeX, topLineY1, topLineY2, bottomLineY1, bottomLineY2, midLineY1, midLineY2;
        const renderValue = renderMode ? renderMode(summary, summaryI) : undefined;
        summaryValueNest = thisSummaryData.map((p) => p.value).sort((a, b) => a - b);
        if (fixedInput !== true || summaryValueNest.length !== 5) {
            summaryValueNest = [
                quantile(summaryValueNest, 0.0),
                quantile(summaryValueNest, 0.25),
                quantile(summaryValueNest, 0.5),
                quantile(summaryValueNest, 0.75),
                quantile(summaryValueNest, 1.0)
            ];
        }
        const iqr = summaryValueNest[3] - summaryValueNest[1];
        let minOutlier, maxOutlier;
        if (outliers) {
            minOutlier = summaryValueNest[1] - iqr * 1.5;
            maxOutlier = summaryValueNest[3] + iqr * 1.5;
            summaryValueNest[0] = Math.max(summaryValueNest[0], minOutlier);
            summaryValueNest[4] = Math.min(summaryValueNest[4], maxOutlier);
        }
        //translate
        if (projection === "vertical") {
            summaryPositionNest = thisSummaryData
                .map((p) => p.scaledVerticalValue)
                .sort((a, b) => b - a);
            translate = `translate(${summary.x + summary.width / 2},0)`;
            if (fixedInput !== true || summaryValueNest.length !== 5) {
                summaryPositionNest = [
                    quantile(summaryPositionNest, 0.0),
                    quantile(summaryPositionNest, 0.25),
                    quantile(summaryPositionNest, 0.5),
                    quantile(summaryPositionNest, 0.75),
                    quantile(summaryPositionNest, 1.0)
                ];
            }
            if (outliers) {
                const positionIQR = summaryPositionNest[3] - summaryPositionNest[1];
                summaryPositionNest[0] = Math.min(summaryPositionNest[0], summaryPositionNest[1] - positionIQR * 1.5);
                summaryPositionNest[4] = Math.max(summaryPositionNest[4], summaryPositionNest[3] + positionIQR * 1.5);
            }
            extentlineX1 = 0;
            extentlineX2 = 0;
            extentlineY1 = summaryPositionNest[0];
            extentlineY2 = summaryPositionNest[4];
            topLineX1 = -columnWidth / 2;
            topLineX2 = columnWidth / 2;
            midLineX1 = -columnWidth / 2;
            midLineX2 = columnWidth / 2;
            bottomLineX1 = -columnWidth / 2;
            bottomLineX2 = columnWidth / 2;
            rectBottomWidth = columnWidth;
            rectBottomHeight = summaryPositionNest[1] - summaryPositionNest[2];
            rectBottomY = summaryPositionNest[2];
            rectBottomX = -columnWidth / 2;
            rectTopWidth = columnWidth;
            rectTopHeight = summaryPositionNest[2] - summaryPositionNest[3];
            rectWholeWidth = columnWidth;
            rectWholeHeight = summaryPositionNest[1] - summaryPositionNest[3];
            rectWholeY = summaryPositionNest[3];
            rectWholeX = -columnWidth / 2;
            rectTopY = summaryPositionNest[3];
            rectTopX = -columnWidth / 2;
            topLineY1 = summaryPositionNest[0];
            topLineY2 = summaryPositionNest[0];
            bottomLineY1 = summaryPositionNest[4];
            bottomLineY2 = summaryPositionNest[4];
            midLineY1 = summaryPositionNest[2];
            midLineY2 = summaryPositionNest[2];
            summaryXYCoords.push({
                label: "Maximum",
                key,
                summaryPieceName: "max",
                x: 0,
                y: summaryPositionNest[4],
                value: summaryValueNest[4]
            }, {
                label: "3rd Quartile",
                key,
                summaryPieceName: "q3area",
                x: 0,
                y: summaryPositionNest[3],
                value: summaryValueNest[3]
            }, {
                label: "Median",
                key,
                summaryPieceName: "median",
                x: 0,
                y: summaryPositionNest[2],
                value: summaryValueNest[2]
            }, {
                label: "1st Quartile",
                key,
                summaryPieceName: "q1area",
                x: 0,
                y: summaryPositionNest[1],
                value: summaryValueNest[1]
            }, {
                label: "Minimum",
                key,
                summaryPieceName: "min",
                x: 0,
                y: summaryPositionNest[0],
                value: summaryValueNest[0]
            });
        }
        else if (projection === "horizontal") {
            summaryPositionNest = thisSummaryData
                .map((p) => p.scaledValue)
                .sort((a, b) => a - b);
            const boxplotY = summary.x + summary.padding;
            translate = `translate(0,${summary.x + summary.width / 2})`;
            if (fixedInput !== true || summaryValueNest.length !== 5) {
                summaryPositionNest = [
                    quantile(summaryPositionNest, 0.0),
                    quantile(summaryPositionNest, 0.25),
                    quantile(summaryPositionNest, 0.5),
                    quantile(summaryPositionNest, 0.75),
                    quantile(summaryPositionNest, 1.0)
                ];
            }
            if (outliers) {
                const positionIQR = summaryPositionNest[3] - summaryPositionNest[1];
                summaryPositionNest[0] = Math.max(summaryPositionNest[0], summaryPositionNest[1] - positionIQR * 1.5);
                summaryPositionNest[4] = Math.min(summaryPositionNest[4], summaryPositionNest[3] + positionIQR * 1.5);
            }
            extentlineY1 = 0;
            extentlineY2 = 0;
            extentlineX1 = summaryPositionNest[0];
            extentlineX2 = summaryPositionNest[4];
            topLineY1 = -columnWidth / 2;
            topLineY2 = columnWidth / 2;
            midLineY1 = -columnWidth / 2;
            midLineY2 = columnWidth / 2;
            bottomLineY1 = -columnWidth / 2;
            bottomLineY2 = columnWidth / 2;
            rectTopHeight = columnWidth;
            rectTopWidth = summaryPositionNest[3] - summaryPositionNest[2];
            rectTopX = summaryPositionNest[2];
            rectTopY = -columnWidth / 2;
            rectBottomHeight = columnWidth;
            rectBottomWidth = summaryPositionNest[2] - summaryPositionNest[1];
            rectBottomX = summaryPositionNest[1];
            rectBottomY = -columnWidth / 2;
            rectWholeHeight = columnWidth;
            rectWholeWidth = summaryPositionNest[3] - summaryPositionNest[1];
            rectWholeX = summaryPositionNest[1];
            rectWholeY = -columnWidth / 2;
            topLineX1 = summaryPositionNest[0];
            topLineX2 = summaryPositionNest[0];
            bottomLineX1 = summaryPositionNest[4];
            bottomLineX2 = summaryPositionNest[4];
            midLineX1 = summaryPositionNest[2];
            midLineX2 = summaryPositionNest[2];
            summaryXYCoords.push({
                label: "Maximum",
                key,
                summaryPieceName: "max",
                x: summaryPositionNest[4],
                y: boxplotY,
                value: summaryValueNest[4]
            }, {
                label: "3rd Quartile",
                key,
                summaryPieceName: "q3area",
                x: summaryPositionNest[3],
                y: boxplotY,
                value: summaryValueNest[3]
            }, {
                label: "Median",
                key,
                summaryPieceName: "median",
                x: summaryPositionNest[2],
                y: boxplotY,
                value: summaryValueNest[2]
            }, {
                label: "1st Quartile",
                key,
                summaryPieceName: "q1area",
                x: summaryPositionNest[1],
                y: boxplotY,
                value: summaryValueNest[1]
            }, {
                label: "Minimum",
                key,
                summaryPieceName: "min",
                x: summaryPositionNest[0],
                y: boxplotY,
                value: summaryValueNest[0]
            });
        }
        if (projection === "radial") {
            summaryPositionNest = thisSummaryData
                .map((p) => p.scaledValue)
                .sort((a, b) => a - b);
            if (fixedInput !== true || summaryValueNest.length !== 5) {
                summaryPositionNest = [
                    quantile(summaryPositionNest, 0.0),
                    quantile(summaryPositionNest, 0.25),
                    quantile(summaryPositionNest, 0.5),
                    quantile(summaryPositionNest, 0.75),
                    quantile(summaryPositionNest, 1.0)
                ];
            }
            extentlineX1 = 0;
            extentlineX2 = 0;
            extentlineY1 = summaryPositionNest[0];
            extentlineY2 = summaryPositionNest[4];
            topLineX1 = -columnWidth / 2;
            topLineX2 = columnWidth / 2;
            midLineX1 = -columnWidth / 2;
            midLineX2 = columnWidth / 2;
            bottomLineX1 = -columnWidth / 2;
            bottomLineX2 = columnWidth / 2;
            rectTopWidth = columnWidth;
            rectTopHeight = summaryPositionNest[1] - summaryPositionNest[3];
            rectTopY = summaryPositionNest[3];
            rectTopX = -columnWidth / 2;
            rectBottomWidth = columnWidth;
            rectBottomHeight = summaryPositionNest[1] - summaryPositionNest[3];
            rectBottomY = summaryPositionNest[3];
            rectBottomX = -columnWidth / 2;
            topLineY1 = summaryPositionNest[0];
            topLineY2 = summaryPositionNest[0];
            bottomLineY1 = summaryPositionNest[4];
            bottomLineY2 = summaryPositionNest[4];
            midLineY1 = summaryPositionNest[2];
            midLineY2 = summaryPositionNest[2];
            const twoPI = Math.PI * 2;
            const bottomLineArcGenerator = arc()
                .innerRadius(bottomLineY1 / 2)
                .outerRadius(bottomLineY1 / 2);
            //        .padAngle(summary.pct_padding * twoPI);
            const topLineArcGenerator = arc()
                .innerRadius(topLineY1 / 2)
                .outerRadius(topLineY1 / 2);
            //        .padAngle(summary.pct_padding * twoPI);
            const midLineArcGenerator = arc()
                .innerRadius(midLineY1 / 2)
                .outerRadius(midLineY1 / 2);
            //        .padAngle(summary.pct_padding * twoPI);
            const bodyArcTopGenerator = arc()
                .innerRadius(summaryPositionNest[1] / 2)
                .outerRadius(midLineY1 / 2);
            //        .padAngle(summary.pct_padding * twoPI);
            const bodyArcBottomGenerator = arc()
                .innerRadius(midLineY1 / 2)
                .outerRadius(summaryPositionNest[3] / 2);
            //        .padAngle(summary.pct_padding * twoPI);
            const bodyArcWholeGenerator = arc()
                .innerRadius(summaryPositionNest[1] / 2)
                .outerRadius(summaryPositionNest[3] / 2);
            //        .padAngle(summary.pct_padding * twoPI);
            let startAngle = summary.pct_start + summary.pct_padding / 2;
            let endAngle = summary.pct + summary.pct_start - summary.pct_padding / 2;
            const midAngle = summary.pct / 2 + summary.pct_start;
            startAngle *= twoPI;
            endAngle *= twoPI;
            const radialAdjustX = adjustedSize[0] / 2;
            const radialAdjustY = adjustedSize[1] / 2;
            //        const bottomPoint = bottomLineArcGenerator.centroid({ startAngle, endAngle })
            //        const topPoint = topLineArcGenerator.centroid({ startAngle, endAngle })
            const bottomPoint = pointOnArcAtAngle$1([0, 0], midAngle, summaryPositionNest[4] / 2);
            const topPoint = pointOnArcAtAngle$1([0, 0], midAngle, summaryPositionNest[0] / 2);
            const thirdPoint = pointOnArcAtAngle$1([0, 0], midAngle, summaryPositionNest[3] / 2);
            const midPoint = pointOnArcAtAngle$1([0, 0], midAngle, summaryPositionNest[2] / 2);
            const firstPoint = pointOnArcAtAngle$1([0, 0], midAngle, summaryPositionNest[1] / 2);
            summaryXYCoords.push({
                label: "Minimum",
                key,
                summaryPieceName: "min",
                x: topPoint[0] + radialAdjustX,
                y: topPoint[1] + radialAdjustY,
                value: summaryValueNest[0]
            }, {
                label: "1st Quartile",
                key,
                summaryPieceName: "q3area",
                x: firstPoint[0] + radialAdjustX,
                y: firstPoint[1] + radialAdjustY,
                value: summaryValueNest[1]
            }, {
                label: "Median",
                key,
                summaryPieceName: "median",
                x: midPoint[0] + radialAdjustX,
                y: midPoint[1] + radialAdjustY,
                value: summaryValueNest[2]
            }, {
                label: "3rd Quartile",
                key,
                summaryPieceName: "q1area",
                x: thirdPoint[0] + radialAdjustX,
                y: thirdPoint[1] + radialAdjustY,
                value: summaryValueNest[3]
            }, {
                label: "Maximum",
                key,
                summaryPieceName: "max",
                x: bottomPoint[0] + radialAdjustX,
                y: bottomPoint[1] + radialAdjustY,
                value: summaryValueNest[4]
            });
            translate = `translate(${radialAdjustX},${radialAdjustY})`;
            renderedSummaryMarks.push({
                //This is a g element
                containerProps: Object.assign(Object.assign({}, eventListeners), { className: calculatedSummaryClass, transform: translate, key: `summaryPiece-${summaryI}`, role: "img", tabIndex: -1, "data-o": key, "aria-label": `${key} boxplot showing ${summaryXYCoords
                        .filter((d) => d.key === key)
                        .map((d) => `${d.label} ${d.value}`)}` }),
                //These are drawn items
                elements: [
                    Object.assign(Object.assign({}, baseMarkProps), { renderMode: renderValue, markType: "line", x1: bottomPoint[0], x2: topPoint[0], y1: bottomPoint[1], y2: topPoint[1], style: Object.assign({ strokeWidth: 2 }, calculatedSummaryStyle, summaryElementStylingFn("whisker", thisSummaryData)) }),
                    Object.assign(Object.assign({}, baseMarkProps), { renderMode: renderValue, markType: "path", d: topLineArcGenerator({ startAngle, endAngle }), style: Object.assign({ strokeWidth: 4 }, calculatedSummaryStyle, { fill: "none" }, summaryElementStylingFn("max", thisSummaryData)) }),
                    Object.assign(Object.assign({}, baseMarkProps), { renderMode: renderValue, markType: "path", d: midLineArcGenerator({ startAngle, endAngle }), style: Object.assign({ strokeWidth: 4 }, calculatedSummaryStyle, { fill: "none" }, summaryElementStylingFn("median", thisSummaryData)) }),
                    Object.assign(Object.assign({}, baseMarkProps), { renderMode: renderValue, markType: "path", d: bottomLineArcGenerator({ startAngle, endAngle }), style: Object.assign({ strokeWidth: 4 }, calculatedSummaryStyle, { fill: "none" }, summaryElementStylingFn("min", thisSummaryData)) }),
                    Object.assign(Object.assign({}, baseMarkProps), { renderMode: renderValue, markType: "path", d: bodyArcWholeGenerator({ startAngle, endAngle }), style: Object.assign({ strokeWidth: 4 }, calculatedSummaryStyle, summaryElementStylingFn("iqrarea", thisSummaryData)) }),
                    Object.assign(Object.assign({}, baseMarkProps), { renderMode: renderValue, markType: "path", d: bodyArcTopGenerator({ startAngle, endAngle }), style: Object.assign({}, calculatedSummaryStyle, { fill: "none", stroke: "none" }, summaryElementStylingFn("q3area", thisSummaryData)) }),
                    Object.assign(Object.assign({}, baseMarkProps), { renderMode: renderValue, markType: "path", d: bodyArcBottomGenerator({ startAngle, endAngle }), style: Object.assign({}, calculatedSummaryStyle, { fill: "none", stroke: "none" }, summaryElementStylingFn("q1area", thisSummaryData)) })
                ]
            });
        }
        else {
            const boxplotMarks = [
                Object.assign(Object.assign({}, baseMarkProps), { renderMode: renderValue, markType: "line", x1: extentlineX1, x2: extentlineX2, y1: extentlineY1, y2: extentlineY2, style: Object.assign({ strokeWidth: 2 }, calculatedSummaryStyle, summaryElementStylingFn("whisker", thisSummaryData)) }),
                Object.assign(Object.assign({}, baseMarkProps), { renderMode: renderValue, markType: "line", x1: topLineX1, x2: topLineX2, y1: topLineY1, y2: topLineY2, style: Object.assign({ strokeWidth: 2 }, calculatedSummaryStyle, summaryElementStylingFn("min", thisSummaryData)) }),
                Object.assign(Object.assign({}, baseMarkProps), { renderMode: renderValue, markType: "line", x1: bottomLineX1, x2: bottomLineX2, y1: bottomLineY1, y2: bottomLineY2, style: Object.assign({ strokeWidth: 2 }, calculatedSummaryStyle, summaryElementStylingFn("max", thisSummaryData)) }),
                Object.assign(Object.assign({}, baseMarkProps), { renderMode: renderValue, markType: "rect", x: rectWholeX, width: rectWholeWidth, y: rectWholeY, height: rectWholeHeight, style: Object.assign({ strokeWidth: 1 }, calculatedSummaryStyle, summaryElementStylingFn("iqrarea", thisSummaryData)) }),
                Object.assign(Object.assign({}, baseMarkProps), { renderMode: renderValue, markType: "rect", x: rectTopX, width: rectTopWidth, y: rectTopY, height: rectTopHeight, style: Object.assign({}, calculatedSummaryStyle, { fill: "none", stroke: "none" }, summaryElementStylingFn("q3area", thisSummaryData)) }),
                Object.assign(Object.assign({}, baseMarkProps), { renderMode: renderValue, markType: "rect", x: rectBottomX, width: rectBottomWidth, y: rectBottomY, height: rectBottomHeight, style: Object.assign({}, calculatedSummaryStyle, { fill: "none", stroke: "none" }, summaryElementStylingFn("q1area", thisSummaryData)) }),
                Object.assign(Object.assign({}, baseMarkProps), { renderMode: renderValue, markType: "line", x1: midLineX1, x2: midLineX2, y1: midLineY1, y2: midLineY2, style: Object.assign({ strokeWidth: 2 }, calculatedSummaryStyle, summaryElementStylingFn("median", thisSummaryData)) })
            ];
            const outlierMarks = [];
            if (outliers) {
                const outlierPoints = thisSummaryData.filter((d) => d.value > maxOutlier || d.value < minOutlier);
                outlierPoints.forEach((point) => {
                    outlierMarks.push(Object.assign(Object.assign({}, baseMarkProps), { renderMode: renderValue, markType: "circle", cx: projection === "horizontal" ? point.scaledValue : 0, cy: projection === "vertical" ? point.scaledVerticalValue : 0, style: Object.assign({ strokeWidth: "1px", stroke: "black", fill: "none", r: 2 }, calculatedSummaryStyle, summaryElementStylingFn("outlier", thisSummaryData)) }));
                });
            }
            renderedSummaryMarks.push({
                containerProps: Object.assign(Object.assign({}, eventListeners), { className: calculatedSummaryClass, transform: translate, key: `summaryPiece-${summaryI}`, role: "img", tabIndex: -1, "data-o": key, "aria-label": `${key} boxplot showing ${summaryXYCoords
                        .filter((d) => d.key === key)
                        .map((d) => `${d.label} ${d.value}`)}` }),
                //These are drawn items
                elements: [...boxplotMarks, ...outlierMarks]
            });
        }
    });
    return { marks: renderedSummaryMarks, xyPoints: summaryXYCoords };
}
function contourRenderFn({ data, type, renderMode, eventListenersGenerator, styleFn, classFn, adjustedSize, baseMarkProps }) {
    const keys = Object.keys(data);
    const renderedSummaryMarks = [];
    const summaryXYCoords = [];
    keys.forEach((key, ordsetI) => {
        const ordset = data[key];
        const renderValue = renderMode && renderMode(ordset, ordsetI);
        type.thresholds = type.thresholds || 8;
        type.bandwidth = type.bandwidth || 12;
        type.resolution = type.resolution || 1000;
        const projectedOrd = [
            { id: ordset, _xyfCoordinates: ordset.xyData.map(contourMap) }
        ];
        const oContours = contouring({
            summaryType: type,
            data: projectedOrd,
            finalXExtent: [0, adjustedSize[0]],
            finalYExtent: [0, adjustedSize[1]]
        });
        const contourMarks = [];
        oContours.forEach((d, i) => {
            d.coordinates.forEach((coords, ii) => {
                const eventListeners = eventListenersGenerator(d, i);
                contourMarks.push(Object.assign(Object.assign(Object.assign({}, baseMarkProps), eventListeners), { renderMode: renderValue, key: `${i}-${ii}`, style: styleFn(ordset.pieceData[0].data, ordsetI), className: classFn(ordset.pieceData[0].data, ordsetI), markType: "path", d: `M${d.coordinates[0].map((p) => p.join(",")).join("L")}Z` }));
            });
        });
        renderedSummaryMarks.push({
            containerProps: {
                key: `contour-container-${ordsetI}`,
                role: "img",
                tabIndex: -1,
                "data-o": key,
                "aria-label": `${key} boxplot showing ${summaryXYCoords
                    .filter((d) => d.key === key)
                    .map((d) => `${d.label} ${d.value}`)}`
            },
            //These are drawn items
            elements: contourMarks
        });
    });
    return { marks: renderedSummaryMarks, xyPoints: summaryXYCoords };
}
function axisGenerator(axisProps, i, axisScale) {
    return (React.createElement(Axis, { label: axisProps.label, key: axisProps.key || `orframe-summary-axis-${i}`, orient: axisProps.orient, size: axisProps.size, ticks: axisProps.ticks, tickSize: axisProps.tickSize, tickFormat: axisProps.tickFormat, tickValues: axisProps.tickValues, rotate: axisProps.rotate, scale: axisScale, className: axisProps.className }));
}
function bucketizedRenderingFn({ data, type, renderMode, eventListenersGenerator, styleFn, classFn, projection, adjustedSize, chartSize, baseMarkProps }) {
    const renderedSummaryMarks = [];
    const summaryXYCoords = [];
    const buckets = type.bins || 25;
    const relativeBuckets = type.relative ? {} : false;
    const summaryValueAccessor = type.binValue || ((d) => d.length);
    const summaryElementStylingFn = type.elementStyleFn || emptyObjectReturnFn;
    let axisCreator;
    if (type.axis) {
        type.axis.orient =
            projection === "horizontal" &&
                ["left", "right"].indexOf(type.axis.orient) === -1
                ? "left"
                : type.axis.orient;
        type.axis.orient =
            projection === "vertical" &&
                ["bottom", "top"].indexOf(type.axis.orient) === -1
                ? "bottom"
                : type.axis.orient;
        axisCreator = axisGenerator;
        if (projection === "radial") {
            console.error("Summary axes cannot be drawn for radial histograms");
            axisCreator = () => null;
        }
    }
    let bucketSize = chartSize / buckets;
    const keys = Object.keys(data);
    let binMax = 0;
    const baseHistogram = histogram();
    const binDomain = [0, chartSize];
    const binBuckets = [];
    for (let x = 0; x < buckets; x++) {
        binBuckets.push(binDomain[0] + (x / buckets) * chartSize);
    }
    const xyValue = projection === "vertical"
        ? (p) => p.piece.scaledVerticalValue
        : (p) => p.piece.scaledValue;
    const instantiatedHistogram = baseHistogram
        .domain(binDomain)
        .thresholds(binBuckets)
        .value(xyValue);
    const calculatedBins = keys.map((key, summaryI) => {
        const summary = data[key];
        const thisSummaryData = summary.xyData;
        const xySorting = projection === "vertical" ? verticalXYSorting : horizontalXYSorting;
        const summaryPositionNest = thisSummaryData.sort(xySorting);
        let keyBins;
        if (type.useBins === false) {
            const calculatedValues = summaryPositionNest.map((value) => xyValue(value));
            keyBins = summaryPositionNest
                .map((value, i) => {
                const bucketArray = [];
                bucketArray.x0 = calculatedValues[i] - 1;
                bucketArray.x1 = calculatedValues[i] + 1;
                bucketArray.push(value);
                return bucketArray;
            })
                .sort((a, b) => a.x0 - b.x0);
            bucketSize = chartSize / keyBins.length;
        }
        else {
            keyBins = instantiatedHistogram(summaryPositionNest);
        }
        keyBins = keyBins.map((d) => ({
            y: d.x0,
            y1: d.x1 - d.x0,
            pieces: d,
            value: summaryValueAccessor(d.map((p) => p.piece.data))
        }));
        if (type.type === "histogram" || type.type === "heatmap") {
            keyBins = keyBins.filter((d) => d.value !== 0);
        }
        const relativeMax = keyBins.length === 0 ? 0 : max(keyBins.map((d) => d.value));
        if (relativeBuckets) {
            relativeBuckets[key] = relativeMax;
        }
        binMax = Math.max(binMax, relativeMax);
        return { bins: keyBins, summary, summaryI, thisSummaryData };
    });
    const numHorizons = type.horizon
        ? binMax / type.horizon
        : type.numHorizons || 4;
    const horizon = type.horizon || binMax / numHorizons;
    calculatedBins.forEach(({ bins, summary, summaryI, thisSummaryData }) => {
        const eventListeners = eventListenersGenerator(summary, summaryI);
        const columnWidth = summary.width;
        const renderValue = renderMode && renderMode(summary, summaryI);
        let calculatedSummaryStyle = thisSummaryData[0]
            ? styleFn(thisSummaryData[0].piece.data, summaryI)
            : {};
        let calculatedSummaryClass = thisSummaryData[0]
            ? classFn(thisSummaryData[0].piece.data, summaryI)
            : "";
        let translate = [summary.middle, 0];
        if (projection === "horizontal") {
            translate = [bucketSize, summary.middle];
        }
        else if (projection === "radial") {
            translate = [adjustedSize[0] / 2, adjustedSize[1] / 2];
        }
        const actualMax = (relativeBuckets && relativeBuckets[summary.name]) || binMax;
        if (type.type === "heatmap" || type.type === "histogram") {
            const mappedBars = groupBarMark({
                bins,
                binMax,
                relativeBuckets,
                columnWidth,
                projection,
                adjustedSize,
                summaryI,
                summary,
                renderValue,
                summaryStyle: calculatedSummaryStyle,
                type,
                baseMarkProps
            });
            const tiles = mappedBars.marks;
            if (projection === "radial") {
                translate = [0, 0];
            }
            if (type.axis && type.type === "histogram") {
                renderedSummaryMarks.push({
                    type: "svg-only-mark",
                    Mark: createSummaryAxis({
                        summary,
                        summaryI,
                        axisSettings: type.axis,
                        axisCreator,
                        projection,
                        actualMax,
                        adjustedSize,
                        columnWidth
                    })
                });
            }
            mappedBars.points.forEach((d) => {
                d.x += translate[0];
                d.y += translate[1];
            });
            summaryXYCoords.push(...mappedBars.points);
            renderedSummaryMarks.push({
                containerProps: Object.assign(Object.assign({}, eventListeners), { transform: `translate(${translate})`, className: calculatedSummaryClass, key: `summaryPiece-${summaryI}`, role: "img", tabIndex: -1, "data-o": summary.name, "aria-label": `${summary.name} ${type.type}` }),
                //These are drawn items
                elements: tiles
            });
        }
        else if (type.type === "violin") {
            const { iqr } = type;
            const subsets = type.subsets || [false];
            const violinElements = [];
            bins[0].y = bins[0].y - bucketSize / 2;
            bins[bins.length - 1].y = bins[bins.length - 1].y + bucketSize / 2;
            subsets.forEach((subsettingFn, subsettingIndex) => {
                let actualBins = bins;
                if (subsettingFn) {
                    calculatedSummaryStyle = thisSummaryData[0]
                        ? styleFn(thisSummaryData[0].piece.data, summaryI, subsettingIndex)
                        : {};
                    calculatedSummaryClass = thisSummaryData[0]
                        ? classFn(thisSummaryData[0].piece.data, summaryI, subsettingIndex)
                        : "";
                    actualBins = bins.map((d) => {
                        const actualPieces = d.pieces
                            .filter((p, pi) => subsettingFn(p.piece, pi))
                            .map((d) => d);
                        const actualValue = summaryValueAccessor(actualPieces);
                        return Object.assign(Object.assign({}, d), { pieces: actualPieces, value: actualValue });
                    });
                }
                let violinArea = area().curve(type.curve || curveCatmullRom);
                let violinPoints = [];
                if (projection === "horizontal") {
                    actualBins.forEach((summaryPoint) => {
                        const xValue = summaryPoint.y - bucketSize / 2;
                        const yValue = ((summaryPoint.value / actualMax) * columnWidth) / 2;
                        violinPoints.push({
                            x: xValue,
                            y0: -yValue,
                            y1: yValue
                        });
                        summaryXYCoords.push({
                            key: summary.name,
                            x: xValue + translate[0],
                            y: yValue + translate[1],
                            pieces: summaryPoint.pieces.map((d) => d.piece),
                            value: summaryPoint.value
                        });
                    });
                    violinArea
                        .x((d) => d.x)
                        .y0((d) => d.y0)
                        .y1((d) => d.y1)
                        .defined((d, i) => d.y0 !== 0 ||
                        (violinPoints[i - 1] && violinPoints[i - 1].y0 !== 0) ||
                        (violinPoints[i + 1] && violinPoints[i + 1].y0 !== 0));
                }
                else if (projection === "vertical") {
                    actualBins.forEach((summaryPoint) => {
                        const yValue = summaryPoint.y + bucketSize / 2;
                        const xValue = ((summaryPoint.value / actualMax) * columnWidth) / 2;
                        violinPoints.push({
                            y: yValue,
                            x0: -xValue,
                            x1: xValue
                        });
                        summaryXYCoords.push({
                            key: summary.name,
                            x: xValue + translate[0],
                            y: yValue + translate[1],
                            pieces: summaryPoint.pieces.map((d) => d.piece),
                            value: summaryPoint.value
                        });
                    });
                    violinArea
                        .y((d) => d.y)
                        .x0((d) => d.x0)
                        .x1((d) => d.x1)
                        .defined((d, i) => d.x0 !== 0 ||
                        (violinPoints[i - 1] && violinPoints[i - 1].x0 !== 0) ||
                        (violinPoints[i + 1] && violinPoints[i + 1].x0 !== 0));
                }
                else if (projection === "radial") {
                    const angle = summary.pct - summary.pct_padding / 2;
                    const midAngle = summary.pct_middle;
                    violinPoints = actualBins;
                    violinArea = (inbins) => {
                        const forward = [];
                        const backward = [];
                        inbins.forEach((bin) => {
                            const outsidePoint = pointOnArcAtAngle$1([0, 0], midAngle + (angle * bin.value) / actualMax / 2, (bin.y + bin.y1 - bucketSize / 2) / 2);
                            const insidePoint = pointOnArcAtAngle$1([0, 0], midAngle - (angle * bin.value) / actualMax / 2, (bin.y + bin.y1 - bucketSize / 2) / 2);
                            //Ugh a terrible side effect has appeared
                            summaryXYCoords.push({
                                key: summary.name,
                                x: insidePoint[0] + translate[0],
                                y: insidePoint[1] + translate[1],
                                pieces: bin.pieces.map((d) => d.piece),
                                value: bin.value
                            });
                            summaryXYCoords.push({
                                key: summary.name,
                                x: outsidePoint[0] + translate[0],
                                y: outsidePoint[1] + translate[1],
                                pieces: bin.pieces.map((d) => d.piece),
                                value: bin.value
                            });
                            forward.push(outsidePoint);
                            backward.push(insidePoint);
                        });
                        return `M${forward.map((d) => d.join(",")).join("L")}L${backward
                            .reverse()
                            .map((d) => d.join(","))
                            .join("L")}Z`;
                    };
                }
                violinElements.push(Object.assign(Object.assign(Object.assign({}, baseMarkProps), eventListeners), { transform: `translate(${translate})`, key: `summaryPiece-${summaryI}-${subsettingIndex}`, renderMode: renderValue, markType: "path", className: calculatedSummaryClass, style: calculatedSummaryStyle, d: violinArea(violinPoints), role: "img", tabIndex: -1, "data-o": summary.name, "aria-label": `${summary.name} distribution` }));
                if (iqr) {
                    let iqrPieces = [];
                    actualBins.forEach((aBin) => {
                        iqrPieces.push(...aBin.pieces);
                    });
                    iqrPieces = iqrPieces
                        .map((d) => projection === "vertical"
                        ? d.piece.scaledVerticalValue
                        : d.piece.scaledValue)
                        .sort((a, b) => a - b);
                    const iqrValues = [
                        quantile(iqrPieces, 0.25),
                        quantile(iqrPieces, 0.5),
                        quantile(iqrPieces, 0.75)
                    ];
                    const iqrLine = projection === "vertical"
                        ? (first, last, translate) => `M${translate[0]},${first}L${translate[0]},${last}`
                        : (first, last, translate) => `M${first},${translate[1]}L${last},${translate[1]}`;
                    violinElements.push(Object.assign(Object.assign(Object.assign({}, baseMarkProps), eventListeners), { key: `summaryPiece-${summaryI}-${subsettingIndex}-iqr-line`, renderMode: renderValue, markType: "path", className: calculatedSummaryClass, style: Object.assign(Object.assign({ stroke: "black", strokeWidth: "2px" }, calculatedSummaryStyle), summaryElementStylingFn("iqr")), d: iqrLine(iqrValues[0], iqrValues[2], translate), role: "img", tabIndex: -1, "data-o": summary.name, "aria-label": `${summary.name} distribution` }));
                    violinElements.push(Object.assign(Object.assign(Object.assign({}, baseMarkProps), eventListeners), { key: `summaryPiece-${summaryI}-${subsettingIndex}-iqr-median`, markType: "circle", className: calculatedSummaryClass, style: Object.assign(Object.assign({ stroke: "black", fill: "black", strokeWidth: "1px", r: "3px" }, calculatedSummaryStyle), summaryElementStylingFn("median")), cx: projection === "vertical" ? translate[0] : iqrValues[1], cy: projection === "vertical" ? iqrValues[1] : translate[1], role: "img", tabIndex: -1, "data-o": summary.name, "aria-label": `${summary.name} distribution` }));
                }
            });
            renderedSummaryMarks.push({
                elements: violinElements
            });
        }
        else if (type.type === "ridgeline") {
            const zeroedStart = Object.assign({}, bins[0], { value: 0 });
            const zeroedEnd = Object.assign({}, bins[bins.length - 1], { value: 0 });
            //Ridgeline plots need to visually signify the zero baseline with their start and end position
            zeroedStart.y = zeroedStart.y - bucketSize / 2;
            zeroedEnd.y = zeroedEnd.y + bucketSize / 2;
            const joyBins = [zeroedStart, ...bins, zeroedEnd];
            let joyPoints = [];
            const interpolatorSetting = type.curve || type.interpolator;
            const actualInterpolator = typeof interpolatorSetting === "string"
                ? curveHash[interpolatorSetting]
                : interpolatorSetting;
            let joyArea = line()
                .curve(actualInterpolator || curveCatmullRom)
                .x((d) => d.x)
                .y((d) => d.y);
            const joyHeight = type.amplitude || 0;
            if (type.axis && type.type === "histogram") {
                renderedSummaryMarks.push({
                    type: "svg-only-mark",
                    Mark: createSummaryAxis({
                        summary,
                        summaryI,
                        axisSettings: Object.assign({ baseline: false }, type.axis),
                        axisCreator,
                        projection,
                        actualMax,
                        adjustedSize,
                        columnWidth
                    })
                });
            }
            if (projection === "horizontal") {
                joyBins.forEach((summaryPoint, i) => {
                    const xValue = summaryPoint.y - bucketSize / 2;
                    const yValue = type.flip
                        ? (summaryPoint.value / actualMax) * (columnWidth + joyHeight) -
                            columnWidth / 2
                        : (-summaryPoint.value / actualMax) * (columnWidth + joyHeight) +
                            columnWidth / 2;
                    joyPoints.push({
                        y: yValue,
                        x: xValue
                    });
                    //Don't make an interaction point for the first or last
                    if (i !== 0 && i !== joyBins.length - 1) {
                        summaryXYCoords.push({
                            key: summary.name,
                            x: xValue + translate[0],
                            y: yValue + translate[1],
                            pieces: summaryPoint.pieces.map((d) => d.piece),
                            value: summaryPoint.value
                        });
                    }
                });
            }
            else if (projection === "vertical") {
                joyBins.forEach((summaryPoint) => {
                    const yValue = summaryPoint.y + bucketSize / 2;
                    const xValue = type.flip === true
                        ? (summaryPoint.value / actualMax) * (columnWidth + joyHeight) -
                            columnWidth / 2
                        : (-summaryPoint.value / actualMax) * (columnWidth + joyHeight) +
                            columnWidth / 2;
                    joyPoints.push({
                        y: yValue,
                        x: xValue
                    });
                    summaryXYCoords.push({
                        key: summary.name,
                        x: xValue + translate[0],
                        y: yValue + translate[1],
                        pieces: summaryPoint.pieces.map((d) => d.piece),
                        value: summaryPoint.value
                    });
                });
            }
            else if (projection === "radial") {
                const angle = summary.pct - summary.pct_padding / 2;
                const midAngle = summary.pct_start + summary.pct_padding / 2;
                translate = [0, 0];
                joyPoints = joyBins;
                joyArea = (inbins) => {
                    const forward = [];
                    inbins.forEach((bin) => {
                        const outsidePoint = pointOnArcAtAngle$1([adjustedSize[0] / 2, adjustedSize[1] / 2], midAngle + (angle * bin.value) / actualMax, (bin.y + bin.y1 - bucketSize / 2) / 2);
                        //Ugh a terrible side effect has appeared
                        summaryXYCoords.push({
                            key: summary.name,
                            x: outsidePoint[0] + translate[0],
                            y: outsidePoint[1] + translate[1],
                            pieces: bin.pieces.map((d) => d.piece),
                            value: bin.value
                        });
                        forward.push(outsidePoint);
                    });
                    return `M${forward.map((d) => d.join(",")).join("L")}Z`;
                };
            }
            if (type.axis) {
                renderedSummaryMarks.push({
                    type: "svg-only-mark",
                    Mark: createSummaryAxis({
                        summary,
                        summaryI,
                        axisSettings: type.axis,
                        axisCreator,
                        projection,
                        actualMax,
                        adjustedSize,
                        columnWidth
                    })
                });
            }
            renderedSummaryMarks.push({
                elements: [
                    Object.assign(Object.assign(Object.assign({}, baseMarkProps), eventListeners), { transform: `translate(${translate})`, key: `summaryPiece-${summaryI}`, renderMode: renderValue, markType: "path", className: calculatedSummaryClass, style: calculatedSummaryStyle, d: joyArea(joyPoints), role: "img", tabIndex: -1, "data-o": summary.name, "aria-label": `${summary.name} distribution` })
                ]
            });
        }
        else if (type.type === "horizon") {
            const zeroedStart = Object.assign({}, bins[0], { value: 0 });
            const zeroedEnd = Object.assign({}, bins[bins.length - 1], { value: 0 });
            zeroedStart.y = zeroedStart.y - bucketSize / 2;
            zeroedEnd.y = zeroedEnd.y + bucketSize / 2;
            const horizonBins = [zeroedStart, ...bins, zeroedEnd];
            let multiBins = [];
            let remainingPieces = true;
            let currentHorizon = 0;
            while (remainingPieces === true) {
                const currentStrip = horizonBins.map((d) => (Object.assign(Object.assign({}, d), { value: Math.max(0, Math.min(d.value - currentHorizon, horizon)) })));
                multiBins.push(currentStrip.map((d) => (Object.assign(Object.assign({}, d), { value: d.value * numHorizons }))));
                currentHorizon += horizon;
                if (max(currentStrip.map((d) => d.value)) < horizon) {
                    remainingPieces = false;
                }
            }
            multiBins = multiBins.filter((d) => sum(d, (p) => p.value) > 0);
            horizonBins.forEach((summaryPoint, i) => {
                if (i !== 0 && i !== horizonBins.length - 1) {
                    if (projection === "horizontal") {
                        const xValue = summaryPoint.y - bucketSize / 2;
                        const yValue = (-summaryPoint.value / actualMax) * columnWidth + columnWidth / 2;
                        summaryXYCoords.push({
                            key: summary.name,
                            x: xValue + translate[0],
                            y: yValue + translate[1],
                            pieces: summaryPoint.pieces.map((d) => d.piece),
                            value: summaryPoint.value
                        });
                    }
                    else if (projection === "vertical") {
                        const yValue = summaryPoint.y + bucketSize / 2;
                        const xValue = type.flip === true
                            ? (summaryPoint.value / actualMax) * columnWidth -
                                columnWidth / 2
                            : (-summaryPoint.value / actualMax) * columnWidth +
                                columnWidth / 2;
                        summaryXYCoords.push({
                            key: summary.name,
                            x: xValue + translate[0],
                            y: yValue + translate[1],
                            pieces: summaryPoint.pieces.map((d) => d.piece),
                            value: summaryPoint.value
                        });
                    }
                }
            });
            const multiBinMarks = multiBins.map((multiBin, multiBinI) => {
                let horizonPoints = [];
                const interpolatorSetting = type.curve || type.interpolator;
                const actualInterpolator = typeof interpolatorSetting === "string"
                    ? curveHash[interpolatorSetting]
                    : interpolatorSetting;
                let horizonArea = line()
                    .curve(actualInterpolator || curveLinear)
                    .x((d) => d.x)
                    .y((d) => d.y);
                if (projection === "horizontal") {
                    multiBin.forEach((summaryPoint) => {
                        const xValue = summaryPoint.y - bucketSize / 2;
                        const yValue = (-summaryPoint.value / actualMax) * columnWidth + columnWidth / 2;
                        horizonPoints.push({
                            y: yValue,
                            x: xValue
                        });
                        //Don't make an interaction point for the first or last
                    });
                }
                else if (projection === "vertical") {
                    multiBin.forEach((summaryPoint) => {
                        const yValue = summaryPoint.y + bucketSize / 2;
                        const xValue = type.flip === true
                            ? (summaryPoint.value / actualMax) * columnWidth -
                                columnWidth / 2
                            : (-summaryPoint.value / actualMax) * columnWidth +
                                columnWidth / 2;
                        horizonPoints.push({
                            y: yValue,
                            x: xValue
                        });
                    });
                }
                else if (projection === "radial") {
                    const angle = summary.pct - summary.pct_padding / 2;
                    const midAngle = summary.pct_start + summary.pct_padding / 2;
                    translate = [0, 0];
                    horizonPoints = multiBin;
                    horizonArea = (inbins) => {
                        const forward = [];
                        inbins.forEach((bin) => {
                            const outsidePoint = pointOnArcAtAngle$1([adjustedSize[0] / 2, adjustedSize[1] / 2], midAngle + (angle * bin.value) / actualMax, (bin.y + bin.y1 - bucketSize / 2) / 2);
                            forward.push(outsidePoint);
                        });
                        return `M${forward.map((d) => d.join(",")).join("L")}Z`;
                    };
                }
                return Object.assign(Object.assign(Object.assign({}, baseMarkProps), eventListeners), { transform: `translate(${translate})`, key: `summaryPiece-${summaryI}-${multiBinI}`, renderMode: renderValue, markType: "path", className: calculatedSummaryClass, style: Object.assign(Object.assign({}, calculatedSummaryStyle), summaryElementStylingFn(multiBin, multiBinI)), d: horizonArea(horizonPoints), role: "img", tabIndex: -1, "data-o": summary.name, "aria-label": `${summary.name} distribution` });
            });
            if (type.axis) {
                renderedSummaryMarks.push({
                    type: "svg-only-mark",
                    Mark: createSummaryAxis({
                        summary,
                        summaryI,
                        axisSettings: type.axis,
                        axisCreator,
                        projection,
                        actualMax,
                        adjustedSize,
                        columnWidth
                    })
                });
            }
            renderedSummaryMarks.push({ elements: multiBinMarks });
        }
    });
    return {
        marks: renderedSummaryMarks,
        xyPoints: summaryXYCoords,
        thresholds: binBuckets
    };
}
const drawSummaries = ({ data, type, renderMode, eventListenersGenerator, styleFn, classFn, projection, adjustedSize, margin, baseMarkProps }) => {
    if (!type || !type.type)
        return;
    type = typeof type === "string" ? { type } : type;
    const chartSize = projection === "vertical" ? adjustedSize[1] : adjustedSize[0];
    return orFrameSummaryRenderer({
        data,
        type,
        renderMode,
        eventListenersGenerator,
        styleFn,
        classFn,
        projection,
        adjustedSize,
        chartSize,
        margin,
        baseMarkProps
    });
};
function summaryInstructionsToMarks(data) {
    const renderedSummaries = [];
    for (const container of data) {
        const renderedElements = [];
        const { elements, containerProps } = container;
        if (container.Mark) {
            renderedSummaries.push(container.Mark);
        }
        else {
            for (const element of elements) {
                renderedElements.push(React.createElement(SemioticMark, Object.assign({}, element)));
            }
            if (containerProps) {
                renderedSummaries.push(React.createElement("g", Object.assign({}, containerProps), renderedElements));
            }
            else {
                renderedSummaries.push(...renderedElements);
            }
        }
    }
    return renderedSummaries;
}
const renderLaidOutSummaries = ({ data, canvasRender, canvasDrawing }) => {
    var _a;
    if (canvasRender()) {
        for (const container of data) {
            if (container.type !== "svg-only-mark") {
                const { transform: containerTransform = "translate(0,0)" } = (_a = container === null || container === void 0 ? void 0 : container.containerProps) !== null && _a !== void 0 ? _a : {
                    transform: "translate(0,0)"
                };
                const [containerX, containerY] = containerTransform
                    .replace("translate(", "")
                    .replace(")", "")
                    .split(",");
                for (const element of container.elements) {
                    const { transform: elementTransform = "translate(0,0)" } = element;
                    const [elementX, elementY] = elementTransform
                        .replace("translate(", "")
                        .replace(")", "")
                        .split(",");
                    const canvasNode = {
                        baseClass: "frame-piece",
                        tx: parseInt(elementX) + parseInt(containerX),
                        ty: parseInt(elementY) + parseInt(containerY),
                        d: {},
                        i: 0,
                        markProps: element,
                        styleFn: () => element.style,
                        renderFn: element.renderMode,
                        classFn: () => ""
                    };
                    canvasDrawing.push(canvasNode);
                }
            }
        }
    }
    return summaryInstructionsToMarks(data.filter((d) => !canvasRender() || d.type === "svg-only-mark"));
};

// components
const marginalPointMapper = (orient, width, data) => {
    const xMod = orient === "left" || orient === "right" ? width / 2 : 0;
    const yMod = orient === "bottom" || orient === "top" ? width / 2 : 0;
    return data.map((p) => [p.xy.x + xMod, p.xy.y + yMod]);
};
function formatValue(value, props) {
    if (props.tickFormat) {
        return props.tickFormat(value);
    }
    if (value.toString) {
        return value.toString();
    }
    return value;
}
const boundingBoxMax = (axisNode, orient) => {
    const axisRef = axisNode.current;
    if (!axisRef)
        return 30;
    const positionType = orient === "left" || orient === "right" ? "width" : "height";
    const axisLabelMax = Math.max(...[...axisRef.querySelectorAll(".axis-label")]
        .map((l) => (l.getBBox && l.getBBox()) || { height: 30, width: 30 })
        .map((d) => d[positionType])) + 25;
    return axisLabelMax;
};
function Axis(props) {
    const { rotate, label, dynamicLabelPosition, orient = "left", marginalSummaryType, tickFormat = marginalSummaryType ? () => "" : (d) => d, size, width = (size && size[0]) || 0, height = (size && size[1]) || 0, className, padding, tickValues, scale, ticks, footer, tickSize, tickLineGenerator, baseline = true, margin = { top: 0, bottom: 0, left: 0, right: 0 }, center = false, annotationFunction, glyphFunction, xyPoints } = props;
    const [hoverPosition, changeHoverPosition] = useState(0);
    const [calculatedLabelPosition, changeCalculatedLabelPosition] = useState(undefined);
    const axisNode = useRef(null);
    useEffect(() => {
        if (!(label === null || label === void 0 ? void 0 : label.position) && dynamicLabelPosition) {
            const newBBMax = boundingBoxMax(axisNode, orient);
            if (newBBMax !== calculatedLabelPosition) {
                changeCalculatedLabelPosition(newBBMax);
            }
        }
    }, [label, dynamicLabelPosition]);
    let { axisParts, position = [0, 0] } = props;
    let axisTickLines;
    if (!axisParts) {
        axisParts = axisPieces({
            padding: padding,
            tickValues,
            scale,
            ticks,
            orient,
            size: [width, height],
            footer,
            tickSize
        });
        axisTickLines = (React.createElement("g", { className: `axis ${className}` }, axisLines({
            axisParts,
            orient,
            tickLineGenerator,
            className,
            scale
        })));
    }
    if (axisParts.length === 0) {
        return null;
    }
    let hoverWidth = 50;
    let hoverHeight = height;
    let hoverX = -50;
    let hoverY = 0;
    let baselineX = 0;
    let baselineY = 0;
    let baselineX2 = 0;
    let baselineY2 = height;
    // hoverFunction is set is switch(orient) below
    let hoverFunction;
    let circleX = 25;
    let textX = -25;
    let textY = 18;
    let lineWidth = width + 25;
    let lineHeight = 0;
    let circleY = hoverPosition;
    let annotationOffset = 0;
    let annotationType = "y";
    switch (orient) {
        case "right":
            position = [position[0], position[1]];
            hoverX = width;
            baselineX2 = baselineX = width;
            annotationOffset = margin.top;
            lineWidth = -width - 25;
            textX = 5;
            hoverFunction = (e) => {
                changeHoverPosition(e.nativeEvent.offsetY - annotationOffset);
            };
            if (center === true) {
                baselineX2 = baselineX = width / 2;
            }
            break;
        case "top":
            position = [position[0], 0];
            hoverWidth = width;
            hoverHeight = 50;
            hoverY = -50;
            hoverX = 0;
            annotationOffset = margin.left;
            annotationType = "x";
            baselineX2 = width;
            baselineY2 = 0;
            if (center === true) {
                baselineY2 = baselineY = height / 2;
            }
            hoverFunction = (e) => {
                changeHoverPosition(e.nativeEvent.offsetX - annotationOffset);
            };
            circleX = hoverPosition;
            circleY = 25;
            textX = 0;
            textY = -10;
            lineWidth = 0;
            lineHeight = height + 25;
            break;
        case "bottom":
            position = [position[0], 0];
            hoverWidth = width;
            hoverHeight = 50;
            baselineY = baselineY2 = hoverY = height;
            baselineX = hoverX = 0;
            baselineX2 = width;
            annotationOffset = margin.left;
            hoverFunction = (e) => {
                changeHoverPosition(e.nativeEvent.offsetX - annotationOffset);
            };
            circleX = hoverPosition;
            circleY = 25;
            textX = 0;
            textY = 15;
            lineWidth = 0;
            lineHeight = -height - 25;
            annotationType = "x";
            if (center === true) {
                baselineY2 = baselineY = height / 2;
            }
            break;
        default:
            position = [position[0], position[1]];
            annotationOffset = margin.top;
            if (center === true) {
                baselineX2 = baselineX = width / 2;
            }
            hoverFunction = (e) => {
                changeHoverPosition(e.nativeEvent.offsetY - annotationOffset);
            };
    }
    let annotationBrush;
    if (annotationFunction) {
        const formattedValue = formatValue(scale.invert(hoverPosition), props);
        const hoverGlyph = glyphFunction ? (glyphFunction({
            lineHeight,
            lineWidth,
            value: scale.invert(hoverPosition)
        })) : (React.createElement("g", null,
            React.isValidElement(formattedValue) ? (React.createElement("g", { transform: `translate(${textX},${textY})` }, formattedValue)) : (React.createElement("text", { x: textX, y: textY }, formattedValue)),
            React.createElement("circle", { r: 5 }),
            React.createElement("line", { x1: lineWidth, y1: lineHeight, style: { stroke: "black" } })));
        const annotationSymbol = hoverPosition ? (React.createElement("g", { style: { pointerEvents: "none" }, transform: `translate(${circleX},${circleY})` }, hoverGlyph)) : null;
        annotationBrush = (React.createElement("g", { className: "annotation-brush", transform: `translate(${hoverX},${hoverY})` },
            React.createElement("rect", { style: { fillOpacity: 0 }, height: hoverHeight, width: hoverWidth, onMouseMove: hoverFunction, onClick: (e) => annotationFunction({
                    className: "dynamic-axis-annotation",
                    type: annotationType,
                    value: scale.invert(hoverPosition),
                    e
                }), onMouseOut: () => {
                    changeHoverPosition(undefined);
                } }),
            annotationSymbol));
    }
    let summaryGraphic;
    if (marginalSummaryType && xyPoints) {
        const summaryWidth = Math.max(margin[orient] - 6, 5);
        const decoratedSummaryType = typeof marginalSummaryType === "string"
            ? { type: marginalSummaryType }
            : marginalSummaryType;
        if (decoratedSummaryType.flip === undefined &&
            (orient === "bottom" || orient === "right")) {
            decoratedSummaryType.flip = true;
        }
        const summaryStyle = decoratedSummaryType.summaryStyle
            ? () => decoratedSummaryType.summaryStyle
            : () => ({
                fill: "black",
                fillOpacity: 0.5,
                stroke: "black",
                strokeDasharray: "0"
            });
        const summaryRenderMode = decoratedSummaryType.renderMode
            ? () => decoratedSummaryType.renderMode
            : () => undefined;
        const summaryClass = decoratedSummaryType.summaryClass
            ? () => decoratedSummaryType.summaryClass
            : () => "";
        const dataFilter = decoratedSummaryType.filter || (() => true);
        const forSummaryData = xyPoints
            .filter((p) => p.x !== undefined && p.y !== undefined && dataFilter(p.data))
            .map((d) => (Object.assign(Object.assign({}, d), { xy: {
                x: orient === "top" || orient === "bottom" ? scale(d.x) : 0,
                y: orient === "left" || orient === "right" ? scale(d.y) : 0
            }, piece: {
                scaledVerticalValue: scale(d.y),
                scaledValue: scale(d.x)
            }, value: orient === "top" || orient === "bottom" ? scale(d.y) : scale(d.x), scaledValue: scale(d.x), scaledVerticalValue: scale(d.y) })));
        const calculatedSummary = drawSummaries({
            data: {
                column: {
                    middle: summaryWidth / 2,
                    pieceData: forSummaryData,
                    width: summaryWidth,
                    xyData: forSummaryData
                }
            },
            type: decoratedSummaryType,
            renderMode: summaryRenderMode,
            eventListenersGenerator: decoratedSummaryType.eventListenersGenerator || (() => ({})),
            styleFn: summaryStyle,
            classFn: summaryClass,
            projection: orient === "top" || orient === "bottom" ? "horizontal" : "vertical",
            adjustedSize: size,
            margin: { top: 0, bottom: 0, left: 0, right: 0 },
            baseMarkProps: {}
        });
        const renderedSummary = summaryInstructionsToMarks(calculatedSummary.marks);
        let points;
        if (decoratedSummaryType.showPoints === true) {
            const mappedPoints = marginalPointMapper(orient, summaryWidth, forSummaryData);
            points = mappedPoints.map((d, i) => (React.createElement("circle", { key: `axis-summary-point-${i}`, cx: d[0], cy: d[1], r: decoratedSummaryType.r || 3, style: decoratedSummaryType.pointStyle || {
                    fill: "black",
                    fillOpacity: 0.1
                } })));
        }
        const translation = {
            left: [-margin.left + 2, 0],
            right: [size[0] + 2, 0],
            top: [0, -margin.top + 2],
            bottom: [0, size[1] + 2]
        };
        summaryGraphic = (React.createElement(SummaryGraphic, { translation: translation, orient: orient, decoratedSummaryType: decoratedSummaryType, summaryWidth: summaryWidth, renderedSummary: renderedSummary, points: points }));
    }
    let axisTitle;
    const axisTickLabels = axisLabels({
        tickFormat,
        axisParts,
        orient,
        rotate,
        center
    });
    if (label) {
        const labelName = label.name || label;
        const labelPosition = label.position || {};
        const locationMod = labelPosition.location || "outside";
        let anchorMod = labelPosition.anchor || "middle";
        const distance = label.locationDistance || calculatedLabelPosition;
        const rotateHash = {
            left: -90,
            right: 90,
            top: 0,
            bottom: 0
        };
        const rotation = labelPosition.rotation || rotateHash[orient];
        const positionHash = {
            left: {
                start: [0, size[1]],
                middle: [0, size[1] / 2],
                end: [0, 0],
                inside: [distance || 15, 0],
                outside: [-(distance || 45), 0]
            },
            right: {
                start: [size[0] + 0, size[1]],
                middle: [size[0] + 0, size[1] / 2],
                end: [size[0] + 0, 0],
                inside: [-(distance || 15), 0],
                outside: [distance || 45, 0]
            },
            top: {
                start: [0, 0],
                middle: [0 + size[0] / 2, 0],
                end: [0 + size[0], 0],
                inside: [0, distance || 15],
                outside: [0, -(distance || 40)]
            },
            bottom: {
                start: [0, size[1]],
                middle: [0 + size[0] / 2, size[1]],
                end: [0 + size[0], size[1]],
                inside: [0, -(distance || 5)],
                outside: [0, distance || 50]
            }
        };
        const translation = positionHash[orient][anchorMod];
        const location = positionHash[orient][locationMod];
        translation[0] = translation[0] + location[0];
        translation[1] = translation[1] + location[1];
        if (anchorMod === "start" && orient === "right") {
            anchorMod = "end";
        }
        else if (anchorMod === "end" && orient === "right") {
            anchorMod = "start";
        }
        axisTitle = (React.createElement(AxisTitle, { className: className, translation: translation, position: position, rotation: rotation, labelName: labelName, anchorMod: anchorMod }));
    }
    const axisAriaLabel = `${orient} axis ${(axisParts &&
        axisParts.length > 0 &&
        `from ${tickFormat(axisParts[0].value, 0)} to ${tickFormat(axisParts[axisParts.length - 1].value, axisParts.length - 1)}`) ||
        "without ticks"}`;
    return (React.createElement("g", { className: className, "aria-label": axisAriaLabel, ref: axisNode },
        annotationBrush,
        axisTickLabels,
        axisTickLines,
        baseline === true ? (React.createElement("line", { key: "baseline", className: `axis-baseline ${className}`, stroke: "black", strokeLinecap: "square", x1: baselineX, x2: baselineX2, y1: baselineY, y2: baselineY2 })) : null,
        axisTitle,
        summaryGraphic));
}

const extent = (inputArray) => inputArray.reduce((p, c) => {
    return [Math.min(c, p[0]), Math.max(c, p[1])];
}, [Infinity, -Infinity]);
const findActualTitle = (rawTitle) => {
    let title = null;
    let orient = "top";
    if (typeof rawTitle === "string") {
        title = rawTitle;
    }
    else if ("title" in rawTitle) {
        title = rawTitle.title;
        orient = rawTitle.orient || "top";
    }
    else {
        title = rawTitle;
    }
    return { orient, title };
};
function roundToTenth(number) {
    return Math.round(number * 10) / 10;
}
const circlePath = (cx, cy, r) => `${[
    "M",
    roundToTenth(cx - r),
    roundToTenth(cy),
    "a",
    r,
    r,
    0,
    1,
    0,
    r * 2,
    0,
    "a",
    r,
    r,
    0,
    1,
    0,
    -(r * 2),
    0
].join(" ")}Z`;
const drawMarginPath = ({ margin, size, inset = 0 }) => {
    const iSize = [size[0] - inset, size[1] - inset];
    return `M0,0 h${size[0]} v${size[1]} h-${size[0]}Z M${margin.left - inset},${margin.top - inset} v${size[1] + inset * 2 - margin.top - margin.bottom} h${iSize[0] + inset * 3 - margin.left - margin.right} v-${iSize[1] + inset * 3 - margin.top - margin.bottom}Z`;
};
const calculateMargin = ({ margin, axes, title: rawTitle, oLabel, projection, size }) => {
    if (margin !== undefined) {
        if (typeof margin === "function") {
            margin = margin({ size });
        }
        if (typeof margin !== "object") {
            return { top: margin, bottom: margin, left: margin, right: margin };
        }
        else if (typeof margin === "object") {
            return Object.assign({ top: 0, bottom: 0, left: 0, right: 0 }, margin);
        }
    }
    const finalMargin = { top: 0, bottom: 0, left: 0, right: 0 };
    let orient = "left";
    if (axes && projection !== "radial") {
        axes.forEach((axisObj) => {
            const axisObjAdditionMargin = axisObj.label ? 60 : 50;
            orient = axisObj.orient;
            finalMargin[orient] = axisObjAdditionMargin;
        });
    }
    const { title, orient: titleOrient } = findActualTitle(rawTitle);
    if (title && !(typeof title === "string" && title.length === 0)) {
        finalMargin[titleOrient] += 40;
    }
    if (oLabel && projection !== "radial") {
        if (orient === "bottom" || orient === "top") {
            finalMargin.left += 50;
        }
        else {
            finalMargin.bottom += 50;
        }
    }
    return finalMargin;
};
function objectifyType(type) {
    if (type instanceof Function || typeof type === "string") {
        return { type: type };
    }
    else if (type === undefined) {
        return {};
    }
    return type;
}
function keyAndObjectifyBarData({ data, renderKey = (d, i) => i, oAccessor, rAccessor: baseRAccessor, originalRAccessor, originalOAccessor, multiAxis = false }) {
    let rAccessor;
    let multiExtents;
    if (multiAxis && baseRAccessor.length > 1) {
        let minNegative = Infinity;
        multiExtents = baseRAccessor.map((accessor) => {
            const basicExtent = extent(data.map(accessor));
            minNegative = Math.min(basicExtent[0] / basicExtent[1], minNegative);
            return basicExtent;
        });
        const rScales = multiExtents.map((ext) => {
            let range = [0, 1];
            let adjustedExtent = ext;
            if (ext[0] < 0 && ext[1] > 0) {
                range[0] = minNegative;
                adjustedExtent[0] = adjustedExtent[1] * minNegative;
            }
            else if (ext[0] < 0 && ext[1] < 0) {
                adjustedExtent[1] = 0;
                range = [-1, 0];
            }
            else {
                adjustedExtent[0] = 0;
            }
            return scaleLinear().domain(ext).range(range);
        });
        rAccessor = rScales.map((scale, i) => (d) => {
            return scale(baseRAccessor[i](d));
        });
    }
    else {
        rAccessor = baseRAccessor;
    }
    const decoratedData = [];
    oAccessor.forEach((actualOAccessor, oIndex) => {
        rAccessor.forEach((actualRAccessor, rIndex) => {
            (data || []).forEach((d) => {
                const baseAppliedKey = renderKey(d, decoratedData.length);
                const appliedKey = (baseAppliedKey !== undefined &&
                    baseAppliedKey.toString &&
                    baseAppliedKey.toString()) ||
                    baseAppliedKey;
                const originalR = originalRAccessor[rIndex];
                const originalO = originalOAccessor[oIndex];
                const rName = typeof originalR === "string" ? originalR : `function-${rIndex}`;
                const oName = typeof originalO === "string" ? originalO : `function-${oIndex}`;
                if (typeof d !== "object") {
                    const expandedData = { value: d, renderKey: appliedKey };
                    const value = actualRAccessor(expandedData);
                    decoratedData.push({
                        data: expandedData,
                        value,
                        rIndex,
                        rName,
                        oIndex,
                        oName,
                        column: appliedKey,
                        renderKey: appliedKey
                    });
                }
                else {
                    const value = actualRAccessor(d);
                    const column = actualOAccessor(d);
                    decoratedData.push({
                        renderKey: appliedKey,
                        data: d,
                        rIndex,
                        rName,
                        oIndex,
                        oName,
                        value,
                        column: column && column.toString ? column.toString() : column
                    });
                }
            });
        });
    });
    return { allData: decoratedData, multiExtents };
}
function adjustedPositionSize({ size = [500, 500], position = [0, 0], margin, projection }) {
    const heightAdjust = margin.top + margin.bottom;
    const widthAdjust = margin.left + margin.right;
    const adjustedPosition = [position[0], position[1]];
    let adjustedSize = [size[0] - widthAdjust, size[1] - heightAdjust];
    if (projection === "radial") {
        const minSize = Math.min(adjustedSize[0], adjustedSize[1]);
        adjustedSize = [minSize, minSize];
    }
    return { adjustedPosition, adjustedSize };
}
function generateFrameTitle({ title: rawTitle = { title: "", orient: "top" }, size }) {
    let finalTitle = null;
    const { title, orient } = findActualTitle(rawTitle);
    let x = 0, y = 0, transform;
    switch (orient) {
        case "top":
            x = size[0] / 2;
            y = 25;
            break;
        case "bottom":
            x = size[0] / 2;
            y = size[1] - 25;
            break;
        case "left":
            x = 25;
            y = size[1] / 2;
            transform = "rotate(-90)";
            break;
        case "right":
            x = size[0] - 25;
            y = size[1] / 2;
            transform = "rotate(90)";
            break;
    }
    const gTransform = `translate(${x},${y})`;
    if (typeof title === "string" && title.length > 0) {
        finalTitle = (React.createElement("g", { transform: gTransform },
            React.createElement("text", { className: "frame-title", transform: transform, style: { textAnchor: "middle", pointerEvents: "none" } }, title)));
    }
    else if (title) {
        //assume if defined then its an svg mark of some sort
        finalTitle = React.createElement("g", { transform: gTransform }, title);
    }
    return finalTitle;
}
function orFrameConnectionRenderer({ type, data, renderMode, eventListenersGenerator, styleFn, classFn, projection, canvasRender, canvasDrawing, baseMarkProps, pieceType }) {
    if (!type.type) {
        return null;
    }
    const renderedConnectorMarks = [];
    const radarMap = new Map();
    const { keyedData, oExtent } = data;
    if (typeof type.type === "function") {
        const connectionRule = type.type;
        const keys = oExtent;
        oExtent.forEach((key, pieceArrayI) => {
            const pieceArray = keyedData[key];
            const nextColumn = keyedData[keys[pieceArrayI + 1]];
            if (nextColumn) {
                const matchArray = nextColumn.map((d, i) => connectionRule(Object.assign(Object.assign({}, d.piece), d.piece.data), i));
                pieceArray.forEach((piece, pieceI) => {
                    const thisConnectionPiece = connectionRule(Object.assign(Object.assign({}, piece.piece), piece.piece.data), pieceI);
                    const targetMatch = connectionRule(Object.assign(Object.assign({}, piece.piece), piece.piece.data), pieceI);
                    const matchingPieceIndex = targetMatch !== undefined &&
                        targetMatch !== false &&
                        matchArray.indexOf(targetMatch);
                    if (thisConnectionPiece !== undefined &&
                        thisConnectionPiece !== null &&
                        matchingPieceIndex !== false &&
                        matchingPieceIndex !== -1) {
                        const matchingPiece = nextColumn[matchingPieceIndex];
                        let markD;
                        if (projection === "radial" && pieceType.type === "point") {
                            if (!radarMap.get(piece)) {
                                radarMap.set(piece, [piece]);
                            }
                            const thisRadar = radarMap.get(piece);
                            if (thisRadar) {
                                thisRadar.push(matchingPiece);
                                radarMap.set(matchingPiece, thisRadar);
                                radarMap.delete(piece);
                            }
                        }
                        else {
                            const { xy } = piece;
                            const { xy: mxy } = matchingPiece;
                            const { x, y, height = 1, width = 1 } = xy;
                            const { x: mx, y: my, height: mheight = 1, width: mwidth = 1 } = mxy;
                            if (projection === "vertical") {
                                markD = drawAreaConnector({
                                    x1: x + width,
                                    x2: mx,
                                    y1: y,
                                    y2: my,
                                    sizeX1: 0,
                                    sizeX2: 0,
                                    sizeY1: height,
                                    sizeY2: mheight
                                });
                            }
                            else if (projection === "horizontal") {
                                const { value } = piece.piece;
                                const { value: mvalue } = matchingPiece.piece;
                                const x1 = value >= 0 ? x - width : piece.piece.bottom - width;
                                const x2 = mvalue >= 0
                                    ? mx - mwidth
                                    : matchingPiece.piece.bottom - mwidth;
                                markD = drawAreaConnector({
                                    x1,
                                    x2,
                                    y1: y + height,
                                    y2: my,
                                    sizeX1: width,
                                    sizeX2: mwidth,
                                    sizeY1: 0,
                                    sizeY2: 0
                                });
                            }
                            else if (projection === "radial") {
                                markD = drawAreaConnector({
                                    x1: x,
                                    x2: mx,
                                    y1: y + height,
                                    y2: my,
                                    sizeX1: width,
                                    sizeX2: mwidth,
                                    sizeY1: 0,
                                    sizeY2: 0
                                });
                            }
                            const renderValue = renderMode && renderMode(piece.piece, pieceI);
                            const source = Object.assign(Object.assign({}, piece.piece.data), piece.piece.data);
                            const target = Object.assign(Object.assign({}, matchingPiece.piece), matchingPiece.piece.data);
                            const calculatedStyle = styleFn({
                                source,
                                target
                            });
                            const eventListeners = eventListenersGenerator({ source, target }, pieceI);
                            if (canvasRender && canvasRender(piece.piece) === true) {
                                const canvasConnector = {
                                    baseClass: "xyframe-line",
                                    tx: 0,
                                    ty: 0,
                                    d: {
                                        source,
                                        target
                                    },
                                    markProps: { d: markD, markType: "path" },
                                    styleFn: styleFn,
                                    renderFn: renderMode,
                                    classFn
                                };
                                canvasDrawing.push(canvasConnector);
                            }
                            else {
                                renderedConnectorMarks.push(React.createElement(SemioticMark, Object.assign({}, baseMarkProps, eventListeners, { renderMode: renderValue, markType: "path", d: markD, className: classFn ? classFn(piece.piece.data, pieceI) : "", key: `connector${piece.piece.renderKey}`, style: calculatedStyle })));
                            }
                        }
                    }
                });
            }
        });
        if (radarMap.size > 0) {
            for (const ring of radarMap.values()) {
                const ringPiece = Object.assign(Object.assign({}, ring[0].piece), ring[0].piece.data);
                const markD = `M${ring.map((d) => `${d.xy.x},${d.xy.y}`).join("L")}Z`;
                if (canvasRender && canvasRender(ringPiece)) {
                    const canvasRadar = {
                        baseClass: "ordinal-radar",
                        tx: 0,
                        ty: 0,
                        d: {
                            source: ringPiece
                        },
                        markProps: { d: markD, markType: "path" },
                        styleFn: styleFn,
                        renderFn: renderMode,
                        classFn
                    };
                    canvasDrawing.push(canvasRadar);
                }
                else {
                    renderedConnectorMarks.push(React.createElement(SemioticMark, Object.assign({}, baseMarkProps, { renderMode: renderMode && renderMode(ringPiece), markType: "path", d: markD, className: classFn ? classFn(ringPiece) : "", key: `ordinal-ring-${ringPiece.renderKey}`, style: styleFn({
                            source: ringPiece
                        }) })));
                }
            }
        }
    }
    else if (type.type) {
        console.error(`Invalid connectorType - Must be a function that takes a data point and determines if it is connected to a data point in the next column`);
    }
    return renderedConnectorMarks;
}
const summaryRenderHash = {
    contour: contourRenderFn,
    boxplot: boxplotRenderFn,
    violin: bucketizedRenderingFn,
    heatmap: bucketizedRenderingFn,
    ridgeline: bucketizedRenderingFn,
    histogram: bucketizedRenderingFn,
    horizon: bucketizedRenderingFn,
    ckbins: ckBinsRenderFn
};
function orFrameSummaryRenderer({ data, type, renderMode, eventListenersGenerator, styleFn, classFn, projection, adjustedSize, chartSize, baseMarkProps, margin }) {
    let summaryRenderFn;
    if (typeof type.type === "function") {
        summaryRenderFn = type.type;
    }
    else if (summaryRenderHash[type.type]) {
        summaryRenderFn = summaryRenderHash[type.type];
    }
    else {
        console.error(`Invalid summary type: ${type.type} - Must be a function or one of the following strings: ${Object.keys(summaryRenderHash).join(", ")}`);
        return {};
    }
    return summaryRenderFn({
        data,
        type,
        renderMode,
        eventListenersGenerator,
        styleFn,
        classFn,
        projection,
        adjustedSize,
        chartSize,
        baseMarkProps,
        margin
    });
}
const orFrameAxisGenerator = ({ projection, axis, adjustedSize, size, rScale, rScaleType, pieceType, rExtent, data, maxColumnValues = 1, xyData, margin, thresholds }) => {
    if (!axis)
        return { axis: undefined, axesTickLines: undefined };
    let generatedAxis, axesTickLines;
    if (projection !== "radial" && axis) {
        axesTickLines = [];
        const axisPosition = [0, 0];
        generatedAxis = axis.map((d, i) => {
            let axisClassname = d.className || "";
            let tickValues;
            const axisDomain = d.extentOverride ? d.extentOverride : rScale.domain();
            const leftRight = ["left", "right"];
            const axisScale = (leftRight.indexOf(d.orient) === -1 && projection !== "vertical") ||
                (leftRight.indexOf(d.orient) !== -1 && projection !== "horizontal")
                ? rScaleType.domain(axisDomain)
                : rScaleType.domain([0, maxColumnValues]);
            const orient = d.orient;
            const axisRange = (leftRight.indexOf(d.orient) === -1 && projection !== "vertical") ||
                (leftRight.indexOf(d.orient) !== -1 && projection !== "horizontal")
                ? rScale.range()
                : [0, projection === "vertical" ? adjustedSize[0] : adjustedSize[1]];
            if (orient === "right") {
                axisScale.range(axisRange.reverse());
                axisClassname += " right y";
            }
            else if (orient === "left") {
                axisClassname += " left y";
                axisScale.range(axisRange.reverse());
            }
            else if (orient === "top") {
                axisClassname += " top x";
                axisScale.range(axisRange);
            }
            else if (orient === "bottom") {
                axisClassname += " bottom x";
                axisScale.range(axisRange);
            }
            if (d.tickValues && Array.isArray(d.tickValues)) {
                tickValues = d.tickValues;
            }
            else if (d.tickValues instanceof Function) {
                //otherwise assume a function
                tickValues = d.tickValues(data, size, rScale);
            }
            else if (!d.tickValues && thresholds) {
                tickValues = thresholds.map((d) => rScale.invert(d));
            }
            const axisParts = axisPieces({
                padding: d.padding,
                tickValues,
                scale: axisScale,
                ticks: d.ticks,
                orient,
                size: adjustedSize,
                footer: d.footer,
                tickSize: d.tickSize,
                jaggedBase: d.jaggedBase
            });
            const axisTickLines = axisLines({
                className: d.className,
                axisParts,
                orient,
                baseMarkProps: {},
                tickLineGenerator: d.tickLineGenerator,
                jaggedBase: d.jaggedBase,
                scale: axisScale
            });
            axesTickLines.push(axisTickLines);
            if (d.baseline === "under") {
                axesTickLines.push(baselineGenerator(d.orient, adjustedSize, d.className));
            }
            const marginalSummaryType = typeof d.marginalSummaryType === "string"
                ? { type: d.marginalSummaryType }
                : d.marginalSummaryType;
            return (React.createElement(Axis, Object.assign({}, d, { key: d.key || `orframe-axis-${i}`, axisParts: axisParts, orient: orient, size: adjustedSize, position: axisPosition, tickValues: tickValues, scale: axisScale, className: axisClassname, marginalSummaryType: marginalSummaryType, margin: margin, xyPoints: xyData.map((d) => ({
                    x: projection === "vertical" ? 0 : d.value,
                    y: projection === "vertical" ? d.value : 0,
                    data: d.data
                })) })));
        });
    }
    else if (projection === "radial" && axis) {
        const { innerRadius = 0 } = pieceType;
        const ticks = [];
        axis.forEach((axisObj) => {
            const { tickValues: baseTickValues = rScale.ticks(Math.max(2, (adjustedSize[0] / 2 - innerRadius) / 50)), label, tickFormat = (d) => d } = axisObj;
            const tickScale = rScaleType
                .domain(rExtent)
                .range([innerRadius, adjustedSize[0] / 2]);
            const tickValues = baseTickValues instanceof Function
                ? baseTickValues({
                    orient: axisObj.orient
                })
                : baseTickValues;
            tickValues.forEach((t, i) => {
                const tickSize = tickScale(t);
                if (!(innerRadius === 0 && t === 0)) {
                    let axisLabel;
                    let ref = "";
                    if (label && i === tickValues.length - 1) {
                        const labelSettings = typeof label === "string"
                            ? { name: label, locationDistance: 15 }
                            : label;
                        const { locationDistance = 15 } = labelSettings;
                        ref = `${Math.random().toString()} `;
                        axisLabel = (React.createElement("g", { className: "axis-label radial", transform: `translate(0,${locationDistance})` },
                            React.createElement("text", { textAnchor: "middle" },
                                React.createElement("textPath", { startOffset: tickSize * Math.PI * 0.5, xlinkHref: `#${ref}` }, label.name))));
                    }
                    ticks.push(React.createElement("g", { key: `orframe-radial-axis-element-${t}`, className: "axis axis-label axis-tick radial", transform: `translate(0,0)` },
                        React.createElement("path", { id: ref, d: circlePath(0, 0, tickSize), r: tickSize, stroke: "gray", fill: "none" }),
                        React.createElement("text", { y: -tickSize + 5, textAnchor: "middle" }, tickFormat(t)),
                        axisLabel));
                }
                return undefined;
            });
        });
        generatedAxis = [
            React.createElement("g", { key: axis[0].key || `orframe-radial-axis-container`, className: "axis-labels", transform: `translate(${adjustedSize[0] / 2},${adjustedSize[1] / 2})` }, ticks)
        ];
    }
    return { axis: generatedAxis, axesTickLines };
};
const canvasEvent = (canvasContext, overlayRegions, canvasMap, e) => {
    const interactionContext = canvasContext.getContext("2d");
    const hoverPoint = interactionContext.getImageData(e.offsetX, e.offsetY, 1, 1);
    const mostCommonRGB = `rgba(${hoverPoint.data[0]},${hoverPoint.data[1]},${hoverPoint.data[2]},255)`;
    let overlay = overlayRegions[canvasMap.get(mostCommonRGB)];
    if (!overlay) {
        const hoverArea = interactionContext.getImageData(e.offsetX - 2, e.offsetY - 2, 5, 5);
        let x = 0;
        while (!overlay && x < 100) {
            overlay =
                overlayRegions[canvasMap.get(`rgba(${hoverArea.data[x]},${hoverArea.data[x + 1]},${hoverArea.data[x + 2]},255)`)];
            x += 4;
        }
    }
    return overlay;
};

var InteractionCanvas = React.memo(function InteractionCanvas({ width, height, overlayRegions, margin, voronoiHover }) {
    let canvasRef = useRef();
    useLayoutEffect(() => {
        renderInteractionCanvas(canvasRef.current, voronoiHover, height, width, overlayRegions, margin);
    }, [width, height, overlayRegions]);
    return (React.createElement("canvas", { className: "frame-canvas-interaction", ref: (canvas) => {
            if (canvas != null && canvasRef.current !== canvas) {
                canvasRef.current = canvas;
                renderInteractionCanvas(canvas, voronoiHover, height, width, overlayRegions, margin);
            }
        }, style: {
            position: "absolute",
            left: `0px`,
            top: `0px`,
            imageRendering: "pixelated",
            pointerEvents: "all",
            opacity: 0
        }, width: width, height: height }));
});
function renderInteractionCanvas(interactionContext, voronoiHover, height, width, overlayRegions, margin) {
    const canvasMap = new Map();
    if (interactionContext === null || !overlayRegions)
        return;
    const boundCanvasEvent = canvasEvent.bind(null, interactionContext, overlayRegions, canvasMap);
    interactionContext.onmousemove = (e) => {
        var _a, _b, _c, _d;
        const overlay = boundCanvasEvent(e);
        if ((_a = overlay === null || overlay === void 0 ? void 0 : overlay.props) === null || _a === void 0 ? void 0 : _a.onMouseEnter) {
            (_b = overlay === null || overlay === void 0 ? void 0 : overlay.props) === null || _b === void 0 ? void 0 : _b.onMouseEnter();
        }
        else if ((_d = (_c = overlay === null || overlay === void 0 ? void 0 : overlay.props) === null || _c === void 0 ? void 0 : _c.children) === null || _d === void 0 ? void 0 : _d[0]) {
            overlay.props.children[0].props.onMouseEnter();
        }
        else {
            voronoiHover(null);
        }
    };
    interactionContext.onmouseout = () => {
        voronoiHover(null);
    };
    interactionContext.onclick = (e) => {
        var _a, _b, _c, _d;
        const overlay = boundCanvasEvent(e);
        if ((_a = overlay === null || overlay === void 0 ? void 0 : overlay.props) === null || _a === void 0 ? void 0 : _a.onClick) {
            (_b = overlay === null || overlay === void 0 ? void 0 : overlay.props) === null || _b === void 0 ? void 0 : _b.onClick();
        }
        else if ((_d = (_c = overlay === null || overlay === void 0 ? void 0 : overlay.props) === null || _c === void 0 ? void 0 : _c.children) === null || _d === void 0 ? void 0 : _d[0]) {
            overlay.props.children[0].props.onClick(e);
        }
    };
    interactionContext.ondblclick = (e) => {
        var _a, _b, _c, _d;
        const overlay = boundCanvasEvent(e);
        if ((_a = overlay === null || overlay === void 0 ? void 0 : overlay.props) === null || _a === void 0 ? void 0 : _a.onDoubleClick) {
            (_b = overlay === null || overlay === void 0 ? void 0 : overlay.props) === null || _b === void 0 ? void 0 : _b.onDoubleClick();
        }
        else if ((_d = (_c = overlay === null || overlay === void 0 ? void 0 : overlay.props) === null || _c === void 0 ? void 0 : _c.children) === null || _d === void 0 ? void 0 : _d[0]) {
            overlay.props.children[0].props.onDoubleClick(e);
        }
    };
    const interactionContext2D = interactionContext.getContext("2d");
    interactionContext2D.imageSmoothingEnabled = false;
    interactionContext2D.setTransform(1, 0, 0, 1, margin.left, margin.top);
    interactionContext2D.clearRect(-margin.left, -margin.top, width, height);
    interactionContext2D.lineWidth = 1;
    overlayRegions.forEach((overlay, oi) => {
        var _a, _b, _c, _d, _e, _f;
        const interactionRGBA = `rgba(${Math.floor(Math.random() * 255)},${Math.floor(Math.random() * 255)},${Math.floor(Math.random() * 255)},255)`;
        canvasMap.set(interactionRGBA, oi);
        const props = overlay.props;
        interactionContext2D.fillStyle = interactionRGBA;
        interactionContext2D.strokeStyle = interactionRGBA;
        if (props.d || ((_c = (_b = (_a = props.children) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.props) === null || _c === void 0 ? void 0 : _c.d)) {
            const overlayD = props.d || props.children[0].props.d;
            const transform = props.transform ||
                ((_f = (_e = (_d = props.children) === null || _d === void 0 ? void 0 : _d[0]) === null || _e === void 0 ? void 0 : _e.props) === null || _f === void 0 ? void 0 : _f.transform) ||
                "translate(0,0)";
            const [x, y] = transform
                .replace("translate(", "")
                .replace(")", "")
                .split(",");
            interactionContext2D.translate(x, y);
            const p = new Path2D(overlayD);
            interactionContext2D.stroke(p);
            interactionContext2D.fill(p);
            interactionContext2D.translate(-x, -y);
        }
        else if (props.markType === "rect") {
            interactionContext2D.fillRect(props.x, props.y, props.width, props.height);
            interactionContext2D.strokeRect(props.x, props.y, props.width, props.height);
        }
    });
}

const generateOMappingFn = (projectedColumns) => (d) => {
    if (d) {
        const columnValues = Object.values(projectedColumns);
        const foundColumns = columnValues.filter((c) => {
            return d[1] >= c.x && d[0] <= c.x + c.width;
        });
        return foundColumns;
    }
    return null;
};
const generateOEndMappingFn = (projectedColumns) => (d, event) => {
    if (d &&
        event.sourceEvent &&
        event.sourceEvent.path &&
        event.sourceEvent.path[1] &&
        event.sourceEvent.path[1].classList.contains("xybrush") &&
        event.target.move) {
        const columnValues = Object.values(projectedColumns);
        const foundColumns = columnValues.filter((c) => d[1] >= c.x && d[0] <= c.x + c.width);
        const firstColumn = foundColumns[0] || {
            x: 0,
            width: 0
        };
        const lastColumn = foundColumns[foundColumns.length - 1] || {
            x: 0,
            width: 0
        };
        const columnPosition = [
            firstColumn.x + Math.min(5, firstColumn.width / 10),
            lastColumn.x + lastColumn.width - Math.min(5, lastColumn.width / 10)
        ];
        select(event.sourceEvent.path[1])
            .transition(750)
            .call(event.target.move, columnPosition);
        return foundColumns;
    }
    return null;
};
const createBrush = (interaction, props) => {
    let semioticBrush, mappingFn, selectedExtent, endMappingFn;
    const { xScale, yScale, size, renderPipeline } = props;
    const brushData = {};
    Object.entries(renderPipeline).forEach(([key, value]) => {
        if (value.data && value.data.length > 0) {
            brushData[key] = value.data;
        }
    });
    const { projection, projectedColumns } = interaction;
    const actualBrush = interaction.brush === "oBrush"
        ? projection === "horizontal"
            ? "yBrush"
            : "xBrush"
        : interaction.brush;
    let { extent } = interaction;
    if (!extent) {
        extent =
            actualBrush === "xyBrush"
                ? [
                    [xScale.invert(0), yScale.invert(0)],
                    [xScale.invert(size[0]), yScale.invert(size[1])]
                ]
                : actualBrush === "xBrush"
                    ? [xScale.invert(0), xScale.invert(size[0])]
                    : [yScale.invert(0), yScale.invert(size[1])];
    }
    if (extent.indexOf && extent.indexOf(undefined) !== -1) {
        return React.createElement("g", null);
    }
    if (actualBrush === "xBrush") {
        const castExtent = [...extent];
        mappingFn = (d) => !d ? null : [xScale.invert(d[0]), xScale.invert(d[1])];
        semioticBrush = brushX();
        selectedExtent = castExtent.map((d) => xScale(d));
        endMappingFn = mappingFn;
    }
    else if (actualBrush === "yBrush") {
        const castExtent = [...extent];
        mappingFn = (d) => !d
            ? null
            : [yScale.invert(d[0]), yScale.invert(d[1])].sort((a, b) => a - b);
        semioticBrush = brushY();
        selectedExtent = castExtent.map((d) => yScale(d)).sort((a, b) => a - b);
        endMappingFn = mappingFn;
    }
    else {
        const typedExtent = extent;
        const castExtent = [...typedExtent.map((ee) => [...ee])];
        if (castExtent.indexOf(undefined) !== -1 ||
            castExtent[0].indexOf(undefined) !== -1 ||
            castExtent[1].indexOf(undefined) !== -1) {
            return React.createElement("g", null);
        }
        semioticBrush = brush();
        mappingFn = (d) => {
            if (!d)
                return null;
            const yValues = [yScale.invert(d[0][1]), yScale.invert(d[1][1])].sort((a, b) => a - b);
            return [
                [xScale.invert(d[0][0]), yValues[0]],
                [xScale.invert(d[1][0]), yValues[1]]
            ];
        };
        const yValues = [yScale(extent[0][1]), yScale(extent[1][1])].sort((a, b) => a - b);
        selectedExtent = castExtent.map((d, i) => [xScale(d[0]), yValues[i]]);
        endMappingFn = mappingFn;
    }
    if (interaction.brush === "oBrush") {
        selectedExtent = null;
        if (interaction.extent) {
            const [leftExtent, rightExtent] = interaction.extent;
            if ((typeof leftExtent === "string" || typeof leftExtent === "number") &&
                (typeof rightExtent === "string" || typeof rightExtent === "number")) {
                selectedExtent = [
                    projectedColumns[leftExtent].x,
                    projectedColumns[rightExtent].x + projectedColumns[rightExtent].width
                ];
            }
        }
        mappingFn = generateOMappingFn(projectedColumns);
        endMappingFn = generateOEndMappingFn(projectedColumns);
    }
    semioticBrush
        .extent([
        [0, 0],
        [size[0], size[1]]
    ])
        .on("start", (event) => {
        brushStart(mappingFn(event.selection), undefined, brushData, undefined, interaction);
    })
        .on("brush", (event) => {
        brushing(mappingFn(event.selection), undefined, brushData, undefined, interaction);
    })
        .on("end", (event) => {
        brushEnd(endMappingFn(event.selection, event), undefined, brushData, undefined, interaction);
    });
    return (React.createElement("g", { className: "brush" },
        React.createElement(Brush, { selectedExtent: selectedExtent, extent: extent, svgBrush: semioticBrush })));
};
const createColumnsBrush = (interaction, props) => {
    const { projection, rScale, oColumns, renderPipeline } = props;
    if (!projection || !rScale || !oColumns)
        return;
    const brushData = {};
    Object.entries(renderPipeline).forEach(([key, value]) => {
        if (value.data && value.data.length > 0) {
            brushData[key] = value.data;
        }
    });
    let semioticBrush, mappingFn;
    const rScaleReverse = rScale
        .copy()
        .domain(rScale.domain())
        .range(rScale.domain().reverse());
    if (projection && projection === "horizontal") {
        mappingFn = (d) => !d ? null : [rScale.invert(d[0]), rScale.invert(d[1])];
    }
    else
        mappingFn = (d) => {
            return !d
                ? null
                : [
                    rScaleReverse(rScale.invert(d[1])),
                    rScaleReverse(rScale.invert(d[0]))
                ];
        };
    const rRange = rScale.range();
    const columnHash = oColumns;
    let brushPosition, selectedExtent;
    const brushes = Object.keys(columnHash).map((c) => {
        if (projection && projection === "horizontal") {
            selectedExtent = interaction.extent[c]
                ? interaction.extent[c].map((d) => rScale(d))
                : interaction.startEmpty
                    ? null
                    : rRange;
            brushPosition = [0, columnHash[c].x];
            semioticBrush = brushX();
            semioticBrush
                .extent([
                [rRange[0], 0],
                [rRange[1], columnHash[c].width]
            ])
                .on("start", (event) => {
                brushStart(mappingFn(event.selection), c, brushData, columnHash[c], interaction);
            })
                .on("brush", (event) => {
                brushing(mappingFn(event.selection), c, brushData, columnHash[c], interaction);
            })
                .on("end", (event) => {
                brushEnd(mappingFn(event.selection), c, brushData, columnHash[c], interaction);
            });
        }
        else {
            selectedExtent = interaction.extent[c]
                ? interaction.extent[c].map((d) => rRange[1] - rScale(d)).reverse()
                : interaction.startEmpty
                    ? null
                    : rRange;
            brushPosition = [columnHash[c].x, 0];
            semioticBrush = brushY();
            semioticBrush
                .extent([
                [0, rRange[0]],
                [columnHash[c].width, rRange[1]]
            ])
                .on("start", (event) => {
                brushStart(mappingFn(event.selection), c, brushData, columnHash[c], interaction);
            })
                .on("brush", (event) => {
                brushing(mappingFn(event.selection), c, brushData, columnHash[c], interaction);
            })
                .on("end", (event) => {
                brushEnd(mappingFn(event.selection), c, brushData, columnHash[c], interaction);
            });
        }
        return (React.createElement("g", { key: `column-brush-${c}`, className: "brush" },
            React.createElement(Brush, { key: `orbrush${c}`, selectedExtent: selectedExtent, svgBrush: semioticBrush, position: brushPosition })));
    });
    return brushes;
};
function InteractionLayer(props) {
    let semioticBrush = null;
    const { interaction, svgSize = [500, 500], margin = { left: 0, right: 0, top: 0, bottom: 0 }, useSpans = false, overlay, points, xScale, yScale, hoverAnnotation, customClickBehavior, customDoubleClickBehavior, customHoverBehavior, disableCanvasInteraction, canvasRendering
    //    voronoiHover
     } = props;
    let { enabled } = props;
    let changeTooltip = useTooltip((state) => state.changeTooltip);
    const voronoiHover = (d) => {
        changeTooltip(d);
    };
    const [overlayRegions, changeOverlayRegions] = useState([]);
    const [interactionCanvas, changeInteractionCanvas] = useState(null);
    const [SpanOrDiv] = useState(() => HOCSpanOrDiv(useSpans));
    useEffect(() => {
        let nextOverlay, interactionCanvas;
        if (disableCanvasInteraction || !overlayRegions) {
            nextOverlay = null;
            interactionCanvas = null;
        }
        else {
            nextOverlay = calculateOverlay(props, voronoiHover);
            if (canvasRendering) {
                interactionCanvas = (React.createElement(InteractionCanvas, { height: svgSize[1], width: svgSize[0], overlayRegions: nextOverlay, margin: margin, voronoiHover: voronoiHover }));
            }
        }
        changeOverlayRegions(nextOverlay);
        changeInteractionCanvas(interactionCanvas);
    }, [
        overlay,
        points,
        xScale,
        yScale,
        !!hoverAnnotation,
        !!customClickBehavior,
        !!customDoubleClickBehavior,
        !!customHoverBehavior
    ]);
    if (interaction && interaction.brush) {
        enabled = true;
        semioticBrush = createBrush(interaction, props);
    }
    if (interaction && interaction.columnsBrush) {
        enabled = true;
        semioticBrush = createColumnsBrush(interaction, props);
    }
    if (!overlayRegions && !semioticBrush) {
        return null;
    }
    return (React.createElement(SpanOrDiv, { span: useSpans, className: "interaction-layer", "data-testid": "interaction-layer", style: {
            position: "absolute",
            background: "none",
            pointerEvents: "none",
            height: "100%",
            width: "100%",
            overflow: "hidden"
        } }, interactionCanvas || (React.createElement("svg", { height: svgSize[1], width: svgSize[0], style: { background: "none", pointerEvents: "none" } },
        React.createElement("g", { className: "interaction-overlay", transform: `translate(${margin.left},${margin.top})`, style: { pointerEvents: enabled ? "all" : "none" } },
            React.createElement("g", { className: "interaction-regions" }, overlayRegions),
            semioticBrush)))));
}

const MAX_TIMEFRAME_MS = 30;
function batchWork(performWork, { signal, timeFrameMs = MAX_TIMEFRAME_MS } = {}) {
    return __awaiter(this, void 0, void 0, function* () {
        return new Promise((resolve, reject) => {
            requestAnimationFrame(function loop() {
                // FIXME the `timestamp` value can be received from rAF,
                // but the value is not in sync with performance.now (in tests only)
                let timestamp = performance.now();
                let elapsed = 0;
                let shouldContinue = false;
                let isAborted = signal != null && signal.aborted;
                try {
                    if (isAborted) {
                        return resolve();
                    }
                    do {
                        shouldContinue = performWork();
                        elapsed = performance.now() - timestamp;
                        isAborted = signal != null && signal.aborted;
                    } while (!isAborted && shouldContinue && elapsed < timeFrameMs);
                    if (!isAborted && shouldContinue) {
                        requestAnimationFrame(loop);
                    }
                    else {
                        resolve();
                    }
                }
                catch (error) {
                    reject(error);
                }
            });
        });
    });
}

const updateVisualizationLayer = (props, handleKeyDown, voronoiHover) => {
    const { xScale, yScale, dataVersion, projectedCoordinateNames, renderPipeline = {}, baseMarkProps = {}, renderOrder = [], sketchyRenderingEngine } = props;
    const canvasDrawing = [];
    const piecesGroup = {};
    const renderedElements = [];
    const renderVizKeys = Object.keys(renderPipeline);
    const renderKeys = renderOrder.concat(renderVizKeys.filter((d) => renderOrder.indexOf(d) === -1));
    renderKeys.forEach((k) => {
        const pipe = renderPipeline[k];
        if (pipe &&
            ((pipe.data &&
                typeof pipe.data === "object" &&
                !Array.isArray(pipe.data)) ||
                (pipe.data && pipe.data.length > 0))) {
            const additionalMarkProps = {
                sketchyGenerator: sketchyRenderingEngine && sketchyRenderingEngine.generator,
                "aria-label": (pipe.ariaLabel && pipe.ariaLabel.items) || "dataviz-element",
                role: "img",
                tabIndex: -1
            };
            const renderedPipe = pipe.behavior(Object.assign({ xScale,
                yScale,
                canvasDrawing,
                projectedCoordinateNames, baseMarkProps: Object.assign(Object.assign({}, baseMarkProps), additionalMarkProps) }, pipe));
            if (renderedPipe && renderedPipe.length > 0) {
                renderedElements.push(React.createElement("g", { key: k, className: k, "data-testid": k, role: "group", tabIndex: 0, "aria-label": (pipe.ariaLabel &&
                        `${renderedPipe.length} ${pipe.ariaLabel.items}s in a ${pipe.ariaLabel.chart}`) ||
                        k, onKeyDown: (e) => handleKeyDown({ e, k, props, piecesGroup }), onBlur: () => {
                        voronoiHover(undefined);
                    }, ref: (thisNode) => thisNode && (piecesGroup[k] = thisNode.childNodes) }, renderedPipe));
            }
        }
    });
    return {
        renderedElements,
        dataVersion,
        canvasDrawing,
        piecesGroup
    };
};
const handleKeyDown = ({ focusedPieceIndex, changeFocusedPieceIndex, changeFocusedVisualizationGroup }) => ({ e: { keyCode }, vizgroup, props, piecesGroup }) => {
    // If enter, focus on the first element
    const { renderPipeline, voronoiHover } = props;
    const pushed = keyCode;
    if (pushed !== 37 && pushed !== 39 && pushed !== 13)
        return;
    let newPieceIndex = 0;
    const vizGroupSetting = {};
    // If a user pressed enter, highlight the first one
    // Let a user move up and down in stacked bar by getting keys of bars?
    if (focusedPieceIndex === null || pushed === 13) {
        vizGroupSetting.focusedVisualizationGroup = vizgroup;
    }
    else if (pushed === 37) {
        newPieceIndex = focusedPieceIndex - 1;
    }
    else if (pushed === 39) {
        newPieceIndex = focusedPieceIndex + 1;
    }
    newPieceIndex =
        newPieceIndex < 0
            ? piecesGroup[vizgroup].length + newPieceIndex
            : newPieceIndex % piecesGroup[vizgroup].length;
    const piece = renderPipeline[vizgroup].accessibleTransform(renderPipeline[vizgroup].data, newPieceIndex, piecesGroup[vizgroup][newPieceIndex]);
    voronoiHover(piece);
    changeFocusedPieceIndex(newPieceIndex);
    changeFocusedVisualizationGroup(vizGroupSetting.focusedVisualizationGroup);
};
function VisualizationLayer(props) {
    const { matte, matteClip, axes, frameKey = "", margin = { left: 0, right: 0, bottom: 0, top: 0 }, title, ariaTitle, axesTickLines, frameRenderOrder, additionalVizElements } = props;
    let changeTooltip = useTooltip((state) => state.changeTooltip);
    const voronoiHover = (d) => {
        changeTooltip(d);
    };
    const [focusedPieceIndex, changeFocusedPieceIndex] = useState(null);
    const [focusedVisualizationGroup, changeFocusedVisualizationGroup] = useState(null);
    const decoratedKeydown = handleKeyDown({
        focusedPieceIndex,
        changeFocusedPieceIndex,
        changeFocusedVisualizationGroup
    });
    const vizState = updateVisualizationLayer(props, decoratedKeydown, voronoiHover);
    const { renderedElements } = vizState;
    useEffect(() => {
        var _a, _b;
        const canvasContext = (_a = props.canvasContext) === null || _a === void 0 ? void 0 : _a.current;
        let updateCtrl = new AbortController();
        if (props.disableContext || !canvasContext)
            return;
        const { sketchyRenderingEngine, width, height, margin, disableProgressiveRendering = false } = props;
        const size = [
            width + margin.left + margin.right,
            height + margin.top + margin.bottom
        ];
        let rc;
        canvasContext.width = size[0];
        canvasContext.height = size[1];
        canvasContext.style.width = size[0];
        canvasContext.style.height = size[1];
        const context = canvasContext.getContext("2d");
        context.scale(devicePixelRatio, devicePixelRatio);
        context.setTransform(1, 0, 0, 1, margin.left, margin.top);
        context.clearRect(-margin.left, -margin.top, size[0], size[1]);
        const renderCanvasPiece = renderCanvas(canvasContext, context, margin, props, sketchyRenderingEngine, rc);
        context.clearRect(-margin.left, -margin.top, size[0], size[1]);
        if (disableProgressiveRendering) {
            for (const piece of vizState.canvasDrawing) {
                renderCanvasPiece(piece);
            }
        }
        else {
            updateCtrl = new AbortController();
            batchCollectionWork(renderCanvasPiece, vizState.canvasDrawing, {
                signal: updateCtrl.signal
            });
        }
        context.setTransform(1, 0, 0, 1, 0, 0);
        context.globalAlpha = 1;
        if (props.canvasPostProcess) {
            props.canvasPostProcess((_b = props.canvasContext) === null || _b === void 0 ? void 0 : _b.current, context, size);
        }
        if (focusedVisualizationGroup !== null &&
            vizState.piecesGroup[focusedVisualizationGroup] &&
            focusedPieceIndex !== null) {
            const focusElParent = vizState.piecesGroup[focusedVisualizationGroup][focusedPieceIndex];
            const focusEl = (focusElParent &&
                [...focusElParent.childNodes].find((child) => child.getAttribute("aria-label"))) ||
                focusElParent;
            focusEl && focusEl.focus && focusEl.focus();
        }
        return function cleanup() {
            updateCtrl.abort();
        };
    }, [
        matte,
        matteClip,
        axes,
        frameKey,
        margin,
        ariaTitle,
        axesTickLines,
        frameRenderOrder,
        additionalVizElements
    ]);
    const renderHash = Object.assign({ ["axes-tick-lines"]: axesTickLines && (React.createElement("g", { key: "visualization-tick-lines", className: "axis axis-tick-lines", "aria-hidden": true }, axesTickLines)), ["axes-labels"]: axes && (React.createElement("g", { key: "visualization-axis-labels", className: "axis axis-labels" }, axes)), matte: matte, ["viz-layer"]: renderedElements && renderedElements.length > 0 ? renderedElements : null }, additionalVizElements);
    let ariaLabel = "";
    const finalTitle = (title && ariaTitle) || title
        ? typeof title !== "string" &&
            title.props &&
            typeof title.props.children === "string"
            ? `titled ${title.props.children}`
            : "with a complex title"
        : "with no title";
    ariaLabel = `Visualization ${finalTitle}. Use arrow keys to navigate elements.`;
    const orderedElements = [];
    frameRenderOrder.forEach((r) => {
        if (renderHash[r]) {
            orderedElements.push(renderHash[r]);
        }
    });
    const renderedDataVisualization = (orderedElements.length > 0 && (React.createElement("g", { className: "data-visualization", "data-testid": "data-visualization", key: "visualization-clip-path", "aria-label": ariaLabel, role: "group", clipPath: matteClip && matte ? `url(#matte-clip${frameKey})` : undefined, transform: `translate(${margin.left},${margin.top})` }, orderedElements))) ||
        null;
    return renderedDataVisualization;
}
function renderCanvas(canvasContext, context, margin, np, sketchyRenderingEngine, rc) {
    const defaultStyle = {
        fill: "black",
        stroke: "black",
        opacity: 1,
        fillOpacity: 1,
        strokeOpacity: 1,
        strokeWidth: 1
    };
    const { position = [0, 0] } = np;
    return (piece) => {
        var _a, _b, _c;
        const combinedDatum = Object.assign(Object.assign({}, piece.d), piece.d.data);
        const style = piece.styleFn
            ? piece.styleFn(combinedDatum, piece.i) || defaultStyle
            : defaultStyle;
        const fill = style.fill ? style.fill : "black";
        const stroke = style.stroke ? style.stroke : "black";
        context.setTransform(1, 0, 0, 1, margin.left, margin.top);
        context.translate(position[0], position[1]);
        context.translate(piece.tx, piece.ty);
        context.fillStyle = fill;
        context.strokeStyle = stroke;
        context.lineWidth = style.strokeWidth ? style.strokeWidth : 0;
        let rcSettings = {};
        const renderObject = piece.markProps.renderMode ||
            (piece.renderFn && piece.renderFn(combinedDatum, piece.i));
        let actualRenderMode = (renderObject && renderObject.renderMode) || renderObject;
        if (actualRenderMode) {
            if (!sketchyRenderingEngine) {
                console.error("You cannot render sketchy graphics without specifying a Rough.js-like library as the sketchyRenderingEngine prop of your frame");
                actualRenderMode = undefined;
            }
            else {
                const RoughCanvas = sketchyRenderingEngine.canvas;
                if (!RoughCanvas) {
                    console.error("The sketchyRenderingEngine you specify does not expose a prop `RoughCanvas` and so cannot render sketchy HTML5 Canvas graphics");
                }
                else {
                    rc = rc || RoughCanvas(canvasContext);
                    const rcExtension = (typeof renderObject === "object" && renderObject) || {};
                    rcSettings = Object.assign({ fill,
                        stroke, strokeWidth: context.lineWidth }, rcExtension);
                }
            }
        }
        if (piece.markProps.markType === "circle" ||
            (piece.markProps.markType === "rect" && piece.markProps.rx > 0)) {
            let vizX = (_a = piece.markProps.cx) !== null && _a !== void 0 ? _a : 0, vizY = (_b = piece.markProps.cy) !== null && _b !== void 0 ? _b : 0, r = (_c = style.r) !== null && _c !== void 0 ? _c : piece.markProps.r;
            if (piece.markProps.width) {
                const halfWidth = piece.markProps.width / 2;
                vizX = piece.markProps.x + halfWidth;
                vizY = piece.markProps.y + halfWidth;
                r = halfWidth;
            }
            if (actualRenderMode === "sketchy") {
                if (context.globalAlpha !== 0)
                    rc.circle(vizX, vizY, r, rcSettings);
            }
            else {
                context.beginPath();
                context.arc(vizX, vizY, r, 0, 2 * Math.PI);
                context.globalAlpha = style.fillOpacity || style.opacity || 1;
                if (style.fill && style.fill !== "none" && context.globalAlpha !== 0) {
                    context.fill();
                }
                context.globalAlpha = style.strokeOpacity || style.opacity || 1;
                if (style.stroke &&
                    style.stroke !== "none" &&
                    context.globalAlpha !== 0)
                    context.stroke();
            }
        }
        else if (piece.markProps.markType === "rect") {
            if (actualRenderMode === "sketchy") {
                context.globalAlpha = style.opacity || 1;
                if (context.globalAlpha !== 0)
                    rc.rectangle(piece.markProps.x, piece.markProps.y, piece.markProps.width, piece.markProps.height, rcSettings);
            }
            else {
                context.globalAlpha = style.fillOpacity || style.opacity || 1;
                if (style.fill && style.fill !== "none" && context.globalAlpha !== 0)
                    context.fillRect(piece.markProps.x, piece.markProps.y, piece.markProps.width, piece.markProps.height);
                context.globalAlpha = style.strokeOpacity || style.opacity || 1;
                if (style.stroke &&
                    style.stroke !== "none" &&
                    context.globalAlpha !== 0)
                    context.strokeRect(piece.markProps.x, piece.markProps.y, piece.markProps.width, piece.markProps.height);
            }
        }
        else if (piece.markProps.markType === "path") {
            if (actualRenderMode === "sketchy") {
                context.globalAlpha = style.opacity || 1;
                rc.path(piece.markProps.d, rcSettings);
            }
            else {
                const p = new Path2D(piece.markProps.d);
                context.globalAlpha = style.strokeOpacity || style.opacity || 1;
                if (style.stroke &&
                    style.stroke !== "none" &&
                    context.globalAlpha !== 0)
                    context.stroke(p);
                context.globalAlpha = style.fillOpacity || style.opacity || 1;
                if (style.fill && style.fill !== "none" && context.globalAlpha !== 0)
                    context.fill(p);
            }
        }
        else if (piece.markProps.markType === "line") {
            if (actualRenderMode === "sketchy") {
                context.globalAlpha = style.opacity || 1;
                rc.line(piece.markProps.x1, piece.markProps.y1, piece.markProps.x2, piece.markProps.y2, rcSettings);
            }
            else {
                context.globalAlpha = style.strokeOpacity || style.opacity || 1;
                context.beginPath();
                context.moveTo(piece.markProps.x1, piece.markProps.y1);
                context.lineTo(piece.markProps.x2, piece.markProps.y2);
                if (style.stroke &&
                    style.stroke !== "none" &&
                    context.globalAlpha !== 0) {
                    context.stroke();
                }
            }
        }
        else {
            console.error("CURRENTLY UNSUPPORTED MARKTYPE FOR CANVAS RENDERING");
        }
    };
}
// using batchWork utility, this function iterates over a collection of data
// in batches of 1000 and invokes `process` function for each data point
function batchCollectionWork(process, data, options) {
    let pointer = 0;
    let batchSize = 1000;
    return batchWork(() => {
        let limit = Math.min(pointer + batchSize, data.length);
        for (let i = pointer; i < limit; i++) {
            process(data[i]);
        }
        pointer = pointer + batchSize;
        return data.length > pointer;
    }, options);
}

const filterDefs = ({ matte, key, additionalDefs }) => (React.createElement("defs", null,
    React.createElement("filter", { id: "paintyFilterHeavy" },
        React.createElement("feGaussianBlur", { id: "gaussblurrer", in: "SourceGraphic", stdDeviation: 4, colorInterpolationFilters: "sRGB", result: "blur" }),
        React.createElement("feColorMatrix", { in: "blur", mode: "matrix", values: "1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 34 -7", result: "gooey" })),
    React.createElement("filter", { id: "paintyFilterLight" },
        React.createElement("feGaussianBlur", { id: "gaussblurrer", in: "SourceGraphic", stdDeviation: 2, colorInterpolationFilters: "sRGB", result: "blur" }),
        React.createElement("feColorMatrix", { in: "blur", mode: "matrix", values: "1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 34 -7", result: "gooey" })),
    React.createElement("clipPath", { id: `matte-clip-${key}` }, matte),
    additionalDefs));
const generateFinalDefs = ({ matte, size, margin, frameKey, additionalDefs, name }) => {
    let marginGraphic;
    if (typeof matte === "function") {
        marginGraphic = matte({ size, margin });
    }
    else if (React.isValidElement(matte)) {
        marginGraphic = matte;
    }
    else if (matte === true) {
        marginGraphic = (React.createElement("path", { fill: "white", transform: `translate(${-margin.left},${-margin.top})`, d: drawMarginPath({
                margin,
                size: size,
                inset: 0
            }), className: `${name}-matte` }));
    }
    const finalFilterDefs = filterDefs({
        matte: marginGraphic,
        key: matte && (frameKey || name),
        additionalDefs: additionalDefs
    });
    return { defs: finalFilterDefs, matte: marginGraphic };
};

const blankArray$1 = [];
const blankObject = {};
const defaultPosition = [0, 0];
const defaultCoordinateNames = { x: "x", y: "y" };
const defaultFrameRenderOrder = [
    "axes-tick-lines",
    "viz-layer",
    "matte",
    "axes-labels",
    "labels"
];
const defaultZeroMargin = { top: 0, bottom: 0, left: 0, right: 0 };
function Frame(props) {
    const { axes, axesTickLines, className = "", name = "", frameKey, projectedCoordinateNames = defaultCoordinateNames, renderPipeline, size, adjustedSize = size, title, xScale, yScale, dataVersion, annotations, projectedYMiddle, interaction, customClickBehavior, customHoverBehavior, customDoubleClickBehavior, points, margin = defaultZeroMargin, backgroundGraphics, foregroundGraphics, beforeElements, afterElements, defaultSVGRule, defaultHTMLRule, adjustedPosition = defaultPosition, legendSettings, annotationSettings = blankObject, overlay, columns, rScale, projection, interactionOverflow, canvasPostProcess, baseMarkProps, useSpans, canvasRendering, renderOrder = blankArray$1, showLinePoints, disableCanvasInteraction = false, sketchyRenderingEngine, disableContext, frameRenderOrder = defaultFrameRenderOrder, additionalVizElements = blankObject, interactionSettings, disableProgressiveRendering, additionalDefs } = props;
    let { hoverAnnotation } = props;
    const canvasContext = useRef(null);
    const [finalDefs, changeFinalDefs] = useState(null);
    const [matte, changeMatte] = useState(null);
    const [SpanOrDiv] = useState(() => HOCSpanOrDiv(props.useSpans));
    useEffect(() => {
        const generatedDefs = generateFinalDefs({
            matte,
            size,
            margin,
            frameKey,
            additionalDefs,
            name
        });
        changeFinalDefs(generatedDefs.defs);
        changeMatte(generatedDefs.matte);
    }, [
        size[0],
        size[1],
        margin.top,
        margin.bottom,
        margin.right,
        margin.left,
        margin.top,
        matte,
        additionalDefs
    ]);
    if (!hoverAnnotation &&
        (customClickBehavior || customHoverBehavior || customDoubleClickBehavior)) {
        hoverAnnotation = blankArray$1;
    }
    const areaAnnotations = [];
    const totalAnnotations = annotations
        ? [...annotations, ...areaAnnotations]
        : areaAnnotations;
    const annotationLayer = (React.createElement(AnnotationLayer, { legendSettings: legendSettings, margin: margin, axes: axes, annotationHandling: annotationSettings, pointSizeFunction: annotationSettings.layout && annotationSettings.layout.pointSizeFunction, labelSizeFunction: annotationSettings.layout && annotationSettings.layout.labelSizeFunction, annotations: totalAnnotations, svgAnnotationRule: (d, i, thisALayer) => defaultSVGRule(Object.assign({ d,
            i, annotationLayer: thisALayer }, renderPipeline)), htmlAnnotationRule: (d, i, thisALayer) => defaultHTMLRule(Object.assign({ d,
            i, annotationLayer: thisALayer }, renderPipeline)), useSpans: useSpans, size: adjustedSize, position: [
            adjustedPosition[0] + margin.left,
            adjustedPosition[1] + margin.top
        ] }));
    const generatedTitle = generateFrameTitle({
        title: title,
        size: size
    });
    const finalBackgroundGraphics = typeof backgroundGraphics === "function"
        ? backgroundGraphics({ size, margin })
        : backgroundGraphics;
    const finalForegroundGraphics = typeof foregroundGraphics === "function"
        ? foregroundGraphics({ size, margin })
        : foregroundGraphics;
    return (React.createElement(SpanOrDiv, { span: useSpans, className: `${className} frame ${name}`, style: {
            background: "none"
        } },
        beforeElements && (React.createElement(SpanOrDiv, { span: useSpans, className: `${name} frame-before-elements` }, beforeElements)),
        React.createElement(SpanOrDiv, { span: useSpans, className: "frame-elements", style: { height: `${size[1]}px`, width: `${size[0]}px` } },
            React.createElement(TooltipProvider, null,
                React.createElement(SpanOrDiv, { span: useSpans, className: "visualization-layer", style: { position: "absolute" } },
                    backgroundGraphics && (React.createElement("svg", { className: "background-graphics", style: { position: "absolute" }, width: size[0], height: size[1] }, backgroundGraphics && (React.createElement("g", { "aria-hidden": true, className: "background-graphics" }, finalBackgroundGraphics)))),
                    React.createElement("svg", { className: "visualization-layer", style: { position: "absolute" }, width: size[0], height: size[1] },
                        finalDefs,
                        React.createElement(VisualizationLayer, { disableContext: disableContext, renderPipeline: renderPipeline, position: adjustedPosition, width: adjustedSize[0], height: adjustedSize[1], projectedCoordinateNames: projectedCoordinateNames, xScale: xScale, yScale: yScale, axes: axes, title: generatedTitle, frameKey: frameKey, canvasContext: canvasContext, dataVersion: dataVersion, matte: matte, margin: margin, canvasPostProcess: canvasPostProcess, baseMarkProps: baseMarkProps, renderOrder: renderOrder, sketchyRenderingEngine: sketchyRenderingEngine, axesTickLines: axesTickLines, additionalVizElements: additionalVizElements, frameRenderOrder: frameRenderOrder, disableProgressiveRendering: disableProgressiveRendering }),
                        generatedTitle && (React.createElement("g", { className: "frame-title" }, generatedTitle)),
                        foregroundGraphics && (React.createElement("g", { "aria-hidden": true, className: "foreground-graphics" }, finalForegroundGraphics)))),
                canvasRendering && (React.createElement("canvas", { className: "frame-canvas", ref: canvasContext, style: {
                        pointerEvents: "none",
                        position: "absolute"
                    }, width: size[0] / 2, height: size[1] / 2 })),
                React.createElement(InteractionLayer, { useSpans: useSpans, hoverAnnotation: hoverAnnotation, projectedX: projectedCoordinateNames.x, projectedY: projectedCoordinateNames.y, projectedYMiddle: projectedYMiddle, interaction: interaction, customClickBehavior: customClickBehavior, customHoverBehavior: customHoverBehavior, customDoubleClickBehavior: customDoubleClickBehavior, points: points, showLinePoints: showLinePoints, canvasRendering: canvasRendering, position: adjustedPosition, margin: margin, size: adjustedSize, svgSize: size, xScale: xScale, yScale: yScale, enabled: true, overlay: overlay, oColumns: columns, rScale: rScale, projection: projection, interactionOverflow: interactionOverflow, disableCanvasInteraction: disableCanvasInteraction, renderPipeline: renderPipeline, advancedSettings: interactionSettings }),
                annotationLayer)),
        afterElements && (React.createElement(SpanOrDiv, { span: useSpans, className: `${name} frame-after-elements` }, afterElements))));
}

const // simple heuristics to check if the tooltip container exceeds the viewport
// if so, capture the suggested offset
checkPosition = ({ tooltipRef, tooltipContentArgs, changeTooltipContentArgsCurrent, changeTooltipContainerInitialDimensions, changeCollision }) => {
    const tooltipContainerInitialDimensions = tooltipRef === null || tooltipRef === void 0 ? void 0 : tooltipRef.getBoundingClientRect();
    if (!tooltipContainerInitialDimensions) {
        return;
    }
    const { right, left, top, bottom, width, height } = tooltipContainerInitialDimensions;
    // flags to indicate whether the data point + tooltip dimension collides with the viewport
    // on each of the 4 directions/sides
    let collision = {
        left: false,
        right: false,
        top: false,
        bottom: false
    };
    if (left + width > window.innerWidth) {
        collision.right = true;
    }
    if (left - width < 0) {
        collision.left = true;
    }
    if (top + height > window.innerHeight) {
        collision.bottom = true;
    }
    if (top - height < 0) {
        collision.top = true;
    }
    changeTooltipContentArgsCurrent();
    changeTooltipContainerInitialDimensions(tooltipContainerInitialDimensions);
    changeCollision(collision);
};
function TooltipPositioner(props) {
    const { tooltipContent, tooltipContentArgs } = props;
    const [collision, changeCollision] = useState(null);
    const [tooltipContainerInitialDimensions, changeTooltipContainerInitialDimensions] = useState(null);
    const [tooltipContentArgsCurrent, changeTooltipContentArgsCurrent] = useState(null);
    const [tooltipNode, setTooltipNode] = useState(null);
    const tooltipNodeRef = useCallback((node) => {
        setTooltipNode(node);
        checkPosition({
            tooltipRef: node,
            tooltipContentArgs,
            changeTooltipContentArgsCurrent,
            changeTooltipContainerInitialDimensions,
            changeCollision
        });
    }, []);
    useEffect(() => {
        // if new args, reset collision state
        changeCollision(null);
        changeTooltipContainerInitialDimensions(null);
    }, [JSON.stringify(tooltipContentArgs)]);
    useEffect(() => {
        if (tooltipNode && !collision) {
            checkPosition({
                tooltipRef: tooltipNode,
                tooltipContentArgs,
                changeTooltipContentArgsCurrent,
                changeTooltipContainerInitialDimensions,
                changeCollision
            });
        }
    }, [tooltipNode, collision]);
    const containerStyle = {
        //to handle issue when the tooltip content has margins set by client,
        // which results in the tooltip container having smaller height,
        // which in turn causes the css transform to be inaccurate
        // (ref: https://www.w3.org/TR/css-box-3/#collapsing-margins)
        overflow: "hidden",
        opacity: collision && tooltipContentArgsCurrent === tooltipContentArgs ? 1 : 0
    };
    const tooltipContainerClasses = collision
        ? [
            "tooltip-container",
            "tooltip-collision-evaluated",
            collision && collision.top && "collision-top",
            collision && collision.bottom && "collision-bottom",
            collision && collision.right && "collision-right",
            collision && collision.left && "collision-left"
        ]
            .filter((el) => el)
            .join(" ")
        : "tooltip-container";
    const tooltipContainerAttributes = {
        offset: { x: 0, y: 0 },
        tooltipContainerInitialDimensions
    };
    return (React.createElement("div", { ref: tooltipNodeRef, style: containerStyle, className: tooltipContainerClasses, "data-testid": "TooltipPositioner" }, tooltipContent(Object.assign(Object.assign({}, tooltipContentArgs), { tooltipContainerAttributes }))));
}

const circleEnclosure = ({ d, i, circle }) => {
    const { padding = 2, radiusPadding = padding, label } = d;
    const noteData = Object.assign({
        dx: 0,
        dy: 0,
        note: { label },
        connector: { end: "arrow" }
    }, d, {
        coordinates: undefined,
        x: circle.x,
        y: circle.y,
        type: AnnotationCalloutCircle,
        subject: {
            radius: circle.r,
            radiusPadding
        },
        i
    });
    if (noteData.rp) {
        switch (noteData.rp) {
            case "top":
                noteData.dx = 0;
                noteData.dy = -circle.r - noteData.rd;
                break;
            case "bottom":
                noteData.dx = 0;
                noteData.dy = circle.r + noteData.rd;
                break;
            case "left":
                noteData.dx = -circle.r - noteData.rd;
                noteData.dy = 0;
                break;
            default:
                noteData.dx = circle.r + noteData.rd;
                noteData.dy = 0;
        }
    }
    //TODO: Support .ra (setting angle)
    return React.createElement(SemioticAnnotation, { key: d.key || `annotation-${i}`, noteData: noteData });
};
const rectangleEnclosure = ({ bboxNodes, d, i }) => {
    const { padding = 0, dx = -25, dy = -25, label } = d;
    const bbox = [
        [
            Math.min(...bboxNodes.map((p) => p.x0)) - padding,
            Math.min(...bboxNodes.map((p) => p.y0)) - padding
        ],
        [
            Math.max(...bboxNodes.map((p) => p.x1)) + padding,
            Math.max(...bboxNodes.map((p) => p.y1)) + padding
        ]
    ];
    const noteData = Object.assign({
        dx: dx,
        dy: dy,
        note: { label },
        connector: { end: "arrow" }
    }, d, {
        type: AnnotationCalloutRect,
        x: bbox[0][0],
        y: bbox[0][1],
        subject: {
            width: bbox[1][0] - bbox[0][0],
            height: bbox[1][1] - bbox[0][1]
        }
    });
    return React.createElement(SemioticAnnotation, { key: d.key || `annotation-${i}`, noteData: noteData });
};
const hullEnclosure = ({ points, d, i }) => {
    const { color = "black", dx = -25, dy = -25, label, padding = 10, buffer = padding, strokeWidth = 10 } = d;
    const hullPoints = polygonHull(points);
    const offset = new Offset();
    const bufferedHull = offset
        .data([...hullPoints, hullPoints[0]])
        .margin(buffer)[0];
    const hullD = `M${bufferedHull.map((d) => d.join(",")).join("L")}Z`;
    const firstCoord = bufferedHull[0];
    const { nx = firstCoord[0] + dx, ny = firstCoord[1] + dy } = d;
    const closestCoordinates = bufferedHull.reduce((p, c) => {
        if (Math.hypot(nx - p[0], ny - p[1]) > Math.hypot(nx - c[0], ny - c[1])) {
            p = c;
        }
        return p;
    }, firstCoord);
    const noteData = Object.assign({
        dx: dx,
        dy: dy,
        note: { label },
        connector: { end: "arrow" }
    }, d, {
        type: AnnotationCalloutCustom,
        x: closestCoordinates[0],
        y: closestCoordinates[1],
        subject: {
            custom: [
                React.createElement("path", { key: "hull-drawing", d: hullD, strokeWidth: strokeWidth, strokeMiterlimit: "10", strokeLinejoin: "miter", strokeLinecap: "butt", fill: "none", stroke: color, transform: `translate(${-closestCoordinates[0]},${-closestCoordinates[1]})` })
            ],
            customID: "hull-annotation"
        }
    });
    return React.createElement(SemioticAnnotation, { key: d.key || `annotation-${i}`, noteData: noteData });
};
const desaturationLayer = ({ style = { fill: "white", fillOpacity: 0.5 }, size, i, key }) => (React.createElement("rect", { "data-testid": "desaturation-layer", key: key || `desaturation-${i}`, x: -5, y: -5, width: size[0] + 10, height: size[1] + 10, style: style }));

const pointsAlong = (along) => ({ d, lines, points, xScale, yScale, pointStyle }) => {
    const alongScale = along === "x" ? xScale : yScale;
    along = along === "yTop" && d.yMiddle ? "yMiddle" : along;
    if (d && d[along]) {
        const { threshold = 1, r = () => 4, styleFn = pointStyle } = d;
        const foundPoints = [];
        const halfThreshold = threshold / 2;
        if (lines && lines.length > 0) {
            lines.forEach((linedata) => {
                const linePoints = linedata.data.filter((p) => {
                    const pAlong = alongScale(p[along]);
                    const dAlong = alongScale(d[along]);
                    return (pAlong <= dAlong + halfThreshold &&
                        pAlong >= dAlong - halfThreshold);
                });
                foundPoints.push(...linePoints);
            });
        }
        if (points && points.length > 0) {
            const pointPoints = points.filter((p) => {
                const pAlong = alongScale(p[along]);
                const dAlong = alongScale(d[along]);
                return (pAlong <= dAlong + halfThreshold && pAlong >= dAlong - halfThreshold);
            });
            foundPoints.push(...pointPoints);
        }
        return foundPoints.map((p, i) => (React.createElement("circle", { key: `found-circle-${i}`, r: r(p, i), style: styleFn(p, i), cx: xScale(p.xMiddle || p.x), cy: yScale(p.yMiddle || p.yTop) })));
    }
    return null;
};
const svgHorizontalPointsAnnotation = pointsAlong("yTop");
const svgVerticalPointsAnnotation = pointsAlong("x");
const svgHighlight = ({ d, i, points = { data: [] }, lines = { data: [], type: {} }, summaries = { data: [] }, idAccessor, xScale, yScale, xyFrameRender, defined }) => {
    let dID;
    const baseID = idAccessor(Object.assign(Object.assign({}, d), d.data), i);
    if (baseID !== undefined) {
        dID = baseID;
    }
    else if (d.parentLine && idAccessor(d.parentLine, i) !== undefined) {
        dID = idAccessor(d.parentLine, i);
    }
    else if (d.parentSummary && idAccessor(d.parentSummary, i) !== undefined) {
        dID = idAccessor(d.parentSummary, i);
    }
    const foundPoints = points.data
        .filter((p, q) => idAccessor(Object.assign(Object.assign({}, p), p.data), q) === dID)
        .map((p, q) => {
        const baseStyle = xyFrameRender.points.styleFn(Object.assign(Object.assign({}, p), p.data));
        const highlightStyle = typeof d.style === "function"
            ? d.style(Object.assign(Object.assign({}, p), p.data), q)
            : d.style || {};
        return (React.createElement("circle", { key: `highlight-point-${q}`, cx: xScale(p.x), cy: yScale(p.y), r: 5, fill: "none", stroke: "black", strokeWidth: 2, style: Object.assign(Object.assign({}, baseStyle), highlightStyle), className: `highlight-annotation ${(d.class &&
                typeof d.class === "function" &&
                d.class(Object.assign(Object.assign({}, p), p.data), q)) ||
                (d.class && d.class) ||
                ""}` }));
    });
    const lineGenerator = area()
        .x((p) => xScale(p.x))
        .y0((p) => yScale(p.yBottom))
        .y1((p) => yScale(p.yTop));
    const interpolatorSetting = lines.type.interpolator || lines.type.curve;
    const actualInterpolator = typeof interpolatorSetting === "string"
        ? curveHash[interpolatorSetting]
        : interpolatorSetting;
    if (actualInterpolator) {
        lineGenerator.curve(actualInterpolator);
    }
    if (defined) {
        lineGenerator.defined((p, q) => defined(p.data, q));
    }
    const foundLines = lines.data
        .filter((p, q) => idAccessor(p, q) === dID)
        .map((p, q) => {
        const baseStyle = xyFrameRender.lines.styleFn(p, q);
        const highlightStyle = typeof d.style === "function" ? d.style(p, q) : d.style || {};
        return (React.createElement("path", { className: `highlight-annotation ${(d.class && typeof d.class === "function" && d.class(p, q)) ||
                (d.class && d.class) ||
                ""}`, key: `highlight-summary-${q}`, d: lineGenerator(p.data), fill: "none", stroke: "black", strokeWidth: 1, style: Object.assign(Object.assign({}, baseStyle), highlightStyle) }));
    });
    const foundSummaries = summaries.data
        .filter((p, q) => idAccessor(p, q) === dID)
        .map((p, q) => {
        const baseStyle = xyFrameRender.summaries.styleFn(p, q);
        const highlightStyle = typeof d.style === "function" ? d.style(p, q) : d.style || {};
        return (React.createElement("path", { className: `highlight-annotation ${(d.class && typeof d.class === "function" && d.class(p, q)) ||
                (d.class && d.class) ||
                ""}`, key: `highlight-summary-${q}`, d: `M${p.coordinates.join("L")}`, fill: "none", stroke: "black", strokeWidth: 1, style: Object.assign(Object.assign({}, baseStyle), highlightStyle) }));
    });
    return [...foundSummaries, ...foundLines, ...foundPoints];
};
const svgXYAnnotation = ({ screenCoordinates, i, d }) => {
    let inlineStyle;
    if (d.color)
        inlineStyle = { fill: d.color };
    const laLine = (React.createElement(SemioticMark, { className: `annotation ${d.type} ${d.className || ""} `, key: `annotationpoint${i}`, markType: "circle", cx: screenCoordinates[0], cy: screenCoordinates[1], style: inlineStyle, fill: "none", stroke: "black", r: 5 }));
    let laLabel;
    if (d.type === "xy") {
        laLabel = (React.createElement(SemioticMark, { markType: "text", key: `${d.label}annotationtext${i}`, x: screenCoordinates[0], y: 10 + screenCoordinates[1], "data-testid": "annotation-xy-label", className: `annotation annotation-xy-label ${d.className || ""} ` }, d.label));
    }
    return [laLine, laLabel];
};
const basicReactAnnotation = ({ screenCoordinates, d, i }) => {
    const noteData = Object.assign({
        dx: 0,
        dy: 0,
        note: { label: d.label, orientation: d.orientation, align: d.align },
        connector: { end: "arrow" }
    }, d, {
        type: d.type,
        screenCoordinates,
        i
    });
    noteData.x = noteData.fixedX ? noteData.fixedX : screenCoordinates[0];
    noteData.y = noteData.fixedY ? noteData.fixedY : screenCoordinates[1];
    return React.createElement(SemioticAnnotation, { key: d.key || `annotation-${i}`, noteData: noteData });
};
const svgXAnnotation = ({ screenCoordinates, d, i, adjustedSize }) => {
    const noteData = Object.assign({
        dx: 50,
        dy: 20,
        y: 0,
        note: { label: d.label },
        connector: { end: "arrow" }
    }, d, {
        type: AnnotationXYThreshold,
        x: screenCoordinates[0],
        subject: {
            x: screenCoordinates[0],
            y1: 0,
            y2: adjustedSize[1]
        },
        i
    });
    return React.createElement(SemioticAnnotation, { key: d.key || `annotation-${i}`, noteData: noteData });
};
const svgYAnnotation = ({ screenCoordinates, d, i, adjustedSize, adjustedPosition }) => {
    const xPosition = i * 25;
    const noteData = Object.assign({
        dx: 50,
        dy: -20,
        x: xPosition,
        note: { label: d.label },
        connector: { end: "arrow" }
    }, d, {
        type: AnnotationXYThreshold,
        y: screenCoordinates[1],
        subject: {
            y: screenCoordinates[1],
            x1: 0,
            x2: adjustedSize[0] + adjustedPosition[0]
        },
        i
    });
    return React.createElement(SemioticAnnotation, { key: d.key || `annotation-${i}`, noteData: noteData });
};
const svgBoundsAnnotation = ({ d, i, adjustedSize, xAccessor, yAccessor, xScale, yScale }) => {
    const startXValue = findFirstAccessorValue(xAccessor, d.bounds[0]);
    const startYValue = findFirstAccessorValue(yAccessor, d.bounds[0]);
    const endXValue = findFirstAccessorValue(xAccessor, d.bounds[1]);
    const endYValue = findFirstAccessorValue(yAccessor, d.bounds[1]);
    const x0Position = startXValue ? xScale(startXValue) : 0;
    const y0Position = startYValue ? yScale(startYValue) : adjustedSize[1];
    const x1Position = endXValue ? xScale(endXValue) : adjustedSize[0];
    const y1Position = endYValue ? yScale(endYValue) : 0;
    const noteData = Object.assign({
        dx: 250,
        dy: -20,
        note: { label: d.label },
        connector: { end: "arrow" }
    }, d, {
        type: AnnotationCalloutRect,
        x: Math.min(x0Position, x1Position),
        y: Math.min(y0Position, y1Position),
        subject: {
            width: Math.abs(x1Position - x0Position),
            height: Math.abs(y0Position - y1Position)
        },
        i
    });
    return React.createElement(SemioticAnnotation, { key: d.key || `annotation-${i}`, noteData: noteData });
};
const svgLineAnnotation = ({ d, i, screenCoordinates }) => {
    const lineGenerator = line()
        .x((p) => p[0])
        .y((p) => p[1]);
    const lineD = lineGenerator(screenCoordinates);
    const laLine = (React.createElement(SemioticMark, { key: `${d.label}annotationline${i}`, markType: "path", d: lineD, className: `annotation annotation-line ${d.className || ""} ` }));
    const laLabel = (React.createElement(SemioticMark, { markType: "text", key: `${d.label}annotationlinetext${i}`, x: (screenCoordinates[0][0] + screenCoordinates[1][0]) / 2, y: (screenCoordinates[0][1] + screenCoordinates[1][1]) / 2, className: `annotation annotation-line-label ${d.className || ""} ` }, d.label));
    return [laLine, laLabel];
};
const svgAreaAnnotation = ({ d, i, xScale, xAccessor, yScale, yAccessor, annotationLayer }) => {
    const mappedCoordinates = `M${d.coordinates
        .map((p) => [
        xScale(findFirstAccessorValue(xAccessor, p)),
        yScale(findFirstAccessorValue(yAccessor, p))
    ])
        .join("L")}Z`;
    const xBounds = extent$1(d.coordinates.map((p) => xScale(findFirstAccessorValue(xAccessor, p))));
    const yBounds = extent$1(d.coordinates.map((p) => yScale(findFirstAccessorValue(yAccessor, p))));
    const xCenter = (xBounds[0] + xBounds[1]) / 2;
    const yCenter = (yBounds[0] + yBounds[1]) / 2;
    const laLine = (React.createElement(SemioticMark, { key: `${d.label}-annotation-area-${i}`, markType: "path", d: mappedCoordinates, className: `annotation annotation-area ${d.className || ""} ` }));
    const laLabel = (React.createElement(SemioticMark, { markType: "text", key: `${d.label}-annotationtext-${i}`, x: xCenter, y: yCenter, transform: `translate(${annotationLayer.position})`, className: `annotation annotation-area-label ${d.className || ""} `, style: { textAnchor: "middle" } }, d.label));
    return [laLine, laLabel];
};
const htmlTooltipAnnotation = ({ content, screenCoordinates, i, d, useSpans }) => {
    //To string because React gives a DOM error if it gets a date
    return (React.createElement(SpanOrDiv, { span: useSpans, key: `xylabel-${i}`, className: `annotation annotation-xy-label ${d.className || ""} `, style: {
            position: "absolute",
            top: `${screenCoordinates[1]}px`,
            left: `${screenCoordinates[0]}px`
        } }, content));
};
const svgRectEncloseAnnotation = ({ d, i, screenCoordinates }) => {
    const bboxNodes = screenCoordinates.map((p) => {
        return {
            x0: (p.x0 = p[0]),
            x1: (p.x1 = p[0]),
            y0: (p.y0 = p[1]),
            y1: (p.y1 = p[1])
        };
    });
    return rectangleEnclosure({ bboxNodes, d, i });
};
const svgEncloseAnnotation = ({ screenCoordinates, d, i }) => {
    const circle = packEnclose(screenCoordinates.map((p) => ({ x: p[0], y: p[1], r: 2 })));
    return circleEnclosure({ d, circle, i });
};
const svgHullEncloseAnnotation = ({ screenCoordinates, d, i }) => {
    return hullEnclosure({ points: screenCoordinates, d, i });
};

const extentValue = extent => (extent && extent.extent) || (Array.isArray(extent) && extent) || [];

const baseDefinedFunction = () => true;
const whichPointsHashY = {
    top: projectedYTop,
    bottom: projectedYBottom,
    orphan: projectedY
};
const whichPointsHashX = {
    top: projectedXTop,
    bottom: projectedXBottom,
    orphan: projectedX
};
const builtInTransformations = {
    stackedarea: stackedArea,
    "stackedarea-invert": stackedArea,
    stackedpercent: stackedArea,
    "stackedpercent-invert": stackedArea,
    linepercent: stackedArea,
    difference: differenceLine,
    bumparea: bumpChart,
    bumpline: bumpChart,
    "bumparea-invert": bumpChart,
    line: lineChart,
    area: lineChart,
    cumulative: cumulativeLine,
    "cumulative-reverse": cumulativeLine
};
function stringToFn(accessor, defaultAccessor, raw) {
    if (!accessor && defaultAccessor) {
        return defaultAccessor;
    }
    else if (typeof accessor === "object") {
        return () => accessor;
    }
    else if (accessor instanceof Function) {
        return accessor;
    }
    else if (raw === true) {
        const castAccessor = accessor;
        return () => castAccessor;
    }
    else if (typeof accessor === "string") {
        return (d) => (d ? d[accessor] : undefined);
    }
    return () => undefined;
}
function stringToArrayFn(accessor, defaultAccessor, raw) {
    if (accessor === undefined) {
        return [stringToFn(undefined, defaultAccessor, raw)];
    }
    let arrayOfAccessors = [];
    if (Array.isArray(accessor)) {
        arrayOfAccessors = accessor;
    }
    else {
        arrayOfAccessors = [accessor];
    }
    return arrayOfAccessors.map((a) => stringToFn(a, defaultAccessor, raw));
}
const calculateDataExtent = ({ lineDataAccessor, xAccessor, yAccessor, summaries, points, lines, lineType, showLinePoints, showSummaryPoints, xExtent, yExtent, invertX, invertY, summaryDataAccessor, summaryType, adjustedSize: size, margin, baseMarkProps, summaryStyleFn, summaryClassFn, summaryRenderModeFn, chartSize, filterRenderedLines, filterRenderedSummaries, filterRenderedPoints, defined = baseDefinedFunction, annotations = [] }) => {
    let fullDataset = [];
    let initialProjectedLines = [];
    let projectedPoints = [], projectedLines = [], projectedSummaries = [];
    if (points) {
        xAccessor.forEach((actualXAccessor, xIndex) => {
            yAccessor.forEach((actualYAccessor, yIndex) => {
                let i = 0;
                for (const d of points) {
                    const x = actualXAccessor(d, i);
                    const y = actualYAccessor(d, i);
                    const projectedPoint = { x, y, data: d, xIndex, yIndex };
                    if (Array.isArray(y)) {
                        projectedPoint[projectedYBottom] = Math.min(...y);
                        projectedPoint[projectedYTop] = Math.max(...y);
                        projectedPoint[projectedYMiddle] =
                            (projectedPoint[projectedYBottom] +
                                projectedPoint[projectedYTop]) /
                                2;
                    }
                    if (Array.isArray(x)) {
                        projectedPoint[projectedXBottom] = Math.min(...x);
                        projectedPoint[projectedXTop] = Math.max(...x);
                        projectedPoint[projectedXMiddle] =
                            (projectedPoint[projectedXBottom] +
                                projectedPoint[projectedXTop]) /
                                2;
                    }
                    projectedPoints.push(projectedPoint);
                    i++;
                }
            });
        });
        fullDataset = [
            ...projectedPoints.map((d) => (Object.assign(Object.assign({}, d), { [projectedX]: d[projectedXTop] || d[projectedXBottom] || d.x, [projectedY]: d[projectedYTop] || d[projectedYBottom] || d.y })))
        ];
    }
    if (lines) {
        initialProjectedLines = projectLineData({
            data: lines,
            lineDataAccessor,
            xProp: projectedX,
            xPropTop: projectedXTop,
            xPropBottom: projectedXBottom,
            yProp: projectedY,
            yPropTop: projectedYTop,
            yPropBottom: projectedYBottom,
            xAccessor,
            yAccessor
        });
        const optionsObject = {
            xProp: projectedX,
            yProp: projectedY,
            yPropMiddle: projectedYMiddle,
            yPropTop: projectedYTop,
            yPropBottom: projectedYBottom,
            xPropMiddle: projectedXMiddle,
            xPropTop: projectedXTop,
            xPropBottom: projectedXBottom
        };
        projectedLines = lineTransformation(lineType, optionsObject)(initialProjectedLines);
        projectedLines.forEach((d) => {
            fullDataset = [
                ...fullDataset,
                ...d.data
                    .filter((p, q) => defined(Object.assign({}, p.data, p), q))
                    .map((p) => {
                    const mappedP = {
                        parentLine: d,
                        y: p.y,
                        x: p.x,
                        xTop: p.xTop,
                        xMiddle: p.xMiddle,
                        xBottom: p.xBottom,
                        yTop: p.yTop,
                        yMiddle: p.yMiddle,
                        yBottom: p.yBottom,
                        data: p.data
                    };
                    if (p.percent) {
                        mappedP.percent = p.percent;
                    }
                    return mappedP;
                })
            ];
        });
        if (showLinePoints) {
            const whichPointsX = showLinePoints === true
                ? projectedXMiddle
                : whichPointsHashX[showLinePoints];
            const whichPointsY = showLinePoints === true
                ? projectedYMiddle
                : whichPointsHashY[showLinePoints];
            projectedLines.forEach((d) => {
                d.data
                    .filter((p, q) => {
                    const isDefined = defined(Object.assign({}, p.data, p));
                    if (isDefined) {
                        if (showLinePoints === "orphan") {
                            const prePoint = d.data[q - 1];
                            const postPoint = d.data[q + 1];
                            if ((!prePoint ||
                                !defined(Object.assign({}, prePoint.data, prePoint))) &&
                                (!postPoint ||
                                    !defined(Object.assign({}, postPoint.data, postPoint)))) {
                                return true;
                            }
                            else {
                                return false;
                            }
                        }
                        else {
                            return true;
                        }
                    }
                    else {
                        return false;
                    }
                })
                    .forEach((p) => {
                    projectedPoints.push(Object.assign(Object.assign({}, p), { parentLine: d, [projectedY]: p[whichPointsY] !== undefined
                            ? p[whichPointsY]
                            : p[projectedYMiddle] !== undefined
                                ? p[projectedYMiddle]
                                : p[projectedYBottom] !== undefined
                                    ? p[projectedYBottom]
                                    : p.y, [projectedX]: p[whichPointsX] !== undefined
                            ? p[whichPointsX]
                            : p[projectedXMiddle] !== undefined
                                ? p[projectedXMiddle]
                                : p[projectedXBottom] !== undefined
                                    ? p[projectedXBottom]
                                    : p.y }));
                });
            });
        }
    }
    if (summaries) {
        projectedSummaries = projectSummaryData({
            data: summaries,
            summaryDataAccessor,
            xAccessor,
            yAccessor
        });
        projectedSummaries.forEach((d) => {
            const baseData = d._baseData;
            if (d._xyfCoordinates.length > 0 && d._xyfCoordinates[0][0][0]) {
                d._xyfCoordinates[0].forEach((multi) => {
                    if (Array.isArray(multi)) {
                        multi
                            .map((p, q) => Object.assign({ parentSummary: d }, baseData[q], {
                            [projectedX]: p[0],
                            [projectedY]: p[1]
                        }))
                            .forEach((e) => {
                            if (showSummaryPoints) {
                                projectedPoints.push(Object.assign(Object.assign({ x: 0 }, e), { [projectedY]: e[projectedYTop] || e[projectedYBottom] || e[projectedY] }));
                            }
                            fullDataset.push(Object.assign({ x: 0, y: 0 }, e));
                        });
                    }
                });
            }
            else if (d._xyfCoordinates.length > 0) {
                if (Array.isArray(d._xyfCoordinates)) {
                    const coordArray = d._xyfCoordinates;
                    coordArray
                        .map((p, q) => (Object.assign(Object.assign({ parentSummary: d }, baseData[q]), { [projectedX]: p[0], [projectedY]: p[1] })))
                        .forEach((e) => {
                        if (showSummaryPoints) {
                            projectedPoints.push(Object.assign(Object.assign({ x: 0 }, e), { [projectedY]: e[projectedYTop] || e[projectedYBottom] || e[projectedY] }));
                        }
                        fullDataset.push(Object.assign({ x: 0, y: 0 }, e));
                    });
                }
            }
        });
    }
    let suitableXAnnotations = [];
    let suitableYAnnotations = [];
    if (xExtent &&
        !Array.isArray(xExtent) &&
        xExtent.includeAnnotations === true) {
        xAccessor.forEach((actualXAccessor) => {
            annotations.forEach((annotation, annotationIndex) => {
                const x = actualXAccessor(annotation, annotationIndex);
                if (isFinite(x)) {
                    suitableXAnnotations.push({
                        [projectedX]: x
                    });
                }
            });
        });
    }
    if (yExtent &&
        !Array.isArray(yExtent) &&
        yExtent.includeAnnotations === true) {
        yAccessor.forEach((actualYAccessor) => {
            annotations.forEach((annotation, annotationIndex) => {
                const y = actualYAccessor(annotation, annotationIndex);
                if (isFinite(y)) {
                    suitableYAnnotations.push({
                        [projectedY]: y
                    });
                }
            });
        });
    }
    const dataForXExtent = [...fullDataset, ...suitableXAnnotations];
    const dataForYExtent = [...fullDataset, ...suitableYAnnotations];
    const calculatedXExtent = [
        min(dataForXExtent.map((d) => d[projectedXBottom] === undefined
            ? d[projectedX]
            : Math.min(d[projectedXTop], d[projectedXBottom]))),
        max(dataForXExtent.map((d) => d[projectedXTop] === undefined
            ? d[projectedX]
            : Math.max(d[projectedXBottom], d[projectedXTop])))
    ];
    const calculatedYExtent = [
        min(dataForYExtent.map((d) => d[projectedYBottom] === undefined
            ? d[projectedY]
            : Math.min(d[projectedYTop], d[projectedYBottom]))),
        max(dataForYExtent.map((d) => d[projectedYTop] === undefined
            ? d[projectedY]
            : Math.max(d[projectedYBottom], d[projectedYTop])))
    ];
    const actualXExtent = extentValue(xExtent);
    const actualYExtent = extentValue(yExtent);
    const xMin = actualXExtent && actualXExtent[0] !== undefined
        ? actualXExtent[0]
        : calculatedXExtent[0];
    const xMax = actualXExtent && actualXExtent[1] !== undefined
        ? actualXExtent[1]
        : calculatedXExtent[1];
    const yMin = actualYExtent && actualYExtent[0] !== undefined
        ? actualYExtent[0]
        : calculatedYExtent[0];
    const yMax = actualYExtent && actualYExtent[1] !== undefined
        ? actualYExtent[1]
        : calculatedYExtent[1];
    let finalYExtent = [yMin, yMax];
    let finalXExtent = [xMin, xMax];
    if (invertX && !(actualXExtent && actualXExtent.length === 2)) {
        finalXExtent = [finalXExtent[1], finalXExtent[0]];
    }
    if ((lineType.type === "bumpline" || invertY) &&
        !(actualYExtent && actualYExtent.length === 2)) {
        finalYExtent = [finalYExtent[1], finalYExtent[0]];
    }
    if (summaryType.type && summaryType.type === "contour") {
        projectedSummaries = contouring({
            summaryType,
            data: projectedSummaries,
            finalXExtent,
            finalYExtent
        });
    }
    else if (summaryType.type && summaryType.type === "linebounds") {
        projectedSummaries = lineBounding({
            summaryType,
            data: projectedSummaries,
            defined
        });
    }
    else if (summaryType.type && summaryType.type === "hexbin") {
        projectedSummaries = hexbinning({
            summaryType,
            data: projectedSummaries[0],
            processedData: summaries && !!summaries[0].processedData,
            preprocess: false,
            finalXExtent,
            finalYExtent,
            size,
            margin,
            baseMarkProps,
            styleFn: summaryStyleFn,
            classFn: summaryClassFn,
            renderFn: summaryRenderModeFn,
            chartSize
        });
        fullDataset = [
            ...projectedSummaries.map((d) => (Object.assign({}, d))),
            ...fullDataset.filter((d) => !d.parentSummary)
        ];
    }
    else if (summaryType.type && summaryType.type === "heatmap") {
        projectedSummaries = heatmapping({
            summaryType,
            data: projectedSummaries[0],
            processedData: summaries && !!summaries[0].processedData,
            preprocess: false,
            finalXExtent,
            finalYExtent,
            size,
            margin,
            baseMarkProps,
            styleFn: summaryStyleFn,
            classFn: summaryClassFn,
            renderFn: summaryRenderModeFn,
            chartSize
        });
        fullDataset = [
            ...projectedSummaries.map((d) => (Object.assign({}, d))),
            ...fullDataset.filter((d) => !d.parentSummary)
        ];
    }
    else if (summaryType.type && summaryType.type === "trendline") {
        projectedSummaries = trendlining({
            summaryType,
            data: projectedSummaries[0],
            preprocess: summaries && !!summaries[0].processedData,
            finalXExtent
        });
        fullDataset = [
            ...projectedSummaries.map((d) => (Object.assign({}, d))),
            ...fullDataset.filter((d) => !d.parentSummary)
        ];
    }
    if (filterRenderedLines) {
        projectedLines = projectedLines.filter(filterRenderedLines);
        fullDataset = fullDataset.filter((d, i) => {
            return !d.parentLine || filterRenderedLines(d.parentLine, i, []);
        });
    }
    if (filterRenderedPoints) {
        fullDataset = fullDataset.filter(filterRenderedPoints);
    }
    if (filterRenderedSummaries) {
        projectedSummaries = projectedSummaries.filter(filterRenderedSummaries);
        fullDataset = fullDataset.filter((d, i) => {
            return !d.parentSummary || filterRenderedSummaries(d.parentSummary, i, []);
        });
    }
    return {
        xExtent: finalXExtent,
        yExtent: finalYExtent,
        projectedLines,
        projectedPoints,
        projectedSummaries,
        fullDataset,
        calculatedXExtent,
        calculatedYExtent
    };
};
const differenceCatch = (olineType, data) => {
    if (!builtInTransformations[olineType] ||
        (olineType === "difference" && data.length !== 2)) {
        return "line";
    }
    return olineType;
};
function lineTransformation(lineType, options) {
    return (data) => builtInTransformations[differenceCatch(lineType.type, data)](Object.assign(Object.assign(Object.assign({}, lineType), options), { data }));
}

const screenScales = ({ xExtent, yExtent, adjustedSize, xScaleType, yScaleType }) => {
    const xDomain = [0, adjustedSize[0]];
    const yDomain = [adjustedSize[1], 0];
    const xScale = xScaleType;
    const yScale = yScaleType;
    if (xScaleType.domain) {
        xScaleType.domain(xExtent);
    }
    if (yScaleType.domain) {
        yScaleType.domain(yExtent);
    }
    xScaleType.range(xDomain);
    yScaleType.range(yDomain);
    return { xScale, yScale };
};
const naturalLanguageLineType = {
    line: { items: "line", chart: "line chart" },
    area: { items: "summary", chart: "summary chart" },
    summary: { items: "summary", chart: "summary chart" },
    cumulative: { items: "line", chart: "cumulative chart" },
    "cumulative-reverse": { items: "line", chart: "cumulative chart" },
    linepercent: { items: "line", chart: "line chart" },
    stackedarea: { items: "stacked area", chart: "stacked area chart" },
    "stackedarea-invert": { items: "stacked area", chart: "stacked area chart" },
    stackedpercent: { items: "stacked area", chart: "stacked area chart" },
    "stackedpercent-invert": {
        items: "stacked area",
        chart: "stacked area chart"
    },
    bumparea: { items: "ranked area", chart: "ranked area chart" },
    "bumparea-invert": { items: "ranked area", chart: "ranked area chart" },
    bumpline: { items: "ranked line", chart: "ranked line chart" },
    difference: {
        items: "line",
        chart: "difference chart"
    }
};
const emptyObjectReturnFunction = () => ({});
const emptyStringReturnFunction = () => "";
const calculateXYFrame = (currentProps, prevState, updateData) => {
    const { legend, lines, lineClass, pointStyle, pointRenderMode, pointClass, summaryClass, canvasLines, canvasPoints, canvasSummaries, defined, size, renderKey, lineType, summaryType, customLineMark, customPointMark, customSummaryMark, summaryStyle, summaryRenderMode, lineStyle, lineRenderMode, xExtent: baseXExtent, yExtent: baseYExtent, title, xScaleType: baseXScaleType = scaleLinear(), yScaleType: baseYScaleType = scaleLinear(), lineIDAccessor, invertX, invertY, showLinePoints, showSummaryPoints, points, lineDataAccessor, summaryDataAccessor, yAccessor, xAccessor, useSummariesAsInteractionLayer, baseMarkProps, filterRenderedLines, filterRenderedSummaries, filterRenderedPoints, annotations } = currentProps;
    let { projectedLines, projectedPoints, projectedSummaries, summaries, fullDataset } = currentProps;
    if (summaryType && points && !summaries) {
        summaries = [{ coordinates: points }];
    }
    else if (summaryType &&
        summaryType.type === "linebounds" &&
        lines &&
        !summaries) {
        summaries = lines;
    }
    const castXScaleType = baseXScaleType;
    const xScaleType = baseXScaleType.domain ? baseXScaleType : castXScaleType();
    const castYScaleType = baseYScaleType;
    const yScaleType = baseYScaleType.domain ? baseYScaleType : castYScaleType();
    const annotatedSettings = {
        xAccessor: stringToArrayFn(xAccessor, (d) => d[0]),
        yAccessor: stringToArrayFn(yAccessor, (d) => d[1]),
        summaryDataAccessor: stringToArrayFn(summaryDataAccessor, (d) => (Array.isArray(d) ? d : d.coordinates)),
        lineDataAccessor: stringToArrayFn(lineDataAccessor, (d) => (Array.isArray(d) ? d : d.coordinates)),
        renderKeyFn: stringToFn(renderKey, (d, i) => `line-${i}`, true),
        lineType: objectifyType(lineType),
        summaryType: objectifyType(summaryType),
        lineIDAccessor: stringToFn(lineIDAccessor, (l) => l.semioticLineID),
        summaries: !summaries || (Array.isArray(summaries) && summaries.length === 0)
            ? undefined
            : !Array.isArray(summaries)
                ? [summaries]
                : !summaryDataAccessor && !summaries[0].coordinates
                    ? [{ coordinates: summaries }]
                    : summaries,
        lines: !lines || (Array.isArray(lines) && lines.length === 0)
            ? undefined
            : !Array.isArray(lines)
                ? [lines]
                : !lineDataAccessor && !lines[0].coordinates
                    ? [{ coordinates: lines }]
                    : lines,
        title: typeof title === "object" &&
            !React.isValidElement(title) &&
            title !== null
            ? title
            : { title, orient: "top" },
        xExtent: Array.isArray(baseXExtent)
            ? baseXExtent
            : !baseXExtent
                ? undefined
                : baseXExtent.extent,
        yExtent: Array.isArray(baseYExtent)
            ? baseYExtent
            : !baseYExtent
                ? undefined
                : baseYExtent.extent
    };
    if (annotatedSettings.lineType.type === "area") {
        annotatedSettings.lineType.y1 = () => 0;
        annotatedSettings.lineType.simpleLine = false;
    }
    const summaryStyleFn = stringToFn(summaryStyle, emptyObjectReturnFunction, true);
    const summaryClassFn = stringToFn(summaryClass, emptyStringReturnFunction, true);
    const summaryRenderModeFn = stringToFn(summaryRenderMode, undefined, true);
    const generatedAxes = currentProps.axes &&
        currentProps.axes.map((axisFnOrObject) => typeof axisFnOrObject === "function"
            ? axisFnOrObject({ size: currentProps.size })
            : axisFnOrObject);
    const margin = calculateMargin({
        margin: currentProps.margin,
        axes: generatedAxes,
        title: annotatedSettings.title,
        size: currentProps.size
    });
    const { adjustedPosition, adjustedSize } = adjustedPositionSize({
        size: currentProps.size,
        margin
    });
    let calculatedXExtent = [], calculatedYExtent = [], yExtent, xExtent, xExtentSettings, yExtentSettings;
    if (typeof baseXExtent === "object") {
        xExtentSettings = baseXExtent;
    }
    else {
        xExtentSettings = { extent: baseXExtent };
    }
    if (typeof baseYExtent === "object") {
        yExtentSettings = baseYExtent;
    }
    else {
        yExtentSettings = { extent: baseYExtent };
    }
    let xScale, yScale;
    if (updateData ||
        (currentProps.dataVersion &&
            currentProps.dataVersion !== prevState.dataVersion)) {
        //This will always fire at this point because xExtent/yExtent are just defined up there so revisit this logic
        if (!xExtent ||
            !yExtent ||
            !fullDataset ||
            (!projectedLines && !projectedPoints && !projectedSummaries)) {
            ({
                xExtent,
                yExtent,
                projectedLines,
                projectedPoints,
                projectedSummaries,
                fullDataset,
                calculatedXExtent,
                calculatedYExtent
            } = calculateDataExtent({
                lineDataAccessor: annotatedSettings.lineDataAccessor,
                summaryDataAccessor: annotatedSettings.summaryDataAccessor,
                xAccessor: annotatedSettings.xAccessor,
                yAccessor: annotatedSettings.yAccessor,
                lineType: annotatedSettings.lineType,
                summaryType: annotatedSettings.summaryType,
                summaries: annotatedSettings.summaries,
                points,
                lines: annotatedSettings.lines,
                showLinePoints,
                showSummaryPoints,
                xExtent: baseXExtent,
                yExtent: baseYExtent,
                invertX,
                invertY,
                adjustedSize,
                margin,
                baseMarkProps,
                summaryStyleFn,
                summaryClassFn,
                summaryRenderModeFn,
                chartSize: size,
                xScaleType,
                yScaleType,
                defined,
                filterRenderedLines,
                filterRenderedSummaries,
                filterRenderedPoints,
                annotations
            }));
        }
        ({ xScale, yScale } = screenScales({
            xExtent,
            yExtent,
            adjustedSize,
            xScaleType: xScaleType.copy(),
            yScaleType: yScaleType.copy()
        }));
    }
    else {
        ({
            xExtent,
            yExtent,
            projectedLines,
            projectedPoints,
            projectedSummaries,
            fullDataset,
            calculatedXExtent,
            calculatedYExtent
        } = prevState);
        if (adjustedSize[0] === prevState.adjustedSize[0] &&
            adjustedSize[1] === prevState.adjustedSize[1]) {
            xScale = prevState.xScale;
            yScale = prevState.yScale;
        }
        else {
            ({ xScale, yScale } = screenScales({
                xExtent,
                yExtent,
                adjustedSize,
                xScaleType,
                yScaleType
            }));
        }
    }
    xExtent =
        Array.isArray(xExtentSettings.extent) && xExtentSettings.extent.length === 2
            ? xExtentSettings.extent
            : xExtent;
    yExtent =
        Array.isArray(yExtentSettings.extent) && yExtentSettings.extent.length === 2
            ? yExtentSettings.extent
            : yExtent;
    const canvasDrawing = [];
    let axes;
    let axesTickLines;
    const existingBaselines = {};
    if (generatedAxes) {
        axesTickLines = [];
        axes = generatedAxes.map((d, i) => {
            let axisClassname = d.className || "";
            axisClassname += " axis";
            let axisScale = yScale;
            if (existingBaselines[d.orient]) {
                d.baseline = d.baseline || false;
            }
            existingBaselines[d.orient] = true;
            if (d.orient === "top" || d.orient === "bottom") {
                axisClassname += " x";
                axisScale = xScale;
            }
            else {
                axisClassname += " y";
            }
            axisClassname += ` ${d.orient}`;
            let tickValues;
            if (d.tickValues && Array.isArray(d.tickValues)) {
                tickValues = d.tickValues;
            }
            else if (d.tickValues instanceof Function) {
                //otherwise assume a function
                tickValues = d.tickValues(fullDataset, currentProps.size, axisScale);
            }
            const axisSize = [adjustedSize[0], adjustedSize[1]];
            const axisParts = axisPieces({
                padding: d.padding,
                tickValues,
                scale: axisScale,
                ticks: d.ticks,
                orient: d.orient,
                size: axisSize,
                footer: d.footer,
                tickSize: d.tickSize,
                jaggedBase: d.jaggedBase
            });
            const axisTickLines = (React.createElement("g", { key: `axes-tick-lines-${i}`, className: `axis ${axisClassname}` },
                axisLines({
                    axisParts,
                    orient: d.orient,
                    tickLineGenerator: d.tickLineGenerator,
                    baseMarkProps,
                    className: axisClassname,
                    jaggedBase: d.jaggedBase,
                    scale: axisScale,
                    showOutboundTickLines: d.showOutboundTickLines
                }),
                d.baseline === "under" &&
                    baselineGenerator(d.orient, adjustedSize, d.className)));
            axesTickLines.push(axisTickLines);
            return (React.createElement(Axis, Object.assign({}, d, { key: d.key || `axis-${i}`, annotationFunction: d.axisAnnotationFunction, axisParts: axisParts, size: axisSize, margin: margin, tickValues: tickValues, scale: axisScale, className: axisClassname, xyPoints: fullDataset })));
        });
    }
    let legendSettings;
    if (legend) {
        legendSettings = legend === true ? {} : legend;
        if (projectedLines && !legendSettings.legendGroups) {
            const typeString = annotatedSettings.lineType.type;
            const type = typeof typeString === "string" &&
                ["stackedarea", "stackedpercent", "bumparea"].indexOf(typeString) === -1
                ? "line"
                : "fill";
            const legendGroups = [
                {
                    styleFn: currentProps.lineStyle,
                    type,
                    items: projectedLines.map((d) => Object.assign({ label: annotatedSettings.lineIDAccessor(d) }, d))
                }
            ];
            legendSettings.legendGroups = legendGroups;
        }
    }
    const areaAnnotations = [];
    if (annotatedSettings.summaryType.label && projectedSummaries) {
        projectedSummaries.forEach((d, i) => {
            if (d.bounds) {
                const bounds = Array.isArray(d.bounds) ? d.bounds : [d.bounds];
                bounds.forEach((labelBounds) => {
                    const label = typeof annotatedSettings.summaryType.label === "function"
                        ? annotatedSettings.summaryType.label(d)
                        : annotatedSettings.summaryType.label;
                    if (label && label !== null) {
                        const labelPosition = label.position || "center";
                        const labelCenter = [
                            xScale(labelBounds[labelPosition][0]),
                            yScale(labelBounds[labelPosition][1])
                        ] || [xScale(d._xyfCoordinates[0]), yScale(d._xyfCoordinates[1])];
                        const labelContent = label.content || ((p) => p.value || p.id || i);
                        areaAnnotations.push({
                            x: labelCenter[0],
                            y: labelCenter[1],
                            dx: label.dx,
                            dy: label.dy,
                            className: label.className,
                            type: label.type || AnnotationCallout,
                            note: label.note || { title: labelContent(d) },
                            subject: label.subject || { text: labelContent(d) },
                            connector: label.connector
                        });
                    }
                });
            }
        });
    }
    else if (annotatedSettings.summaryType.showSlope &&
        projectedSummaries &&
        projectedSummaries[0]) {
        const firstSummary = projectedSummaries[0];
        const firstPoint = firstSummary._xyfCoordinates[0];
        const finalPoint = firstSummary._xyfCoordinates[firstSummary._xyfCoordinates.length - 1];
        areaAnnotations.push({
            x: firstPoint[0],
            y: firstPoint[1],
            x1: finalPoint[0],
            x2: finalPoint[1],
            type: "trendline-annotation",
            value: firstSummary.value,
            r2: firstSummary.r2
        });
    }
    const lineAriaLabel = annotatedSettings.lineType.type !== undefined &&
        typeof annotatedSettings.lineType.type === "string" &&
        naturalLanguageLineType[annotatedSettings.lineType.type];
    const xyFrameRender = {
        lines: {
            accessibleTransform: (data, i) => (Object.assign(Object.assign({}, data[i].data[data[i].data.length - 1]), { type: "frame-hover" })),
            data: projectedLines,
            styleFn: stringToFn(lineStyle, emptyObjectReturnFunction, true),
            classFn: stringToFn(lineClass, emptyStringReturnFunction, true),
            renderMode: stringToFn(lineRenderMode, undefined, true),
            canvasRender: stringToFn(canvasLines, undefined, true),
            customMark: customLineMark,
            type: annotatedSettings.lineType,
            defined: defined,
            renderKeyFn: annotatedSettings.renderKeyFn,
            ariaLabel: lineAriaLabel,
            axesData: generatedAxes,
            behavior: createLines
        },
        summaries: {
            accessibleTransform: (data, i) => (Object.assign(Object.assign({}, data[i]), { type: "frame-hover" })),
            data: projectedSummaries,
            styleFn: summaryStyleFn,
            classFn: summaryClassFn,
            renderMode: summaryRenderModeFn,
            canvasRender: stringToFn(canvasSummaries, undefined, true),
            customMark: customSummaryMark,
            type: annotatedSettings.summaryType,
            renderKeyFn: annotatedSettings.renderKeyFn,
            behavior: createSummaries
        },
        points: {
            accessibleTransform: (data, i) => (Object.assign({ type: "frame-hover" }, (data[i].data || data[i]))),
            data: projectedPoints,
            styleFn: stringToFn(pointStyle, emptyObjectReturnFunction, true),
            classFn: stringToFn(pointClass, emptyStringReturnFunction, true),
            renderMode: stringToFn(pointRenderMode, undefined, true),
            canvasRender: stringToFn(canvasPoints, undefined, true),
            customMark: customPointMark,
            renderKeyFn: annotatedSettings.renderKeyFn,
            showLinePoints,
            behavior: createPoints
        }
    };
    if (xExtentSettings.onChange &&
        prevState.calculatedXExtent.join(",") !== calculatedXExtent.join(",")) {
        xExtentSettings.onChange(calculatedXExtent);
    }
    if (yExtentSettings.onChange &&
        prevState.calculatedYExtent.join(",") !== calculatedYExtent.join(",")) {
        yExtentSettings.onChange(calculatedYExtent);
    }
    let overlay = undefined;
    if (useSummariesAsInteractionLayer && projectedSummaries) {
        overlay = createSummaries({
            xScale,
            yScale,
            data: projectedSummaries
        }).map((m, i) => (Object.assign(Object.assign({}, m.props), { style: { fillOpacity: 0 }, overlayData: projectedSummaries && projectedSummaries[i] // luckily createSummaries is a map fn
         })));
    }
    return {
        lineData: currentProps.lines,
        pointData: currentProps.points,
        summaryData: currentProps.summaries,
        dataVersion: currentProps.dataVersion,
        projectedLines,
        projectedPoints,
        projectedSummaries,
        canvasDrawing,
        fullDataset,
        adjustedPosition,
        adjustedSize,
        backgroundGraphics: currentProps.backgroundGraphics,
        foregroundGraphics: currentProps.foregroundGraphics,
        axesData: generatedAxes,
        axes,
        axesTickLines,
        renderNumber: prevState.renderNumber + 1,
        xScale,
        yScale,
        xAccessor: annotatedSettings.xAccessor,
        yAccessor: annotatedSettings.yAccessor,
        xExtent: [
            xExtent[0] === undefined ? calculatedXExtent[0] : xExtent[0],
            xExtent[1] === undefined ? calculatedXExtent[1] : xExtent[1]
        ],
        yExtent: [
            yExtent[0] === undefined ? calculatedYExtent[0] : yExtent[0],
            yExtent[1] === undefined ? calculatedYExtent[1] : yExtent[1]
        ],
        calculatedXExtent,
        calculatedYExtent,
        margin,
        legendSettings,
        areaAnnotations,
        xyFrameRender,
        size,
        annotatedSettings,
        overlay,
        props: currentProps
    };
};

//size is a special case and handled checking the actual values in the size array
const sharedChangeProps = [
    "customClickBehavior",
    "customHoverBehavior",
    "customDoubleClickBehavior",
    "hoverAnnotation",
    "name",
    "title",
    "margin",
    "className",
    "additionalDefs",
    "renderKey"
];
const xyFrameDataProps = [
    "lines",
    "points",
    "summaries",
    "xScaleType",
    "yScaleType",
    "xAccessor",
    "yAccessor",
    "lineDataAccessor",
    "areaDataAccessor",
    "summaryDataAccessor",
    "lineType",
    "showLinePoints",
    "showSummaryPoints",
    "defined",
    "summaryType"
];
const xyFrameChangeProps = [
    ...sharedChangeProps,
    ...xyFrameDataProps,
    "axes",
    "xExtent",
    "yExtent",
    "invertX",
    "invertY",
    "defined",
    "lineStyle",
    "pointStyle",
    "summaryStyle",
    "lineClass",
    "pointClass",
    "areaClass",
    "summaryClass",
    "canvasPoints",
    "customPointMark",
    "customLineMark",
    "lineIDAccessor"
];
const orFrameChangeProps = [
    ...sharedChangeProps,
    "pieceHoverAnnotation",
    "summaryHoverAnnotation",
    "data",
    "orient",
    "oScaleType",
    "rScaleType",
    "oExtent",
    "rExtent",
    "invertO",
    "invertR",
    "oAccessor",
    "rAccessor",
    "oPadding",
    "projection",
    "type",
    "summaryType",
    "connectorType",
    "dataAccessor",
    "rBaseline",
    "oSort",
    "dynamicColumnWidth",
    "style",
    "connectorStyle",
    "summaryStyle",
    "summaryPosition",
    "oLabel",
    "axes",
    "renderFn"
];
const networkFrameChangeProps = [
    ...sharedChangeProps,
    "graph",
    "nodes",
    "edges",
    "nodeIDAccessor",
    "sourceAccessor",
    "targetAccessor",
    "nodeSizeAccessor",
    "customNodeIcon",
    "nodeLabels",
    "edgeWidthAccessor",
    "networkType",
    "renderFn",
    "nodeStyle",
    "edgeStyle",
    "edgeType",
    "filterRenderedNodes"
];

// this hooks should only be used for transitioning from class to function components
// this hooks is supposed to replicate behavior of getDerivedStateFromProps
// and some assumptions related to it:
// 1. initial state, defined in "constructor" should not trigger additional computation
// 2. the computation function can opt-out if it thinks that nothing has changed, so the existing state should be used
//
// ideally, the state must be refactored to be function component friendly, so this hooks should become irrelevant
function useDerivedStateFromProps(fn, props, initialState) {
    let prevRef = useRef(initialState);
    let state = useMemo(() => {
        let state = prevRef.current;
        let patch = fn(props, state);
        if (patch != null) {
            return Object.assign(Object.assign({}, state), patch);
        }
        return state;
    }, [props]);
    prevRef.current = state;
    return state;
}

// to properly replicate behavior of componentWillUnmount, the effect must not have dependencies
// which is why we need to store latest props and state values in ref that can be accessible during unmount
// TODO get rid of the behavior and props.onUnmount
function useLegacyUnmountCallback(props, state) {
    let ref = useRef();
    ref.current = [props, state];
    useEffect(() => {
        return () => {
            const [props, state] = ref.current;
            const onUnmount = props.onUnmount;
            if (onUnmount) {
                onUnmount(props, state);
            }
        };
    }, []);
}

let xyframeKey = "";
const chars = "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";
for (let i = 32; i > 0; --i)
    xyframeKey += chars[Math.floor(Math.random() * chars.length)];
const projectedCoordinateNames$1 = {
    y: projectedY,
    x: projectedX,
    yMiddle: projectedYMiddle,
    yTop: projectedYTop,
    yBottom: projectedYBottom,
    xMiddle: projectedXMiddle,
    xTop: projectedXTop,
    xBottom: projectedXBottom
};
const defaultProps$2 = {
    annotations: [],
    foregroundGraphics: undefined,
    size: [500, 500],
    className: "",
    lineType: "line",
    name: "xyframe",
    dataVersion: undefined
};
const XYFrame = React.memo(function XYFrame(allProps) {
    const props = Object.assign(Object.assign({}, defaultProps$2), allProps);
    const baseState = {
        SpanOrDiv: HOCSpanOrDiv(props.useSpans),
        size: [500, 500],
        dataVersion: undefined,
        lineData: undefined,
        pointData: undefined,
        summaryData: undefined,
        projectedLines: undefined,
        projectedPoints: undefined,
        projectedSummaries: undefined,
        fullDataset: [],
        adjustedPosition: [0, 0],
        adjustedSize: [500, 500],
        backgroundGraphics: null,
        foregroundGraphics: null,
        axesData: undefined,
        axes: undefined,
        axesTickLines: undefined,
        renderNumber: 0,
        margin: { top: 0, bottom: 0, left: 0, right: 0 },
        calculatedXExtent: [0, 0],
        calculatedYExtent: [0, 0],
        xAccessor: [(d) => d.x],
        yAccessor: [(d) => d.y],
        xExtent: [0, 0],
        yExtent: [0, 0],
        areaAnnotations: [],
        xScale: scaleLinear(),
        yScale: scaleLinear(),
        title: null,
        legendSettings: undefined,
        xyFrameRender: {},
        canvasDrawing: [],
        annotatedSettings: {
            xAccessor: undefined,
            yAccessor: undefined,
            summaryDataAccessor: undefined,
            lineDataAccessor: undefined,
            renderKeyFn: undefined,
            lineType: undefined,
            summaryType: undefined,
            lineIDAccessor: undefined,
            summaries: undefined,
            lines: undefined,
            title: undefined,
            xExtent: undefined,
            yExtent: undefined
        },
        overlay: undefined,
        props
    };
    const initialState = useMemo(() => (Object.assign(Object.assign({}, baseState), calculateXYFrame(props, baseState, true))), []);
    const state = useDerivedStateFromProps(deriveXYFrameState, props, initialState);
    const { size, className, annotationSettings, annotations, additionalDefs, hoverAnnotation, interaction, customClickBehavior, customHoverBehavior, customDoubleClickBehavior, canvasPostProcess, baseMarkProps, useSpans, canvasSummaries, canvasPoints, canvasLines, afterElements, beforeElements, renderOrder, matte, frameKey, showLinePoints, sketchyRenderingEngine, disableContext, frameRenderOrder, disableCanvasInteraction, interactionSettings, disableProgressiveRendering } = props;
    const { backgroundGraphics, foregroundGraphics, adjustedPosition, adjustedSize, margin, axes, axesTickLines, xScale, yScale, fullDataset, dataVersion, areaAnnotations, legendSettings, xyFrameRender, annotatedSettings, overlay } = state;
    useLegacyUnmountCallback(props, state);
    return (React.createElement(Frame, { name: "xyframe", renderPipeline: xyFrameRender, adjustedPosition: adjustedPosition, size: size, projectedCoordinateNames: projectedCoordinateNames$1, xScale: xScale, yScale: yScale, axes: axes, axesTickLines: axesTickLines, title: annotatedSettings.title, dataVersion: dataVersion, matte: matte, className: className, adjustedSize: adjustedSize, frameKey: frameKey || xyframeKey, additionalDefs: additionalDefs, hoverAnnotation: hoverAnnotation, defaultSVGRule: (args) => defaultXYSVGRule(props, state, args), defaultHTMLRule: (args) => defaultXYHTMLRule(props, state, args), annotations: areaAnnotations.length > 0
            ? [...annotations, ...areaAnnotations]
            : annotations, annotationSettings: annotationSettings, legendSettings: legendSettings, projectedYMiddle: projectedYMiddle, interaction: interaction, customClickBehavior: customClickBehavior, customHoverBehavior: customHoverBehavior, customDoubleClickBehavior: customDoubleClickBehavior, points: fullDataset, showLinePoints: typeof showLinePoints === "string" ? showLinePoints : undefined, margin: margin, backgroundGraphics: backgroundGraphics, foregroundGraphics: foregroundGraphics, beforeElements: beforeElements, afterElements: afterElements, disableContext: disableContext, canvasPostProcess: canvasPostProcess, baseMarkProps: baseMarkProps, useSpans: useSpans, canvasRendering: !!(canvasSummaries || canvasPoints || canvasLines), renderOrder: renderOrder, overlay: overlay, sketchyRenderingEngine: sketchyRenderingEngine, frameRenderOrder: frameRenderOrder, disableCanvasInteraction: disableCanvasInteraction, interactionSettings: interactionSettings, disableProgressiveRendering: disableProgressiveRendering }));
});
function deriveXYFrameState(nextProps, prevState) {
    const { props } = prevState;
    const { xExtent: oldXExtent = [], yExtent: oldYExtent = [], size: oldSize, dataVersion: oldDataVersion, lineData, summaryData, pointData } = prevState;
    const { xExtent: baseNewXExtent, yExtent: baseNewYExtent, size: newSize, dataVersion: newDataVersion, lines: newLines, summaries: newSummaries, points: newPoints } = nextProps;
    const newXExtent = extentValue(baseNewXExtent);
    const newYExtent = extentValue(baseNewYExtent);
    const extentChange = (oldXExtent[0] !== newXExtent[0] && newXExtent[0] !== undefined) ||
        (oldYExtent[0] !== newYExtent[0] && newYExtent[0] !== undefined) ||
        (oldXExtent[1] !== newXExtent[1] && newXExtent[1] !== undefined) ||
        (oldYExtent[1] !== newYExtent[1] && newYExtent[1] !== undefined);
    const lineChange = lineData !== newLines;
    const summaryChange = summaryData !== newSummaries;
    const pointChange = pointData !== newPoints;
    if ((oldDataVersion && oldDataVersion !== newDataVersion) ||
        !prevState.fullDataset) {
        return calculateXYFrame(nextProps, prevState, true);
    }
    else if (lineChange ||
        summaryChange ||
        pointChange ||
        oldSize[0] !== newSize[0] ||
        oldSize[1] !== newSize[1] ||
        extentChange ||
        (!oldDataVersion &&
            xyFrameChangeProps.find((d) => props[d] !== nextProps[d]))) {
        const dataChanged = lineChange ||
            summaryChange ||
            pointChange ||
            extentChange ||
            !!xyFrameChangeProps.find((d) => props[d] !== nextProps[d]);
        return calculateXYFrame(nextProps, prevState, dataChanged);
    }
    return null;
}
function defaultXYSVGRule(props, state, { d: baseD, i, lines, summaries, points, annotationLayer }) {
    const { showLinePoints, defined, margin: baseMargin, size, svgAnnotationRules } = props;
    const { xyFrameRender, xScale, yScale, xAccessor, yAccessor, axesData, annotatedSettings } = state;
    let screenCoordinates = [];
    const idAccessor = annotatedSettings.lineIDAccessor;
    if (baseD.type === "highlight") {
        return svgHighlight({
            d: baseD,
            i,
            idAccessor,
            lines,
            summaries,
            points,
            xScale,
            yScale,
            xyFrameRender,
            defined
        });
    }
    const d = baseD.coordinates
        ? baseD
        : findPointByID({
            point: baseD,
            idAccessor,
            lines,
            xScale,
            projectedX,
            xAccessor
        });
    if (!d)
        return null;
    const margin = calculateMargin({
        margin: baseMargin,
        axes: axesData,
        title: annotatedSettings.title,
        size: size
    });
    const { adjustedPosition, adjustedSize } = adjustedPositionSize({
        size: size,
        margin
    });
    if (!d.coordinates && !d.bounds) {
        screenCoordinates = [
            relativeX({
                point: d,
                projectedXMiddle,
                projectedX,
                xAccessor,
                xScale
            }) || 0,
            relativeY({
                point: d,
                projectedYMiddle,
                projectedY,
                yAccessor,
                yScale,
                showLinePoints
            }) || 0
        ];
    }
    else if (!d.bounds) {
        screenCoordinates = d.coordinates.reduce((coords, p) => {
            const xCoordinate = relativeX({
                point: p,
                projectedXMiddle,
                projectedX,
                xAccessor,
                xScale
            });
            const yCoordinate = relativeY({
                point: p,
                projectedYMiddle,
                projectedY,
                yAccessor,
                yScale
            });
            if (Array.isArray(yCoordinate)) {
                return [
                    ...coords,
                    [xCoordinate, Math.min(...yCoordinate)],
                    [xCoordinate, Math.max(...yCoordinate)]
                ];
            }
            else if (Array.isArray(xCoordinate)) {
                return [
                    ...coords,
                    [Math.min(...xCoordinate), yCoordinate],
                    [Math.max(...xCoordinate), yCoordinate]
                ];
            }
            else {
                return [...coords, [xCoordinate, yCoordinate]];
            }
        }, []);
    }
    const { voronoiHover } = annotationLayer;
    const customSVG = svgAnnotationRules &&
        svgAnnotationRules({
            d,
            i,
            screenCoordinates,
            xScale,
            yScale,
            xAccessor,
            yAccessor,
            xyFrameProps: props,
            xyFrameState: state,
            summaries,
            points,
            lines,
            voronoiHover,
            adjustedPosition,
            adjustedSize,
            annotationLayer
        });
    if (svgAnnotationRules !== undefined && customSVG !== null) {
        return customSVG;
    }
    else if (d.type === "desaturation-layer") {
        return desaturationLayer({
            style: d.style instanceof Function ? d.style(d, i) : d.style,
            size: adjustedSize,
            i,
            key: d.key
        });
    }
    else if (d.type === "xy" || d.type === "frame-hover") {
        return svgXYAnnotation({ d, i, screenCoordinates });
    }
    else if (d.type === "react-annotation" || typeof d.type === "function") {
        return basicReactAnnotation({ d, screenCoordinates, i });
    }
    else if (d.type === "enclose") {
        return svgEncloseAnnotation({ d, screenCoordinates, i });
    }
    else if (d.type === "enclose-rect") {
        return svgRectEncloseAnnotation({ d, screenCoordinates, i });
    }
    else if (d.type === "enclose-hull") {
        return svgHullEncloseAnnotation({ d, screenCoordinates, i });
    }
    else if (d.type === "x") {
        return svgXAnnotation({
            d,
            screenCoordinates,
            i,
            adjustedSize
        });
    }
    else if (d.type === "y") {
        return svgYAnnotation({
            d,
            screenCoordinates,
            i,
            adjustedSize,
            adjustedPosition
        });
    }
    else if (d.type === "bounds") {
        return svgBoundsAnnotation({
            d,
            i,
            adjustedSize,
            xAccessor,
            yAccessor,
            xScale,
            yScale
        });
    }
    else if (d.type === "line") {
        return svgLineAnnotation({ d, i, screenCoordinates });
    }
    else if (d.type === "area") {
        return svgAreaAnnotation({
            d,
            i,
            xScale,
            xAccessor,
            yScale,
            yAccessor,
            annotationLayer
        });
    }
    else if (d.type === "horizontal-points") {
        return svgHorizontalPointsAnnotation({
            d,
            lines: lines.data,
            points: points.data,
            xScale,
            yScale,
            pointStyle: points.styleFn
        });
    }
    else if (d.type === "vertical-points") {
        return svgVerticalPointsAnnotation({
            d,
            lines: lines.data,
            points: points.data,
            xScale,
            yScale,
            pointStyle: points.styleFn
        });
    }
    return null;
}
function defaultXYHTMLRule(props, state, { d: baseD, i, lines, summaries, points, annotationLayer }) {
    const { xAccessor, yAccessor, xScale, yScale, SpanOrDiv, annotatedSettings, axesData } = state;
    const { voronoiHover } = annotationLayer;
    let screenCoordinates = [];
    const { useSpans, tooltipContent, optimizeCustomTooltipPosition, htmlAnnotationRules, size, showLinePoints, margin: baseMargin } = props;
    const idAccessor = annotatedSettings.lineIDAccessor;
    const d = findPointByID({
        point: baseD,
        idAccessor,
        lines,
        xScale,
        projectedX,
        xAccessor
    });
    if (!d) {
        return null;
    }
    const xCoord = d[projectedXMiddle] || d[projectedX] || findFirstAccessorValue(xAccessor, d);
    const yCoord = d[projectedYMiddle] || d[projectedY] || findFirstAccessorValue(yAccessor, d);
    const xString = xCoord && xCoord.toString ? xCoord.toString() : xCoord;
    const yString = yCoord && yCoord.toString ? yCoord.toString() : yCoord;
    const margin = calculateMargin({
        margin: baseMargin,
        axes: axesData,
        title: annotatedSettings.title,
        size: size
    });
    const { adjustedPosition, adjustedSize } = adjustedPositionSize({
        size,
        margin
    });
    if (!d.coordinates) {
        screenCoordinates = [
            xScale(xCoord) || 0,
            relativeY({
                point: d,
                projectedYMiddle,
                projectedY,
                showLinePoints,
                yAccessor,
                yScale
            }) || 0
        ];
    }
    else {
        screenCoordinates = d.coordinates.map((p) => {
            const foundP = findPointByID({
                point: Object.assign({ x: 0, y: 0 }, p),
                idAccessor,
                lines,
                xScale,
                projectedX,
                xAccessor
            });
            return [
                (xScale(findFirstAccessorValue(xAccessor, d)) || 0) +
                    adjustedPosition[0],
                (relativeY({
                    point: foundP,
                    projectedYMiddle,
                    projectedY,
                    yAccessor,
                    yScale
                }) || 0) + adjustedPosition[1]
            ];
        });
    }
    const customAnnotation = htmlAnnotationRules &&
        htmlAnnotationRules({
            d,
            i,
            screenCoordinates,
            xScale,
            yScale,
            xAccessor,
            yAccessor,
            xyFrameProps: props,
            xyFrameState: state,
            summaries,
            points,
            lines,
            voronoiHover,
            adjustedPosition,
            adjustedSize,
            annotationLayer
        });
    if (htmlAnnotationRules && customAnnotation !== null) {
        return customAnnotation;
    }
    if (d.type === "frame-hover") {
        let content = (React.createElement(SpanOrDiv, { span: useSpans, className: "tooltip-content", "data-testid": "tooltip-content" },
            React.createElement("p", { key: "html-annotation-content-1" }, xString),
            React.createElement("p", { key: "html-annotation-content-2" }, yString),
            d.percent ? (React.createElement("p", { key: "html-annotation-content-3" },
                Math.floor(d.percent * 1000) / 10,
                "%")) : null));
        if (d.type === "frame-hover" && tooltipContent) {
            content = optimizeCustomTooltipPosition ? (React.createElement(TooltipPositioner, { tooltipContent: tooltipContent, tooltipContentArgs: d })) : (tooltipContent(d));
        }
        return htmlTooltipAnnotation({
            content,
            screenCoordinates,
            i,
            d,
            useSpans
        });
    }
    return null;
}
XYFrame.displayName = "XYFrame";

const derivePieceValue = (accessor, piece) => {
    const pieceVal = accessor(piece);
    return (pieceVal && pieceVal.toString && pieceVal.toString()) || pieceVal;
};
function polarToCartesian(centerX, centerY, radius, angleInDegrees) {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
    return {
        x: centerX + radius * Math.cos(angleInRadians),
        y: centerY + radius * Math.sin(angleInRadians)
    };
}
function pieContentGenerator({ column, useSpans }) {
    return (React.createElement(SpanOrDiv, { span: useSpans, className: "tooltip-content", "data-testid": "tooltip-content" },
        React.createElement("p", { key: "or-annotation-1" }, column.name),
        React.createElement("p", { key: "or-annotation-2" }, `${(column.pct * 100).toFixed(0)}%`)));
}
function arcBracket({ x, y, radius, startAngle, endAngle, inset, outset, curly = true }) {
    const start = polarToCartesian(x, y, radius + outset, endAngle);
    const end = polarToCartesian(x, y, radius + outset, startAngle);
    const innerStart = polarToCartesian(x, y, radius + outset - inset, endAngle);
    const innerEnd = polarToCartesian(x, y, radius + outset - inset, startAngle);
    const angleSize = endAngle - startAngle;
    const largeArcFlag = angleSize <= 180 ? "0" : "1";
    let d;
    if (curly) {
        const curlyOffset = Math.min(10, angleSize / 4);
        const middleLeft = polarToCartesian(x, y, radius + outset, (startAngle + endAngle) / 2 + curlyOffset);
        const middle = polarToCartesian(x, y, radius + outset + 10, (startAngle + endAngle) / 2);
        const middleRight = polarToCartesian(x, y, radius + outset, (startAngle + endAngle) / 2 - curlyOffset);
        d = [
            "M",
            innerStart.x,
            innerStart.y,
            "L",
            start.x,
            start.y,
            "A",
            radius + outset,
            radius + outset,
            0,
            0,
            0,
            middleLeft.x,
            middleLeft.y,
            "A",
            radius + outset,
            radius + outset,
            1,
            0,
            1,
            middle.x,
            middle.y,
            "A",
            radius + outset,
            radius + outset,
            1,
            0,
            1,
            middleRight.x,
            middleRight.y,
            "A",
            radius + outset,
            radius + outset,
            0,
            0,
            0,
            end.x,
            end.y,
            "L",
            innerEnd.x,
            innerEnd.y
        ].join(" ");
    }
    else {
        d = [
            "M",
            innerStart.x,
            innerStart.y,
            "L",
            start.x,
            start.y,
            "A",
            radius + outset,
            radius + outset,
            0,
            largeArcFlag,
            0,
            end.x,
            end.y,
            "L",
            innerEnd.x,
            innerEnd.y
        ].join(" ");
    }
    const midAngle = (startAngle + endAngle) / 2;
    let textOffset, largeTextArcFlag, finalTextEnd, finalTextStart, arcFlip;
    const lowerArc = midAngle > 90 && midAngle < 270;
    if (lowerArc) {
        textOffset = 12;
        largeTextArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
        arcFlip = 0;
    }
    else {
        largeTextArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
        textOffset = 5;
        arcFlip = 1;
    }
    textOffset += curly ? 10 : 0;
    const textStart = polarToCartesian(x, y, radius + outset + textOffset, endAngle);
    const textEnd = polarToCartesian(x, y, radius + outset + textOffset, startAngle);
    if (lowerArc) {
        finalTextStart = textStart;
        finalTextEnd = textEnd;
    }
    else {
        finalTextStart = textEnd;
        finalTextEnd = textStart;
    }
    const textD = [
        "M",
        finalTextStart.x,
        finalTextStart.y,
        "A",
        radius + outset + textOffset,
        radius + outset + textOffset,
        arcFlip,
        largeTextArcFlag,
        arcFlip,
        finalTextEnd.x,
        finalTextEnd.y
    ].join(" ");
    return { arcPath: d, textArcPath: textD };
}
const getColumnScreenCoordinates = ({ d, projectedColumns, oAccessor, summaryType, type, projection, adjustedPosition, adjustedSize }) => {
    const column = typeof d.column === "object"
        ? d.column
        : projectedColumns[d.facetColumn] ||
            projectedColumns[findFirstAccessorValue(oAccessor, d)];
    if (!column) {
        return { coordinates: [0, 0], pieces: undefined, column: undefined };
    }
    const pieces = column.pieceData || column.pieces;
    const positionValue = (summaryType.type && summaryType.type !== "none") ||
        ["swarm", "point", "clusterbar", "timeline"].find((p) => p === type.type)
        ? max(pieces.map((p) => p.scaledValue))
        : projection === "horizontal"
            ? max(pieces.map((p) => p.value >= 0 ? p.scaledValue + p.bottom : p.bottom))
            : min(pieces.map((p) => p.value >= 0 ? p.bottom - p.scaledValue : p.bottom));
    let xPosition = column.middle + adjustedPosition[0];
    let yPosition = (summaryType.type && summaryType.type !== "none") ||
        ["swarm", "point", "clusterbar", "timeline"].find((p) => p === type.type)
        ? adjustedSize[1] - positionValue
        : positionValue;
    yPosition += 10;
    if (projection === "horizontal") {
        yPosition = column.middle;
        xPosition = positionValue + adjustedPosition[0];
    }
    else if (projection === "radial") {
        const { pieArc } = column;
        const { translate, outerPoint, centroid } = pieArc;
        xPosition = (centroid[0] + outerPoint[0]) / 2 + translate[0];
        yPosition = (centroid[1] + outerPoint[1]) / 2 + translate[1];
    }
    return { coordinates: [xPosition, yPosition], pieces, column };
};
const svgHighlightRule$1 = ({ d, pieceIDAccessor, orFrameRender, oAccessor }) => {
    const thisID = pieceIDAccessor(d);
    const thisO = findFirstAccessorValue(oAccessor, d);
    const { pieces } = orFrameRender;
    const { styleFn } = pieces;
    const foundPieces = (pieces &&
        pieces.data
            .filter((p) => {
            return ((thisID === undefined ||
                pieceIDAccessor(Object.assign(Object.assign({}, p.piece), p.piece.data)) === thisID) &&
                (thisO === undefined ||
                    findFirstAccessorValue(oAccessor, p.piece.data) === thisO));
        })
            .map((p, q) => {
            let styleObject = {
                style: styleFn(Object.assign(Object.assign({}, p.piece), p.piece.data))
            };
            if (d.style && typeof d.style === "function") {
                styleObject = {
                    style: Object.assign(Object.assign({}, styleObject), d.style(Object.assign(Object.assign({}, p.piece), p.piece.data)))
                };
            }
            else if (d.style) {
                styleObject = { style: Object.assign(Object.assign({}, styleObject), d.style) };
            }
            const styledD = Object.assign(Object.assign({}, p.renderElement), styleObject);
            const className = `highlight-annotation ${(d.class &&
                typeof d.class === "function" &&
                d.class(p.piece.data, q)) ||
                (d.class && d.class) ||
                ""}`;
            if (React.isValidElement(p.renderElement)) {
                return React.cloneElement(p.renderElement, Object.assign(Object.assign({}, styleObject), { className }));
            }
            return (React.createElement(SemioticMark, Object.assign({ fill: "none", stroke: "black", strokeWidth: "2px", key: `highlight-piece-${q}` }, styledD, { className: className })));
        })) ||
        [];
    return [...foundPieces];
};
const findIDPiece = (pieceIDAccessor, oColumn, d) => {
    const foundIDValue = pieceIDAccessor(d);
    const pieceID = foundIDValue === "" && d.rName ? d.rName : foundIDValue;
    const basePieces = oColumn &&
        oColumn.pieceData.filter((r) => r.rName === pieceID || pieceIDAccessor(r.data) === pieceID);
    if (pieceID === "" ||
        basePieces === undefined ||
        basePieces === false ||
        basePieces.length !== 1)
        return d;
    const basePiece = basePieces[0];
    const reactAnnotationProps = [
        "type",
        "label",
        "note",
        "connector",
        "disabled",
        "color",
        "subject"
    ];
    if (basePiece) {
        reactAnnotationProps.forEach((prop) => {
            if (d[prop])
                basePiece[prop] = d[prop];
        });
    }
    return basePiece;
};
const screenProject = ({ p, adjustedSize, rScale, oColumn, rAccessor, idPiece, projection, rScaleType }) => {
    const basePValue = findFirstAccessorValue(rAccessor, p) || p.value;
    const pValue = Array.isArray(basePValue)
        ? Math.max(...basePValue)
        : basePValue;
    let o;
    if (oColumn) {
        o = oColumn.middle;
    }
    else {
        o = 0;
    }
    if (oColumn && projection === "radial") {
        return pointOnArcAtAngle$1([adjustedSize[0] / 2, adjustedSize[1] / 2], oColumn.pct_middle, idPiece && (idPiece.x === undefined ? idPiece.scaledValue : idPiece.x)
            ? idPiece.x / 2 || (idPiece.bottom + idPiece.scaledValue / 2) / 2
            : pValue / 2);
    }
    if (projection === "horizontal") {
        return [
            idPiece && idPiece.scaledEndValue
                ? idPiece.scaledEndValue
                : idPiece && idPiece.scaledValue
                    ? idPiece.value >= 0
                        ? idPiece.bottom + idPiece.scaledValue
                        : idPiece.bottom
                    : rScale(pValue),
            o
        ];
    }
    const newScale = rScaleType
        .copy()
        .domain(rScale.domain())
        .range(rScale.range().reverse());
    return [
        o,
        idPiece && (idPiece.x === undefined ? idPiece.scaledValue : idPiece.x)
            ? idPiece.y === undefined
                ? idPiece.value >= 0
                    ? idPiece.bottom - idPiece.scaledValue
                    : idPiece.bottom
                : idPiece.y
            : newScale(pValue)
    ];
};
const svgORRule = ({ d, i, screenCoordinates, projection }) => {
    return (React.createElement(SemioticMark, { markType: "text", key: `${d.label}annotationtext${i}`, x: parseInt(screenCoordinates[0] + (projection === "horizontal" ? 10 : 0)), y: parseInt(screenCoordinates[1] + (projection === "vertical" ? 10 : 0)), className: `annotation annotation-or-label ${d.className || ""}`, "data-testid": "annotation-or-label", textAnchor: "middle" }, d.label));
};
const basicReactAnnotationRule = ({ d, i, screenCoordinates }) => {
    const noteData = Object.assign({
        dx: 0,
        dy: 0,
        note: { label: d.label, orientation: d.orientation, align: d.align },
        connector: { end: "arrow" }
    }, d, {
        x: screenCoordinates[0],
        y: screenCoordinates[1],
        type: typeof d.type === "function" ? d.type : undefined,
        screenCoordinates
    });
    if (d.fixedX)
        noteData.x = d.fixedX;
    if (d.fixedY)
        noteData.y = d.fixedY;
    return React.createElement(SemioticAnnotation, { key: d.key || `annotation-${i}`, noteData: noteData });
};
const svgEncloseRule$1 = ({ d, i, screenCoordinates }) => {
    const circle = packEnclose(screenCoordinates.map((p) => ({ x: p[0], y: p[1], r: 2 })));
    return circleEnclosure({ d, i, circle });
};
const svgRRule = ({ d, i, screenCoordinates, rScale, rAccessor, adjustedSize, adjustedPosition, projection }) => {
    let x, y, xPosition, yPosition, subject, dx, dy;
    if (projection === "radial") {
        return (React.createElement(SemioticAnnotation, { key: d.key || `annotation-${i}`, noteData: Object.assign({
                dx: 50,
                dy: 50,
                note: { label: d.label },
                connector: { end: "arrow" }
            }, d, {
                type: AnnotationCalloutCircle,
                subject: {
                    radius: rScale(findFirstAccessorValue(rAccessor, d)) / 2,
                    radiusPadding: 0
                },
                x: adjustedSize[0] / 2,
                y: adjustedSize[1] / 2
            }) }));
    }
    else if (projection === "horizontal") {
        dx = 50;
        dy = 50;
        yPosition = d.offset || i * 25;
        x = screenCoordinates[0];
        y = yPosition;
        subject = {
            x,
            y1: 0,
            y2: adjustedSize[1] + adjustedPosition[1]
        };
    }
    else {
        dx = 50;
        dy = -20;
        xPosition = d.offset || i * 25;
        y = screenCoordinates[1];
        x = xPosition;
        subject = {
            y,
            x1: 0,
            x2: adjustedSize[0] + adjustedPosition[0]
        };
    }
    const noteData = Object.assign({
        dx,
        dy,
        note: { label: d.label },
        connector: { end: "arrow" }
    }, d, {
        type: AnnotationXYThreshold,
        x,
        y,
        subject
    });
    return React.createElement(SemioticAnnotation, { key: d.key || `annotation-${i}`, noteData: noteData });
};
const svgCategoryRule = ({ projection, d, i, categories, adjustedSize }) => {
    const { bracketType = "curly", position = projection === "vertical" ? "top" : "left", depth = 30, offset = 0, padding = 0 } = d;
    const actualCategories = Array.isArray(d.categories)
        ? d.categories
        : [d.categories];
    const cats = actualCategories.map((c) => categories[c]);
    if (projection === "radial") {
        const arcPadding = padding / adjustedSize[1];
        const leftX = min(cats.map((p) => p.pct_start + p.pct_padding / 2 + arcPadding / 2));
        const rightX = max(cats.map((p) => p.pct_start + p.pct - p.pct_padding / 2 - arcPadding / 2));
        const chartSize = Math.min(adjustedSize[0], adjustedSize[1]) / 2;
        const centerX = adjustedSize[0] / 2;
        const centerY = adjustedSize[1] / 2;
        const { arcPath, textArcPath } = arcBracket({
            x: 0,
            y: 0,
            radius: chartSize,
            startAngle: leftX * 360,
            endAngle: rightX * 360,
            inset: depth,
            outset: offset,
            curly: bracketType === "curly"
        });
        const textPathID = `text-path-${i}-${Math.random()}`;
        return (React.createElement("g", { className: "category-annotation annotation", transform: `translate(${centerX},${centerY})` },
            React.createElement("path", { d: arcPath, fill: "none", stroke: "black" }),
            React.createElement("path", { id: textPathID, d: textArcPath, style: { display: "none" } }),
            React.createElement("text", { "font-size": "12.5" },
                React.createElement("textPath", { startOffset: "50%", textAnchor: "middle", xlinkHref: `#${textPathID}` }, d.label))));
    }
    else {
        const leftX = min(cats.map((p) => p.x));
        const rightX = max(cats.map((p) => p.x + p.width));
        if (projection === "vertical") {
            let yPosition = position === "top" ? 0 : adjustedSize[1];
            yPosition += position === "top" ? -offset : offset;
            const noteData = {
                type: AnnotationBracket,
                y: yPosition,
                x: leftX - padding,
                note: {
                    title: d.title || d.label,
                    label: d.title ? d.label : undefined
                },
                subject: {
                    type: bracketType,
                    width: rightX - leftX + padding * 2,
                    depth: position === "top" ? -depth : depth
                }
            };
            return React.createElement(SemioticAnnotation, { key: d.key || `annotation-${i}`, noteData: noteData });
        }
        else if (projection === "horizontal") {
            let yPosition = position === "left" ? 0 : adjustedSize[0];
            yPosition += position === "left" ? -offset : offset;
            const noteData = {
                type: AnnotationBracket,
                x: yPosition,
                y: leftX - padding,
                note: {
                    title: d.title || d.label,
                    label: d.title ? d.label : undefined
                },
                subject: {
                    type: bracketType,
                    height: rightX - leftX + padding * 2,
                    depth: position === "left" ? -depth : depth
                }
            };
            return React.createElement(SemioticAnnotation, { key: d.key || `annotation-${i}`, noteData: noteData });
        }
    }
};
const htmlFrameHoverRule$1 = ({ d, i, rAccessor, oAccessor, projection, tooltipContent, optimizeCustomTooltipPosition, useSpans, pieceIDAccessor, projectedColumns, adjustedSize, rScale, type, rScaleType }) => {
    tooltipContent =
        tooltipContent === "pie"
            ? () => pieContentGenerator({
                column: d.column,
                useSpans
            })
            : tooltipContent;
    //To string because React gives a DOM error if it gets a date
    let contentFill;
    const pO = findFirstAccessorValue(oAccessor, d) || d.column;
    const oColumn = projectedColumns[pO];
    const idPiece = findIDPiece(pieceIDAccessor, oColumn, d);
    if (!idPiece) {
        return null;
    }
    const screenCoordinates = ((type.type === "clusterbar" ||
        type.type === "point" ||
        type.type === "swarm") &&
        d.x !== undefined &&
        d.y !== undefined) ||
        d.isSummaryData
        ? [d.x, d.y]
        : screenProject({
            p: d,
            adjustedSize,
            rScale,
            oColumn,
            rAccessor,
            idPiece,
            projection,
            rScaleType
        });
    if (d.isSummaryData) {
        let summaryContent = d.label;
        if (d.pieces && d.pieces.length !== 0) {
            if (d.pieces.length === 1) {
                summaryContent = [];
                rAccessor.forEach((actualRAccessor) => {
                    summaryContent.push(actualRAccessor(d.pieces[0].data));
                });
            }
            else {
                summaryContent = [];
                rAccessor.forEach((actualRAccessor) => {
                    const pieceData = extent$1(d.pieces.map((p) => p.data).map(actualRAccessor));
                    summaryContent.push(`From ${pieceData[0]} to ${pieceData[1]}`);
                });
            }
        }
        const summaryLabel = React.createElement("p", { key: "html-annotation-content-2" }, summaryContent);
        contentFill = [
            React.createElement("p", { key: "html-annotation-content-1" }, d.key),
            summaryLabel,
            React.createElement("p", { key: "html-annotation-content-3" }, d.value)
        ];
    }
    else if (d.data) {
        contentFill = [];
        oAccessor.forEach((actualOAccessor, i) => {
            if (idPiece.data) {
                const pieceOVal = derivePieceValue(actualOAccessor, idPiece.data);
                contentFill.push(React.createElement("p", { key: `html-annotation-content-o-${i}` }, pieceOVal));
            }
        });
        rAccessor.forEach((actualRAccessor, i) => {
            if (idPiece.data) {
                const pieceRVal = derivePieceValue(actualRAccessor, idPiece.data);
                contentFill.push(React.createElement("p", { key: `html-annotation-content-r-${i}` }, pieceRVal));
            }
        });
    }
    else if (d.label) {
        contentFill = d.label;
    }
    let content = (React.createElement(SpanOrDiv, { span: useSpans, className: "tooltip-content", "data-testid": "tooltip-content" }, contentFill));
    if (d.type === "frame-hover" && tooltipContent && idPiece) {
        const tooltipContentArgs = Object.assign(Object.assign({}, idPiece), idPiece.data);
        content = optimizeCustomTooltipPosition ? (React.createElement(TooltipPositioner, { tooltipContent: tooltipContent, tooltipContentArgs: tooltipContentArgs })) : (tooltipContent(tooltipContentArgs));
    }
    return (React.createElement(SpanOrDiv, { span: useSpans, key: `xylabel-${i}`, className: `annotation annotation-or-label ${projection} ${d.className || ""}`, "data-testid": "annotation-or-label", style: {
            position: "absolute",
            top: `${parseInt(screenCoordinates[1])}px`,
            left: `${parseInt(screenCoordinates[0])}px`
        } }, content));
};
const htmlColumnHoverRule = ({ d, i, summaryType, oAccessor, type, adjustedPosition, adjustedSize, projection, tooltipContent, optimizeCustomTooltipPosition, useSpans, projectedColumns }) => {
    //we need to ignore negative pieces to make sure the hover behavior populates on top of the positive bar
    const { coordinates: [xPosition, yPosition], pieces, column } = getColumnScreenCoordinates({
        d,
        projectedColumns,
        oAccessor,
        summaryType,
        type,
        projection,
        adjustedPosition,
        adjustedSize
    });
    if (column === undefined) {
        return null;
    }
    //To string because React gives a DOM error if it gets a date
    const oContent = [];
    oAccessor.forEach((actualOAccessor, i) => {
        if (pieces[0].data) {
            const pieceOVal = derivePieceValue(actualOAccessor, pieces[0].data);
            oContent.push(React.createElement("p", { key: `or-annotation-o-${i}` }, pieceOVal));
        }
    });
    let content = (React.createElement(SpanOrDiv, { span: useSpans, className: "tooltip-content", "data-testid": "tooltip-content" },
        oContent,
        React.createElement("p", { key: "or-annotation-2" }, sum(pieces.map((p) => p.value).filter((p) => p > 0)))));
    if (d.type === "column-hover" && tooltipContent) {
        if (tooltipContent === "pie") {
            tooltipContent = pieContentGenerator;
        }
        const tooltipContentArgs = Object.assign(Object.assign({}, d), { pieces: pieces.map((p) => p.data), column,
            oAccessor });
        content = optimizeCustomTooltipPosition ? (React.createElement(TooltipPositioner, { tooltipContent: tooltipContent, tooltipContentArgs: tooltipContentArgs })) : (tooltipContent(tooltipContentArgs));
    }
    else if (d.label) {
        content = (React.createElement(SpanOrDiv, { span: useSpans, className: "tooltip-content", "data-testid": "tooltip-content" }, d.label));
    }
    return (React.createElement(SpanOrDiv, { span: useSpans, key: `orlabel-${i}`, className: `annotation annotation-or-label ${projection} ${d.className || ""}`, "data-testid": "annotation-or-label", style: {
            position: "absolute",
            top: `${parseInt(yPosition)}px`,
            left: `${parseInt(xPosition)}px`
        } }, content));
};
const svgRectEncloseRule$1 = ({ d, i, screenCoordinates }) => {
    const bboxNodes = screenCoordinates.map((p) => {
        return {
            x0: (p.x0 = p[0]),
            x1: (p.x1 = p[0]),
            y0: (p.y0 = p[1]),
            y1: (p.y1 = p[1])
        };
    });
    return rectangleEnclosure({ bboxNodes, d, i });
};
const svgOrdinalLine = ({ screenCoordinates, d, voronoiHover }) => {
    const lineGenerator = line()
        .x((d) => d[0])
        .y((d) => d[1]);
    if (d.curve) {
        const interpolator = curveHash[d.curve] || d.curve;
        lineGenerator.curve(interpolator);
    }
    const lineStyle = typeof d.lineStyle === "function" ? d.lineStyle(d) : d.lineStyle || {};
    return (React.createElement("g", { key: "ordinal-line-annotation" },
        React.createElement("path", { stroke: "black", fill: "none", style: lineStyle, d: lineGenerator(screenCoordinates) }),
        (d.points || d.interactive) &&
            screenCoordinates.map((p, q) => {
                const pointStyle = typeof d.pointStyle === "function"
                    ? d.pointStyle(d.coordinates[q], q)
                    : d.pointStyle || {};
                return (React.createElement("g", { transform: `translate(${p[0]},${p[1]})`, key: `ordinal-line-point-${q}` },
                    d.points && (React.createElement("circle", { style: pointStyle, r: d.radius || 5, fill: "black" })),
                    d.interactive && (React.createElement("circle", { style: { pointerEvents: "all" }, r: d.hoverRadius || 15, opacity: 0, onMouseEnter: () => voronoiHover(Object.assign(Object.assign({ type: "frame-hover" }, d.coordinates[q]), { data: d.coordinates[q] })), onMouseOut: () => voronoiHover() }))));
            })));
};

const twoPI$1 = Math.PI * 2;
const radialBarFeatureGenerator = ({ type, ordset, adjustedSize, piece, i }) => {
    let { innerRadius } = type;
    const { offsetAngle = 0, angleRange = [0, 360] } = type;
    const offsetPct = offsetAngle / 360;
    const rangePct = angleRange.map((d) => d / 360);
    const rangeMod = rangePct[1] - rangePct[0];
    const adjustedPct = rangeMod < 1 ? scaleLinear().domain([0, 1]).range(rangePct) : (d) => d;
    let innerSize = type.type === "clusterbar"
        ? 0
        : type.type === "timeline"
            ? piece.scaledValue / 2
            : piece.bottom / 2;
    let outerSize = type.type === "clusterbar"
        ? piece.scaledValue / 2
        : type.type === "timeline"
            ? piece.scaledEndValue / 2
            : piece.scaledValue / 2 + piece.bottom / 2;
    if (innerRadius) {
        innerRadius = parseInt(innerRadius, 10);
        const canvasRadius = adjustedSize[0] / 2;
        const donutMod = (canvasRadius - innerRadius) / canvasRadius;
        innerSize = innerSize * donutMod + innerRadius;
        outerSize = outerSize * donutMod + innerRadius;
    }
    const arcGenerator = arc().innerRadius(innerSize).outerRadius(outerSize);
    const angle = (type.type === "clusterbar"
        ? (ordset.pct - ordset.pct_padding) / ordset.pieceData.length
        : ordset.pct) * rangeMod;
    const startAngle = adjustedPct(type.type === "clusterbar"
        ? ordset.pct_start +
            (i / ordset.pieceData.length) * (ordset.pct - ordset.pct_padding)
        : ordset.pct === 1
            ? 0
            : ordset.pct_start + offsetPct);
    const endAngle = ordset.pct === 1
        ? rangePct[1]
        : Math.max(startAngle, startAngle + angle - ordset.pct_padding / 2);
    const startAngleFinal = startAngle * twoPI$1;
    const endAngleFinal = endAngle * twoPI$1;
    const markD = arcGenerator({
        startAngle: startAngleFinal,
        endAngle: endAngleFinal
    });
    const centroid = arcGenerator.centroid({
        startAngle: startAngleFinal,
        endAngle: endAngleFinal
    });
    const xOffset = adjustedSize[0] / 2;
    const yOffset = adjustedSize[1] / 2;
    const xPosition = centroid[0] + xOffset;
    const yPosition = centroid[1] + yOffset;
    const outerPoint = pointOnArcAtAngle([0, 0], (startAngle + endAngle) / 2, piece.scaledValue / 2);
    const xy = {
        arcGenerator: arcGenerator,
        startAngle: startAngleFinal,
        endAngle: endAngleFinal,
        dx: outerPoint[0],
        dy: outerPoint[1]
    };
    const translate = `translate(${xOffset},${yOffset})`;
    return {
        xPosition,
        yPosition,
        xy,
        translate,
        markProps: {
            markType: "path",
            d: markD,
            tx: xOffset,
            ty: yOffset,
            transform: translate
        }
    };
};
const iconBarCustomMark = ({ type, projection, finalHeight, finalWidth, styleFn, renderValue, classFn }) => (piece, i, xy) => {
    const iconD = typeof type.icon === "string" ? type.icon : type.icon(piece.data, i);
    const { iconPadding = 1, resize = "auto" } = type;
    const iconBounds = pathBounds(iconD);
    const iconTranslate = [
        0 - iconBounds.x1 + iconPadding,
        0 - iconBounds.y1 + iconPadding
    ];
    iconBounds.height += iconPadding * 2;
    iconBounds.width += iconPadding * 2;
    const icons = [];
    let stackedIconSize = iconBounds.height;
    let stackedIconNumber = 1;
    let iconScale = 1;
    const spaceToUse = projection === "horizontal" ? finalHeight : finalWidth;
    const sizeToFit = projection === "horizontal" ? iconBounds.height : iconBounds.width;
    const sizeToPad = projection === "horizontal" ? iconBounds.width : iconBounds.height;
    const spaceToFill = projection === "horizontal" ? xy.width : xy.height;
    const spaceToStackFill = projection === "horizontal" ? xy.height : xy.width;
    if (resize === "auto") {
        stackedIconSize = spaceToUse / sizeToFit;
        if (stackedIconSize < 1) {
            iconScale = stackedIconSize;
        }
        else {
            stackedIconNumber = Math.floor(stackedIconSize);
            iconScale =
                1 + (stackedIconSize - stackedIconNumber) / stackedIconNumber;
        }
    }
    else if (resize === "fixed") {
        iconScale = spaceToUse / sizeToFit;
    }
    //  const finalIconWidth = iconBounds.width * iconScale;
    const finalIconHeight = iconBounds.height * iconScale;
    const spaceToStep = sizeToPad * iconScale;
    const spaceToStackStep = sizeToFit * iconScale;
    iconTranslate[0] = iconTranslate[0] * iconScale;
    iconTranslate[1] = iconTranslate[1] * iconScale;
    const randoClipID = `iso-clip-${i}-${Math.random()}`;
    const clipPath = `url(#${randoClipID})`;
    if (xy.width > 0) {
        icons.push(React.createElement("clipPath", { key: randoClipID, id: randoClipID },
            React.createElement("rect", { x: 0, y: 0, width: xy.width, height: xy.height })));
        const iconPieces = [];
        const stepStart = projection === "horizontal" ? 0 : xy.height - finalIconHeight;
        const stepper = projection === "horizontal" ? spaceToStep : -spaceToStep;
        const stepTest = projection === "horizontal"
            ? (step, spaceToFillValue) => step < spaceToFillValue
            : (step, spaceToFillValue, stepperValue) => step > 0 + stepperValue;
        for (let step = stepStart; stepTest(step, spaceToFill, stepper); step += stepper) {
            for (let stack = 0; stack < spaceToStackFill; stack += spaceToStackStep) {
                const stepX = projection === "horizontal" ? step : stack;
                const stepY = projection === "horizontal" ? stack : step;
                const paddedX = stepX + iconTranslate[0];
                const paddedY = stepY + iconTranslate[1];
                iconPieces.push(React.createElement(SemioticMark, { markType: "path", key: `icon-${step}-${stack}`, transform: `translate(${paddedX},${paddedY}) scale(${iconScale})`, vectorEffect: "non-scaling-stroke", d: iconD, style: styleFn(Object.assign(Object.assign({}, piece), piece.data), i), renderMode: renderValue, className: classFn(Object.assign(Object.assign({}, piece), piece.data), i) }));
            }
        }
        icons.push(React.createElement("g", { key: `clipped-region-${i}`, clipPath: clipPath }, iconPieces));
    }
    return icons;
};
function pointOnArcAtAngle(center, angle, distance) {
    const radians = Math.PI * (angle + 0.75) * 2;
    const xPosition = center[0] + distance * Math.cos(radians);
    const yPosition = center[1] + distance * Math.sin(radians);
    return [xPosition, yPosition];
}
function clusterBarLayout({ type, data, renderMode, eventListenersGenerator, styleFn, projection, classFn, adjustedSize, chartSize, margin, baseMarkProps, rScale }) {
    let allCalculatedPieces = [];
    const keys = Object.keys(data);
    keys.forEach((key, ordsetI) => {
        const ordset = data[key];
        const barColumnWidth = Math.max(ordset.width, 1);
        const clusterWidth = barColumnWidth / ordset.pieceData.length;
        let currentX = 0;
        let currentY = 0;
        const calculatedPieces = ordset.pieceData.map((piece, i) => {
            const renderValue = renderMode && renderMode(piece.data, i);
            let xPosition = piece.x;
            let yPosition = piece.base;
            let finalWidth = clusterWidth;
            let finalHeight = piece.scaledValue;
            let xy = { x: 0, y: 0 };
            if (!piece.negative) {
                yPosition -= piece.scaledValue;
            }
            if (projection === "horizontal") {
                //TODO: NEGATIVE FOR HORIZONTAL
                yPosition = piece.x;
                xPosition = piece.base;
                finalHeight = clusterWidth;
                finalWidth = piece.scaledValue;
                xy.x = piece.scaledValue;
                if (piece.negative) {
                    xPosition -= piece.scaledValue;
                    xy.x = xPosition;
                }
            }
            let translate, markProps = {};
            if (projection === "radial") {
                ({ xPosition, yPosition, markProps, xy } = radialBarFeatureGenerator({
                    type,
                    ordset,
                    adjustedSize,
                    piece,
                    i
                }));
                xy.x = xPosition;
            }
            else {
                xPosition += currentX;
                yPosition += currentY;
                markProps = {
                    markType: "rect",
                    x: xPosition,
                    y: yPosition,
                    width: Math.max(0, finalWidth),
                    height: Math.max(0, finalHeight),
                    rx: 0,
                    ry: 0
                };
                if (projection === "vertical") {
                    xy.x = xPosition;
                }
            }
            const eventListeners = eventListenersGenerator(piece, i);
            xy.y = yPosition;
            xy.middle = clusterWidth / 2;
            xy.height = finalHeight;
            xy.width = finalWidth;
            if (type.icon && projection !== "radial") {
                type.customMark = iconBarCustomMark({
                    type,
                    projection,
                    finalHeight,
                    finalWidth,
                    styleFn,
                    renderValue,
                    classFn
                });
            }
            else if (type.icon && projection === "radial") {
                console.error("Icons are currently unsupported on radial charts");
            }
            const renderElementObject = type.customMark ? (React.createElement("g", { key: `piece-${piece.renderKey}`, transform: `translate(${xPosition},${yPosition})` }, type.customMark(Object.assign(Object.assign(Object.assign({}, piece.data), piece), { x: xPosition, y: yPosition }), i, Object.assign(Object.assign({}, xy), { baseMarkProps,
                renderMode,
                styleFn,
                classFn,
                adjustedSize,
                chartSize,
                margin,
                rScale })))) : (Object.assign(Object.assign({ className: classFn(Object.assign(Object.assign({}, piece), piece.data), i), renderMode: renderValue, key: `piece-${piece.renderKey}`, transform: translate, style: styleFn(Object.assign(Object.assign({}, piece), piece.data), ordsetI) }, markProps), eventListeners));
            const calculatedPiece = {
                o: key,
                xy,
                piece,
                renderElement: renderElementObject
            };
            if (projection === "horizontal") {
                currentY += finalHeight;
            }
            else {
                currentX += finalWidth;
            }
            //        currentOffset += pieceSize
            return calculatedPiece;
        });
        allCalculatedPieces = [...allCalculatedPieces, ...calculatedPieces];
    });
    return allCalculatedPieces;
}
function barLayout({ type, data, renderMode, eventListenersGenerator, styleFn, projection, classFn, adjustedSize, chartSize, margin, baseMarkProps, rScale }) {
    const keys = Object.keys(data);
    let allCalculatedPieces = [];
    keys.forEach((key, ordsetI) => {
        const ordset = data[key];
        const barColumnWidth = Math.max(ordset.width, 1);
        ordset.pieceData.forEach((piece, i) => {
            const pieceSize = piece.scaledValue;
            const renderValue = renderMode && renderMode(piece.data, i);
            let xPosition = piece.x;
            let yPosition = piece.bottom;
            let finalWidth = barColumnWidth;
            let finalHeight = pieceSize;
            let xy = {};
            if (!piece.negative) {
                yPosition -= piece.scaledValue;
            }
            if (projection === "vertical") {
                xy = {
                    x: xPosition,
                    y: yPosition,
                    middle: barColumnWidth / 2,
                    height: finalHeight,
                    width: finalWidth
                };
            }
            else if (projection === "horizontal") {
                yPosition = piece.x;
                xPosition = piece.bottom;
                finalHeight = barColumnWidth;
                finalWidth = pieceSize;
                xy = {
                    x: xPosition + piece.scaledValue,
                    y: yPosition,
                    middle: barColumnWidth / 2,
                    height: finalHeight,
                    width: finalWidth
                };
                if (piece.negative) {
                    xPosition = piece.bottom - piece.scaledValue;
                }
            }
            let markProps;
            if (projection === "radial") {
                ({ markProps, xPosition, yPosition } = radialBarFeatureGenerator({
                    type,
                    ordset,
                    adjustedSize,
                    piece,
                    i
                }));
                finalHeight = undefined;
                finalWidth = undefined;
                xy = {
                    x: xPosition,
                    y: yPosition,
                    middle: barColumnWidth / 2,
                    height: finalHeight,
                    width: finalWidth
                };
            }
            else {
                markProps = {
                    markType: "rect",
                    x: xPosition,
                    y: yPosition,
                    width: Math.max(0, finalWidth),
                    height: Math.max(0, finalHeight),
                    rx: 0,
                    ry: 0
                };
            }
            const eventListeners = eventListenersGenerator(piece, i);
            if (type.icon && projection !== "radial") {
                type.customMark = iconBarCustomMark({
                    type,
                    projection,
                    finalHeight,
                    finalWidth,
                    styleFn,
                    renderValue,
                    classFn
                });
            }
            else if (type.icon && projection !== "horizontal") {
                console.error("Icons are currently unsupported in radial charts");
            }
            const renderElementObject = type.customMark ? (React.createElement("g", { key: `piece-${piece.renderKey}`, transform: `translate(${xPosition},${yPosition})`, role: "img", tabIndex: -1 }, type.customMark(Object.assign(Object.assign(Object.assign({}, piece.data), piece), { x: xPosition, y: yPosition }), i, Object.assign(Object.assign({}, xy), { baseMarkProps,
                renderMode,
                styleFn,
                classFn,
                adjustedSize,
                chartSize,
                margin,
                rScale })))) : (Object.assign(Object.assign({ className: classFn(Object.assign(Object.assign({}, piece), piece.data), i), renderMode: renderValue, key: `piece-${piece.renderKey}`, style: styleFn(Object.assign(Object.assign({}, piece), piece.data), ordsetI) }, eventListeners), markProps));
            allCalculatedPieces.push({
                o: key,
                xy,
                piece,
                renderElement: renderElementObject
            });
        });
    });
    return allCalculatedPieces;
}
function timelineLayout({ type, data, renderMode, eventListenersGenerator, styleFn, projection, classFn, adjustedSize, chartSize, margin, baseMarkProps, rScale }) {
    let allCalculatedPieces = [];
    const keys = Object.keys(data);
    keys.forEach((key, ordsetI) => {
        const ordset = data[key];
        const calculatedPieces = [];
        ordset.pieceData.forEach((piece, i) => {
            let scaledValue, scaledBottom;
            const renderValue = renderMode && renderMode(piece.data, i);
            let xPosition = ordset.x;
            let height = piece.scaledEndValue - piece.scaledValue;
            let yPosition = piece.scaledVerticalValue - height;
            let width = ordset.width;
            let markProps = {
                markType: "rect",
                height: height < 0 ? -height : height,
                width,
                x: xPosition,
                y: height < 0 ? yPosition + height : yPosition
            };
            if (projection === "horizontal") {
                yPosition = ordset.x;
                xPosition = piece.scaledValue;
                scaledValue = piece.scaledEndValue - piece.scaledValue;
                scaledBottom = piece.scaledBottom;
                width = piece.scaledEndValue - piece.scaledValue;
                height = ordset.width;
                markProps = {
                    markType: "rect",
                    height,
                    width: width < 0 ? -width : width,
                    x: width < 0 ? xPosition + width : xPosition,
                    y: yPosition
                };
            }
            else if (projection === "radial") {
                ({ markProps } = radialBarFeatureGenerator({
                    piece,
                    type,
                    ordset,
                    adjustedSize,
                    i
                }));
            }
            //Only return the actual piece if you're rendering points, otherwise you just needed to iterate and calculate the points for the contour summary type
            const eventListeners = eventListenersGenerator(piece, i);
            const xy = {
                x: xPosition,
                y: yPosition,
                scaledValue,
                scaledBottom,
                height
            };
            const renderElementObject = type.customMark ? (React.createElement("g", { key: `piece-${piece.renderKey}`, transform: `translate(${xPosition},${yPosition + height})` }, type.customMark(Object.assign(Object.assign(Object.assign({}, piece.data), piece), { x: xPosition, y: yPosition }), i, Object.assign(Object.assign({}, xy), { baseMarkProps,
                renderMode,
                styleFn,
                classFn,
                adjustedSize,
                chartSize,
                margin,
                rScale })))) : (Object.assign(Object.assign({ className: classFn(Object.assign(Object.assign({}, piece), piece.data), i), renderMode: renderValue, key: `piece-${piece.renderKey}`, style: styleFn(Object.assign(Object.assign({}, piece), piece.data), ordsetI) }, markProps), eventListeners));
            const calculatedPiece = {
                o: key,
                xy,
                piece,
                renderElement: renderElementObject
            };
            calculatedPieces.push(calculatedPiece);
        });
        allCalculatedPieces = [...allCalculatedPieces, ...calculatedPieces];
    });
    return allCalculatedPieces;
}
function pointLayout({ type, data, renderMode, eventListenersGenerator, styleFn, projection, classFn, adjustedSize, chartSize, margin, baseMarkProps, rScale }) {
    const circleRadius = type.r || 3;
    let allCalculatedPieces = [];
    const keys = Object.keys(data);
    keys.forEach((key, ordsetI) => {
        const ordset = data[key];
        const calculatedPieces = [];
        ordset.pieceData.forEach((piece, i) => {
            const renderValue = renderMode && renderMode(piece.data, i);
            let xPosition = ordset.middle;
            let yPosition = piece.scaledVerticalValue;
            if (projection === "horizontal") {
                yPosition = ordset.middle;
                xPosition = piece.scaledValue;
            }
            else if (projection === "radial") {
                const angle = ordset.pct_middle;
                const rPosition = piece.scaledValue / 2;
                const baseCentroid = pointOnArcAtAngle([adjustedSize[0] / 2, adjustedSize[1] / 2], angle, rPosition);
                xPosition = baseCentroid[0];
                yPosition = baseCentroid[1];
            }
            //Only return the actual piece if you're rendering points, otherwise you just needed to iterate and calculate the points for the contour summary type
            const actualCircleRadius = typeof circleRadius === "function"
                ? circleRadius(piece, i)
                : circleRadius;
            const eventListeners = eventListenersGenerator(piece, i);
            const renderElementObject = type.customMark ? (React.createElement("g", { key: `piece-${piece.renderKey}`, transform: `translate(${xPosition},${yPosition})` }, type.customMark(Object.assign(Object.assign(Object.assign({}, piece.data), piece), { x: xPosition, y: yPosition, width: ordset.width }), i, {
                r: circleRadius,
                x: xPosition,
                y: yPosition,
                baseMarkProps,
                renderMode,
                styleFn,
                classFn,
                adjustedSize,
                chartSize,
                margin,
                rScale
            }))) : (Object.assign({ className: classFn(Object.assign(Object.assign({}, piece), piece.data), i), markType: "rect", renderMode: renderValue, key: `piece-${piece.renderKey}`, height: actualCircleRadius * 2, width: actualCircleRadius * 2, x: xPosition - actualCircleRadius, y: yPosition - actualCircleRadius, rx: actualCircleRadius, ry: actualCircleRadius, style: styleFn(Object.assign(Object.assign({}, piece), piece.data), ordsetI) }, eventListeners));
            const calculatedPiece = {
                o: key,
                xy: {
                    x: xPosition,
                    y: yPosition
                },
                piece,
                renderElement: renderElementObject
            };
            calculatedPieces.push(calculatedPiece);
        });
        allCalculatedPieces = [...allCalculatedPieces, ...calculatedPieces];
    });
    return allCalculatedPieces;
}
function swarmLayout({ type, data, renderMode, eventListenersGenerator, styleFn, projection, classFn, adjustedSize, chartSize, margin, baseMarkProps, rScale }) {
    let allCalculatedPieces = [];
    const iterations = type.iterations || 120;
    const columnKeys = Object.keys(data);
    columnKeys.forEach((key, ordsetI) => {
        const oColumn = data[key];
        const anglePiece = 1 / columnKeys.length;
        const oData = oColumn.pieceData;
        const adjustedColumnWidth = oColumn.width;
        const circleRadius = type.r ||
            Math.max(2, Math.min(5, (4 * adjustedColumnWidth) / oData.length));
        const simulation = forceSimulation(oData)
            .force("y", forceY((d) => d.scaledValue).strength(type.strength || 2))
            .force("x", forceX(oColumn.middle))
            .force("collide", forceCollide(circleRadius))
            .stop();
        if (projection === "vertical") {
            simulation.force("y", forceY((d) => d.scaledVerticalValue).strength(type.strength || 2));
        }
        for (let i = 0; i < iterations; ++i)
            simulation.tick();
        const calculatedPieces = oData.map((piece, i) => {
            const renderValue = renderMode && renderMode(piece.data, i);
            let xPosition = piece.x;
            let yPosition = piece.y;
            if (projection === "horizontal") {
                yPosition = piece.x;
                xPosition = piece.y;
            }
            else if (projection === "radial") {
                const angle = oColumn.pct_middle;
                xPosition =
                    ((piece.x - oColumn.middle) / adjustedColumnWidth) * anglePiece;
                const rPosition = piece.scaledValue / 2;
                const xAngle = angle + xPosition;
                const baseCentroid = pointOnArcAtAngle([adjustedSize[0] / 2, adjustedSize[1] / 2], xAngle, rPosition);
                xPosition = baseCentroid[0];
                yPosition = baseCentroid[1];
            }
            const actualCircleRadius = typeof circleRadius === "function"
                ? circleRadius(piece, i)
                : circleRadius;
            const eventListeners = eventListenersGenerator(piece, i);
            const renderElementObject = type.customMark ? (React.createElement("g", { key: `piece-${piece.renderKey}`, transform: `translate(${xPosition},${yPosition})` }, type.customMark(Object.assign(Object.assign(Object.assign({}, piece.data), piece), { x: xPosition, y: yPosition }), i, {
                x: xPosition,
                y: yPosition,
                r: circleRadius,
                baseMarkProps,
                renderMode,
                styleFn,
                classFn,
                adjustedSize,
                chartSize,
                margin,
                rScale
            }))) : (Object.assign({ className: classFn(Object.assign(Object.assign({}, piece), piece.data), i), markType: "rect", renderMode: renderValue, key: `piece-${piece.renderKey}`, height: actualCircleRadius * 2, width: actualCircleRadius * 2, x: xPosition - actualCircleRadius, y: yPosition - actualCircleRadius, rx: actualCircleRadius, ry: actualCircleRadius, style: styleFn(Object.assign(Object.assign({}, piece), piece.data), ordsetI) }, eventListeners));
            const calculatedPiece = {
                o: key,
                xy: {
                    x: xPosition,
                    y: yPosition
                },
                piece,
                renderElement: renderElementObject
            };
            return calculatedPiece;
        });
        allCalculatedPieces = [...allCalculatedPieces, ...calculatedPieces];
    });
    return allCalculatedPieces;
}

const genericFunction = value => () => value;

const layoutHash = {
    clusterbar: clusterBarLayout,
    bar: barLayout,
    point: pointLayout,
    swarm: swarmLayout,
    timeline: timelineLayout
};
const midMod = (d) => (d.middle ? d.middle : 0);
const zeroFunction = genericFunction(0);
const twoPI = Math.PI * 2;
const naturalLanguageTypes = {
    bar: { items: "bar", chart: "bar chart" },
    clusterbar: { items: "bar", chart: "grouped bar chart" },
    swarm: { items: "point", chart: "swarm plot" },
    point: { items: "point", chart: "point plot" },
    timeline: { items: "bar", chart: "timeline" }
};
const calculateMappedMiddles = (oScale, middleMax, padding) => {
    const oScaleDomainValues = oScale.domain();
    const mappedMiddles = {};
    oScaleDomainValues.forEach((p, q) => {
        const base = oScale(p) - padding;
        const next = oScaleDomainValues[q + 1]
            ? oScale(oScaleDomainValues[q + 1])
            : middleMax;
        const diff = (next - base) / 2;
        mappedMiddles[p] = base + diff;
    });
    return mappedMiddles;
};
const calculateOrdinalFrame = (currentProps, currentState) => {
    let oLabels;
    const projectedColumns = {};
    const { oPadding: padding = 0, summaryType: baseSummaryType, type: baseType, connectorType: baseConnectorType, oAccessor: baseOAccessor, rAccessor: baseRAccessor, connectorStyle: baseConnectorStyle, style: baseStyle, rExtent: baseRExtent, oSort, pieceClass: basePieceClass, summaryStyle: baseSummaryStyle, summaryClass: baseSummaryClass, dynamicColumnWidth, projection, customHoverBehavior, customClickBehavior, customDoubleClickBehavior, size, pixelColumnWidth, title: baseTitle, oLabel, hoverAnnotation, pieceHoverAnnotation, summaryHoverAnnotation, backgroundGraphics, foregroundGraphics, oScaleType, rScaleType, legend, renderKey: baseRenderKey, data, margin: baseMargin, oExtent: baseOExtent, axes: baseAxes, pieceIDAccessor: basePieceIDAccessor, multiAxis, baseMarkProps = {}, annotations, sketchyRenderingEngine } = currentProps;
    const summaryType = objectifyType(baseSummaryType);
    const pieceType = objectifyType(baseType);
    const connectorType = objectifyType(baseConnectorType);
    const oAccessor = stringToArrayFn(baseOAccessor, (d) => d.renderKey);
    const rAccessor = stringToArrayFn(baseRAccessor, (d) => d.value || 1);
    const renderKey = stringToFn(baseRenderKey, (d, i) => i);
    const eventListenersGenerator = () => ({});
    const connectorStyle = stringToFn(baseConnectorStyle, () => ({}), true);
    const summaryStyle = stringToFn(baseSummaryStyle, () => ({}), true);
    const pieceStyle = stringToFn(baseStyle, () => ({}), true);
    const pieceClass = stringToFn(basePieceClass, () => "", true);
    const summaryClass = stringToFn(baseSummaryClass, () => "", true);
    const title = typeof baseTitle === "object" &&
        !React.isValidElement(baseTitle) &&
        baseTitle !== null
        ? baseTitle
        : { title: baseTitle, orient: "top" };
    const pieceIDAccessor = stringToFn(basePieceIDAccessor, () => "");
    const originalRAccessor = Array.isArray(baseRAccessor)
        ? baseRAccessor
        : [baseRAccessor];
    const originalOAccessor = Array.isArray(baseOAccessor)
        ? baseOAccessor
        : [baseOAccessor];
    const { allData, multiExtents } = keyAndObjectifyBarData({
        data,
        renderKey,
        oAccessor,
        rAccessor,
        originalRAccessor,
        originalOAccessor,
        multiAxis
    });
    let columnOverlays;
    const prevProps = currentState.props;
    const shouldRecalculateOverlay = currentProps.data !== prevProps.data ||
        currentProps.size[0] !== prevProps.size[0] ||
        currentProps.size[1] !== prevProps.size[1] ||
        currentProps.margin !== prevProps.margin ||
        !currentState.columnOverlays ||
        currentState.columnOverlays.length === 0 ||
        currentProps.customClickBehavior !== prevProps.customClickBehavior ||
        currentProps.customDoubleClickBehavior !==
            prevProps.customDoubleClickBehavior ||
        currentProps.customHoverBehavior !== prevProps.customHoverBehavior;
    let arrayWrappedAxis;
    if (Array.isArray(baseAxes)) {
        arrayWrappedAxis = baseAxes.map((axisFnOrObject) => typeof axisFnOrObject === "function"
            ? axisFnOrObject({ size: currentProps.size })
            : axisFnOrObject);
    }
    else if (baseAxes) {
        arrayWrappedAxis = [baseAxes].map((axisFnOrObject) => typeof axisFnOrObject === "function"
            ? axisFnOrObject({ size: currentProps.size })
            : axisFnOrObject);
    }
    if (multiExtents && baseAxes) {
        arrayWrappedAxis.forEach((d, i) => {
            d.extentOverride = multiExtents[i];
        });
    }
    const margin = calculateMargin({
        margin: baseMargin,
        axes: arrayWrappedAxis,
        title,
        oLabel,
        projection,
        size
    });
    const { adjustedPosition, adjustedSize } = adjustedPositionSize({
        size,
        margin,
        projection
    });
    const oExtentSettings = baseOExtent === undefined || Array.isArray(baseOExtent)
        ? { extent: baseOExtent }
        : baseOExtent;
    const calculatedOExtent = allData.reduce((p, c) => {
        const baseOValue = c.column;
        const oValue = baseOValue !== undefined ? String(baseOValue) : baseOValue;
        if (p.indexOf(oValue) === -1) {
            p.push(oValue);
        }
        return p;
    }, []);
    let oExtent = oExtentSettings.extent || calculatedOExtent;
    if (pieceType.type === "barpercent") {
        const oExtentSums = oExtent
            .map((d) => allData
            .filter((p) => String(p.column) === d)
            .reduce((p, c) => p + c.value, 0))
            .reduce((p, c, i) => {
            p[oExtent[i]] = c;
            return p;
        }, {});
        allData.forEach((d) => {
            d.value = (oExtentSums[d.column] && d.value / oExtentSums[d.column]) || 0;
        });
        pieceType.type = "bar";
    }
    if (pixelColumnWidth) {
        if (projection === "radial") {
            console.error("pixelColumnWidth is not honored in radial mode");
        }
        else if (projection === "vertical") {
            adjustedSize[0] = oExtent.length * pixelColumnWidth;
        }
        else {
            adjustedSize[1] = oExtent.length * pixelColumnWidth;
        }
    }
    const oDomain = (projection === "vertical" && [0, adjustedSize[0]]) || [
        0,
        adjustedSize[1]
    ];
    const cwHash = oExtent.reduce((p, c) => {
        p[c] = (1 / oExtent.length) * oDomain[1];
        p.total += p[c];
        return p;
    }, { total: 0 });
    const castOScaleType = oScaleType;
    const oScale = dynamicColumnWidth
        ? scaleOrdinal()
        : (castOScaleType === null || castOScaleType === void 0 ? void 0 : castOScaleType.domain)
            ? castOScaleType
            : castOScaleType();
    oScale.domain(oExtent);
    let maxColumnValues;
    const rExtentSettings = baseRExtent === undefined || Array.isArray(baseRExtent)
        ? { extent: baseRExtent, onChange: undefined, includeAnnotations: false }
        : baseRExtent;
    let rExtent = rExtentSettings.extent;
    let subZeroRExtent = [0, 0];
    if (pieceType.type === "bar" &&
        summaryType.type &&
        summaryType.type !== "none") {
        pieceType.type = "none";
    }
    const annotationsForExtent = [];
    if (rExtentSettings.includeAnnotations && annotations) {
        rAccessor.forEach((actualRAccessor) => {
            annotations.forEach((annotation, annotationIndex) => {
                const r = actualRAccessor(annotation, annotationIndex);
                if (isFinite(r)) {
                    annotationsForExtent.push(r);
                }
            });
        });
    }
    if (pieceType.type === "timeline") {
        const rData = allData.map((d) => d.value);
        const leftExtent = extent$1(rData.map((d) => d[0]));
        const rightExtent = extent$1(rData.map((d) => d[1]));
        rExtent = extent$1([...leftExtent, ...rightExtent, ...annotationsForExtent]);
    }
    else if (pieceType.type !== "bar") {
        rExtent = extent$1([
            ...allData.map((d) => d.value),
            ...annotationsForExtent
        ]);
    }
    else {
        const nestedPositiveData = [];
        const nestedNegativeData = [];
        const positiveDataKeys = {};
        const negativeDataKeys = {};
        for (const datum of allData) {
            if (datum.value >= 0) {
                if (!positiveDataKeys[datum.column]) {
                    positiveDataKeys[datum.column] = {
                        column: datum.column,
                        value: 0
                    };
                    nestedPositiveData.push(positiveDataKeys[datum.column]);
                }
                positiveDataKeys[datum.column].value += datum.value;
            }
            else {
                if (!negativeDataKeys[datum.column]) {
                    negativeDataKeys[datum.column] = {
                        column: datum.column,
                        value: 0
                    };
                    nestedNegativeData.push(negativeDataKeys[datum.column]);
                }
                negativeDataKeys[datum.column].value += datum.value;
            }
        }
        const positiveAnnotations = annotationsForExtent.filter((d) => d > 0);
        rExtent = [
            0,
            nestedPositiveData.length === 0 && positiveAnnotations.length === 0
                ? 0
                : Math.max(max([
                    ...nestedPositiveData.map((d) => d.value),
                    ...positiveAnnotations
                ]), 0)
        ];
        const negativeAnnotations = annotationsForExtent.filter((d) => d < 0);
        subZeroRExtent = [
            0,
            nestedNegativeData.length === 0
                ? 0
                : Math.min(min([
                    ...nestedNegativeData.map((d) => d.value),
                    ...negativeAnnotations
                ]), 0)
        ];
        rExtent = [subZeroRExtent[1], rExtent[1]];
    }
    if ((pieceType.type === "clusterbar" || multiAxis) && rExtent[0] > 0) {
        rExtent[0] = 0;
    }
    const calculatedRExtent = rExtent;
    if (rExtentSettings.extent &&
        rExtentSettings.extent[0] !== undefined &&
        rExtentSettings.extent[1] !== undefined) {
        rExtent = rExtentSettings.extent;
    }
    else {
        if (rExtentSettings.extent &&
            rExtentSettings.extent[1] !== undefined &&
            rExtentSettings.extent[0] === undefined) {
            rExtent[1] = rExtentSettings.extent[1];
        }
        if (rExtentSettings.extent &&
            rExtentSettings.extent[0] !== undefined &&
            rExtentSettings.extent[1] === undefined) {
            rExtent[0] = rExtentSettings.extent[0];
        }
    }
    if (currentProps.invertR ||
        (rExtentSettings.extent &&
            rExtentSettings.extent[0] > rExtentSettings.extent[1])) {
        rExtent = [rExtent[1], rExtent[0]];
    }
    const nestedPieces = {};
    for (const datum of allData) {
        if (!nestedPieces[datum.column]) {
            nestedPieces[datum.column] = [];
        }
        nestedPieces[datum.column].push(datum);
    }
    if (oSort !== undefined) {
        oExtent = oExtent.sort((a, b) => oSort(a, b, nestedPieces[a].map((d) => d.data), nestedPieces[b].map((d) => d.data)));
        oScale.domain(oExtent);
    }
    if (dynamicColumnWidth) {
        let columnValueCreator;
        if (typeof dynamicColumnWidth === "string") {
            columnValueCreator = (d) => sum(d, (p) => p.data[dynamicColumnWidth]);
        }
        else {
            columnValueCreator = (d) => dynamicColumnWidth(d.map((p) => p.data));
        }
        const thresholdDomain = [0];
        const columnValues = [];
        maxColumnValues = 0;
        oExtent.forEach((d) => {
            const oValues = allData.filter((p) => p.column === d);
            const columnValue = columnValueCreator(oValues);
            columnValues.push(columnValue);
            maxColumnValues += columnValue;
        });
        cwHash.total = 0;
        oExtent.forEach((d, i) => {
            const oValue = columnValues[i];
            const stepValue = (oValue / maxColumnValues) * (oDomain[1] - oDomain[0]);
            cwHash[d] = stepValue;
            cwHash.total += stepValue;
            if (i !== oExtent.length - 1) {
                thresholdDomain.push(stepValue + thresholdDomain[i]);
            }
        });
        oScale.range(thresholdDomain);
    }
    else {
        oScale.range(oDomain);
    }
    const rDomain = (projection === "vertical" && [0, adjustedSize[1]]) || [
        0,
        adjustedSize[0]
    ];
    const castRScaleType = rScaleType;
    // if rScaleType has a domain that means it's instantiated, otherwise, it needs to be instantiated
    const instantiatedRScaleType = castRScaleType.domain
        ? castRScaleType
        : castRScaleType();
    const zeroCheck = instantiatedRScaleType(0);
    if (rExtentSettings.extent &&
        rExtentSettings.extent[0] !== undefined &&
        (isNaN(zeroCheck) || zeroCheck === -Infinity || zeroCheck === Infinity)) {
        rExtent[0] = rExtentSettings.extent[0];
    }
    const rScale = instantiatedRScaleType.copy().domain(rExtent).range(rDomain);
    const rScaleReverse = scaleLinear().domain(rDomain).range(rDomain.reverse());
    const rScaleVertical = instantiatedRScaleType
        .copy()
        .domain(rExtent)
        .range(rDomain);
    const columnWidth = cwHash ? 0 : oScale.bandwidth();
    let pieceData = [];
    let mappedMiddleSize = adjustedSize[1];
    if (projection === "vertical") {
        mappedMiddleSize = adjustedSize[0];
    }
    const mappedMiddles = calculateMappedMiddles(oScale, mappedMiddleSize, padding);
    pieceData = oExtent.map((d) => (nestedPieces[d] ? nestedPieces[d] : []));
    let zeroValue = projection === "vertical" ? rScaleReverse(rScale(0)) : rScale(0);
    if ((isNaN(zeroValue) || zeroValue === -Infinity || zeroValue === Infinity) &&
        rExtentSettings.extent &&
        rExtentSettings.extent[0] !== undefined &&
        (zeroCheck === -Infinity || zeroCheck === Infinity)) {
        zeroValue =
            projection === "vertical"
                ? rScaleReverse(rScale(rExtentSettings.extent[0]))
                : rScale(rExtentSettings.extent[0]);
    }
    oExtent.forEach((o, i) => {
        projectedColumns[o] = {
            name: o,
            padding,
            pieceData: pieceData[i],
            pieces: pieceData[i]
        };
        projectedColumns[o].x = oScale(o) + padding / 2;
        projectedColumns[o].y = 0;
        projectedColumns[o].middle = mappedMiddles[o] + padding / 2;
        let negativeOffset = zeroValue;
        let positiveOffset = zeroValue;
        let negativeBaseValue = 0;
        let positiveBaseValue = 0;
        projectedColumns[o].pieceData.forEach((piece) => {
            let valPosition;
            if (pieceType.type === "timeline") {
                piece.scaledValue = rScale(piece.value[0]);
                piece.scaledEndValue = rScale(piece.value[1]);
                piece.scaledVerticalValue = rScaleVertical(piece.value[0]);
            }
            else if (pieceType.type !== "bar" && pieceType.type !== "clusterbar") {
                piece.scaledValue = rScale(piece.value);
                piece.scaledVerticalValue = rScaleVertical(piece.value);
            }
            else if (pieceType.type === "clusterbar") {
                valPosition =
                    projection === "vertical"
                        ? rScaleReverse(rScale(piece.value))
                        : rScale(piece.value);
                piece.scaledValue = Math.abs(zeroValue - valPosition);
            }
            piece.x = projectedColumns[o].x;
            if (piece.value >= 0) {
                if (pieceType.type === "bar") {
                    piece.scaledValue =
                        projection === "vertical"
                            ? positiveOffset -
                                rScaleReverse(rScale(positiveBaseValue + piece.value))
                            : rScale(positiveBaseValue + piece.value) - positiveOffset;
                    positiveBaseValue += piece.value;
                }
                piece.base = zeroValue;
                piece.bottom = pieceType.type === "bar" ? positiveOffset : 0;
                piece.middle = piece.scaledValue / 2 + positiveOffset;
                positiveOffset =
                    projection === "vertical"
                        ? positiveOffset - piece.scaledValue
                        : positiveOffset + piece.scaledValue;
                piece.negative = false;
            }
            else {
                if (pieceType.type === "bar") {
                    piece.scaledValue =
                        projection === "vertical"
                            ? Math.abs(rScale(piece.value) - rScale(0))
                            : Math.abs(rScale(piece.value) - zeroValue);
                    negativeBaseValue += piece.value;
                }
                piece.base = zeroValue;
                piece.bottom = pieceType.type === "bar" ? negativeOffset : 0;
                piece.middle = negativeOffset - piece.scaledValue / 2;
                negativeOffset =
                    projection === "vertical"
                        ? negativeOffset + piece.scaledValue
                        : negativeOffset - piece.scaledValue;
                piece.negative = true;
            }
        });
        if (cwHash) {
            projectedColumns[o].width = cwHash[o] - padding;
            if (currentProps.ordinalAlign === "center") {
                if (i === 0) {
                    projectedColumns[o].x =
                        projectedColumns[o].x - projectedColumns[o].width / 2;
                    projectedColumns[o].middle =
                        projectedColumns[o].middle - projectedColumns[o].width / 2;
                }
                else {
                    projectedColumns[o].x =
                        projectedColumns[oExtent[i - 1]].x +
                            projectedColumns[oExtent[i - 1]].width;
                    projectedColumns[o].middle =
                        projectedColumns[o].x + projectedColumns[o].width / 2;
                }
            }
            projectedColumns[o].pct = cwHash[o] / cwHash.total;
            projectedColumns[o].pct_start =
                (projectedColumns[o].x - oDomain[0]) / cwHash.total;
            projectedColumns[o].pct_padding = padding / cwHash.total;
            projectedColumns[o].pct_middle =
                (projectedColumns[o].middle - oDomain[0]) / cwHash.total;
        }
        else {
            projectedColumns[o].width = columnWidth - padding;
            if (currentProps.ordinalAlign === "center") {
                projectedColumns[o].x =
                    projectedColumns[o].x - projectedColumns[o].width / 2;
                projectedColumns[o].middle =
                    projectedColumns[o].middle - projectedColumns[o].width / 2;
            }
            projectedColumns[o].pct = columnWidth / adjustedSize[1];
            projectedColumns[o].pct_start =
                (projectedColumns[o].x - oDomain[0]) / adjustedSize[1];
            projectedColumns[o].pct_padding = padding / adjustedSize[1];
            projectedColumns[o].pct_middle =
                (projectedColumns[o].middle - oDomain[0]) / adjustedSize[1];
        }
    });
    const labelArray = [];
    const pieArcs = [];
    const labelSettings = typeof oLabel === "object"
        ? Object.assign({ label: true, padding: 5 }, oLabel)
        : { orient: "default", label: oLabel, padding: 5 };
    if (oLabel || hoverAnnotation) {
        const offsetPct = (pieceType.offsetAngle && pieceType.offsetAngle / 360) || 0;
        const rangePct = (pieceType.angleRange &&
            pieceType.angleRange.map((d) => d / 360)) || [0, 1];
        const rangeMod = rangePct[1] - rangePct[0];
        const adjustedPct = rangeMod < 1 ? scaleLinear().domain([0, 1]).range(rangePct) : (d) => d;
        oExtent.forEach((d) => {
            const arcGenerator = arc()
                .innerRadius(0)
                .outerRadius(rScale.range()[1] / 2);
            const angle = projectedColumns[d].pct * rangeMod;
            const startAngle = adjustedPct(projectedColumns[d].pct_start + offsetPct);
            const endAngle = startAngle + angle;
            const midAngle = startAngle + angle / 2;
            const markD = arcGenerator({
                startAngle: startAngle * twoPI,
                endAngle: endAngle * twoPI
            });
            const translate = [adjustedSize[0] / 2, adjustedSize[1] / 2];
            const centroid = arcGenerator.centroid({
                startAngle: startAngle * twoPI,
                endAngle: endAngle * twoPI
            });
            const addedPadding = centroid[1] > 0 &&
                (!labelSettings.orient ||
                    labelSettings.orient === "default" ||
                    labelSettings.orient === "edge")
                ? 8
                : 0;
            const outerPoint = pointOnArcAtAngle$1([0, 0], midAngle, rScale.range()[1] / 2 + labelSettings.padding + addedPadding);
            const pieArc = {
                startAngle,
                endAngle,
                midAngle,
                markD,
                translate,
                centroid,
                outerPoint
            };
            projectedColumns[d].pieArc = pieArc;
            pieArcs.push(pieArc);
        });
    }
    if (currentProps.oLabel) {
        let labelingFn;
        if (typeof labelSettings.label === "function") {
            labelingFn = labelSettings.label;
        }
        else {
            const labelStyle = {
                textAnchor: "middle"
            };
            if (projection === "horizontal" && labelSettings.orient === "right") {
                labelStyle.textAnchor = "start";
            }
            else if (projection === "horizontal") {
                labelStyle.textAnchor = "end";
            }
            labelingFn = (d, p, i) => {
                const additionalStyle = {};
                let transformRotate;
                if (projection === "radial" && labelSettings.orient === "stem") {
                    transformRotate = `rotate(${pieArcs[i].outerPoint[0] < 0
                        ? pieArcs[i].midAngle * 360 + 90
                        : pieArcs[i].midAngle * 360 - 90})`;
                }
                else if (projection === "radial" &&
                    labelSettings.orient === "annotation") {
                    const { centroid } = pieArcs[i];
                    const labelIndex = pieArcs.filter((p, q) => q < i &&
                        centroid[0] < 0 === pieArcs[q].centroid[0] < 0 &&
                        centroid[1] < 0 === pieArcs[q].centroid[1] < 0).length;
                    const labelMod = labelIndex * 15;
                    let labelLength = d.length * 7;
                    let textAnchor = "start";
                    let positionProps = { dx: 0, dy: 0 };
                    if (centroid[0] < 0) {
                        textAnchor = "end";
                        labelLength = -labelLength;
                        positionProps.dx = -35;
                    }
                    else {
                        positionProps.dx = 35;
                    }
                    if (centroid[1] < 0) {
                        positionProps.dy = -35 - labelMod;
                    }
                    else {
                        positionProps.dy = 35 + labelMod;
                    }
                    return (React.createElement("g", null,
                        React.createElement("path", { fill: "none", stroke: "black", strokeWidth: 2, d: `M0,0L${positionProps.dx},${positionProps.dy}L${positionProps.dx + labelLength},${positionProps.dy}` }),
                        React.createElement("text", { textAnchor: textAnchor, x: positionProps.dx, y: positionProps.dy - 2 }, d)));
                }
                else if (projection === "radial" &&
                    labelSettings.orient !== "center") {
                    transformRotate = `rotate(${pieArcs[i].outerPoint[1] < 0
                        ? pieArcs[i].midAngle * 360
                        : pieArcs[i].midAngle * 360 + 180})`;
                }
                if (projection === "radial" &&
                    labelSettings.orient === "stem" &&
                    ((pieArcs[i].outerPoint[0] > 0 && labelSettings.padding < 0) ||
                        (pieArcs[i].outerPoint[0] < 0 && labelSettings.padding >= 0))) {
                    additionalStyle.textAnchor = "end";
                }
                else if (projection === "radial" && labelSettings.orient === "stem") {
                    additionalStyle.textAnchor = "start";
                }
                return (React.createElement("text", Object.assign({}, labelStyle, additionalStyle, { transform: transformRotate }), d));
            };
        }
        oExtent.forEach((d, i) => {
            let xPosition = projectedColumns[d].middle;
            let yPosition = 0;
            if (projection === "horizontal") {
                yPosition = projectedColumns[d].middle;
                if (labelSettings.orient === "right") {
                    xPosition = adjustedSize[0] + 3;
                }
                else {
                    xPosition = -3;
                }
            }
            else if (projection === "radial") {
                if (labelSettings.orient === "annotation") {
                    xPosition =
                        pieArcs[i].centroid[0] * 0.25 +
                            pieArcs[i].outerPoint[0] * 0.75 +
                            pieArcs[i].translate[0];
                    yPosition =
                        pieArcs[i].centroid[1] * 0.25 +
                            pieArcs[i].outerPoint[1] * 0.75 +
                            pieArcs[i].translate[1];
                }
                else if (labelSettings.orient === "center") {
                    xPosition = pieArcs[i].centroid[0] + pieArcs[i].translate[0];
                    yPosition = pieArcs[i].centroid[1] + pieArcs[i].translate[1];
                }
                else {
                    xPosition = pieArcs[i].outerPoint[0] + pieArcs[i].translate[0];
                    yPosition = pieArcs[i].outerPoint[1] + pieArcs[i].translate[1];
                }
            }
            const labelValue = labelSettings.labelFormatter
                ? labelSettings.labelFormatter(d)
                : d;
            const label = labelingFn(labelValue, projectedColumns[d].pieceData.map((d) => d.data), i, projectedColumns[d]);
            labelArray.push(React.createElement("g", { key: `olabel-${i}`, transform: `translate(${xPosition},${yPosition})` }, label));
        });
        if (projection === "vertical") {
            let labelY;
            if (labelSettings.orient === "top") {
                labelY = -15;
            }
            else {
                labelY = 15 + rScale.range()[1];
            }
            oLabels = (React.createElement("g", { key: "ordinalframe-labels-container", className: "ordinal-labels", transform: `translate(0,${labelY})` }, labelArray));
        }
        else if (projection === "horizontal") {
            oLabels = (React.createElement("g", { key: "ordinalframe-labels-container", className: "ordinal-labels" }, labelArray));
        }
        else if (projection === "radial") {
            oLabels = (React.createElement("g", { key: "ordinalframe-labels-container", className: "ordinal-labels" }, labelArray));
        }
    }
    if (!currentProps.pieceHoverAnnotation &&
        !currentProps.summaryHoverAnnotation &&
        (currentProps.hoverAnnotation ||
            currentProps.customClickBehavior ||
            currentProps.customDoubleClickBehavior ||
            currentProps.customHoverBehavior)) {
        if (shouldRecalculateOverlay) {
            columnOverlays = oExtent.map((d, i) => {
                const barColumnWidth = projectedColumns[d].width;
                let xPosition = projectedColumns[d].x;
                let yPosition = 0;
                let height = rScale.range()[1];
                let width = barColumnWidth + padding;
                if (projection === "horizontal") {
                    yPosition = projectedColumns[d].x;
                    xPosition = 0;
                    width = rScale.range()[1];
                    height = barColumnWidth;
                }
                if (projection === "radial") {
                    const { markD, centroid, translate, midAngle } = pieArcs[i];
                    const radialMousePackage = {
                        type: "column-hover",
                        column: projectedColumns[d],
                        pieces: projectedColumns[d].pieceData,
                        summary: projectedColumns[d].pieceData,
                        arcAngles: {
                            centroid,
                            translate,
                            midAngle,
                            length: rScale.range()[1] / 2
                        }
                    };
                    return {
                        markType: "path",
                        key: `hover${d}`,
                        d: markD,
                        transform: `translate(${translate.join(",")})`,
                        style: { opacity: 0 },
                        overlayData: radialMousePackage,
                        onDoubleClick: customDoubleClickBehavior &&
                            ((e) => {
                                customDoubleClickBehavior(radialMousePackage, e);
                            }),
                        onClick: customClickBehavior &&
                            ((e) => {
                                customClickBehavior(radialMousePackage, e);
                            }),
                        onMouseEnter: customHoverBehavior &&
                            ((e) => {
                                customHoverBehavior(radialMousePackage, e);
                            }),
                        onMouseLeave: customHoverBehavior &&
                            ((e) => {
                                customHoverBehavior(e);
                            })
                    };
                }
                const baseMousePackage = {
                    type: "column-hover",
                    column: projectedColumns[d],
                    pieces: projectedColumns[d].pieceData,
                    summary: projectedColumns[d].pieceData
                };
                return {
                    markType: "rect",
                    key: `hover-${d}`,
                    x: xPosition,
                    y: yPosition,
                    height: height,
                    width: width,
                    style: { opacity: 0 },
                    onDoubleClick: customDoubleClickBehavior &&
                        ((e) => {
                            customDoubleClickBehavior(baseMousePackage, e);
                        }),
                    onClick: customClickBehavior &&
                        ((e) => {
                            customClickBehavior(baseMousePackage, e);
                        }),
                    onMouseEnter: customHoverBehavior &&
                        ((e) => {
                            customHoverBehavior(baseMousePackage, e);
                        }),
                    onMouseLeave: (e) => {
                        customHoverBehavior(undefined, e);
                    },
                    overlayData: baseMousePackage
                };
            });
        }
        else {
            columnOverlays = currentState.columnOverlays;
        }
    }
    const { renderMode, canvasSummaries, summaryRenderMode, connectorClass, connectorRenderMode, canvasConnectors, canvasPieces } = currentProps;
    let pieceDataXY;
    const pieceRenderMode = stringToFn(renderMode, undefined, true);
    const pieceCanvasRender = stringToFn(canvasPieces, undefined, true);
    const summaryCanvasRender = stringToFn(canvasSummaries, undefined, true);
    const connectorCanvasRender = stringToFn(canvasConnectors, undefined, true);
    const pieceTypeForXY = pieceType.type && pieceType.type !== "none" ? pieceType.type : "point";
    const pieceTypeLayout = typeof pieceTypeForXY === "function"
        ? pieceTypeForXY
        : layoutHash[pieceTypeForXY];
    const calculatedPieceData = pieceTypeLayout({
        type: pieceType,
        data: projectedColumns,
        renderMode: pieceRenderMode,
        eventListenersGenerator,
        styleFn: pieceStyle,
        projection,
        classFn: pieceClass,
        adjustedSize,
        chartSize: size,
        margin,
        rScale,
        baseMarkProps: Object.assign(Object.assign({}, baseMarkProps), { sketchyGenerator: sketchyRenderingEngine && sketchyRenderingEngine.generator })
    });
    const keyedData = calculatedPieceData.reduce((p, c) => {
        if (c.o) {
            if (!p[c.o]) {
                p[c.o] = [];
            }
            p[c.o].push(c);
        }
        return p;
    }, {});
    Object.keys(projectedColumns).forEach((d) => {
        projectedColumns[d].xyData = keyedData[d] || [];
    });
    let calculatedSummaries = {};
    if (summaryType.type && summaryType.type !== "none") {
        calculatedSummaries = drawSummaries({
            data: projectedColumns,
            type: summaryType,
            renderMode: stringToFn(summaryRenderMode, undefined, true),
            styleFn: stringToFn(summaryStyle, () => ({}), true),
            classFn: stringToFn(summaryClass, () => "", true),
            projection,
            eventListenersGenerator,
            adjustedSize,
            baseMarkProps: Object.assign(Object.assign({}, baseMarkProps), { sketchyGenerator: sketchyRenderingEngine && sketchyRenderingEngine.generator }),
            //        chartSize: size,
            margin
        });
        calculatedSummaries.originalData = projectedColumns;
    }
    const yMod = projection === "horizontal" ? midMod : zeroFunction;
    const xMod = projection === "vertical" ? midMod : zeroFunction;
    const basePieceData = calculatedPieceData
        .map((d) => {
        if (d.piece && d.xy) {
            return Object.assign(Object.assign({}, d.piece), { type: "frame-hover", x: d.xy.x + xMod(d.xy), y: d.xy.y + yMod(d.xy) });
        }
        return null;
    })
        .filter((d) => d);
    if ((pieceHoverAnnotation &&
        ["bar", "clusterbar", "timeline"].indexOf(pieceType.type) === -1) ||
        summaryHoverAnnotation) {
        if (summaryHoverAnnotation && calculatedSummaries.xyPoints) {
            pieceDataXY = calculatedSummaries.xyPoints.map((d) => Object.assign({}, d, {
                type: "frame-hover",
                isSummaryData: true,
                x: d.x,
                y: d.y
            }));
        }
        else if (pieceHoverAnnotation && calculatedPieceData) {
            //Check interaction layer rendering when only one point per column
            pieceDataXY = basePieceData;
        }
    }
    const { axis, axesTickLines } = orFrameAxisGenerator({
        axis: arrayWrappedAxis,
        data: allData,
        projection,
        adjustedSize,
        size,
        rScale,
        rScaleType: instantiatedRScaleType.copy(),
        pieceType,
        rExtent,
        maxColumnValues,
        xyData: basePieceData,
        margin,
        thresholds: calculatedSummaries.thresholds
    });
    if (pieceHoverAnnotation &&
        ["bar", "clusterbar", "timeline"].indexOf(pieceType.type) !== -1) {
        const yMod = projection === "horizontal" ? midMod : zeroFunction;
        const xMod = projection === "vertical" ? midMod : zeroFunction;
        if (shouldRecalculateOverlay) {
            columnOverlays = calculatedPieceData.map((d, i) => {
                const mousePackage = Object.assign(Object.assign({}, d.piece), { x: d.xy.x + xMod(d.xy), y: d.xy.y + yMod(d.xy) });
                if (React.isValidElement(d.renderElement)) {
                    return {
                        renderElement: d.renderElement,
                        overlayData: mousePackage
                    };
                }
                return Object.assign(Object.assign({}, d.renderElement), { key: `hover-${i}`, type: "frame-hover", style: { opacity: 0 }, overlayData: mousePackage, onClick: customClickBehavior &&
                        ((e) => {
                            customClickBehavior(mousePackage.data, e);
                        }), onDoubleClick: customDoubleClickBehavior &&
                        ((e) => {
                            customDoubleClickBehavior(mousePackage.data, e);
                        }), onMouseEnter: customHoverBehavior &&
                        ((e) => {
                            customHoverBehavior(mousePackage.data, e);
                        }), onMouseLeave: customHoverBehavior &&
                        ((e) => {
                            customHoverBehavior(undefined, e);
                        }) });
            });
        }
        else {
            columnOverlays = currentState.columnOverlays;
        }
    }
    const typeAriaLabel = (pieceType.type !== undefined &&
        typeof pieceType.type !== "function" &&
        naturalLanguageTypes[pieceType.type]) || {
        items: "piece",
        chart: "ordinal chart"
    };
    const orFrameRender = {
        connectors: {
            accessibleTransform: (data, i) => data[i],
            projection,
            data: { keyedData, oExtent },
            styleFn: stringToFn(connectorStyle, () => ({}), true),
            classFn: stringToFn(connectorClass, () => "", true),
            renderMode: stringToFn(connectorRenderMode, undefined, true),
            canvasRender: connectorCanvasRender,
            behavior: orFrameConnectionRenderer,
            type: connectorType,
            eventListenersGenerator,
            pieceType
        },
        summaries: {
            accessibleTransform: (data, i) => {
                const columnName = oExtent[i];
                const summaryPackage = {
                    type: "column-hover",
                    column: projectedColumns[columnName],
                    pieces: projectedColumns[columnName].pieceData,
                    summary: projectedColumns[columnName].pieceData,
                    oAccessor
                };
                return summaryPackage;
            },
            data: calculatedSummaries.marks,
            behavior: renderLaidOutSummaries,
            canvasRender: summaryCanvasRender,
            styleFn: stringToFn(summaryStyle, () => ({}), true),
            classFn: stringToFn(summaryClass, () => "", true)
        },
        pieces: {
            accessibleTransform: (data, i) => (Object.assign(Object.assign({}, (data[i].piece ? Object.assign(Object.assign({}, data[i].piece), data[i].xy) : data[i])), { type: "frame-hover" })),
            shouldRender: pieceType.type && pieceType.type !== "none",
            data: calculatedPieceData,
            behavior: renderLaidOutPieces,
            canvasRender: pieceCanvasRender,
            styleFn: stringToFn(pieceStyle, () => ({}), true),
            classFn: stringToFn(pieceClass, () => "", true),
            axis: arrayWrappedAxis,
            ariaLabel: typeAriaLabel
        }
    };
    if (rExtentSettings.onChange &&
        (currentState.calculatedRExtent || []).join(",") !==
            (calculatedRExtent || []).join(",")) {
        rExtentSettings.onChange(calculatedRExtent);
    }
    if (oExtentSettings.onChange &&
        (currentState.calculatedOExtent || []).join(",") !==
            (calculatedOExtent || []).join(",")) {
        oExtentSettings.onChange(calculatedOExtent);
    }
    let legendSettings;
    if (legend) {
        legendSettings = legend === true ? {} : legend;
    }
    return {
        pieceDataXY,
        adjustedPosition,
        adjustedSize,
        backgroundGraphics,
        foregroundGraphics,
        axisData: arrayWrappedAxis,
        axes: axis,
        axesTickLines,
        oLabels: { labels: oLabels },
        title,
        columnOverlays,
        renderNumber: currentState.renderNumber + 1,
        oAccessor,
        rAccessor,
        oScaleType,
        rScaleType: instantiatedRScaleType,
        oExtent,
        rExtent,
        oScale,
        rScale,
        calculatedOExtent,
        calculatedRExtent,
        projectedColumns,
        margin,
        legendSettings,
        orFrameRender,
        summaryType,
        type: pieceType,
        pieceIDAccessor,
        props: currentProps
    };
};

const xScale$1 = scaleLinear();
const yScale$1 = scaleLinear();
const projectedCoordinatesObject = { y: "y", x: "x" };
const defaultOverflow = { top: 0, bottom: 0, left: 0, right: 0 };
const defaultProps$1 = {
    annotations: [],
    foregroundGraphics: [],
    projection: "vertical",
    size: [500, 500],
    className: "",
    data: [],
    oScaleType: scaleBand(),
    rScaleType: scaleLinear,
    type: "none",
    useSpans: false,
    optimizeCustomTooltipPosition: false
};
const OrdinalFrame = React.memo(function OrdinalFrame(allProps) {
    const props = Object.assign(Object.assign({}, defaultProps$1), allProps);
    const baseState = {
        adjustedPosition: [],
        adjustedSize: [],
        backgroundGraphics: undefined,
        foregroundGraphics: undefined,
        axisData: undefined,
        renderNumber: 0,
        oLabels: { labels: [] },
        oAccessor: stringToArrayFn("renderKey"),
        rAccessor: stringToArrayFn("value"),
        oScale: scaleBand(),
        rScale: scaleLinear(),
        axes: undefined,
        calculatedOExtent: [],
        calculatedRExtent: [0, 1],
        columnOverlays: [],
        dataVersion: undefined,
        legendSettings: undefined,
        margin: { top: 0, bottom: 0, left: 0, right: 0 },
        oExtent: [],
        oScaleType: scaleBand(),
        orFrameRender: {},
        pieceDataXY: [],
        pieceIDAccessor: stringToFn("semioticPieceID"),
        projectedColumns: {},
        rExtent: [],
        rScaleType: scaleLinear(),
        summaryType: { type: "none" },
        title: {},
        type: { type: "none" },
        props
    };
    const initialState = useMemo(() => (Object.assign(Object.assign({}, baseState), calculateOrdinalFrame(props, baseState))), []);
    const state = useDerivedStateFromProps(deriveOrdinalFrameState, props, initialState);
    useLegacyUnmountCallback(props, state);
    const { className, annotationSettings, annotations, matte, renderKey, interaction, customClickBehavior, customHoverBehavior, customDoubleClickBehavior, projection, backgroundGraphics, foregroundGraphics, afterElements, beforeElements, disableContext, summaryType, summaryHoverAnnotation, pieceHoverAnnotation, hoverAnnotation, canvasPostProcess, baseMarkProps, useSpans, canvasPieces, canvasSummaries, canvasConnectors, renderOrder, additionalDefs, sketchyRenderingEngine, frameRenderOrder, disableCanvasInteraction, disableProgressiveRendering } = props;
    const { orFrameRender, projectedColumns, adjustedPosition, adjustedSize, legendSettings, columnOverlays, axesTickLines, axes, margin, pieceDataXY, oLabels, title } = state;
    const size = [
        adjustedSize[0] + margin.left + margin.right,
        adjustedSize[1] + margin.top + margin.bottom
    ];
    let interactionOverflow;
    if (summaryType && summaryType.amplitude) {
        if (projection === "horizontal") {
            interactionOverflow = {
                top: summaryType.amplitude,
                bottom: 0,
                left: 0,
                right: 0
            };
        }
        else if (projection === "radial") {
            interactionOverflow = defaultOverflow;
        }
        else {
            interactionOverflow = {
                top: 0,
                bottom: 0,
                left: summaryType.amplitude,
                right: 0
            };
        }
    }
    const renderedForegroundGraphics = typeof foregroundGraphics === "function"
        ? foregroundGraphics({ size, margin })
        : foregroundGraphics;
    return (React.createElement(Frame, { name: "ordinalframe", renderPipeline: orFrameRender, adjustedPosition: adjustedPosition, adjustedSize: adjustedSize, size: size, xScale: xScale$1, yScale: yScale$1, axes: axes, useSpans: useSpans, axesTickLines: axesTickLines, title: title, matte: matte, additionalDefs: additionalDefs, className: `${className} ${projection}`, frameKey: "none", renderFn: renderKey, projectedCoordinateNames: projectedCoordinatesObject, defaultSVGRule: (args) => defaultORSVGRule(props, state, args), defaultHTMLRule: (args) => defaultORHTMLRule(props, state, args), hoverAnnotation: summaryHoverAnnotation || pieceHoverAnnotation || hoverAnnotation, annotations: annotations, annotationSettings: annotationSettings, legendSettings: legendSettings, interaction: interaction && Object.assign(Object.assign({}, interaction), { brush: interaction.columnsBrush !== true && "oBrush", projection,
            projectedColumns }), customClickBehavior: customClickBehavior, customHoverBehavior: customHoverBehavior, customDoubleClickBehavior: customDoubleClickBehavior, points: pieceDataXY, margin: margin, columns: projectedColumns, backgroundGraphics: backgroundGraphics, foregroundGraphics: renderedForegroundGraphics, beforeElements: beforeElements, afterElements: afterElements, overlay: columnOverlays, rScale: state.rScale, projection: projection, disableContext: disableContext, interactionOverflow: interactionOverflow, canvasPostProcess: canvasPostProcess, baseMarkProps: baseMarkProps, canvasRendering: !!(canvasPieces || canvasSummaries || canvasConnectors), renderOrder: renderOrder, disableCanvasInteraction: disableCanvasInteraction, sketchyRenderingEngine: sketchyRenderingEngine, frameRenderOrder: frameRenderOrder, additionalVizElements: oLabels, disableProgressiveRendering: disableProgressiveRendering }));
});
function deriveOrdinalFrameState(nextProps, prevState) {
    const { props } = prevState;
    if ((prevState.dataVersion &&
        prevState.dataVersion !== nextProps.dataVersion) ||
        !prevState.projectedColumns ||
        props.size[0] !== nextProps.size[0] ||
        props.size[1] !== nextProps.size[1] ||
        (!prevState.dataVersion &&
            orFrameChangeProps.find((d) => {
                return props[d] !== nextProps[d];
            }))) {
        return Object.assign(Object.assign({}, calculateOrdinalFrame(nextProps, prevState)), { props: nextProps });
    }
    else {
        return { props: nextProps };
    }
}
function defaultORSVGRule(props, state, { d, i, annotationLayer }) {
    const { projection, svgAnnotationRules } = props;
    const { adjustedPosition, adjustedSize, oAccessor, rAccessor, oScale, rScale, projectedColumns, orFrameRender, pieceIDAccessor, rScaleType, summaryType, type } = state;
    let screenCoordinates = [0, 0];
    if (d.isColumnAnnotation) {
        const { coordinates: [xPosition, yPosition] } = getColumnScreenCoordinates({
            d,
            projectedColumns,
            oAccessor,
            summaryType,
            type,
            projection,
            adjustedPosition,
            adjustedSize
        });
        screenCoordinates = [xPosition, yPosition];
    }
    else if (d.coordinates || (d.type === "enclose" && d.neighbors)) {
        screenCoordinates = (d.coordinates || d.neighbors).map((p) => {
            const pO = findFirstAccessorValue(oAccessor, p) || p.column;
            const oColumn = projectedColumns[pO];
            const idPiece = findIDPiece(pieceIDAccessor, oColumn, p);
            return screenProject({
                p,
                adjustedSize,
                rScale,
                rAccessor,
                idPiece,
                projection,
                oColumn,
                rScaleType
            });
        });
    }
    else {
        const pO = findFirstAccessorValue(oAccessor, d) || d.column;
        const oColumn = projectedColumns[pO];
        const idPiece = findIDPiece(pieceIDAccessor, oColumn, d);
        screenCoordinates = screenProject({
            p: d,
            adjustedSize,
            rScale,
            rAccessor,
            idPiece,
            projection,
            oColumn,
            rScaleType
        });
    }
    const { voronoiHover } = annotationLayer;
    //TODO: Process your rules first
    const customAnnotation = svgAnnotationRules &&
        svgAnnotationRules({
            d,
            i,
            oScale,
            rScale,
            oAccessor,
            rAccessor,
            orFrameProps: props,
            orFrameState: state,
            screenCoordinates,
            adjustedPosition,
            adjustedSize,
            annotationLayer,
            categories: projectedColumns,
            voronoiHover
        });
    if (svgAnnotationRules && customAnnotation !== null) {
        return customAnnotation;
    }
    else if (d.type === "desaturation-layer") {
        return desaturationLayer({
            style: d.style instanceof Function ? d.style(d, i) : d.style,
            size: adjustedSize,
            i,
            key: d.key
        });
    }
    else if (d.type === "ordinal-line") {
        return svgOrdinalLine({ d, screenCoordinates, voronoiHover });
    }
    else if (d.type === "or") {
        return svgORRule({ d, i, screenCoordinates, projection });
    }
    else if (d.type === "highlight") {
        return svgHighlightRule$1({
            d,
            pieceIDAccessor,
            orFrameRender,
            oAccessor
        });
    }
    else if (d.type === "react-annotation" || typeof d.type === "function") {
        return basicReactAnnotationRule({ d, i, screenCoordinates });
    }
    else if (d.type === "enclose") {
        return svgEncloseRule$1({ d, i, screenCoordinates });
    }
    else if (d.type === "enclose-rect") {
        return svgRectEncloseRule$1({ d, screenCoordinates, i });
    }
    else if (d.type === "r") {
        return svgRRule({
            d,
            i,
            screenCoordinates,
            rScale,
            rAccessor,
            projection,
            adjustedSize,
            adjustedPosition
        });
    }
    else if (d.type === "category") {
        return svgCategoryRule({
            projection,
            d,
            i,
            categories: state.projectedColumns,
            adjustedSize
        });
    }
    return null;
}
function defaultORHTMLRule(props, state, { d, i, annotationLayer }) {
    const { adjustedPosition, adjustedSize, oAccessor, rAccessor, oScale, rScale, projectedColumns, summaryType, type, pieceIDAccessor, rScaleType } = state;
    const { htmlAnnotationRules, tooltipContent, optimizeCustomTooltipPosition, projection, size, useSpans } = props;
    let screenCoordinates = [0, 0];
    const { voronoiHover } = annotationLayer;
    if (d.coordinates || (d.type === "enclose" && d.neighbors)) {
        screenCoordinates = (d.coordinates || d.neighbors).map((p) => {
            const pO = findFirstAccessorValue(oAccessor, p) || p.column;
            const oColumn = projectedColumns[pO];
            const idPiece = findIDPiece(pieceIDAccessor, oColumn, p);
            return screenProject({
                p,
                adjustedSize,
                rScale,
                rAccessor,
                idPiece,
                projection,
                oColumn,
                rScaleType
            });
        });
    }
    else if (d.type === "column-hover") {
        const { coordinates: [xPosition, yPosition] } = getColumnScreenCoordinates({
            d,
            projectedColumns,
            oAccessor,
            summaryType,
            type,
            projection,
            adjustedPosition,
            adjustedSize
        });
        screenCoordinates = [xPosition, yPosition];
    }
    else {
        const pO = findFirstAccessorValue(oAccessor, d) || d.column;
        const oColumn = projectedColumns[pO];
        const idPiece = findIDPiece(pieceIDAccessor, oColumn, d);
        screenCoordinates = screenProject({
            p: d,
            adjustedSize,
            rScale,
            rAccessor,
            idPiece,
            projection,
            oColumn,
            rScaleType
        });
    }
    const flippedRScale = projection === "vertical"
        ? rScaleType.domain(rScale.domain()).range(rScale.range().reverse())
        : rScale;
    //TODO: Process your rules first
    const customAnnotation = htmlAnnotationRules &&
        htmlAnnotationRules({
            d,
            i,
            oScale,
            rScale: flippedRScale,
            oAccessor,
            rAccessor,
            orFrameProps: props,
            screenCoordinates,
            adjustedPosition,
            adjustedSize,
            annotationLayer,
            orFrameState: state,
            categories: state.projectedColumns,
            voronoiHover
        });
    if (htmlAnnotationRules && customAnnotation !== null) {
        return customAnnotation;
    }
    if (d.type === "frame-hover") {
        return htmlFrameHoverRule$1({
            d,
            i,
            rAccessor,
            oAccessor,
            projection,
            tooltipContent,
            optimizeCustomTooltipPosition,
            projectedColumns,
            useSpans,
            pieceIDAccessor,
            adjustedSize,
            rScale,
            type,
            rScaleType
        });
    }
    else if (d.type === "column-hover") {
        return htmlColumnHoverRule({
            d,
            i,
            summaryType,
            oAccessor,
            projectedColumns,
            type,
            adjustedPosition,
            adjustedSize,
            projection,
            tooltipContent,
            optimizeCustomTooltipPosition,
            useSpans
        });
    }
    return null;
}
OrdinalFrame.displayName = "OrdinalFrame";

const MiniMap = (props) => {
    const { brushStart, brush, brushEnd, xBrushable, yBrushable, yBrushExtent, xBrushExtent } = props, rest = __rest(props, ["brushStart", "brush", "brushEnd", "xBrushable", "yBrushable", "yBrushExtent", "xBrushExtent"]);
    const interactivity = {
        start: brushStart,
        during: brush,
        end: brushEnd
    };
    if (xBrushable && yBrushable) {
        interactivity.brush = "xyBrush";
        if (xBrushExtent || yBrushExtent) {
            interactivity.extent = [[0, 0], [...props.size]];
        }
        if (xBrushExtent) {
            interactivity.extent[0] = xBrushExtent;
        }
        if (yBrushExtent) {
            interactivity.extent[1] = yBrushExtent;
        }
    }
    else if (xBrushable) {
        interactivity.brush = "xBrush";
        if (xBrushExtent) {
            interactivity.extent = xBrushExtent;
        }
    }
    else if (yBrushable) {
        interactivity.brush = "yBrush";
        if (yBrushExtent) {
            interactivity.extent = yBrushExtent;
        }
    }
    return React.createElement(XYFrame, Object.assign({}, rest, { interaction: interactivity }));
};

const generateMinimap = (props) => {
    const { xAccessor, yAccessor, points, lines, minimap, summaries, size, lineDataAccessor, lineType, lineStyle, summaryStyle, pointStyle, lineClass, summaryClass, pointClass, lineRenderMode, pointRenderMode, summaryRenderMode, canvasLines, canvasPoints, canvasSummaries, axes, margin, useSpans, name, annotations, summaryType, interactionSettings } = props;
    const miniDefaults = {
        position: [0, 0],
        size: [size[0], size[1] * 0.25],
        xAccessor: xAccessor,
        yAccessor: yAccessor,
        points: points,
        lines: lines,
        summaries: summaries,
        lineDataAccessor: lineDataAccessor,
        xBrushable: true,
        yBrushable: true,
        brushStart: () => { },
        brush: () => { },
        brushEnd: () => { },
        lineType: lineType,
        lineStyle,
        summaryStyle,
        pointStyle,
        lineClass,
        summaryClass,
        pointClass,
        lineRenderMode,
        pointRenderMode,
        summaryRenderMode,
        canvasLines,
        canvasPoints,
        canvasSummaries,
        axes,
        margin,
        useSpans,
        name,
        annotations,
        summaryType,
        interactionSettings
    };
    const combinedOptions = Object.assign(Object.assign(Object.assign({}, miniDefaults), minimap), { hoverAnnotation: false });
    return React.createElement(MiniMap, Object.assign({}, combinedOptions));
};
function MinimapXYFrame(props) {
    const miniMap = generateMinimap(props);
    const options = {};
    const { minimap, renderBefore } = props, rest = __rest(props, ["minimap", "renderBefore"]);
    if (renderBefore) {
        options.beforeElements = miniMap;
    }
    else {
        options.afterElements = miniMap;
    }
    return React.createElement(XYFrame, Object.assign({}, rest, options));
}

const htmlFrameHoverRule = ({ d: baseD, i, tooltipContent, optimizeCustomTooltipPosition, useSpans, nodes, edges, nodeIDAccessor }) => {
    const d = baseD.x !== undefined && baseD.y !== undefined
        ? baseD
        : baseD.edge
            ? Object.assign(Object.assign({}, (edges.find((p) => nodeIDAccessor(p.source) === nodeIDAccessor(baseD.source) &&
                nodeIDAccessor(p.target) === nodeIDAccessor(baseD.target)) || {})), baseD) : nodes.find((p) => nodeIDAccessor(p) === baseD.id);
    if (!d)
        return null;
    let content = d.edge ? (React.createElement(SpanOrDiv, { span: useSpans, className: "tooltip-content", "data-testid": "tooltip-content" },
        React.createElement("p", { key: "html-annotation-content-1" },
            (d.source || d.edge.source).id,
            " to ",
            (d.target || d.edge.target).id))) : (React.createElement(SpanOrDiv, { span: useSpans, className: "tooltip-content", "data-testid": "tooltip-content" },
        React.createElement("p", { key: "html-annotation-content-1" }, d.id),
        React.createElement("p", { key: "html-annotation-content-2" },
            "Degree: ",
            d.degree)));
    if (d.type === "frame-hover" && tooltipContent) {
        content = optimizeCustomTooltipPosition ? (React.createElement(TooltipPositioner, { tooltipContent: tooltipContent, tooltipContentArgs: d })) : (tooltipContent(d));
    }
    return (React.createElement(SpanOrDiv, { span: useSpans, key: `network-annotation-label-${i}`, className: `annotation annotation-network-label ${d.className || ""}`, "data-testid": "network-tooltip-container", style: {
            position: "absolute",
            top: `${d.y}px`,
            left: `${d.x}px`
        } }, content));
};
const svgNodeRule = ({ d, i, nodeSizeAccessor }) => {
    if (!d) {
        return null;
    }
    const noteData = Object.assign({
        dx: d.dx || -25,
        dy: d.dy || -25,
        x: d.x,
        y: d.y,
        note: { label: d.label, orientation: d.orientation, align: d.align },
        connector: { end: "arrow" }
    }, d, {
        type: AnnotationCalloutCircle,
        subject: {
            radius: d.radius || d.radius || nodeSizeAccessor(d)
        }
    });
    return React.createElement(SemioticAnnotation, { key: d.key || `annotation-${i}`, noteData: noteData });
};
const svgReactAnnotationRule = ({ d, i, projectedNodes, nodeIDAccessor }) => {
    const selectedNode = d.x !== undefined && d.y !== undefined
        ? d
        : projectedNodes.find((p) => nodeIDAccessor(p) === d.id);
    if (!selectedNode) {
        return null;
    }
    const noteData = Object.assign({
        dx: 0,
        dy: 0,
        x: selectedNode.x,
        y: selectedNode.y,
        note: { label: d.label },
        connector: { end: "arrow" }
    }, d, { type: typeof d.type === "function" ? d.type : undefined });
    return React.createElement(SemioticAnnotation, { key: d.key || `annotation-${i}`, noteData: noteData });
};
const svgEncloseRule = ({ d, i, projectedNodes, nodeIDAccessor, nodeSizeAccessor }) => {
    const selectedNodes = projectedNodes.filter((p) => d.ids.indexOf(nodeIDAccessor(p)) !== -1);
    if (selectedNodes.length === 0) {
        return null;
    }
    const circle = packEnclose(selectedNodes.map((p) => ({ x: p.x, y: p.y, r: nodeSizeAccessor(p) })));
    return circleEnclosure({ circle, d, i });
};
const svgRectEncloseRule = ({ d, i, projectedNodes, nodeIDAccessor, nodeSizeAccessor }) => {
    const selectedNodes = projectedNodes.filter((p) => d.ids.indexOf(nodeIDAccessor(p)) !== -1);
    if (selectedNodes.length === 0) {
        return null;
    }
    const bboxNodes = selectedNodes.map((p) => {
        if (p.shapeNode) {
            return {
                x0: p.x0,
                x1: p.x1,
                y0: p.y0,
                y1: p.y1
            };
        }
        const nodeSize = nodeSizeAccessor(p);
        return {
            x0: p.x - nodeSize,
            x1: p.x + nodeSize,
            y0: p.y - nodeSize,
            y1: p.y + nodeSize
        };
    });
    return rectangleEnclosure({ bboxNodes, d, i });
};
const svgHullEncloseRule = ({ d, i, projectedNodes, nodeIDAccessor, nodeSizeAccessor }) => {
    const selectedNodes = projectedNodes.filter((p) => d.ids.indexOf(nodeIDAccessor(p)) !== -1);
    if (selectedNodes.length === 0) {
        return null;
    }
    const projectedPoints = [];
    selectedNodes.forEach((p) => {
        if (p.shapeNode) {
            projectedPoints.push({ x: p.x0, y: p.y0 });
            projectedPoints.push({ x: p.x0, y: p.y1 });
            projectedPoints.push({ x: p.x1, y: p.y0 });
            projectedPoints.push({ x: p.x1, y: p.y1 });
        }
        else {
            const nodeSize = nodeSizeAccessor(p);
            projectedPoints.push({ x: p.x - nodeSize, y: p.y - nodeSize });
            projectedPoints.push({ x: p.x + nodeSize, y: p.y - nodeSize });
            projectedPoints.push({ x: p.x - nodeSize, y: p.y + nodeSize });
            projectedPoints.push({ x: p.x + nodeSize, y: p.y + nodeSize });
        }
    });
    return hullEnclosure({ points: projectedPoints.map((d) => [d.x, d.y]), d, i });
};
const svgHighlightRule = ({ d, i, networkFrameRender }) => {
    const { nodes } = networkFrameRender;
    const { customMark, styleFn: baseStyle } = nodes;
    let styleFn = baseStyle;
    if (d.style && typeof d.style === "function") {
        styleFn = (d) => (Object.assign(Object.assign({}, baseStyle(d)), d.style(d)));
    }
    else if (d.style) {
        styleFn = (d) => (Object.assign(Object.assign({}, baseStyle(d)), d.style));
    }
    const transform = `translate(${d.x},${d.y})`;
    const baseMarkProps = {};
    const HighlightMark = customMark({
        d,
        styleFn,
        transform,
        baseMarkProps,
        key: `highlight-${i}`
    });
    return HighlightMark;
};

const horizontalDagreLineGenerator = line()
    .curve(curveMonotoneX)
    .x((d) => d.x)
    .y((d) => d.y);
const verticalDagreLineGenerator = line()
    .curve(curveMonotoneY)
    .x((d) => d.x)
    .y((d) => d.y);
function sankeyEdgeSort(a, b, direction) {
    if (a.circular && !b.circular)
        return -1;
    if (b.circular && !a.circular)
        return 1;
    const first = direction === "down" ? "y" : "x";
    const second = direction === "down" ? "x" : "y";
    return a.source[first] === b.source[first]
        ? a.sankeyWidth === b.sankeyWidth
            ? a.source[second] - b.source[second]
            : b.sankeyWidth - a.sankeyWidth
        : a.source[first] - b.source[first];
}
const sigmoidLinks = {
    horizontal: linkHorizontal()
        .x((d) => d.x)
        .y((d) => d.y),
    vertical: linkVertical()
        .x((d) => d.x)
        .y((d) => d.y),
    radial: d.lineArc
};
const customEdgeHashD = {
    curve: (d, projection = "vertical") => sigmoidLinks[projection](d),
    linearc: (d$1) => d.lineArc(d$1),
    ribbon: (d$1) => d.ribbon(d$1, d$1.width),
    arrowhead: (d$1) => d.arrowHead(d$1, d$1.target.nodeSize, d$1.width, d$1.width * 1.5),
    halfarrow: (d$1) => d.halfArrow(d$1, d$1.target.nodeSize, d$1.width, d$1.width * 1.5),
    nail: (d$1) => d.nail(d$1, d$1.source.nodeSize),
    comet: (d$1) => d.comet(d$1, d$1.target.nodeSize),
    taffy: (d$1) => d.taffy(d$1, d$1.source.nodeSize / 2, d$1.target.nodeSize / 2, (d$1.source.nodeSize + d$1.target.nodeSize) / 4)
};
const circleNodeGenerator = ({ d, i, styleFn, renderMode, key, className, transform, baseMarkProps }) => {
    //this is repetitious
    return (React.createElement(SemioticMark, Object.assign({}, baseMarkProps, { key: key, transform: transform, markType: "rect", width: d.nodeSize * 2, height: d.nodeSize * 2, ry: d.nodeSize * 2, rx: d.nodeSize * 2, x: -d.nodeSize, y: -d.nodeSize, style: styleFn(d, i), renderMode: renderMode ? renderMode(d, i) : undefined, className: className, "aria-label": `Node ${d.id}`, tabIndex: -1 })));
};
const gridProps = (gridSize) => {
    return {
        x: -gridSize / 2,
        y: -gridSize / 2,
        width: gridSize,
        height: gridSize
    };
};
const matrixEdgeGenerator = (size, nodes) => ({ d, i, styleFn, renderMode, key, className, baseMarkProps }) => {
    const gridSize = Math.min(...size) / nodes.length;
    return (React.createElement("g", { key: key },
        React.createElement(SemioticMark, Object.assign({}, baseMarkProps, { renderMode: renderMode ? renderMode(d, i) : undefined, key: key, className: className, transform: `translate(${d.source.y},${d.target.y})`, markType: "rect", style: styleFn(d, i), "aria-label": `Connection from ${d.source.id} to ${d.target.id}`, tabIndex: -1 }, gridProps(gridSize)))));
};
const arcEdgeGenerator = (size) => {
    const yAdjust = size[1] / size[0];
    function arcDiagramArc(d) {
        const draw = line().curve(curveBasis);
        const midX = (d.source.x + d.target.x) / 2;
        const midY = d.source.x - d.target.x;
        return draw([
            [d.source.x, 0],
            [midX, midY * yAdjust],
            [d.target.x, 0]
        ]);
    }
    return ({ d, i, styleFn, renderMode, key, className, baseMarkProps }) => {
        return (React.createElement(SemioticMark, Object.assign({}, baseMarkProps, { renderMode: renderMode ? renderMode(d, i) : undefined, key: key, className: className, markType: "path", transform: `translate(0,${size[1] / 2})`, d: arcDiagramArc(d), style: styleFn(d, i), "aria-label": `Connection from ${d.source.id} to ${d.target.id}`, tabIndex: -1 })));
    };
};
const ArrowedPath = (props) => {
    const { d, width, edgeLength, circular } = props;
    const pathRef = useRef(null);
    useEffect(() => {
        if (pathRef === null || pathRef === void 0 ? void 0 : pathRef.current) {
            const circularMod = circular ? width : edgeLength;
            const arrowHeadSize = Math.max(Math.min(width, circularMod) / 5, 2);
            let arrows = pathArrows()
                .arrowLength(arrowHeadSize * 2.5)
                .gapLength(100)
                .arrowHeadSize(arrowHeadSize)
                .path(d);
            select(pathRef.current).selectAll("*").remove();
            select(pathRef.current).call(arrows);
            select(pathRef.current)
                .selectAll(":not(.arrow-head)")
                .style("fill", "none")
                .style("stroke-width", arrowHeadSize / 4)
                .style("stroke", "white");
            select(pathRef.current).selectAll(".arrow-head").style("fill", "white");
        }
    }, [d]);
    return React.createElement("g", { ref: pathRef });
};
const sankeyArrowGenerator = (props) => {
    const { d, i, styleFn, renderMode, key, className, baseMarkProps, generatedPath } = props;
    const { showArrows } = d;
    let arrowPath = "";
    if (d.circular) {
        const { circularPathData } = d;
        const { sourceX, sourceY, leftFullExtent, rightFullExtent, verticalFullExtent, targetX, targetY } = circularPathData;
        arrowPath = `M${sourceX},${sourceY}L${leftFullExtent},${sourceY}L${leftFullExtent},${verticalFullExtent}L${rightFullExtent},${verticalFullExtent}L${rightFullExtent},${targetY}L${targetX},${targetY}`;
    }
    else {
        let x0 = d.source.x1, x1 = d.target.x0, xi = interpolateNumber(x0, x1), x2 = xi(curvature), x3 = xi(1 - curvature), y0 = d.y0, y1 = d.y1;
        arrowPath = `M${x0},${y0}C${x2},${y0} ${x3},${y1} ${x1},${y1}`;
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(SemioticMark, Object.assign({}, baseMarkProps, { renderMode: renderMode ? renderMode(d, i) : undefined, key: key, className: className, markType: "path", d: generatedPath, style: styleFn(d, i), "aria-label": `Connection from ${d.source.id} to ${d.target.id}`, tabIndex: -1 })),
        showArrows && (React.createElement(ArrowedPath, { d: arrowPath, width: d.sankeyWidth, edgeLength: Math.abs(d.target.x - d.source.x), circular: d.circular }))));
};
const chordEdgeGenerator = (size) => ({ d, i, styleFn, renderMode, key, className, baseMarkProps }) => (React.createElement(SemioticMark, Object.assign({}, baseMarkProps, { renderMode: renderMode ? renderMode(d, i) : undefined, key: key, className: className, transform: `translate(${size[0] / 2},${size[1] / 2})`, markType: "path", d: d.d, style: styleFn(d, i), "aria-label": `Connection from ${d.source.id} to ${d.target.id}`, tabIndex: -1 })));
const dagreEdgeGenerator = (direction) => {
    const dagreLineGenerator = direction === "LR" || direction === "RL"
        ? horizontalDagreLineGenerator
        : verticalDagreLineGenerator;
    return ({ d, i, styleFn, renderMode, key, className, baseMarkProps }) => {
        if (d.ribbon || d.parallelEdges) {
            const ribbonGenerator = linearRibbon();
            ribbonGenerator.x((p) => p.x);
            ribbonGenerator.y((p) => p.y);
            ribbonGenerator.r(() => d.weight || 1);
            if (d.parallelEdges) {
                const sortedParallelEdges = d.parallelEdges.sort((a, b) => b.weight - a.weight);
                return (React.createElement("g", { key: `${key}` }, ribbonGenerator({
                    points: d.points,
                    multiple: d.parallelEdges
                }).map((ribbonD, ribbonI) => (React.createElement(SemioticMark, Object.assign({}, baseMarkProps, { renderMode: renderMode ? renderMode(d, i) : undefined, key: `${key}-${ribbonI}`, className: className, markType: "path", d: ribbonD, style: styleFn(sortedParallelEdges[ribbonI], i), "aria-label": `Connection from ${d.source.id} to ${d.target.id}`, tabIndex: -1 }))))));
            }
            return (React.createElement(SemioticMark, Object.assign({}, baseMarkProps, { renderMode: renderMode ? renderMode(d, i) : undefined, key: key, className: className, markType: "path", d: ribbonGenerator(d.points), style: styleFn(d, i), "aria-label": `Connection from ${d.source.id} to ${d.target.id}`, tabIndex: -1 })));
        }
        return (React.createElement(SemioticMark, Object.assign({}, baseMarkProps, { renderMode: renderMode ? renderMode(d, i) : undefined, key: key, className: className, markType: "path", d: dagreLineGenerator(d.points), style: styleFn(d, i), "aria-label": `Connection from ${d.source.id} to ${d.target.id}`, tabIndex: -1 })));
    };
};
const sankeyNodeGenerator = ({ d, i, styleFn, renderMode, key, className, transform, baseMarkProps }) => {
    const height = d.direction !== "down" ? d.height : d.width;
    const width = d.direction !== "down" ? d.width : d.height;
    if (!d) {
        return React.createElement("g", null);
    }
    return (React.createElement(SemioticMark, Object.assign({}, baseMarkProps, { renderMode: renderMode ? renderMode(d, i) : undefined, key: key, className: className, transform: transform, markType: "rect", height: height, width: width, x: -width / 2, y: -height / 2, rx: 0, ry: 0, style: styleFn(d), "aria-label": `Node ${d.id}`, tabIndex: -1 })));
};
const chordNodeGenerator = (size) => ({ d, i, styleFn, renderMode, key, className, baseMarkProps }) => {
    if (!d) {
        return React.createElement("g", null);
    }
    return (React.createElement(SemioticMark, Object.assign({}, baseMarkProps, { renderMode: renderMode ? renderMode(d, i) : undefined, key: key, className: className, transform: `translate(${size[0] / 2},${size[1] / 2})`, markType: "path", d: d.d, style: styleFn(d, i), "aria-label": `Node ${d.id}`, tabIndex: -1 })));
};
const matrixNodeGenerator = (size, nodes) => {
    const gridSize = Math.min(...size);
    const stepSize = gridSize / (nodes.length + 1);
    return ({ d, i, styleFn, renderMode, key, className, baseMarkProps }) => {
        if (!d) {
            return React.createElement("g", null);
        }
        const showText = stepSize > 6;
        const showLine = stepSize > 3;
        const showRect = stepSize > 0.5;
        const textProps = {
            textAnchor: "end",
            fontSize: `${stepSize / 2}px`
        };
        const style = styleFn(d, i);
        const renderModeValue = renderMode ? renderMode(d, i) : undefined;
        return (React.createElement("g", { key: key, className: className },
            showRect && (React.createElement(SemioticMark, { markType: "rect", x: stepSize / 2, y: d.y - stepSize / 2, width: gridSize - stepSize, height: stepSize, style: Object.assign(Object.assign({}, style), { stroke: "none" }), renderMode: renderModeValue, baseMarkProps: baseMarkProps })),
            showRect && (React.createElement(SemioticMark, { markType: "rect", y: stepSize / 2, x: d.y - stepSize / 2, height: gridSize - stepSize, width: stepSize, style: Object.assign(Object.assign({}, style), { stroke: "none" }), renderMode: renderModeValue, baseMarkProps: baseMarkProps })),
            showLine && (React.createElement(SemioticMark, { markType: "line", stroke: "black", x1: 0, x2: gridSize - stepSize / 2, y1: d.y - stepSize / 2, y2: d.y - stepSize / 2, style: style, renderMode: renderModeValue, baseMarkProps: baseMarkProps })),
            showLine && (React.createElement(SemioticMark, { markType: "line", stroke: "black", y1: 0, y2: gridSize - stepSize / 2, x1: d.y - stepSize / 2, x2: d.y - stepSize / 2, style: style, renderMode: renderModeValue, baseMarkProps: baseMarkProps })),
            showLine && i === nodes.length - 1 && (React.createElement(SemioticMark, { markType: "line", stroke: "black", x1: 0, x2: gridSize - stepSize / 2, y1: d.y + stepSize / 2, y2: d.y + stepSize / 2, style: style, renderMode: renderModeValue, baseMarkProps: baseMarkProps })),
            showLine && i === nodes.length - 1 && (React.createElement(SemioticMark, { markType: "line", stroke: "black", y1: 0, y2: gridSize - stepSize / 2, x1: d.y + stepSize / 2, x2: d.y + stepSize / 2, style: style, renderMode: renderModeValue, baseMarkProps: baseMarkProps })),
            showText && (React.createElement("text", Object.assign({ x: 0, y: d.y + stepSize / 5 }, textProps), d.id)),
            showText && (React.createElement("text", Object.assign({ transform: `translate(${d.y}) rotate(90) translate(0,${stepSize / 5})` }, textProps, { y: 0 }), d.id))));
    };
};
const radialRectNodeGenerator = (size, center, type) => {
    const radialArc = arc();
    const { angleRange = [0, 360] } = type;
    const rangePct = angleRange.map((d) => d / 360);
    const rangeMod = rangePct[1] - rangePct[0];
    const adjustedPct = rangeMod < 1 ? scaleLinear().domain([0, 1]).range(rangePct) : (d) => d;
    return ({ d, i, styleFn, renderMode, key, className, baseMarkProps }) => {
        if (!d) {
            return React.createElement("g", null);
        }
        radialArc.innerRadius(d.y0 / 2).outerRadius(d.y1 / 2);
        return (React.createElement(SemioticMark, Object.assign({}, baseMarkProps, { key: key, transform: `translate(${center})`, markType: "path", d: radialArc({
                startAngle: adjustedPct(d.x0 / size[0]) * Math.PI * 2,
                endAngle: adjustedPct(d.x1 / size[0]) * Math.PI * 2
            }), style: styleFn(d, i), renderMode: renderMode ? renderMode(d, i) : undefined, className: className, "aria-label": `Node ${d.id}`, tabIndex: -1 })));
    };
};
const radialLabelGenerator = (node, nodei, nodeIDAccessor, size) => {
    const anglePct = (node.x1 + node.x0) / 2 / size[0];
    const nodeLabel = nodeIDAccessor(node, nodei);
    const labelRotate = anglePct > 0.5 ? anglePct * 360 + 90 : anglePct * 360 - 90;
    return (React.createElement("g", { transform: `rotate(${labelRotate})` }, typeof nodeLabel === "string" ? (React.createElement("text", { textAnchor: "middle", y: 5 }, nodeLabel)) : (nodeLabel)));
};
const hierarchicalRectNodeGenerator = ({ d, i, styleFn, renderMode, key, className, baseMarkProps }) => {
    if (!d) {
        return React.createElement("g", null);
    }
    //this is repetitious
    return (React.createElement(SemioticMark, Object.assign({}, baseMarkProps, { key: key, transform: `translate(0,0)`, markType: "rect", width: d.x1 - d.x0, height: d.y1 - d.y0, x: d.x0, y: d.y0, rx: 0, ry: 0, style: styleFn(d, i), renderMode: renderMode ? renderMode(d, i) : undefined, className: className, "aria-label": `Node ${d.id}`, tabIndex: -1 })));
};
const genericLineGenerator = (d) => `M${d.source.x},${d.source.y}L${d.target.x},${d.target.y}`;
const drawNodes = ({ data, renderKeyFn, customMark, styleFn, classFn, renderMode, canvasDrawing, canvasRenderFn, baseMarkProps, networkSettings }) => {
    const markGenerator = customMark;
    const renderedData = [];
    if (networkSettings.type === "matrix" && canvasRenderFn) {
        return;
    }
    if (networkSettings) {
        let i = 0;
        for (const d of data) {
            const generatedMark = markGenerator({
                d,
                i,
                renderKeyFn,
                styleFn,
                classFn,
                renderMode,
                key: renderKeyFn ? renderKeyFn(d, i) : d.id || `node-${i}`,
                className: `node ${classFn(d, i)}`,
                transform: `translate(${d.x},${d.y})`,
                baseMarkProps
            });
            if (canvasRenderFn && canvasRenderFn(d, i) === true) {
                const { transform = "translate(0,0)" } = generatedMark.props;
                const [x, y] = transform
                    .replace("translate(", "")
                    .replace(")", "")
                    .split(",");
                const canvasNode = {
                    baseClass: "frame-piece",
                    tx: x,
                    ty: y,
                    d,
                    i,
                    markProps: generatedMark.props,
                    styleFn,
                    renderFn: renderMode,
                    classFn
                };
                canvasDrawing.push(canvasNode);
            }
            else {
                // CUSTOM MARK IMPLEMENTATION
                renderedData.push(markGenerator({
                    d,
                    i,
                    renderKeyFn,
                    styleFn,
                    classFn,
                    renderMode,
                    key: renderKeyFn ? renderKeyFn(d, i) : d.id || `node-${i}`,
                    className: `node ${classFn(d, i)}`,
                    transform: `translate(${d.x},${d.y})`,
                    baseMarkProps
                }));
            }
            i++;
        }
    }
    return renderedData;
};
const drawEdges = (settings) => {
    const { data: baseData, renderKeyFn, customMark, styleFn, classFn, renderMode, canvasRenderFn, canvasDrawing, type, baseMarkProps, networkSettings, projection, numberOfNodes, size } = settings;
    const { type: networkType, direction, edgeSort = sankeyEdgeSort } = networkSettings;
    const data = networkType === "sankey"
        ? baseData.sort((a, b) => edgeSort(a, b, direction))
        : baseData;
    let dGenerator = genericLineGenerator;
    const renderedData = [];
    if (canvasRenderFn && networkSettings.type === "matrix") {
        let i = 0;
        const gridSize = Math.floor(Math.min(...size) / numberOfNodes);
        for (const d of data) {
            const canvasEdge = {
                baseClass: "frame-piece",
                tx: d.source.y,
                ty: d.target.y,
                d,
                i,
                markProps: Object.assign({ markType: "rect" }, gridProps(gridSize)),
                styleFn,
                renderFn: renderMode,
                classFn
            };
            canvasDrawing.push(canvasEdge);
            i++;
        }
    }
    else if (customMark) {
        // CUSTOM MARK IMPLEMENTATION
        if (type) {
            if (typeof type === "function") {
                dGenerator = type;
            }
            else if (customEdgeHashD[type]) {
                dGenerator = (d) => customEdgeHashD[type](d, projection);
            }
        }
        let i = 0;
        for (const d of data) {
            const renderedCustomMark = customMark({
                d,
                i,
                renderKeyFn,
                styleFn,
                classFn,
                renderMode,
                key: renderKeyFn ? renderKeyFn(d, i) : `edge-${i}`,
                className: `${classFn(d, i)} edge`,
                transform: `translate(${d.x},${d.y})`,
                baseMarkProps,
                generatedPath: dGenerator(d)
            });
            if (renderedCustomMark &&
                renderedCustomMark.props &&
                (renderedCustomMark.props.markType !== "path" ||
                    renderedCustomMark.props.d)) {
                renderedData.push(renderedCustomMark);
            }
            i++;
        }
    }
    else {
        if (type) {
            if (typeof type === "function") {
                dGenerator = type;
            }
            else if (customEdgeHashD[type]) {
                dGenerator = (d) => customEdgeHashD[type](d, projection);
            }
        }
        let i = 0;
        for (const d of data) {
            const renderedD = dGenerator(d);
            if (renderedD && canvasRenderFn && canvasRenderFn(d, i) === true) {
                const canvasEdge = {
                    baseClass: "frame-piece",
                    tx: d.x,
                    ty: d.y,
                    d,
                    i,
                    markProps: { markType: "path", d: renderedD },
                    styleFn,
                    renderFn: renderMode,
                    classFn
                };
                canvasDrawing.push(canvasEdge);
            }
            else if (renderedD) {
                renderedData.push(React.createElement(SemioticMark, Object.assign({}, baseMarkProps, { key: renderKeyFn ? renderKeyFn(d, i) : `edge-${i}`, markType: "path", renderMode: renderMode ? renderMode(d, i) : undefined, className: `${classFn(d)} edge`, d: renderedD, style: styleFn(d, i), tabIndex: -1, role: "img", "aria-label": `connection from ${d.source.id} to ${d.target.id}` })));
            }
        }
    }
    return renderedData;
};
function topologicalSort(nodesArray, edgesArray) {
    // adapted from https://simplapi.wordpress.com/2015/08/19/detect-graph-cycle-in-javascript/
    const nodes = [];
    const nodeMap = new Map();
    for (const edge of edgesArray) {
        if (!edge.source.id || !edge.target.id) {
            return false;
        }
        if (!nodeMap.has(edge.source.id)) {
            const newNode = { _id: edge.source.id, links: [] };
            nodeMap.set(edge.source.id, newNode);
            nodes.push(newNode);
        }
        if (!nodeMap.has(edge.target.id)) {
            const newNode = { _id: edge.target.id, links: [] };
            nodeMap.set(edge.target.id, newNode);
            nodes.push(newNode);
        }
        nodeMap.get(edge.source.id).links.push(edge.target.id);
    }
    // Test if a node got any icoming edge
    function hasIncomingEdge(list, node) {
        for (let i = 0, l = list.length; i < l; ++i) {
            if (list[i].links.indexOf(node._id) !== -1) {
                return true;
            }
        }
        return false;
    }
    // Kahn Algorithm
    const L = [], S = nodes.filter((node) => !hasIncomingEdge(nodes, node));
    let n = null;
    while (S.length) {
        // Remove a node n from S
        n = S.pop();
        // Add n to tail of L
        L.push(n);
        let i = n.links.length;
        while (i--) {
            // Getting the node associated to the current stored id in links
            const m = nodes[nodes.map((d) => d._id).indexOf(n.links[i])];
            // Remove edge e from the graph
            n.links.pop();
            if (!hasIncomingEdge(nodes, m)) {
                S.push(m);
            }
        }
    }
    // If any of them still got links, there is cycle somewhere
    const nodeWithEdge = nodes.find((node) => node.links.length !== 0);
    return nodeWithEdge ? null : L;
}
const curvature = 0.5;
const ribbonLink = (d) => {
    const diff = d.direction === "down"
        ? Math.abs(d.target.y - d.source.y)
        : Math.abs(d.source.x - d.target.x);
    // const halfWidth = d.width / 2
    const testCoordinates = d.direction === "down"
        ? [
            {
                x: d.y0,
                y: d.source.y
            },
            {
                x: d.y0,
                y: d.source.y + diff / 3
            },
            {
                x: d.y1,
                y: d.target.y - diff / 3
            },
            {
                x: d.y1,
                y: d.target.y
            }
        ]
        : [
            {
                x: d.source.x0,
                y: d.y0
            },
            {
                x: d.source.x0 + diff / 3,
                y: d.y0
            },
            {
                x: d.target.x0 - diff / 3,
                y: d.y1
            },
            {
                x: d.target.x0,
                y: d.y1
            }
        ];
    const linkGenerator = linearRibbon();
    linkGenerator.x((d) => d.x);
    linkGenerator.y((d) => d.y);
    linkGenerator.r(() => d.sankeyWidth / 2);
    return linkGenerator(testCoordinates);
};
const areaLink = (d) => {
    let x0, x1, x2, x3, y0, y1, xi, y2, y3;
    if (d.direction === "down") {
        x0 = d.y0 - d.sankeyWidth / 2;
        x1 = d.y1 - d.sankeyWidth / 2;
        x2 = d.y1 + d.sankeyWidth / 2;
        x3 = d.y0 + d.sankeyWidth / 2;
        y0 = d.source.y1;
        y1 = d.target.y0;
        xi = interpolateNumber(y0, y1);
        y2 = xi(curvature);
        y3 = xi(1 - curvature);
        return `M${x0},${y0}C${x0},${y2} ${x1},${y3} ${x1},${y1}L${x2},${y1}C${x2},${y3} ${x3},${y2} ${x3},${y0}Z`;
    }
    (x0 = d.source.x1), // eslint-disable-line no-sequences
        (x1 = d.target.x0),
        (xi = interpolateNumber(x0, x1)),
        (x2 = xi(curvature)),
        (x3 = xi(1 - curvature)),
        (y0 = d.y0 - d.sankeyWidth / 2),
        (y1 = d.y1 - d.sankeyWidth / 2),
        (y2 = d.y1 + d.sankeyWidth / 2),
        (y3 = d.y0 + d.sankeyWidth / 2);
    return `M${x0},${y0}C${x2},${y0} ${x3},${y1} ${x1},${y1}L${x1},${y2}C${x3},${y2} ${x2},${y3} ${x0},${y3}Z`;
};
function circularAreaLink(link) {
    const linkGenerator = linearRibbon();
    linkGenerator.x((d) => d.x);
    linkGenerator.y((d) => d.y);
    linkGenerator.r(() => link.sankeyWidth / 2);
    const xyForLink = link.direction === "down"
        ? [
            {
                x: link.circularPathData.sourceY,
                y: link.circularPathData.sourceX
            },
            {
                x: link.circularPathData.sourceY,
                y: link.circularPathData.leftFullExtent
            },
            {
                x: link.circularPathData.verticalFullExtent,
                y: link.circularPathData.leftFullExtent
            },
            {
                x: link.circularPathData.verticalFullExtent,
                y: link.circularPathData.rightFullExtent
            },
            {
                x: link.circularPathData.targetY,
                y: link.circularPathData.rightFullExtent
            },
            {
                x: link.circularPathData.targetY,
                y: link.circularPathData.targetX
            }
        ]
        : [
            {
                x: link.circularPathData.sourceX,
                y: link.circularPathData.sourceY
            },
            {
                x: link.circularPathData.leftFullExtent,
                y: link.circularPathData.sourceY
            },
            {
                x: link.circularPathData.leftFullExtent,
                y: link.circularPathData.verticalFullExtent
            },
            {
                x: link.circularPathData.rightFullExtent,
                y: link.circularPathData.verticalFullExtent
            },
            {
                x: link.circularPathData.rightFullExtent,
                y: link.circularPathData.targetY
            },
            {
                x: link.circularPathData.targetX,
                y: link.circularPathData.targetY
            }
        ];
    return linkGenerator(xyForLink);
}
const hierarchyDecorator = (hierarchy, hashEntries, nodeIDAccessor, nodes) => {
    if (hierarchy.children) {
        for (const child of hierarchy.children) {
            const theseEntries = hashEntries.filter((entry) => entry[1] === child.id);
            for (const entry of theseEntries) {
                const idNode = nodes.find((node) => nodeIDAccessor(node) === entry[0]) || {};
                const newNode = Object.assign(Object.assign({ id: entry[0] }, idNode), { children: [], childMap: {} });
                child.childMap.set(entry[0], newNode);
                child.children.push(newNode);
            }
            if (child.children.length > 0) {
                hierarchyDecorator(child, hashEntries, nodeIDAccessor, nodes);
            }
        }
    }
};
const softStack = (edges, nodes, sourceAccessor, targetAccessor, nodeIDAccessor) => {
    let hierarchy = { id: "root-generated", children: [], childMap: new Map() };
    const discoveredHierarchyMap = new Map();
    const targetToSourceMap = new Map();
    let hasLogicalRoot = true;
    let isHierarchical = true;
    for (let i = 0; i < edges.length; i++) {
        const edge = edges[i];
        const source = sourceAccessor(edge);
        const target = targetAccessor(edge);
        const sourceID = typeof source === "object" ? nodeIDAccessor(source) : source;
        const targetID = typeof target === "object" ? nodeIDAccessor(target) : target;
        targetToSourceMap.set(targetID, sourceID);
        if (!discoveredHierarchyMap.has(sourceID)) {
            discoveredHierarchyMap.set(sourceID, targetID);
        }
        else {
            isHierarchical = false;
            break;
        }
    }
    if (isHierarchical) {
        const hashEntries = [];
        for (const entry of discoveredHierarchyMap) {
            hashEntries.push(entry);
            const target = entry[1];
            if (!discoveredHierarchyMap.has(target)) {
                discoveredHierarchyMap.set(target, "root-generated");
                const idNode = nodes.find((node) => nodeIDAccessor(node) === target) || {};
                const newNode = Object.assign(Object.assign({ id: target }, idNode), { children: [], childMap: new Map() });
                hierarchy.childMap.set(target, newNode);
                hierarchy.children.push(newNode);
            }
        }
        hierarchyDecorator(hierarchy, hashEntries, nodeIDAccessor, nodes);
        nodes.forEach((node) => {
            const nodeID = nodeIDAccessor(node);
            if (!discoveredHierarchyMap.has(nodeID) &&
                !targetToSourceMap.has(nodeID)) {
                hierarchy.children.push(Object.assign(Object.assign({ id: nodeID }, node), { children: [], childMap: new Map() }));
            }
        });
        if (hierarchy.children.length === 1) {
            hierarchy = hierarchy.children[0];
            hasLogicalRoot = false;
        }
        return { hierarchy, isHierarchical: true, hasLogicalRoot };
    }
    return { hierarchy: {}, isHierarchical: false, hasLogicalRoot: false };
};

function determineNodeIcon(baseCustomNodeIcon, networkSettings, size, nodes) {
    if (baseCustomNodeIcon)
        return baseCustomNodeIcon;
    const center = [size[0] / 2, size[1] / 2];
    switch (networkSettings.type) {
        case "sankey":
            return sankeyNodeGenerator;
        case "partition":
            return networkSettings.projection === "radial"
                ? radialRectNodeGenerator(size, center, networkSettings)
                : hierarchicalRectNodeGenerator;
        case "treemap":
            return networkSettings.projection === "radial"
                ? radialRectNodeGenerator(size, center, networkSettings)
                : hierarchicalRectNodeGenerator;
        case "circlepack":
            return circleNodeGenerator;
        case "chord":
            return chordNodeGenerator(size);
        case "dagre":
            return hierarchicalRectNodeGenerator;
        case "matrix":
            return matrixNodeGenerator(size, nodes);
    }
    return circleNodeGenerator;
}
function determineEdgeIcon({ baseCustomEdgeIcon, networkSettings, size, graph, nodes }) {
    if (baseCustomEdgeIcon)
        return baseCustomEdgeIcon;
    switch (networkSettings.type) {
        case "partition":
            return () => null;
        case "treemap":
            return () => null;
        case "circlepack":
            return () => null;
        case "chord":
            return chordEdgeGenerator(size);
        case "matrix":
            return matrixEdgeGenerator(size, nodes);
        case "arc":
            return arcEdgeGenerator(size);
        case "dagre":
            if (graph)
                return dagreEdgeGenerator(graph.graph().rankdir);
        case "sankey":
            return sankeyArrowGenerator;
    }
    return undefined;
}
const basicMiddle = (d) => ({
    edge: d,
    x: (d.source.x + d.target.x) / 2,
    y: (d.source.y + d.target.y) / 2
});
const edgePointHash = {
    sankey: (d) => ({
        edge: d,
        x: (d.source.x1 + d.target.x0) / 2,
        y: d.circularPathData
            ? d.circularPathData.verticalFullExtent
            : ((d.y0 + d.y1) / 2 + (d.y0 + d.y1) / 2) / 2
    }),
    force: basicMiddle,
    tree: basicMiddle,
    cluster: basicMiddle,
    matrix: (d) => {
        return {
            edge: d,
            x: d.source.y,
            y: d.target.y
        };
    }
};
const hierarchicalTypeHash = {
    dendrogram: tree,
    tree,
    circlepack: pack,
    cluster,
    treemap,
    partition
};
const hierarchicalProjectable = {
    partition: true,
    cluster: true,
    tree: true,
    dendrogram: true
};
const radialProjectable = {
    partition: true,
    cluster: true,
    tree: true,
    dendrogram: true
};
const sankeyOrientHash = {
    left: sankeyLeft,
    right: sankeyRight,
    center: sankeyCenter,
    justify: sankeyJustify
};
function breadthFirstCompontents(baseNodes, hash) {
    const componentMap = {
        "0": { componentNodes: [], componentEdges: [] }
    };
    const components = [componentMap["0"]];
    let componentID = 0;
    traverseNodesBF(baseNodes, true);
    function traverseNodesBF(nodes, top) {
        for (const node of nodes) {
            const hashNode = hash.get(node);
            if (!hashNode) {
                componentMap["0"].componentNodes.push(node);
            }
            else if (hashNode.component === -99) {
                if (top === true) {
                    componentID++;
                    componentMap[componentID] = {
                        componentNodes: [],
                        componentEdges: []
                    };
                    components.push(componentMap[componentID]);
                }
                hashNode.component = componentID;
                componentMap[componentID].componentNodes.push(node);
                componentMap[componentID].componentEdges.push(...hashNode.edges);
                const traversibleNodes = [...hashNode.connectedNodes];
                traverseNodesBF(traversibleNodes, hash);
            }
        }
    }
    return components.sort((a, b) => b.componentNodes.length - a.componentNodes.length);
}
const matrixify = ({ edgeHash, nodes, edgeWidthAccessor, nodeIDAccessor }) => {
    const matrix = [];
    for (const nodeSource of nodes) {
        const nodeSourceID = nodeIDAccessor(nodeSource);
        const sourceRow = [];
        matrix.push(sourceRow);
        for (const nodeTarget of nodes) {
            const nodeTargetID = nodeIDAccessor(nodeTarget);
            const theEdge = edgeHash.get(`${nodeSourceID}|${nodeTargetID}`);
            if (theEdge) {
                sourceRow.push(edgeWidthAccessor(theEdge));
            }
            else {
                sourceRow.push(0);
            }
        }
    }
    return matrix;
};
const emptyArray = [];
const baseNodeProps = {
    id: undefined,
    degree: 0,
    inDegree: 0,
    outDegree: 0,
    x: 0,
    y: 0,
    x1: 0,
    x0: 0,
    y1: 0,
    y0: 0,
    height: 0,
    width: 0,
    radius: 0,
    r: 0,
    direction: undefined,
    textHeight: 0,
    textWidth: 0,
    fontSize: 0,
    scale: 1,
    nodeSize: 0,
    component: -99,
    shapeNode: false
};
const baseNetworkSettings = {
    iterations: 500,
    hierarchicalNetwork: false
};
const baseGraphSettings = {
    nodeHash: new Map(),
    edgeHash: new Map(),
    nodes: [],
    edges: [],
    hierarchicalNetwork: false,
    type: "force"
};
function recursiveIDAccessor(idAccessor, node, accessorString) {
    if (node.parent) {
        accessorString = `${accessorString}-${recursiveIDAccessor(idAccessor, Object.assign(Object.assign({}, node.parent), node.parent.data), accessorString)}`;
    }
    return `${accessorString}-${idAccessor(Object.assign(Object.assign({}, node), node.data))}`;
}
const defaultHierarchicalIDAccessor = (d) => d.id || d.descendantIndex;
const nodesEdgesFromHierarchy = (baseRootNode, idAccessor = defaultHierarchicalIDAccessor) => {
    var _a, _b;
    const edges = [];
    const nodes = [];
    const rootNode = baseRootNode.descendants
        ? baseRootNode
        : hierarchy(baseRootNode);
    const descendants = rootNode.descendants();
    let i = 0;
    for (const node of descendants) {
        node.descendantIndex = i;
        i++;
    }
    for (const node of descendants) {
        const generatedID = `${(_a = idAccessor(Object.assign(Object.assign({}, node), node.data))) !== null && _a !== void 0 ? _a : defaultHierarchicalIDAccessor(node)}-${node.parent
            ? (_b = recursiveIDAccessor(idAccessor, Object.assign(Object.assign({}, node.parent), node.parent.data), "")) !== null && _b !== void 0 ? _b : node.parent.name
            : "root"}`;
        const dataD = Object.assign(node, node.data || {}, {
            hierarchicalID: generatedID
        });
        nodes.push(dataD);
        if (node.parent !== null) {
            const dataParent = Object.assign(node.parent, node.parent.data || {});
            edges.push({
                source: dataParent,
                target: dataD,
                depth: node.depth,
                weight: 1,
                value: 1,
                _NWFEdgeKey: generatedID
            });
        }
    }
    return { edges, nodes };
};
const calculateNetworkFrame = (currentProps, prevState) => {
    const { graph, nodes = Array.isArray(graph) || typeof graph === "function"
        ? emptyArray
        : (graph && graph.nodes) || emptyArray, edges = typeof graph === "function"
        ? emptyArray
        : Array.isArray(graph)
            ? graph
            : (graph && graph.edges) || emptyArray, networkType, size, nodeStyle, nodeClass, canvasNodes, edgeStyle, edgeClass, canvasEdges, nodeRenderMode, edgeRenderMode, nodeLabels, title: baseTitle, margin: baseMargin, hoverAnnotation, customNodeIcon: baseCustomNodeIcon, customEdgeIcon: baseCustomEdgeIcon, filterRenderedNodes } = currentProps;
    let { edgeType } = currentProps;
    let networkSettings;
    const nodeHierarchicalIDFill = {};
    let networkSettingsKeys = ["type"];
    if (typeof networkType === "string") {
        networkSettings = Object.assign(Object.assign({ type: networkType }, baseNetworkSettings), { graphSettings: baseGraphSettings });
    }
    else {
        if (networkType)
            networkSettingsKeys = Object.keys(networkType);
        networkSettings = Object.assign(Object.assign(Object.assign({ type: "force" }, baseNetworkSettings), networkType), { graphSettings: baseGraphSettings });
    }
    if (networkSettings.projection === "vertical" &&
        networkSettings.type === "sankey") {
        networkSettings.direction = "down";
    }
    networkSettingsKeys.push("height", "width");
    const title = typeof baseTitle === "object" &&
        !React.isValidElement(baseTitle) &&
        baseTitle !== null
        ? baseTitle
        : { title: baseTitle, orient: "top" };
    const margin = calculateMargin({
        margin: baseMargin,
        title,
        size
    });
    const { adjustedPosition, adjustedSize } = adjustedPositionSize({
        size,
        margin
    });
    networkSettings.graphSettings.nodes = nodes;
    networkSettings.graphSettings.edges = edges;
    networkSettings.graphSettings.filterRenderedNodes = filterRenderedNodes;
    let { edgeHash, nodeHash } = networkSettings.graphSettings;
    const createPointLayer = networkSettings.type === "treemap" ||
        networkSettings.type === "partition" ||
        networkSettings.type === "sankey";
    const nodeIDAccessor = stringToFn(currentProps.nodeIDAccessor, (d) => {
        return d ? d.id : undefined;
    });
    const sourceAccessor = stringToFn(currentProps.sourceAccessor, (d) => d.source);
    const targetAccessor = stringToFn(currentProps.targetAccessor, (d) => d.target);
    const nodeSizeAccessor = typeof currentProps.nodeSizeAccessor === "number"
        ? genericFunction(currentProps.nodeSizeAccessor)
        : stringToFn(currentProps.nodeSizeAccessor, (d) => d.r || 5);
    const edgeWidthAccessor = stringToFn(currentProps.edgeWidthAccessor, (d) => d.weight || 1);
    const nodeStyleFn = stringToFn(nodeStyle, () => ({}), true);
    const nodeClassFn = stringToFn(nodeClass, () => "", true);
    const nodeRenderModeFn = stringToFn(nodeRenderMode, undefined, true);
    const nodeCanvasRenderFn = canvasNodes && stringToFn(canvasNodes, undefined, true);
    let { projectedNodes, projectedEdges } = prevState;
    const isHierarchical = typeof networkSettings.type === "string" &&
        hierarchicalTypeHash[networkSettings.type];
    const changedData = !prevState.projectedNodes ||
        !prevState.projectedEdges ||
        prevState.graphSettings.nodes !== nodes ||
        prevState.graphSettings.edges !== edges ||
        isHierarchical ||
        prevState.graphSettings.filterRenderedNodes !== filterRenderedNodes;
    if (networkSettings.type === "dagre") {
        const dagreGraph = graph;
        const dagreNodeMap = new Map();
        projectedNodes = dagreGraph.nodes().map((n) => {
            const baseNode = dagreGraph.node(n);
            const dagreNode = Object.assign(Object.assign({}, baseNode), { x0: baseNode.x - baseNode.width / 2, x1: baseNode.x + baseNode.width / 2, y0: baseNode.y - baseNode.height / 2, y1: baseNode.y + baseNode.height / 2, id: n, shapeNode: true, sourceLinks: [], targetLinks: [] });
            dagreNodeMap.set(n, dagreNode);
            return dagreNode;
        });
        projectedEdges = dagreGraph.edges().map((e) => {
            const dagreEdge = dagreGraph.edge(e);
            const baseEdge = Object.assign(Object.assign({}, dagreEdge), { points: dagreEdge.points.map((d) => (Object.assign({}, d))) });
            baseEdge.source = projectedNodes.find((p) => p.id === e.v);
            baseEdge.target = projectedNodes.find((p) => p.id === e.w);
            baseEdge.points.unshift({ x: baseEdge.source.x, y: baseEdge.source.y });
            baseEdge.points.push({ x: baseEdge.target.x, y: baseEdge.target.y });
            dagreNodeMap.get(e.v).targetLinks.push(baseEdge);
            dagreNodeMap.get(e.w).sourceLinks.push(baseEdge);
            return baseEdge;
        });
    }
    else if (changedData) {
        const previousNodes = projectedNodes;
        edgeHash = new Map();
        nodeHash = new Map();
        networkSettings.graphSettings.edgeHash = edgeHash;
        networkSettings.graphSettings.nodeHash = nodeHash;
        projectedNodes = [];
        projectedEdges = [];
        const fixFunction = typeof networkSettings.fixExistingNodes === "function"
            ? networkSettings.fixExistingNodes
            : networkSettings.fixExistingNodes
                ? () => true
                : false;
        nodes.forEach((node) => {
            const projectedNode = Object.assign({}, node);
            const id = nodeIDAccessor(projectedNode);
            const existingNode = previousNodes.find((prevNode) => prevNode.id === id);
            const equivalentOldNode = existingNode || { x: undefined, y: undefined };
            nodeHash.set(id, projectedNode);
            nodeHash.set(node, projectedNode);
            projectedNodes.push(projectedNode);
            projectedNode.id = id;
            projectedNode.inDegree = 0;
            projectedNode.outDegree = 0;
            projectedNode.degree = 0;
            projectedNode.x = equivalentOldNode.x;
            projectedNode.y = equivalentOldNode.y;
            if (existingNode && fixFunction && fixFunction(existingNode)) {
                projectedNode.fx = existingNode.x;
                projectedNode.fy = existingNode.y;
            }
        });
        let operationalEdges = edges;
        let baseEdges = edges;
        if (isHierarchical && Array.isArray(edges)) {
            const createdHierarchicalData = softStack(edges, nodes, sourceAccessor, targetAccessor, nodeIDAccessor);
            if (createdHierarchicalData.isHierarchical) {
                baseEdges = createdHierarchicalData.hierarchy;
                projectedNodes = [];
            }
            else {
                console.error("You've sent an edge list that is not strictly hierarchical (there are nodes with multiple parents) defaulting to force-directed network layout");
                networkSettings.type = "force";
            }
        }
        if (!Array.isArray(baseEdges)) {
            networkSettings.hierarchicalNetwork = true;
            const rootNode = hierarchy(baseEdges, networkSettings.hierarchyChildren);
            rootNode.sum(networkSettings.hierarchySum || ((d) => d.value));
            if (isHierarchical) {
                const layout = networkSettings.layout || isHierarchical;
                const hierarchicalLayout = layout();
                const networkSettingKeys = Object.keys(networkSettings);
                if ((networkSettings.type === "dendrogram" ||
                    networkSettings.type === "tree" ||
                    networkSettings.type === "cluster") &&
                    hierarchicalLayout.separation) {
                    hierarchicalLayout.separation((a, b) => (nodeSizeAccessor(Object.assign(Object.assign({}, a), a.data)) || 1) +
                        (networkSettings.nodePadding || 0) +
                        (nodeSizeAccessor(Object.assign(Object.assign({}, b), b.data)) || 1));
                }
                networkSettingKeys.forEach((key) => {
                    if (hierarchicalLayout[key]) {
                        hierarchicalLayout[key](networkSettings[key]);
                    }
                });
                const layoutSize = networkSettings.projection === "horizontal" && isHierarchical
                    ? [adjustedSize[1], adjustedSize[0]]
                    : adjustedSize;
                if (!networkSettings.nodeSize && hierarchicalLayout.size) {
                    hierarchicalLayout.size(layoutSize);
                }
                hierarchicalLayout(rootNode);
            }
            operationalEdges = nodesEdgesFromHierarchy(rootNode, nodeIDAccessor).edges;
        }
        baseNodeProps.shapeNode = createPointLayer;
        if (Array.isArray(operationalEdges)) {
            operationalEdges.forEach((edge) => {
                const source = sourceAccessor(edge);
                const target = targetAccessor(edge);
                const sourceTarget = [source, target];
                sourceTarget.forEach((nodeDirection) => {
                    if (!nodeHash.get(nodeDirection)) {
                        const nodeObject = typeof nodeDirection === "object"
                            ? Object.assign(Object.assign({}, baseNodeProps), nodeDirection) : Object.assign(Object.assign({}, baseNodeProps), { id: nodeDirection, createdByFrame: true });
                        const nodeIDValue = nodeObject.id || nodeIDAccessor(nodeObject);
                        nodeHierarchicalIDFill[nodeIDValue]
                            ? (nodeHierarchicalIDFill[nodeIDValue] += 1)
                            : (nodeHierarchicalIDFill[nodeIDValue] = 1);
                        if (!nodeObject.id) {
                            const nodeSuffix = nodeHierarchicalIDFill[nodeIDValue] === 1
                                ? ""
                                : `-${nodeHierarchicalIDFill[nodeIDValue]}`;
                            nodeObject.id = `${nodeIDValue}${nodeSuffix}`;
                        }
                        nodeHash.set(nodeDirection, nodeObject);
                        projectedNodes.push(nodeObject);
                    }
                });
                const edgeWeight = edge.weight || 1;
                const sourceNode = nodeHash.get(source);
                const targetNode = nodeHash.get(target);
                targetNode.inDegree += edgeWeight;
                sourceNode.outDegree += edgeWeight;
                targetNode.degree += edgeWeight;
                sourceNode.degree += edgeWeight;
                const edgeKey = `${nodeIDAccessor(sourceNode) || source}|${nodeIDAccessor(targetNode) || target}`;
                const newEdge = Object.assign({}, edge, {
                    source: nodeHash.get(source),
                    target: nodeHash.get(target)
                });
                edgeHash.set(edgeKey, newEdge);
                projectedEdges.push(newEdge);
                if (networkSettings.type === "matrix") {
                    projectedEdges.push(Object.assign(Object.assign({}, newEdge), { source: newEdge.target, target: newEdge.source }));
                }
            });
        }
    }
    else {
        edgeHash = new Map();
        networkSettings.graphSettings.edgeHash = edgeHash;
        projectedEdges.forEach((edge) => {
            const edgeSource = typeof edge.source === "string"
                ? edge.source
                : nodeIDAccessor(edge.source);
            const edgeTarget = typeof edge.target === "string"
                ? edge.target
                : nodeIDAccessor(edge.target);
            const edgeKey = `${edgeSource}|${edgeTarget}`;
            edgeHash.set(edgeKey, edge);
        });
    }
    const customNodeIcon = determineNodeIcon(baseCustomNodeIcon, networkSettings, adjustedSize, projectedNodes);
    const customEdgeIcon = determineEdgeIcon({
        baseCustomEdgeIcon,
        networkSettings,
        size: adjustedSize,
        nodes: projectedNodes,
        graph
    });
    if ((networkSettings.type === "sankey" ||
        networkSettings.type === "flowchart") &&
        topologicalSort(projectedNodes, projectedEdges) === null) {
        networkSettings.customSankey = sankeyCircular;
    }
    networkSettings.width = size[0];
    networkSettings.height = size[1];
    let networkSettingsChanged = false;
    networkSettingsKeys.forEach((key) => {
        if (key !== "edgeType" &&
            key !== "graphSettings" &&
            networkSettings[key] !== prevState.graphSettings[key]) {
            networkSettingsChanged = true;
        }
    });
    //Support bubble chart with circle pack and with force
    if (networkSettings.type === "sankey") {
        edgeType = (d) => d.circular
            ? circularAreaLink(d)
            : edgeType === "angled"
                ? ribbonLink(d)
                : areaLink(d);
    }
    else if (isHierarchical) {
        projectedNodes.forEach((node) => {
            if (createPointLayer) {
                node.x = (node.x0 + node.x1) / 2;
                node.y = (node.y0 + node.y1) / 2;
            }
            if (typeof networkSettings.type === "string" &&
                hierarchicalProjectable[networkSettings.type] &&
                networkSettings.projection === "horizontal") {
                const ox = node.x;
                node.x = node.y;
                node.y = ox;
                if (createPointLayer) {
                    const ox0 = node.x0;
                    const ox1 = node.x1;
                    node.x0 = node.y0;
                    node.x1 = node.y1;
                    node.y0 = ox0;
                    node.y1 = ox1;
                }
            }
            else if (typeof networkSettings.type === "string" &&
                radialProjectable[networkSettings.type] &&
                networkSettings.projection === "radial") {
                const radialPoint = node.depth === 0
                    ? [adjustedSize[0] / 2, adjustedSize[1] / 2]
                    : pointOnArcAtAngle$1([adjustedSize[0] / 2, adjustedSize[1] / 2], node.x / adjustedSize[0], node.y / 2);
                node.x = radialPoint[0];
                node.y = radialPoint[1];
            }
            else {
                node.x = node.x;
                node.y = node.y;
                if (createPointLayer) {
                    node.x0 = node.x0;
                    node.x1 = node.x1;
                    node.y0 = node.y0;
                    node.y1 = node.y1;
                }
            }
        });
    }
    let generateEphemeralEdges = false;
    if (networkSettings.type !== "static" &&
        (changedData || networkSettingsChanged)) {
        let components = [
            {
                componentNodes: projectedNodes,
                componentEdges: projectedEdges
            }
        ];
        if (networkSettings.type === "chord") {
            const radius = adjustedSize[1] / 2;
            const { groupWidth = 20, padAngle = 0.01, sortGroups } = networkSettings;
            const arcGenerator = arc()
                .innerRadius(radius - groupWidth)
                .outerRadius(radius);
            const ribbonGenerator = ribbon().radius(radius - groupWidth);
            const matrixifiedNetwork = matrixify({
                edgeHash: edgeHash,
                nodes: projectedNodes,
                edgeWidthAccessor,
                nodeIDAccessor
            });
            const chordLayout = chord().padAngle(padAngle);
            if (sortGroups) {
                chordLayout.sortGroups(sortGroups);
            }
            const chords = chordLayout(matrixifiedNetwork);
            const groups = chords.groups;
            groups.forEach((group) => {
                const groupCentroid = arcGenerator.centroid(group);
                const groupD = arcGenerator(group);
                const groupNode = projectedNodes[group.index];
                groupNode.d = groupD;
                groupNode.index = group.index;
                groupNode.x = groupCentroid[0] + adjustedSize[0] / 2;
                groupNode.y = groupCentroid[1] + adjustedSize[1] / 2;
            });
            chords.forEach((generatedChord) => {
                const chordD = ribbonGenerator(generatedChord);
                //this is incorrect should use edgeHash
                const nodeSourceID = nodeIDAccessor(projectedNodes[generatedChord.source.index]);
                const nodeTargetID = nodeIDAccessor(projectedNodes[generatedChord.target.index]);
                const chordEdge = edgeHash.get(`${nodeSourceID}|${nodeTargetID}`);
                chordEdge.d = chordD;
                const chordBounds = pathBounds(chordD);
                chordEdge.x =
                    adjustedSize[0] / 2 + (chordBounds.x1 + chordBounds.x2) / 2;
                chordEdge.y =
                    adjustedSize[1] / 2 + (chordBounds.y1 + chordBounds.y2) / 2;
            });
        }
        else if (networkSettings.type === "sankey" ||
            networkSettings.type === "flowchart") {
            const { orient = "center", iterations = 100, nodePadding, nodePaddingRatio = nodePadding ? undefined : 0.5, nodeWidth = networkSettings.type === "flowchart" ? 2 : 24, customSankey, direction = "right", showArrows = false } = networkSettings;
            const sankeyOrient = sankeyOrientHash[orient];
            const actualSankey = customSankey || sankeyCircular;
            let frameExtent = [[0, 0], adjustedSize];
            if (networkSettings.direction === "up" ||
                networkSettings.direction === "down") {
                frameExtent = [
                    [0, 0],
                    [adjustedSize[1], adjustedSize[0]]
                ];
            }
            //CREATE FAKE EDGES TO GET UP TO PASSED VALUE
            generateEphemeralEdges = projectedNodes.some((n) => !n.createdByFrame && n.value > 0);
            if (generateEphemeralEdges) {
                const edgeValueMap = new Map();
                for (const edge of projectedEdges) {
                    if (!edgeValueMap.has(edge.source.id)) {
                        edgeValueMap.set(edge.source.id, {
                            source: 0,
                            target: 0
                        });
                    }
                    if (!edgeValueMap.has(edge.target.id)) {
                        edgeValueMap.set(edge.target.id, {
                            source: 0,
                            target: 0
                        });
                    }
                    edgeValueMap.get(edge.source.id).source += edge.value;
                    edgeValueMap.get(edge.target.id).target += edge.value;
                }
                for (const node of projectedNodes) {
                    if (!node.createdByFrame) {
                        let maxEdgeValue = 0;
                        if (edgeValueMap.has(node.id)) {
                            maxEdgeValue = Math.max(edgeValueMap.get(node.id).source, edgeValueMap.get(node.id).target);
                        }
                        if (node.value > maxEdgeValue) {
                            projectedEdges.push({
                                source: node,
                                target: node,
                                value: node.value - maxEdgeValue,
                                ephemeral: true
                            });
                        }
                    }
                }
            }
            const frameSankey = actualSankey()
                .extent(frameExtent)
                .links(projectedEdges)
                .nodes(projectedNodes)
                .nodeAlign(sankeyOrient)
                .nodeId(nodeIDAccessor)
                .nodeWidth(nodeWidth)
                .iterations(iterations);
            if (generateEphemeralEdges) {
                projectedEdges = projectedEdges.filter((e) => !e.ephemeral);
            }
            if (frameSankey.nodePaddingRatio && nodePaddingRatio) {
                frameSankey.nodePaddingRatio(nodePaddingRatio);
            }
            else if (nodePadding) {
                frameSankey.nodePadding(nodePadding);
            }
            frameSankey();
            projectedNodes.forEach((d) => {
                d.height = d.y1 - d.y0;
                d.width = d.x1 - d.x0;
                d.x = d.x0 + d.width / 2;
                d.y = d.y0 + d.height / 2;
                d.radius = d.height / 2;
                d.direction = direction;
            });
            projectedEdges.forEach((d) => {
                d.showArrows = showArrows;
                d.sankeyWidth = d.width;
                d.direction = direction;
                d.width = undefined;
            });
        }
        else if (networkSettings.type === "force") {
            const { iterations = 500, edgeStrength = 0.1, distanceMax = Infinity, edgeDistance, forceManyBody: nsForceMB = (d) => -25 * nodeSizeAccessor(d) } = networkSettings;
            const linkForce = forceLink().strength((d) => Math.min(2.5, d.weight ? d.weight * edgeStrength : edgeStrength));
            if (edgeDistance) {
                linkForce.distance(edgeDistance);
            }
            const simulation = networkSettings.simulation ||
                forceSimulation().force("charge", forceManyBody().distanceMax(distanceMax).strength(nsForceMB));
            //        simulation.force("link", linkForce).nodes(projectedNodes)
            simulation.nodes(projectedNodes);
            const forceMod = adjustedSize[1] / adjustedSize[0];
            if (!simulation.force("x")) {
                simulation.force("x", forceX(adjustedSize[0] / 2).strength(forceMod * 0.1));
            }
            if (!simulation.force("y")) {
                simulation.force("y", forceY(adjustedSize[1] / 2).strength(0.1));
            }
            if (projectedEdges.length !== 0 && !simulation.force("link")) {
                simulation.force("link", linkForce);
                simulation.force("link").links(projectedEdges);
            }
            //reset alpha if it's too cold
            if (simulation.alpha() < 0.1) {
                simulation.alpha(1);
            }
            simulation.stop();
            for (let i = 0; i < iterations; ++i) {
                simulation.tick();
            }
        }
        else if (networkSettings.type === "motifs") {
            const componentMap = new Map();
            projectedEdges.forEach((edge) => {
                [edge.source, edge.target].forEach((node) => {
                    if (!componentMap.get(node)) {
                        componentMap.set(node, {
                            node,
                            component: -99,
                            connectedNodes: [],
                            edges: []
                        });
                    }
                });
                componentMap.get(edge.source).connectedNodes.push(edge.target);
                componentMap.get(edge.target).connectedNodes.push(edge.source);
                componentMap.get(edge.source).edges.push(edge);
            });
            components = breadthFirstCompontents(projectedNodes, componentMap);
            const largestComponent = Math.max(projectedNodes.length / 3, components[0].componentNodes.length);
            const layoutSize = size[0] > size[1] ? size[1] : size[0];
            const layoutDirection = size[0] > size[1] ? "horizontal" : "vertical";
            const { iterations = 500, edgeStrength = 0.1, edgeDistance, padding = 0 } = networkSettings;
            let currentX = padding;
            let currentY = padding;
            components.forEach(({ componentNodes, componentEdges }) => {
                const linkForce = forceLink().strength((d) => Math.min(2.5, d.weight ? d.weight * edgeStrength : edgeStrength));
                if (edgeDistance) {
                    linkForce.distance(edgeDistance);
                }
                const componentLayoutSize = Math.max(componentNodes.length / largestComponent, 0.2) * layoutSize;
                const xBound = componentLayoutSize + currentX;
                const yBound = componentLayoutSize + currentY;
                if (layoutDirection === "horizontal") {
                    if (yBound > size[1]) {
                        currentX = componentLayoutSize + currentX + padding;
                        currentY = componentLayoutSize + padding;
                    }
                    else {
                        currentY = componentLayoutSize + currentY + padding;
                    }
                }
                else {
                    if (xBound > size[0]) {
                        currentY = componentLayoutSize + currentY + padding;
                        currentX = componentLayoutSize + padding;
                    }
                    else {
                        currentX = componentLayoutSize + currentX + padding;
                    }
                }
                const xCenter = currentX - componentLayoutSize / 2;
                const yCenter = currentY - componentLayoutSize / 2;
                const simulation = forceSimulation()
                    .force("charge", forceManyBody().strength(networkSettings.forceManyBody ||
                    ((d) => -25 * nodeSizeAccessor(d))))
                    .force("link", linkForce);
                simulation
                    .force("x", forceX(xCenter))
                    .force("y", forceY(yCenter))
                    .nodes(componentNodes);
                simulation.force("link").links(componentEdges);
                simulation.stop();
                for (let i = 0; i < iterations; ++i)
                    simulation.tick();
                const maxX = max(componentNodes.map((d) => d.x));
                const maxY = max(componentNodes.map((d) => d.y));
                const minX = min(componentNodes.map((d) => d.x));
                const minY = min(componentNodes.map((d) => d.y));
                const resetX = scaleLinear()
                    .domain([minX, maxX])
                    .range([currentX - componentLayoutSize, currentX - 20]);
                const resetY = scaleLinear()
                    .domain([minY, maxY])
                    .range([currentY - componentLayoutSize, currentY - 20]);
                componentNodes.forEach((node) => {
                    node.x = resetX(node.x);
                    node.y = resetY(node.y);
                });
            });
        }
        else if (networkSettings.type === "matrix") {
            if (networkSettings.sort) {
                projectedNodes = projectedNodes.sort(networkSettings.sort);
            }
            const gridSize = Math.min(...adjustedSize);
            const stepSize = gridSize / (projectedNodes.length + 1);
            projectedNodes.forEach((node, index) => {
                node.x = 0;
                node.y = (index + 1) * stepSize;
            });
        }
        else if (networkSettings.type === "arc") {
            if (networkSettings.sort) {
                projectedNodes = projectedNodes.sort(networkSettings.sort);
            }
            const stepSize = adjustedSize[0] / (projectedNodes.length + 2);
            projectedNodes.forEach((node, index) => {
                node.x = (index + 1) * stepSize;
                node.y = adjustedSize[1] / 2;
            });
        }
        else if (typeof networkSettings.type === "function") {
            networkSettings.type({
                nodes: projectedNodes,
                edges: projectedEdges
            });
        }
        else {
            projectedNodes.forEach((node) => {
                node.x = node.x === undefined ? (node.x0 + node.x1) / 2 : node.x;
                node.y = node.y === undefined ? node.y0 : node.y;
            });
        }
        prevState.graphSettings.nodes = currentProps.nodes;
        prevState.graphSettings.edges = currentProps.edges;
    }
    //filter out user-defined nodes
    projectedNodes = projectedNodes.filter(filterRenderedNodes);
    projectedEdges = projectedEdges.filter((d) => projectedNodes.indexOf(d.target) !== -1 &&
        projectedNodes.indexOf(d.source) !== -1);
    if (networkSettings.direction === "flip") {
        projectedNodes.forEach((node) => {
            // const ox = node.x
            // const oy = node.y
            node.x = adjustedSize[0] - node.x;
            node.y = adjustedSize[1] - node.y;
        });
    }
    else if (networkSettings.direction === "up" ||
        networkSettings.direction === "down") {
        const mod = networkSettings.direction === "up"
            ? (value) => adjustedSize[1] - value
            : (value) => value;
        projectedNodes.forEach((node) => {
            const ox = node.x;
            const ox0 = node.x0;
            const ox1 = node.x1;
            node.x = mod(node.y);
            node.x0 = mod(node.y0);
            node.x1 = mod(node.y1);
            node.y = ox;
            node.y0 = ox0;
            node.y1 = ox1;
        });
    }
    else if (networkSettings.direction === "left") {
        projectedNodes.forEach((node) => {
            node.x = adjustedSize[0] - node.x;
            node.x0 = adjustedSize[0] - node.x0;
            node.x1 = adjustedSize[0] - node.x1;
        });
    }
    if (typeof networkSettings.zoom === "function") {
        networkSettings.zoom(projectedNodes, projectedEdges, adjustedSize);
    }
    else if (networkSettings.zoom !== false &&
        networkSettings.type !== "matrix" &&
        networkSettings.type !== "chord" &&
        networkSettings.type !== "sankey" &&
        networkSettings.type !== "partition" &&
        networkSettings.type !== "treemap" &&
        networkSettings.type !== "circlepack" &&
        networkSettings.type !== "dagre") {
        // ZOOM SHOULD MAINTAIN ASPECT RATIO, ADD "stretch" to fill whole area
        const xMin = min(projectedNodes.map((p) => p.x - nodeSizeAccessor(p)));
        const xMax = max(projectedNodes.map((p) => p.x + nodeSizeAccessor(p)));
        const yMin = min(projectedNodes.map((p) => p.y - nodeSizeAccessor(p)));
        const yMax = max(projectedNodes.map((p) => p.y + nodeSizeAccessor(p)));
        const xSize = Math.abs(xMax - xMin);
        const ySize = Math.abs(yMax - yMin);
        const networkAspectRatio = xSize / ySize;
        const baseAspectRatio = adjustedSize[0] / adjustedSize[1];
        let yMod, xMod;
        if (networkSettings.zoom === "stretch") {
            yMod = 0;
            xMod = 0;
        }
        else if (xSize > ySize) {
            if (networkAspectRatio > baseAspectRatio) {
                xMod = 0;
                yMod = (adjustedSize[1] - (adjustedSize[0] / xSize) * ySize) / 2;
            }
            else {
                yMod = 0;
                xMod = (adjustedSize[0] - (adjustedSize[1] / ySize) * xSize) / 2;
            }
        }
        else {
            if (networkAspectRatio > baseAspectRatio) {
                xMod = 0;
                yMod = (adjustedSize[1] - (adjustedSize[0] / xSize) * ySize) / 2;
            }
            else {
                yMod = 0;
                xMod = (adjustedSize[0] - (adjustedSize[1] / ySize) * xSize) / 2;
            }
        }
        const projectionScaleX = scaleLinear()
            .domain([xMin, xMax])
            .range([xMod, adjustedSize[0] - xMod]);
        const projectionScaleY = scaleLinear()
            .domain([yMin, yMax])
            .range([yMod, adjustedSize[1] - yMod]);
        projectedNodes.forEach((node) => {
            node.x = projectionScaleX(node.x);
            node.y = projectionScaleY(node.y);
        });
    }
    else if (networkSettings.zoom !== false &&
        networkSettings.projection !== "radial" &&
        (networkSettings.type === "partition" ||
            networkSettings.type === "treemap" ||
            networkSettings.type === "dagre")) {
        const xMin = min(projectedNodes.map((p) => p.x0));
        const xMax = max(projectedNodes.map((p) => p.x1));
        const yMin = min(projectedNodes.map((p) => p.y0));
        const yMax = max(projectedNodes.map((p) => p.y1));
        const projectionScaleX = scaleLinear()
            .domain([xMin, xMax])
            .range([margin.left, adjustedSize[0] - margin.right]);
        const projectionScaleY = scaleLinear()
            .domain([yMin, yMax])
            .range([margin.top, adjustedSize[1] - margin.bottom]);
        projectedNodes.forEach((node) => {
            node.x = projectionScaleX(node.x);
            node.y = projectionScaleY(node.y);
            node.x0 = projectionScaleX(node.x0);
            node.y0 = projectionScaleY(node.y0);
            node.x1 = projectionScaleX(node.x1);
            node.y1 = projectionScaleY(node.y1);
            node.zoomedHeight = node.y1 - node.y0;
            node.zoomedWidth = node.x1 - node.x0;
        });
        projectedEdges.forEach((edge) => {
            if (edge.points) {
                edge.points.forEach((p) => {
                    p.x = projectionScaleX(p.x);
                    p.y = projectionScaleY(p.y);
                });
            }
        });
    }
    else if (networkSettings.zoom !== false &&
        networkSettings.type === "sankey" &&
        (projectedEdges.some((e) => e.circular) || generateEphemeralEdges)) {
        const circularLinks = projectedEdges.filter((e) => e.circular);
        const xMinEdge = min(circularLinks, (e) => e.circularPathData.rightFullExtent - e.sankeyWidth / 2) || Infinity;
        const xMaxEdge = max(circularLinks, (e) => e.circularPathData.leftFullExtent + e.sankeyWidth / 2) || -Infinity;
        const yMinEdge = min(circularLinks, (e) => e.circularPathData.verticalFullExtent - e.sankeyWidth / 2) || Infinity;
        const yMaxEdge = max(circularLinks, (e) => e.circularPathData.verticalFullExtent + e.sankeyWidth / 2) || -Infinity;
        const yMinNode = min(projectedNodes, (node) => node.y0);
        const yMaxNode = max(projectedNodes, (node) => node.y1);
        const xMinNode = min(projectedNodes, (node) => node.x0);
        const xMaxNode = max(projectedNodes, (node) => node.x1);
        const sankeyMinX = Math.min(xMinEdge, xMinNode);
        const sankeyMaxX = Math.max(xMaxEdge, xMaxNode);
        const sankeyMinY = Math.min(yMinEdge, yMinNode);
        const sankeyMaxY = Math.max(yMaxEdge, yMaxNode);
        const projectionScaleX = scaleLinear()
            .domain([sankeyMinX, sankeyMaxX])
            .range([0, adjustedSize[0]]);
        const projectionScaleY = scaleLinear()
            .domain([sankeyMinY, sankeyMaxY])
            .range([0, adjustedSize[1]]);
        const widthFactor = adjustedSize[1] / (sankeyMaxY - sankeyMinY);
        for (const node of projectedNodes) {
            node.x = projectionScaleX(node.x);
            node.x0 = projectionScaleX(node.x0);
            node.x1 = projectionScaleX(node.x1);
            node.y = projectionScaleY(node.y);
            node.y0 = projectionScaleY(node.y0);
            node.y1 = projectionScaleY(node.y1);
            node.width = node.x1 - node.x0;
            node.height = node.y1 - node.y0;
        }
        for (const edge of projectedEdges) {
            if (edge.circular) {
                edge.circularPathData.sourceX = projectionScaleX(edge.circularPathData.sourceX);
                edge.circularPathData.sourceY = projectionScaleY(edge.circularPathData.sourceY);
                edge.circularPathData.leftFullExtent = projectionScaleX(edge.circularPathData.leftFullExtent);
                edge.circularPathData.verticalFullExtent = projectionScaleY(edge.circularPathData.verticalFullExtent);
                edge.circularPathData.rightFullExtent = projectionScaleX(edge.circularPathData.rightFullExtent);
                edge.circularPathData.targetX = projectionScaleX(edge.circularPathData.targetX);
                edge.circularPathData.targetY = projectionScaleY(edge.circularPathData.targetY);
            }
            else {
                edge.y0 = projectionScaleY(edge.y0);
                edge.y1 = projectionScaleY(edge.y1);
            }
            edge.sankeyWidth = edge.sankeyWidth * widthFactor;
        }
    }
    projectedNodes.forEach((node) => {
        node.nodeSize = nodeSizeAccessor(node);
    });
    projectedEdges.forEach((edge) => {
        edge.width = edgeWidthAccessor(edge);
    });
    let legendSettings;
    if (currentProps.legend) {
        legendSettings = currentProps.legend;
        if (!legendSettings.legendGroups) {
            ///Something auto for networks
            const legendGroups = [
                {
                    styleFn: currentProps.nodeStyle,
                    type: "fill",
                    items: []
                }
            ];
            legendSettings.legendGroups = legendGroups;
        }
    }
    const networkFrameRender = {
        edges: {
            accessibleTransform: (data, i) => {
                const edgeX = (data[i].source.x + data[i].target.x) / 2;
                const edgeY = (data[i].source.y + data[i].target.y) / 2;
                return Object.assign(Object.assign({ type: "frame-hover" }, data[i]), { x: edgeX, y: edgeY });
            },
            data: projectedEdges,
            styleFn: stringToFn(edgeStyle, () => ({}), true),
            classFn: stringToFn(edgeClass, () => "", true),
            renderMode: stringToFn(edgeRenderMode, undefined, true),
            canvasRenderFn: canvasEdges && stringToFn(canvasEdges, undefined, true),
            renderKeyFn: currentProps.edgeRenderKey
                ? currentProps.edgeRenderKey
                : (d) => d._NWFEdgeKey || d.key || `${d.source.id}-${d.target.id}`,
            behavior: drawEdges,
            projection: networkSettings.projection,
            type: edgeType,
            customMark: customEdgeIcon,
            networkSettings,
            numberOfNodes: projectedNodes.length,
            size: adjustedSize
        },
        nodes: {
            accessibleTransform: (data, i) => (Object.assign(Object.assign({ type: "frame-hover" }, data[i]), (data[i].data || {}))),
            data: projectedNodes,
            styleFn: nodeStyleFn,
            classFn: nodeClassFn,
            renderMode: nodeRenderModeFn,
            canvasRenderFn: nodeCanvasRenderFn,
            customMark: customNodeIcon,
            behavior: drawNodes,
            renderKeyFn: currentProps.nodeRenderKey,
            networkSettings
        }
    };
    const nodeLabelAnnotations = [];
    if (currentProps.nodeLabels && projectedNodes) {
        projectedNodes.forEach((node, nodei) => {
            const feasibleLabel = nodeLabels && nodeLabels !== true && nodeLabels(node);
            if (nodeLabels === true || feasibleLabel) {
                const actualLabel = networkSettings.projection === "radial" && node.depth !== 0
                    ? radialLabelGenerator(node, nodei, nodeLabels === true ? nodeIDAccessor : nodeLabels, adjustedSize)
                    : nodeLabels === true
                        ? nodeIDAccessor(node, nodei)
                        : feasibleLabel;
                let nodeLabel;
                if (React.isValidElement(actualLabel)) {
                    nodeLabel = {
                        key: `node-label-${nodei}`,
                        type: "basic-node-label",
                        x: node.x,
                        y: node.y,
                        element: actualLabel
                    };
                }
                else {
                    nodeLabel = {
                        key: `node-label-${nodei}`,
                        className: "node-label",
                        dx: 0,
                        dy: 0,
                        x: node.x,
                        y: node.y,
                        note: { label: actualLabel },
                        connector: { end: "none" },
                        type: AnnotationLabel,
                        subject: { radius: nodeSizeAccessor(node) + 2 }
                    };
                }
                nodeLabelAnnotations.push(nodeLabel);
            }
        });
    }
    let projectedXYPoints;
    const overlay = [];
    const areaBasedTypes = [
        "circlepack",
        "treemap",
        "partition",
        "chord"
        //    "matrix"
    ];
    if ((hoverAnnotation &&
        areaBasedTypes.find((d) => d === networkSettings.type)) ||
        hoverAnnotation === "area") {
        if (hoverAnnotation !== "edge") {
            const renderedNodeOverlays = projectedNodes.map((d, i) => (Object.assign({ overlayData: d }, customNodeIcon({
                d,
                i,
                transform: `translate(${d.x},${d.y})`,
                styleFn: () => ({ opacity: 0 })
            }).props)));
            overlay.push(...renderedNodeOverlays);
        }
        if (hoverAnnotation !== "node") {
            projectedEdges.forEach((d, i) => {
                const generatedIcon = customEdgeIcon({
                    d,
                    i,
                    transform: `translate(${d.x},${d.y})`,
                    styleFn: () => ({ opacity: 0 })
                });
                if (generatedIcon) {
                    overlay.push(Object.assign({ overlayData: Object.assign(Object.assign({}, d), { x: d.x === undefined ? (d.source.x + d.target.x) / 2 : d.x, y: d.y === undefined ? (d.source.y + d.target.y) / 2 : d.y, edge: true }) }, generatedIcon.props));
                }
            });
        }
    }
    else if (hoverAnnotation === "edge" &&
        typeof networkSettings.type === "string" &&
        edgePointHash[networkSettings.type]) {
        projectedXYPoints = projectedEdges.map(edgePointHash[networkSettings.type]);
    }
    else if (Array.isArray(hoverAnnotation) ||
        hoverAnnotation === true ||
        hoverAnnotation === "node") {
        projectedXYPoints = projectedNodes;
        if (changedData || networkSettingsChanged)
            projectedXYPoints = [...projectedNodes];
    }
    else if (hoverAnnotation === "all" &&
        typeof networkSettings.type === "string") {
        projectedXYPoints = [
            ...projectedEdges.map(edgePointHash[networkSettings.type]),
            ...projectedNodes
        ];
    }
    return {
        adjustedPosition: adjustedPosition,
        adjustedSize: adjustedSize,
        backgroundGraphics: currentProps.backgroundGraphics,
        foregroundGraphics: currentProps.foregroundGraphics,
        title,
        renderNumber: prevState.renderNumber + 1,
        projectedNodes,
        projectedEdges,
        projectedXYPoints,
        overlay,
        nodeIDAccessor,
        sourceAccessor,
        targetAccessor,
        nodeSizeAccessor,
        edgeWidthAccessor,
        margin,
        legendSettings,
        networkFrameRender,
        nodeLabelAnnotations,
        graphSettings: Object.assign(Object.assign({}, networkSettings.graphSettings), networkSettings),
        props: currentProps
    };
};

const blankArray = [];
const matrixRenderOrder = ["nodes", "edges"];
const generalRenderOrder = ["edges", "nodes"];
const projectedCoordinateNames = { y: "y", x: "x" };
const xScale = scaleLinear();
const yScale = scaleLinear();
const defaultProps = {
    annotations: [],
    foregroundGraphics: [],
    size: [500, 500],
    className: "",
    name: "networkframe",
    networkType: { type: "force", iterations: 500 },
    filterRenderedNodes: (d) => d.id !== "root-generated"
};
const NetworkFrame = React.memo(function NetworkFrame(allProps) {
    const props = Object.assign(Object.assign({}, defaultProps), allProps);
    const baseState = {
        dataVersion: undefined,
        nodeData: [],
        edgeData: [],
        adjustedPosition: [],
        adjustedSize: [],
        backgroundGraphics: null,
        foregroundGraphics: null,
        projectedNodes: [],
        projectedEdges: [],
        renderNumber: 0,
        nodeLabelAnnotations: [],
        graphSettings: {
            type: "empty-start",
            nodes: [],
            edges: [],
            nodeHash: new Map(),
            edgeHash: new Map(),
            hierarchicalNetwork: false
        },
        edgeWidthAccessor: stringToFn("weight"),
        legendSettings: undefined,
        margin: { top: 0, left: 0, right: 0, bottom: 0 },
        networkFrameRender: {},
        nodeIDAccessor: stringToFn("id"),
        nodeSizeAccessor: genericFunction(5),
        overlay: [],
        projectedXYPoints: [],
        sourceAccessor: stringToFn("source"),
        targetAccessor: stringToFn("target"),
        title: { title: undefined },
        props
    };
    const initialState = useMemo(() => (Object.assign(Object.assign({}, baseState), calculateNetworkFrame(props, baseState))), []);
    const state = useDerivedStateFromProps(deriveNetworkFrameState, props, initialState);
    useLegacyUnmountCallback(props, state);
    const { annotations = [], annotationSettings, className, customClickBehavior, customDoubleClickBehavior, customHoverBehavior, size, matte, hoverAnnotation, beforeElements, afterElements, interaction, disableContext, canvasPostProcess, baseMarkProps, useSpans, canvasNodes, canvasEdges, additionalDefs, renderOrder = state.graphSettings && state.graphSettings.type === "matrix"
        ? matrixRenderOrder
        : generalRenderOrder, sketchyRenderingEngine, frameRenderOrder, disableCanvasInteraction, interactionSettings, disableProgressiveRendering } = props;
    const { backgroundGraphics, foregroundGraphics, projectedXYPoints, margin, legendSettings, adjustedPosition, adjustedSize, networkFrameRender, nodeLabelAnnotations, overlay, title } = state;
    let formattedOverlay;
    if (overlay && overlay.length > 0) {
        formattedOverlay = overlay;
    }
    let activeHoverAnnotation;
    if (Array.isArray(hoverAnnotation)) {
        activeHoverAnnotation = hoverAnnotation;
    }
    else if ((customClickBehavior || customDoubleClickBehavior || customHoverBehavior) &&
        (hoverAnnotation === undefined || hoverAnnotation === false)) {
        activeHoverAnnotation = blankArray;
    }
    else {
        activeHoverAnnotation = !!hoverAnnotation;
    }
    return (React.createElement(Frame, { name: "networkframe", renderPipeline: networkFrameRender, adjustedPosition: adjustedPosition, adjustedSize: adjustedSize, size: size, xScale: xScale, yScale: yScale, title: title, matte: matte, className: className, additionalDefs: additionalDefs, frameKey: "none", projectedCoordinateNames: projectedCoordinateNames, defaultSVGRule: (args) => defaultNetworkSVGRule(props, state, args), defaultHTMLRule: (args) => defaultNetworkHTMLRule(props, state, args), hoverAnnotation: activeHoverAnnotation, annotations: [...annotations, ...nodeLabelAnnotations], annotationSettings: annotationSettings, legendSettings: legendSettings, interaction: interaction, customClickBehavior: customClickBehavior, customHoverBehavior: customHoverBehavior, customDoubleClickBehavior: customDoubleClickBehavior, points: projectedXYPoints, margin: margin, overlay: formattedOverlay, backgroundGraphics: backgroundGraphics, foregroundGraphics: foregroundGraphics, beforeElements: beforeElements, afterElements: afterElements, disableContext: disableContext, canvasPostProcess: canvasPostProcess, baseMarkProps: baseMarkProps, useSpans: !!useSpans, canvasRendering: !!(canvasNodes || canvasEdges), renderOrder: renderOrder, disableCanvasInteraction: disableCanvasInteraction, sketchyRenderingEngine: sketchyRenderingEngine, frameRenderOrder: frameRenderOrder, interactionSettings: interactionSettings, disableProgressiveRendering: disableProgressiveRendering }));
});
function deriveNetworkFrameState(nextProps, prevState) {
    const { props } = prevState;
    if ((prevState.dataVersion &&
        prevState.dataVersion !== nextProps.dataVersion) ||
        (!prevState.projectedNodes && !prevState.projectedEdges) ||
        props.size[0] !== nextProps.size[0] ||
        props.size[1] !== nextProps.size[1] ||
        (!prevState.dataVersion &&
            networkFrameChangeProps.find((d) => {
                return props[d] !== nextProps[d];
            }))) {
        return Object.assign(Object.assign({}, calculateNetworkFrame(nextProps, prevState)), { props: nextProps });
    }
    return { props: nextProps };
}
function defaultNetworkSVGRule(props, state, { d: baseD, i, annotationLayer }) {
    const { projectedNodes, projectedEdges, nodeIDAccessor, nodeSizeAccessor, networkFrameRender, adjustedSize, adjustedPosition } = state;
    //TODO PASS FRAME STYLE FNs TO HIGHLIGHT
    const { svgAnnotationRules } = props;
    const d = baseD.ids
        ? baseD
        : baseD.edge
            ? Object.assign(Object.assign({}, (projectedEdges.find((p) => {
                return (nodeIDAccessor(p.source) === nodeIDAccessor(baseD.edge.source) &&
                    nodeIDAccessor(p.target) === nodeIDAccessor(baseD.edge.target));
            }) || {})), baseD) : Object.assign(Object.assign({}, (projectedNodes.find((p) => nodeIDAccessor(p) === baseD.id) || {})), baseD);
    const { voronoiHover } = annotationLayer;
    if (svgAnnotationRules) {
        const customAnnotation = svgAnnotationRules({
            d,
            i,
            networkFrameProps: props,
            networkFrameState: state,
            nodes: projectedNodes,
            edges: projectedEdges,
            voronoiHover,
            screenCoordinates: [d.x, d.y],
            adjustedPosition,
            adjustedSize,
            annotationLayer
        });
        if (customAnnotation !== null) {
            return customAnnotation;
        }
    }
    if (d.type === "node") {
        return svgNodeRule({
            d,
            i,
            nodeSizeAccessor
        });
    }
    else if (d.type === "desaturation-layer") {
        return desaturationLayer({
            style: d.style instanceof Function ? d.style(d, i) : d.style,
            size: adjustedSize,
            i,
            key: d.key
        });
    }
    else if (d.type === "basic-node-label") {
        return (React.createElement("g", { key: d.key || `basic-${i}`, transform: `translate(${d.x},${d.y})` }, baseD.element || baseD.label));
    }
    else if (d.type === "react-annotation" || typeof d.type === "function") {
        return svgReactAnnotationRule({
            d,
            i,
            projectedNodes,
            nodeIDAccessor
        });
    }
    else if (d.type === "enclose") {
        return svgEncloseRule({
            d,
            i,
            projectedNodes,
            nodeIDAccessor,
            nodeSizeAccessor
        });
    }
    else if (d.type === "enclose-rect") {
        return svgRectEncloseRule({
            d,
            i,
            projectedNodes,
            nodeIDAccessor,
            nodeSizeAccessor
        });
    }
    else if (d.type === "enclose-hull") {
        return svgHullEncloseRule({
            d,
            i,
            projectedNodes,
            nodeIDAccessor,
            nodeSizeAccessor
        });
    }
    else if (d.type === "highlight") {
        return svgHighlightRule({
            d,
            i,
            networkFrameRender
        });
    }
    return null;
}
function defaultNetworkHTMLRule(props, state, { d: baseD, i, annotationLayer }) {
    const { tooltipContent, optimizeCustomTooltipPosition, htmlAnnotationRules, useSpans } = props;
    const { projectedNodes, projectedEdges, nodeIDAccessor, adjustedSize, adjustedPosition } = state;
    const { voronoiHover } = annotationLayer;
    const d = baseD.ids
        ? baseD
        : baseD.edge
            ? Object.assign(Object.assign({}, (projectedEdges.find((p) => nodeIDAccessor(p.source) === nodeIDAccessor(baseD.source) &&
                nodeIDAccessor(p.target) === nodeIDAccessor(baseD.target)) || {})), baseD) : Object.assign(Object.assign({}, (projectedNodes.find((p) => nodeIDAccessor(p) === baseD.id) || {})), baseD);
    if (htmlAnnotationRules) {
        const customAnnotation = htmlAnnotationRules({
            d,
            i,
            networkFrameProps: props,
            networkFrameState: state,
            nodes: projectedNodes,
            edges: projectedEdges,
            voronoiHover,
            screenCoordinates: [d.x, d.y],
            adjustedPosition,
            adjustedSize,
            annotationLayer
        });
        if (customAnnotation !== null) {
            return customAnnotation;
        }
    }
    if (d.type === "frame-hover") {
        return htmlFrameHoverRule({
            d,
            i,
            tooltipContent,
            optimizeCustomTooltipPosition,
            useSpans,
            nodes: projectedNodes,
            edges: projectedEdges,
            nodeIDAccessor
        });
    }
    return null;
}
NetworkFrame.displayName = "NetworkFrame";

const framePropHash = {
    NetworkFrame: true,
    XYFrame: true,
    OrdinalFrame: true,
    ResponsiveNetworkFrame: true,
    ResponsiveXYFrame: true,
    ResponsiveOrdinalFrame: true,
    SparkNetworkFrame: true,
    SparkXYFrame: true,
    SparkOrdinalFrame: true
};
const invertKeys = {
    rExtent: "invertR",
    xExtent: "invertX",
    yExtent: "invertY"
};
const buildNewState = (prevState, extentValue, extentType, extentPosition, invertedExtent) => {
    const oldExtents = prevState.rawExtents[extentType] || {};
    oldExtents[extentPosition] = extentValue;
    const extentMinMaxValues = Object.values(oldExtents)
        .flat()
        .filter((d) => d !== undefined && d !== null && !isNaN(d));
    let baseExtent = [
        Math.min(...extentMinMaxValues),
        Math.max(...extentMinMaxValues)
    ];
    if (invertedExtent) {
        baseExtent = baseExtent.reverse();
    }
    return {
        extents: Object.assign(Object.assign({}, prevState.extents), { [extentType]: extentMinMaxValues.length === 0 ? undefined : baseExtent }),
        rawExtents: Object.assign(Object.assign({}, prevState.rawExtents), { [extentType]: oldExtents })
    };
};
function validFrameProps(originalProps) {
    const newProps = Object.assign({}, originalProps);
    return newProps;
}
const processFacetController = memoize((props, state) => {
    return React.Children.map(props.children, (child, index) => {
        if (!child)
            return null;
        return mapChildrenWithAppropriateProps({
            child,
            index,
            originalAnnotations: child.props.annotations || [],
            props,
            state
        });
    });
});
/**
 * Helper for creating extent if we have a  min/max value
 * use that else use the onChange version so we can in return
 * normalize all of the facets to have the same extents
 */
const createExtent = (extentType, props, state, index) => {
    return state.extents && state.extents[extentType]
        ? {
            onChange: extentHandler(extentType, props, state, index),
            extent: state.extents[extentType]
        }
        : { onChange: extentHandler(extentType, props, state, index) };
};
/**
 * Whenever the extent changes, create the min/max values for each extentType
 * so this could be rExtent for OrdinalFrame or x/yExtent for the XYFrame
 */
const extentHandler = (extentType, props, state, extentPosition) => {
    const invertedExtent = props[invertKeys[extentType]] || false;
    return (extentValue) => {
        const newState = buildNewState(state, extentValue, extentType, extentPosition, invertedExtent);
        state.changeRawExtents(newState.rawExtents);
        state.changeExtents(newState.extents);
        return extentValue;
    };
};
/**
 * Map hover annotations and extent to child. Initially the extent is an object with
 * an onChange handler however once each of those resolve we then create an
 * extent that matches between all of them. This logic can be found in createExtent and also
 * extentHandler
 */
const mapChildrenWithAppropriateProps = ({ child, index, originalAnnotations, props, state }) => {
    const childType = child.type;
    const frameType = childType.displayName;
    const annotations = generateChildAnnotations({
        state,
        props,
        originalAnnotations
    });
    const customProps = Object.assign(Object.assign({}, props), { annotations });
    if (!framePropHash[frameType]) {
        return React.cloneElement(child, { facetprops: customProps });
    }
    // pieceHoverAnnotation could be an object, so we need to be explicit in checking for true
    if (props.hoverAnnotation || props.pieceHoverAnnotation) {
        customProps.customHoverBehavior = (d) => {
            state.changeFacetHover(d);
            state.changeFacetHoverAnnotations(props.hoverAnnotation || props.pieceHoverAnnotation);
        };
    }
    if ((frameType === "OrdinalFrame" ||
        frameType === "ResponsiveOrdinalFrame" ||
        frameType === "SparkOrdinalFrame") &&
        props.sharedRExtent === true) {
        const invertedExtent = customProps[invertKeys["rExtent"]] || false;
        customProps.rExtent = createExtent("rExtent", props, state, index);
        customProps.onUnmount = () => {
            const newState = buildNewState(state, [], "rExtent", index, invertedExtent);
            state.changeRawExtents(newState.rawExtents);
            state.changeExtents(newState.extents);
        };
    }
    if ((frameType === "XYFrame" ||
        frameType === "ResponsiveXYFrame" ||
        frameType === "SparkXYFrame") &&
        props.sharedXExtent === true) {
        const invertedExtent = customProps[invertKeys["xExtent"]] || false;
        customProps.xExtent = createExtent("xExtent", props, state, index);
        customProps.onUnmount = () => {
            const newState = buildNewState(state, [], "xExtent", index, invertedExtent);
            state.changeRawExtents(newState.rawExtents);
            state.changeExtents(newState.extents);
        };
    }
    if ((frameType === "XYFrame" ||
        frameType === "ResponsiveXYFrame" ||
        frameType === "SparkXYFrame") &&
        props.sharedYExtent === true) {
        const invertedExtent = customProps[invertKeys["yExtent"]] || false;
        customProps.yExtent = createExtent("yExtent", props, state, index);
        customProps.onUnmount = () => {
            const newState = buildNewState(state, [], "yExtent", index, invertedExtent);
            state.changeRawExtents(newState.rawExtents);
            state.changeExtents(newState.extents);
        };
    }
    if (customProps.pieceHoverAnnotation) {
        customProps.pieceHoverAnnotation = [];
    }
    else if (customProps.hoverAnnotation) {
        customProps.hoverAnnotation = [];
    }
    return React.cloneElement(child, validFrameProps(customProps));
};
/**
 * Remove and add required annotation props for specific annotation types.
 */
const generateChildAnnotations = ({ originalAnnotations, state, props }) => {
    let annotationBase = state.facetHoverAnnotations;
    const { hoverAnnotation, pieceHoverAnnotation } = props;
    const annotationSettings = hoverAnnotation || pieceHoverAnnotation;
    if (!annotationSettings || !annotationBase) {
        return originalAnnotations;
    }
    if (state.facetHover) {
        const annotations = [...originalAnnotations];
        if (annotationSettings === true) {
            annotationBase = [Object.assign({}, state.facetHover)];
        }
        else {
            const annotationMap = annotationSettings;
            annotationBase = annotationMap.map((annotation) => {
                const decoratedAnnotation = typeof annotation === "function"
                    ? annotation(state.facetHover)
                    : Object.assign(Object.assign({}, state.facetHover), annotation);
                return decoratedAnnotation;
            });
        }
        if (Array.isArray(annotationBase)) {
            annotationBase.forEach((annotation) => {
                if (typeof annotation !== "function") {
                    if (annotation.type === "column-hover") {
                        annotation.facetColumn = annotation.column.name;
                        annotation.column = undefined;
                    }
                    else {
                        if (!annotation.type) {
                            annotation.type = "frame-hover";
                        }
                        annotation.y = undefined;
                        annotation.yBottom = undefined;
                        annotation.yMiddle = undefined;
                        annotation.yTop = undefined;
                    }
                }
            });
            annotations.push(...annotationBase);
        }
        return annotations;
    }
    return originalAnnotations;
};
function FacetController(props) {
    const [extents, changeExtents] = useState({});
    const [rawExtents, changeRawExtents] = useState({});
    const [facetHover, changeFacetHover] = useState(undefined);
    const [facetHoverAnnotations, changeFacetHoverAnnotations] = useState(undefined);
    return (React.createElement(React.Fragment, null, processFacetController(props, {
        extents,
        rawExtents,
        facetHover,
        facetHoverAnnotations,
        changeFacetHoverAnnotations,
        changeExtents,
        changeRawExtents,
        changeFacetHover
    })));
}

function useBoundingRect(ref) {
    const [rect, setRect] = useState(null);
    useLayoutEffect(() => {
        const element = ref.current;
        if (element != null) {
            setRect(element.getBoundingClientRect());
            // TypeScript 3.9 does not know about resize observer
            // @ts-ignore
            const observer = new ResizeObserver((entries) => {
                if (entries.length > 0) {
                    setRect(entries[0].contentRect);
                }
            });
            observer.observe(element);
            return () => observer.disconnect();
        }
    }, []);
    return rect;
}

const createResponsiveFrame = (ParticularFrame) => {
    function ResponsiveFrame(props) {
        const { responsiveWidth, responsiveHeight, size = [500, 500], dataVersion, debounce = 200, gridDisplay } = props, rest = __rest(props, ["responsiveWidth", "responsiveHeight", "size", "dataVersion", "debounce", "gridDisplay"]);
        const actualSize = [...size];
        let returnEmpty = false;
        let sceneRef = useRef();
        let rect = useBoundingRect(sceneRef);
        if (responsiveWidth) {
            if (rect == null)
                returnEmpty = true;
            else
                actualSize[0] = rect.width;
        }
        if (responsiveHeight) {
            if (rect == null)
                returnEmpty = true;
            else
                actualSize[1] = rect.height;
        }
        const dataVersionWithSize = dataVersion + actualSize.toString() + debounce;
        return (React.createElement("div", { "data-testid": "responsive-container", className: "responsive-container", style: gridDisplay
                ? { minWidth: "0px", minHeight: "0px" }
                : { height: "100%", width: "100%" }, ref: sceneRef }, !returnEmpty && (React.createElement(ParticularFrame, Object.assign({}, rest, { size: actualSize, dataVersion: dataVersion ? dataVersionWithSize : undefined })))));
    }
    ResponsiveFrame.isResizing = setTimeout(() => { });
    ResponsiveFrame.displayName = `Responsive${ParticularFrame.displayName}`;
    return ResponsiveFrame;
};

const ResponsiveNetworkFrame = 
/*#__PURE__*/ createResponsiveFrame(NetworkFrame);

const ResponsiveMinimapXYFrame = 
/*#__PURE__*/ createResponsiveFrame(MinimapXYFrame);

const ResponsiveXYFrame = /*#__PURE__*/ createResponsiveFrame(XYFrame);

const ResponsiveOrdinalFrame = 
/*#__PURE__*/ createResponsiveFrame(OrdinalFrame);

const allFrameDefaults = {
    margin: 0
};
function sparkNetworkSettings(originalSettings = "force") {
    let finalSettings = {};
    if (originalSettings) {
        finalSettings = originalSettings;
        if (originalSettings === "force")
            finalSettings = { type: "force" };
        return Object.assign({ edgeStrength: 2, edgeDistance: 5, nodePadding: 1, nodeWidth: 5, groupWidth: 4 }, finalSettings);
    }
    return originalSettings;
}
const axisDefaults = {
    tickFormat: () => "",
    baseline: false
};
const xyFrameDefaults = (props) => (Object.assign(Object.assign(Object.assign({}, allFrameDefaults), props), { hoverAnnotation: props.hoverAnnotation, axes: props.axes
        ? props.axes.map((a) => (Object.assign(Object.assign({}, axisDefaults), a)))
        : props.axes }));
const ordinalFrameDefaults = (props) => (Object.assign(Object.assign(Object.assign({}, allFrameDefaults), props), { hoverAnnotation: props.hoverAnnotation, axes: props.axes
        ? props.axes.map((a) => (Object.assign(Object.assign({}, axisDefaults), a)))
        : props.axes }));
const networkFrameDefaults = (props) => (Object.assign(Object.assign(Object.assign(Object.assign({}, allFrameDefaults), { nodeSizeAccessor: 2 }), props), { networkType: sparkNetworkSettings(props.networkType) }));
function createSparkFrame(Frame, defaults, frameName) {
    return (props) => {
        const { size = [], sparkStyle = {} } = props;
        const [containerHeight, changeContainerHeight] = useState(30);
        const actualSize = [];
        actualSize[0] =
            typeof size === "number" ? size : size[0] ? size[0] : containerHeight;
        actualSize[1] = containerHeight;
        const sparkNodeRef = useCallback((node) => {
            if (node) {
                const lineHeight = +window.getComputedStyle(node).lineHeight.split("px")[0] - 5;
                changeContainerHeight(isNaN(lineHeight) ? node.offsetHeight : lineHeight);
            }
        }, []);
        return (React.createElement("span", { style: Object.assign({
                width: `${actualSize[0]}px`,
                height: `${actualSize[1]}px`,
                display: "inline-block",
                marginLeft: "5px",
                marginRight: "5px"
            }, sparkStyle), ref: sparkNodeRef },
            React.createElement(Frame, Object.assign({}, defaults(props), { size: actualSize, useSpans: true }))));
    };
}

const SparkXYFrame = /*#__PURE__*/ createSparkFrame(XYFrame, xyFrameDefaults);

const SparkOrdinalFrame = /*#__PURE__*/ createSparkFrame(OrdinalFrame, ordinalFrameDefaults);

const SparkNetworkFrame = /*#__PURE__*/ createSparkFrame(NetworkFrame, networkFrameDefaults);

export { SemioticAnnotation as Annotation, AnnotationLayer, Axis, Brush, DividedLine, FacetController, InteractionLayer, Legend, SemioticMark as Mark, MiniMap, MinimapXYFrame, NetworkFrame, OrdinalFrame, ResponsiveMinimapXYFrame, ResponsiveNetworkFrame, ResponsiveOrdinalFrame, ResponsiveXYFrame, SparkNetworkFrame, SparkOrdinalFrame, SparkXYFrame, VisualizationLayer, XYFrame, calculateDataExtent, funnelize, heatmapping, hexbinning, nodesEdgesFromHierarchy };
